import { AllocBasis, AnyRow, BenchmarkLevel, BenchmarkMetricId, BenchmarkPeer, Cell, Compare, DataFlag, Grain, isVendorRow, Multiplier, OtbOnOrder, OtbPeriodLine, PlanNote, PriceBasis, ReorderBasis, ReportKey, RowAllocation, SourceAllocation, Status, StoreAllocation, Summary, TargetBasis, Tier, TrendBasis, TrendNote, ClassRow, Freshness, OtbBasis, VendorRow, type ClassReportPayload, Capabilities, ReportParams, VendorRowsParams, BenchmarkMetrics, BenchmarkPayload, BenchmarkSums } from '../../../interfaces/reports/WeeklyPlanning.interface';
import { FilterSelection } from '../../../hooks/usePendingFilterSelection';
import type { ReportColumnState, FilterOption, ChartSeries } from './ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SavedViewRecord } from '../../../services/reports/SavedViews.service';
import { downloadCsv } from '../downloadCsv';

// ===================================================================================================================
// ==== metrics.ts ====================================================================================================
// ===================================================================================================================
// Display conventions shared by the weekly planning reports: number / currency / cover formatting, the grain's unit wording,
// epoch weeks and months, CSV cell quoting and mojibake repair. A leaf module (imports only the interface) so the benchmark
// and metric catalogues can use it at module load.

export const EMPTY = '—';
export const MINUS = '−';
export const WEEKS_CAP = 52;
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A real number (not NaN, not null / undefined): the kit's one numeric guard. */
export const isNum = (v: unknown): v is number => typeof v === 'number' && !Number.isNaN(v);

const isNil = (v: unknown): v is null | undefined => v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));
const sign = (v: number, plus = false): string => (v < 0 ? MINUS : plus && v > 0 ? '+' : '');
const grouped = (v: number, digits = 0): string => Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
/** A value that would display as 0 is treated as 0, so "−$0" never appears. */
const zeroIfRoundsToZero = (v: number, digits = 0): number => (Number(Math.abs(v).toFixed(digits)) === 0 ? 0 : v);
const money = (v: number | null | undefined, plus: boolean, digits = 0): string => {
  if (isNil(v)) return EMPTY;
  const r = zeroIfRoundsToZero(v, digits);
  return `${sign(r, plus)}$${grouped(r, digits)}`;
};
const units = (v: number | null | undefined, plus: boolean): string => {
  if (isNil(v)) return EMPTY;
  const r = zeroIfRoundsToZero(v);
  return `${sign(r, plus)}${grouped(r)}`;
};

export const fmtCurrency = (v: number | null | undefined): string => money(v, false);
export const fmtCurrencyCents = (v: number | null | undefined): string => money(v, false, 2);
export const fmtDiffCurrency = (v: number | null | undefined): string => money(v, true);
export const fmtUnits = (v: number | null | undefined): string => units(v, false);
export const fmtDiffUnits = (v: number | null | undefined): string => units(v, true);
/** `ratio` is a fraction (0.457 → "45.7%"), never a percentage. */
export const fmtPct = (ratio: number | null | undefined, digits = 1): string => (isNil(ratio) ? EMPTY : `${sign(ratio)}${grouped(ratio * 100, digits)}%`);
/** A change as a signed percentage: "+12.5%", "−37.5%", "0.0%"; em dash for null. */
export const signedPct = (ratio: number | null | undefined): string => (isNil(ratio) ? EMPTY : ratio > 0 ? `+${fmtPct(ratio)}` : fmtPct(ratio));
export const fmtPctPts = (ratio: number | null | undefined): string => (isNil(ratio) ? EMPTY : `${sign(ratio)}${grouped(ratio * 100, 1)} pts`);
/**
 * Weeks (or months) of cover to one decimal, "<cap>+" above the cap (Monthly passes `coverCap`, 12, so it reads "12+"). A null
 * renders "—" here: showing "52+" for the no-sales case (`wos === null` with inventory > 0) is the column renderer's job.
 */
export const fmtWeeks = (v: number | null | undefined, cap: number = WEEKS_CAP): string => (isNil(v) ? EMPTY : v > cap ? `${cap}+` : grouped(v, 1));
/** The real weeks / months of cover to one decimal, never capped: a chart tooltip shows 70.0 where the axis reads "52+". */
export const fmtWeeksExact = (v: number | null | undefined): string => (isNil(v) ? EMPTY : grouped(v, 1));
export const fmtCover = (value: number | null | undefined, target: number | null | undefined, cap: number = WEEKS_CAP): string => `${fmtWeeks(value, cap)} / ${fmtWeeks(target, cap)}`;
/** "Mon d" from the date part of an ISO string, read as UTC. */
export const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return EMPTY;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? EMPTY : `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

/** Text a spreadsheet would run as a formula (=, +, @, tab, CR at the start); csvCell and the CSV label cells guard it with an apostrophe. */
export const CSV_FORMULA_RE = /^[=+@\t\r]/;

/**
 * As VendorPerformanceReport's csvCell: quotes a value that contains a comma, quote or line break (doubling inner quotes). Text
 * that a spreadsheet would run as a formula is prefixed with an apostrophe; a leading minus is a number, not a formula.
 */
export const csvCell = (value: string, isText = false): string => {
  const safe = isText && CSV_FORMULA_RE.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

// ---- units ---------------------------------------------------------------------------------------------------------------
// The units of the report follow the table's grain. Weekly: weeks of stock (WOS), the 52 weeks rule. Monthly: months of stock
// (MOS), the 12 months rule, the peers' figures too. Aging buckets (Aging 5–8w / 8+w) are a weeks definition and stay.

export const COVER_UNIT: Record<Grain, string> = { weekly: 'WOS', monthly: 'MOS' };
export const coverUnit = (grain: Grain | null | undefined): string => COVER_UNIT[grain === 'monthly' ? 'monthly' : 'weekly'];
/** The cover a table shows as "<cap>+": 52 weeks in Weekly, 12 months in Monthly. Display only: the figures are never capped. */
export const COVER_CAP: Record<Grain, number> = { weekly: 52, monthly: 12 };
export const coverCap = (grain: Grain | null | undefined): number => COVER_CAP[grain === 'monthly' ? 'monthly' : 'weekly'];

type Rule = [RegExp, string | ((m: string, ...rest: string[]) => string)];
// Order matters: the longest phrases first. The aging buckets ("received 5–8 weeks ago", "older than 12 weeks") match no rule.
const WEEKLY_RULES: Rule[] = [
  [/Weeks \(Weekly\) or months \(Monthly\) of stock: BOM inventory ÷ average sales per week or month/g, 'Weeks of stock: BOM inventory ÷ average sales per week'],
  [/Beginning of month \(or week\)/g, 'Beginning of week'],
  // Wendi (2026-10-22): the start-of-period inventory is BOW (beginning of week) in Weekly, BOM in Monthly.
  [/Beginning-of-period \(BOM\) inventory/g, 'Beginning-of-week (BOW) inventory'],
  [/Sales WTD \(week to date\) or Sales MTD \(month to date\)/g, 'Sales WTD (week to date)'],
  [/\bweek or month\b/g, 'week'],
  [/Weeks \(or months\) of stock \(WOS \/ MOS\)/g, 'Weeks of stock (WOS)'],
  [/\bweeks \(or months\)/g, 'weeks'],
  [/\bWeeks \(or months\)/g, 'Weeks'],
  [/\bweeks \(months\)/g, 'weeks'],
  [/\bWOS \/ MOS\b/g, 'WOS'],
  [/\bweeks and months\b/g, 'weeks'],
  // Wendi (2026-10-22): after the phrase rules, the bare tokens: BOW / WOS / weeks in Weekly, never BOM / MOS / months (LY BOM is a month figure).
  [/\bLY BOM\b/g, 'LY month-start'],
  [/\bBOM\b/g, 'BOW'],
  [/\bMOS\b/g, 'WOS'],
  [/\b[Mm]onths of (stock|supply)\b/g, (m: string) => `${m.startsWith('M') ? 'W' : 'w'}eeks of ${m.endsWith('stock') ? 'stock' : 'supply'}`],
];
const MONTHLY_RULES: Rule[] = [
  [/Weeks \(Weekly\) or months \(Monthly\) of stock: BOM inventory ÷ average sales per week or month/g, 'Months of stock: BOM inventory ÷ average sales per month'],
  [/Beginning of month \(or week\)/g, 'Beginning of month'],
  [/Beginning-of-period \(BOM\) inventory/g, 'Beginning-of-month (BOM) inventory'],
  [/month-end inventory ÷ sales per week/g, 'month-end inventory ÷ sales'],
  [/Sales WTD \(week to date\) or Sales MTD \(month to date\)/g, 'Sales MTD (month to date)'],
  [/\bweek or month\b/g, 'month'],
  [/Weeks \(or months\) of stock \(WOS \/ MOS\)/g, 'Months of stock (MOS)'],
  [/\bweeks \(or months\)/g, 'months'],
  [/\bWeeks \(or months\)/g, 'Months'],
  [/\bweeks \(months\)/g, 'months'],
  [/\bWOS \/ MOS\b/g, 'MOS'],
  [/\bweeks and months\b/g, 'months'],
  [/ per week \(sales ÷ 4\.33\)/g, ''],
  [/\blast 52 weeks\b/g, 'last 12 months'],
  [/"52 wk"/g, '"12 mo"'],
  [/\b52\+ weeks\b/g, '12+ months'], // before the bare rule: "52+ weeks" is a months phrase in Monthly, not "12+ weeks"
  [/\b52\+/g, '12+'],
  [/\bno sales in 52 weeks\b/g, 'no sales in 12 months'],
  [/\bWeeks of (stock|supply)\b/g, 'Months of $1'],
  [/\bweeks of (stock|supply)\b/g, 'months of $1'],
  [/\bWOS\b/g, 'MOS'],
  [/\bBOW\b/g, 'BOM'],
];

/** The text in the grain's own unit: "weeks (or months)" becomes "weeks" / "months", WOS / MOS the one, the 52 weeks rule 12 months in Monthly. */
export const unitize = (text: string, grain: Grain | null | undefined): string =>
  grain ? (grain === 'monthly' ? MONTHLY_RULES : WEEKLY_RULES).reduce((t, [re, to]) => (typeof to === 'string' ? t.replace(re, to) : t.replace(re, to)), text) : text;

// ---- start-of-period inventory (Wendi Tanner, 2026-10-22) -----------------------------------------------------------
// The inventory at the start of the period is "BOW" (beginning of week) in Weekly and "BOM" (beginning of month) in Monthly,
// wherever a column, metric, KPI, matrix line or CSV header names it. Without a grain it reads BOM (the neutral catalogue label).
export const bomAbbr = (grain: Grain | null | undefined): 'BOW' | 'BOM' => (grain === 'weekly' ? 'BOW' : 'BOM');
/** The grain's words in one place: the start-of-period inventory, the cover and its unit. Every user-facing string reads these (or goes through `unitize`). */
export interface PeriodLabels { bom: 'BOW' | 'BOM'; cover: 'WOS' | 'MOS'; unit: 'week' | 'month'; units: 'weeks' | 'months'; coverLong: 'weeks of stock' | 'months of stock' }
export const periodLabels = (grain: Grain | null | undefined): PeriodLabels =>
  grain === 'monthly'
    ? { bom: 'BOM', cover: 'MOS', unit: 'month', units: 'months', coverLong: 'months of stock' }
    : { bom: 'BOW', cover: 'WOS', unit: 'week', units: 'weeks', coverLong: 'weeks of stock' };
export const bomInventoryLabel = (grain: Grain | null | undefined): string => `${bomAbbr(grain)} inventory`;
export const bomShortLabel = (grain: Grain | null | undefined): string => `${bomAbbr(grain)} Inv`;
export const bomTooltip = (grain: Grain | null | undefined): string => (grain === 'weekly' ? 'beginning of week' : 'beginning of month');

// ---- epoch weeks and months ----------------------------------------------------------------------------------------------
// Epoch weeks as utils.dim_date defines them: week 0 starts Sunday 1969-12-28 (UTC), weeks run Sunday to Saturday. Epoch
// months are months since 1970-01. Everything here is UTC so a label never shifts with the viewer's timezone.

const DAY_MS = 86_400_000;
const EPOCH_SHIFT_DAYS = 4; // 1970-01-01 is a Thursday: four days after week 0's Sunday

/**
 * Epoch week containing the UTC instant `d` (floored to its UTC day). Pass a UTC-midnight Date, `new Date(Date.UTC(y, m, d))`,
 * never `new Date(y, m, d)`: a local-midnight Date east of UTC is the previous UTC day and lands a Sunday in the week before.
 */
export const epochWeekOf = (d: Date): number => Math.floor((Math.floor(d.getTime() / DAY_MS) + EPOCH_SHIFT_DAYS) / 7);
export const epochWeekStart = (week: number): Date => new Date((week * 7 - EPOCH_SHIFT_DAYS) * DAY_MS);
export const epochWeekEnd = (week: number): Date => new Date((week * 7 - EPOCH_SHIFT_DAYS + 6) * DAY_MS);

/**
 * The epoch week or month containing an ISO date ('2026-10-15'), read as a calendar date: only the YYYY-MM-DD part counts,
 * never an instant, so the server's America/Los_Angeles date is kept as is.
 */
export const periodContaining = (isoDate: string, grain: Grain): number => {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
  return grain === 'weekly' ? epochWeekOf(new Date(Date.UTC(y, m - 1, d))) : (y - 1970) * 12 + (m - 1);
};

/** The epoch week of today's local calendar date. */
export const currentEpochWeek = (): number => {
  const now = new Date();
  return epochWeekOf(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
};

/**
 * The current epoch month from the UTC calendar, for a page that needs a Monthly `currentPeriod` before the first payload
 * arrives. Not `epochMonthOfWeek(currentEpochWeek)`, which gives the month the current week STARTED in.
 */
export const currentEpochMonth = (now: Date = new Date()): number => (now.getUTCFullYear() - 1970) * 12 + now.getUTCMonth();

export const formatWeekLabel = (week: number, opts: { withYear?: boolean } = {}): string => {
  const s = epochWeekStart(week);
  const e = epochWeekEnd(week);
  const right = s.getUTCMonth() === e.getUTCMonth() ? `${e.getUTCDate()}` : `${MONTHS[e.getUTCMonth()]} ${e.getUTCDate()}`;
  return `${MONTHS[s.getUTCMonth()]} ${s.getUTCDate()}–${right}${opts.withYear ? `, ${e.getUTCFullYear()}` : ''}`;
};

export const formatPeriodLabel = (period: number, grain: Grain): string =>
  grain === 'weekly' ? formatWeekLabel(period) : `${MONTHS[period % 12]} ${1970 + Math.floor(period / 12)}`;

const epochMonthStart = (epochMonth: number): Date => new Date(Date.UTC(1970 + Math.floor(epochMonth / 12), epochMonth % 12, 1));

/** Epoch weeks whose Sunday falls inside the epoch month (what the backend calls weeksStartingInMonth). */
export const weeksOfMonth = (epochMonth: number): number[] => {
  const first = epochMonthStart(epochMonth);
  const next = epochMonthStart(epochMonth + 1);
  const out: number[] = [];
  for (let w = epochWeekOf(first); epochWeekStart(w) < next; w++) {
    if (epochWeekStart(w) >= first) out.push(w);
  }
  return out;
};

/**
 * Epoch month of the week's Sunday. Computed from UTC parts rather than EpochMonthService.dateToEpochMonth, which reads the
 * Date in local time via moment and would drift by a month for viewers west of UTC on the first of a month.
 */
export const epochMonthOfWeek = (week: number): number => {
  const s = epochWeekStart(week);
  return (s.getUTCFullYear() - 1970) * 12 + s.getUTCMonth();
};

// ---- mojibake ------------------------------------------------------------------------------------------------------------
// Product and vendor titles sometimes reach us as UTF-8 text that was read as Mac Roman ("Pav√©" for "Pavé") or as
// Windows-1252 / Latin-1 ("PavÃ©"). A title is repaired only when it carries a telltale lead character of those misreadings
// (Mac Roman "√ ¬ ‚"; Windows-1252 "Ã Â â") AND every character maps back to a byte AND the bytes are valid UTF-8 with no
// replacement character. Anything else is returned unchanged.
const MAC_ROMAN_HIGH = 'ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄€‹›ﬁﬂ‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ';
const WIN_1252_C1 = '€�‚ƒ„…†‡ˆ‰Š‹Œ�Ž��‘’“”•–—˜™š›œ�žŸ';
const MAC_ROMAN_LEADS = /[√¬‚]/;
const WIN_1252_LEADS = /[ÃÂâ]/;
const MAC_ROMAN_BYTES = new Map<string, number>(Array.from(MAC_ROMAN_HIGH).map((ch, i) => [ch, 0x80 + i]));
const WIN_1252_BYTES = new Map<string, number>([...Array.from(WIN_1252_C1).map((ch, i): [string, number] => [ch, 0x80 + i]), ...Array.from({ length: 96 }, (_, i): [string, number] => [String.fromCharCode(0xa0 + i), 0xa0 + i])].filter(([ch]) => ch !== '�'));

const decodeVia = (s: string, table: Map<string, number>): string | null => {
  const bytes: number[] = [];
  for (const ch of s) {
    const code = ch.codePointAt(0) as number;
    const b = code < 0x80 ? code : table.get(ch);
    if (b === undefined) return null;
    bytes.push(b);
  }
  try {
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(bytes));
    return decoded.includes('�') ? null : decoded;
  } catch {
    return null;
  }
};

/** A product / vendor title with UTF-8-read-as-Mac-Roman / Windows-1252 mojibake repaired; other text is returned as is. */
export const fixMojibake = (value: string | null | undefined): string => {
  if (!value) return value ?? '';
  const viaMac = MAC_ROMAN_LEADS.test(value) ? decodeVia(value, MAC_ROMAN_BYTES) : null;
  if (viaMac !== null && viaMac !== value) return viaMac;
  const viaWin = WIN_1252_LEADS.test(value) ? decodeVia(value, WIN_1252_BYTES) : null;
  return viaWin !== null && viaWin !== value ? viaWin : value;
};

// Metric formulas shared by the three weekly planning reports, mirroring
// laravel/code/app/Services/Reports/WeeklyPlanning/Metrics.php (spec section 4.5).
//
// Same rules as the PHP: pure and unrounded (round at display time), any denominator that is not
// strictly positive beyond EPSILON gives null (never 0, Infinity or a sign-flipped number), negative
// inventory gives null while zero inventory is a real 0, and every ratio sums its parts before
// dividing. One deliberate difference: ratios here are FRACTIONS (0.25), not the PHP percentages
// (25.0), because the kit's formatters (fmtPct, fmtPctPts) take fractions.

const EPSILON = 1e-9;

/** Strictly positive beyond float-cancellation noise: the single "computable denominator" rule. */
export const isPositive = (v: number): boolean => v > EPSILON;
/**
 * the smallest baseline a "vs plan / LY / average" percentage is worked out against (one dollar or unit). A plan of 0, or
 * of a rounding crumb, gave "+633,343,100.0%"; below this the percentage is "—" (or "new" where something happened: see vsCompareCell).
 */
export const MIN_PCT_BASE = 1;
export const isUsablePctBase = (baseline: number): boolean => baseline >= MIN_PCT_BASE;

/** The numeric measures of a period cell (`-?` so Cell's optional keys do not add `undefined`). */
export type Measure = { [K in keyof Cell]-?: Cell[K] extends number | null | undefined ? K : never }[keyof Cell];
/** Measures that have a plan and a last-year counterpart. */
// Inventory compares at the START of the period: plan inventory and LY inventory (`inventoryLy`, last year's BOM) are
// both start-of-period figures, and M1 compares BOM only with BOM.
export type ComparableMeasure = 'sales' | 'inflow' | 'inventoryStart' | 'markdowns';
export type Periods = Record<string, Cell>;

/** Sum of a measure over the given periods, skipping nulls; null when no period has a value. */
export const sumCells = (periods: Periods, measure: Measure, keys: number[]): number | null => {
  let sum = 0;
  let any = false;
  for (const k of keys) {
    const v = periods[String(k)]?.[measure];
    if (isNum(v)) {
      sum += v;
      any = true;
    }
  }
  return any ? sum : null;
};

/**
 * Average of a measure over the given periods. Every period counts in the denominator (a null is a
 * zero for the average, as the PHP's "one entry per period, zeros included" rule requires); null when
 * there are no periods or no period has a value. Note the asymmetry with the payload: an all-null
 * lookback gives null here, whereas the backend's `summary.avgPeriodSales` is 0.0 for the same row.
 */
export const avgOverPeriods = (periods: Periods, measure: Measure, keys: number[]): number | null => {
  if (keys.length === 0) return null;
  const sum = sumCells(periods, measure, keys);
  return sum === null ? null : sum / keys.length;
};

/** A row's value as a fraction of its parent row's value (Metrics::pctOfParent ÷ 100). */
export const pctOfParent = (value: number, parentValue: number): number | null =>
  isPositive(parentValue) ? value / parentValue : null;

/** SUM(numerators) ÷ SUM(denominators) as a fraction (Metrics::ratioPct ÷ 100). */
export const ratioPct = (numerators: number[], denominators: number[]): number | null => {
  const den = denominators.reduce((a, b) => a + b, 0);
  if (!isPositive(den)) return null;
  return numerators.reduce((a, b) => a + b, 0) / den;
};

/** (current − baseline) ÷ baseline as a fraction (Metrics::pctChange ÷ 100). */
export const pctChange = (current: number, baseline: number): number | null =>
  isUsablePctBase(baseline) ? (current - baseline) / baseline : null;

/** Inflow plan − on order (within the OTB span). May be negative (overbought). */
export const otb = (inflowPlan: number, onOrder: number): number => inflowPlan - onOrder;

// Backend mirrors (ground rule: Metrics ↔ metrics.ts). The reports read summary.deviation / summary.otbDev from the
// payload; nothing in the browser recomputes them today. Keep these in step with Metrics.php if anything starts to.
/** Metrics::deviation: plan BOM − actual BOM; negative when over plan, null when either side is unknown. */
export const deviation = (planBom: number | null | undefined, actualBom: number | null | undefined): number | null =>
  isNum(planBom) && isNum(actualBom) ? planBom - actualBom : null;

/** Metrics::otbDev: OTB + deviation; null when either is unknown. */
export const otbDev = (otbValue: number | null | undefined, dev: number | null | undefined): number | null =>
  isNum(otbValue) && isNum(dev) ? otbValue + dev : null;

const PLAN_FIELD: Record<ComparableMeasure, Measure> = { sales: 'salesPlan', inflow: 'inflowPlan', inventoryStart: 'inventoryPlan', markdowns: 'markdownsPlan' };
const LY_FIELD: Record<ComparableMeasure, Measure> = { sales: 'salesLy', inflow: 'inflowLy', inventoryStart: 'inventoryLy', markdowns: 'markdownsLy' };

/**
 * The comparison baseline for a cell's measure: its plan, its last-year value, or the average of the
 * measure over the lookback periods (`ctx` required for 'avg'; null without it).
 */
export const compareValue = (
  cell: Cell,
  measure: ComparableMeasure,
  compare: Compare,
  ctx?: { periods: Periods; lookback: number[] },
): number | null => {
  if (compare === 'plan') return cell[PLAN_FIELD[measure]] ?? null;
  if (compare === 'ly') return cell[LY_FIELD[measure]] ?? null;
  return ctx ? avgOverPeriods(ctx.periods, measure, ctx.lookback) : null;
};

/**
 * Class-level fresh %: inflow over the lookback ÷ the current period's ending inventory, as a
 * fraction capped at 1 (Metrics::freshPct ÷ 100). Null when the inflow is unknown or the inventory
 * is not positive. The denominator is the current period's `inventoryEnd` (today's on-hand), not
 * `summary.inventory`, which is the inventory at the start of the target period.
 */
export const classFreshPct = (periods: Periods, lookback: number[], currentPeriod: number): number | null => {
  const inflow = sumCells(periods, 'inflow', lookback);
  const inventory = periods[String(currentPeriod)]?.inventoryEnd;
  if (inflow === null || !isNum(inventory) || !isPositive(inventory)) return null;
  return Math.min(1, inflow / inventory);
};

/**
 * Cover in periods: inventory ÷ average sales over the lookback (Metrics::wos / Metrics::mos,
 * weeks in Weekly mode and months in Monthly mode). Null for negative or unknown inventory, an empty
 * lookback, or an average that is not positive (no sales in the lookback).
 */
export const coverPeriods = (inventory: number | null | undefined, periods: Periods, lookback: number[]): number | null => {
  if (!isNum(inventory) || inventory < 0) return null;
  const avg = avgOverPeriods(periods, 'sales', lookback);
  if (avg === null || !isPositive(avg)) return null;
  return inventory / avg;
};

// (P1-11): the ONE price-basis rule of the three weekly planning reports. Every value cell, KPI, chart value
// and row detail reads a dollar figure through `valueOnBasis` / `summaryOnBasis`, so Weekly Action, Inventory Health and
// Plan & OTB show the same number for the same class and a row never mixes cost and retail.
//
// - Retail: the retail field, as before.
// - Cost: the field's own cost figure from the payload, never retail × a markup or a cost complement (M1: "If cost isn't in
// the export, say so"). The cost figures the payload carries:
// cells: inflowCost (inflows at cost), inflowCostPlan (Monthly: V3 inflow_cost_plan / ReportDataSource inflowPlanCost;
// Weekly V3 plans have none), inventoryCostStart / inventoryCostEnd (class rows Tiers A/B: facts InventoryCost and
// sku_detail cost on hand, back-calculated in Weekly with inflowCost − cogs; vendor / SKU rows: units × cost per unit;
// Tier C: none);
// summary: onOrderCost, otbCost (Monthly class rows: plan inflow at cost − received at cost − on order at cost), with its
// inputs inflowCostPlanSpan / receivedCostToDate / otbOnOrderCost; row: onOrderCost.
// Sales, Sales Trend, plan sales, plan inventory, markdowns, the LY figures and OTB + Dev have no cost figure (plans carry
// no BOM or sales at cost), so on the cost basis they read "—" titled NOT_AT_COST_TOOLTIP.
// - "—" with that title only when the retail figure exists: an empty retail value stays a plain "—".
// - Ratios (WOS, vs plan, vs compare, Trend %, Fresh %, % of Total, aging) and statuses are unit-consistent and do not
// change with the basis; statuses and actions read the retail summary on every basis.
// - Totals at cost (`costTotal`) are null when any member has the retail figure but not its cost: no partial cost totals.

export const NOT_AT_COST_TOOLTIP = 'Not available at cost: the data has no cost figure for this value, so it is left blank rather than shown at retail.';
/** One KPI note for OTB + Dev on the cost basis (plans carry no BOM at cost). */
export const OTB_DEV_NOT_AT_COST = 'Not available at cost: plans have no BOM at cost';
/** The OTB KPI's note on the cost basis when no cost plan exists (Weekly V3 plans carry no inflow at cost). */
export const OTB_NOT_AT_COST = 'Not available at cost: the plan has no inflows at cost';

/** The dollar fields of a period cell that the reports display. */
export type CellBasisField = 'sales' | 'salesPlan' | 'markdowns' | 'markdownsPlan' | 'inflow' | 'inflowPlan' | 'inventoryStart' | 'inventoryEnd' | 'inventoryPlan' | 'salesLy' | 'markdownsLy' | 'inflowLy' | 'inventoryLy';
/** The dollar fields of a row summary that the reports display. */
export type SummaryBasisField = 'onOrder' | 'otb' | 'otbDev' | 'inflowPlanSpan' | 'receivedToDate' | 'otbOnOrder' | 'otbReceived' | 'onOrderNoOtb';

/** Each cell field's cost twin; absent = no cost figure exists. */
export const CELL_COST_FIELD: Partial<Record<CellBasisField, keyof Cell>> = {
  inflow: 'inflowCost', inflowPlan: 'inflowCostPlan', inventoryStart: 'inventoryCostStart', inventoryEnd: 'inventoryCostEnd',
};
/** Each summary field's cost twin; absent = no cost figure exists. */
export const SUMMARY_COST_FIELD: Partial<Record<SummaryBasisField, keyof Summary>> = {
  onOrder: 'onOrderCost', otb: 'otbCost', inflowPlanSpan: 'inflowCostPlanSpan', receivedToDate: 'receivedCostToDate', otbOnOrder: 'otbOnOrderCost',
  otbReceived: 'otbReceivedCost', onOrderNoOtb: 'onOrderNoOtbCost',
};

/**
 * A value on a basis. `available` is false when the basis is cost and the retail figure exists but has no cost figure
 * (the cell then reads "—" titled NOT_AT_COST_TOOLTIP).
 */
export interface BasisValue { value: number | null; basis: PriceBasis; available: boolean }

/** The core rule: retail as is; cost when it is a number, else unavailable (when there was a retail figure to show). */
export const pickBasis = (retail: number | null | undefined, cost: number | null | undefined, basis: PriceBasis): BasisValue => {
  if (basis !== 'cost') return { value: isNum(retail) ? retail : null, basis, available: true };
  if (isNum(cost)) return { value: cost, basis, available: true };
  return { value: null, basis, available: !isNum(retail) };
};

/** A period cell's dollar field on the basis (see the header). */
export const valueOnBasis = (cell: Cell | undefined | null, field: CellBasisField, basis: PriceBasis): BasisValue => {
  const retail = cell ? (cell[field] as number | null | undefined) : null;
  const costField = CELL_COST_FIELD[field];
  return pickBasis(retail, costField && cell ? (cell[costField] as number | null | undefined) : null, basis);
};

/** A summary's dollar field on the basis; on order falls back to the row's own on order fields (rows without a summary). */
export const summaryOnBasis = (row: AnyRow, field: SummaryBasisField, basis: PriceBasis): BasisValue => {
  const s = row.summary;
  const costField = SUMMARY_COST_FIELD[field];
  let retail = s ? (s[field] as number | null | undefined) : null;
  let cost = s && costField ? (s[costField] as number | null | undefined) : null;
  if (field === 'onOrder') {
    retail = retail ?? row.onOrderRetail;
    cost = cost ?? row.onOrderCost;
  }
  if (field === 'otbOnOrder' && s && s.otbOnOrder === undefined) {
    // Older summaries: the OTB nets the whole on order.
    retail = s.onOrder;
    cost = s.otbOnOrderCost !== undefined ? s.otbOnOrderCost : s.onOrderCost;
  }
  if (field === 'otbReceived' && s && s.otbReceived === undefined) {
    // Rows that are not merged: the OTB nets all the received.
    retail = s.receivedToDate;
    cost = s.otbReceivedCost !== undefined ? s.otbReceivedCost : s.receivedCostToDate;
  }
  return pickBasis(retail, cost, basis);
};

/** A displayed value's text and title: "—" titled NOT_AT_COST_TOOLTIP when not available on the basis. */
export const basisText = (v: BasisValue, fmt: (n: number | null | undefined) => string): { text: string; title?: string } =>
  (v.available ? { text: fmt(v.value) } : { text: EMPTY, title: NOT_AT_COST_TOOLTIP });

/**
 * Σ of members on a basis: null when nothing is known, and on the cost basis null as soon as one member has the retail
 * figure but not its cost (a partial cost total would read as the whole at cost). `available` false in that case.
 */
export const costTotal = (values: BasisValue[]): BasisValue & { value: number | null } => {
  const basis = values[0]?.basis ?? 'retail';
  if (values.some((v) => !v.available)) return { value: null, basis, available: false };
  const nums = values.map((v) => v.value).filter(isNum);
  return { value: nums.length === 0 ? null : nums.reduce((a, b) => a + b, 0), basis, available: true };
};

/**
 * the plan / last-year / average a SALES compare line ("vs $1,007") is drawn against, on the basis. Sales and
 * their plans, last-year figures and averages exist at retail only (no cost twin), so at cost the figure is not available and the
 * line is hidden, never the retail plan under a cost view. (A measure with a cost plan, e.g. Inflows' `inflowPlan`, reads
 * `valueOnBasis` instead: its cost plan when one exists, else "—".)
 */
export const salesCompareOnBasis = (base: number | null | undefined, basis: PriceBasis): BasisValue => pickBasis(base, null, basis);

/** The CSV header's basis mark on a dollar column: "On order (cost)" / "On order (retail)". */
export const basisSuffix = (basis: PriceBasis): string => (basis === 'cost' ? ' (cost)' : ' (retail)');

// Sales Trend in the browser (decision D). The backend puts trendPct / salesTrend /
// trendBasis (+ trendNote on 'lastYear' cells) on the cells of a trend period — the period containing
// the payload's salesThrough (its last sales day) and any later period that has started — and nulls
// elsewhere. Those flat fields are the 'auto' method (= 'plan'); `trendAlt.lastYear` /
// `trendAlt.linear` carry the other methods' values in the same payload.
//
// Rules recorded here:
// - EVERY trend consumer (columns, comparisons, KPI, charts, row detail, CSV, Plan & OTB's matrix)
// reads through `trendOf(cell, method)` (or `comparableSales`, built on it), so the method
// selector (Part B) only has to set `CellContext.trendMethod`. 'auto' / 'plan' → the flat
// fields; 'lastYear' / 'linear' → `trendAlt[method]`, falling back to the flat fields when the cell
// has no trendAlt (Tier C, older payloads).
// - salesTrend is always READ from the cell, never re-derived as sales ÷ trendPct: an Unallocated
// row's Sales Trend is a remainder (class Sales Trend − Σ SKU Sales Trend).
// - Subtotals built here follow the backend's aggregate rule exactly (TrendService::aggregateFields):
// Σ salesTrend; trendPct = Σ sales of the children that trend and carry a salesTrend ÷ that Σ,
// clamped to 0..1, or the children's common trendPct when Σ salesTrend is 0 or every child is
// 'projected' (Tier C, never trended again); basis = the children's common basis, else 'mixed';
// trendNote (lastYear only) = the children's common note, else null; trendAlt per method likewise.
// - Comparisons in a trend period (vs Plan = the full-period plan, vs LY = the full LY period, vs Avg
// = the lookback average; the Sales Trend KPI; the sales variance chart; Plan & OTB's Sales Act) use
// `comparableSales` (Sales Trend) instead of sales. Cover / WOS keep using lookback actuals. Sales
// Trend is derived by the backend from RETAIL sales, so it is compared with the retail plan at any
// price basis.

/** methods: 'auto' (default) and 'plan' read the flat fields; the others read `trendAlt`. */
export type TrendMethod = 'auto' | 'plan' | 'lastYear' | 'linear';
export const TREND_METHODS: readonly TrendMethod[] = ['auto', 'plan', 'lastYear', 'linear'];
/** The selector's wording (Part B). */
export const TREND_METHOD_LABELS: Record<TrendMethod, string> = { auto: 'Auto', plan: 'By plan', lastYear: 'By last year', linear: 'Days elapsed' };
/** The How it works paragraph: the four methods and the fallback chain. */
export const TREND_METHODS_HELP =
  'Trended sales project the period still in progress from its sales so far (sales so far ÷ % of period elapsed). Auto uses the plan’s daily sales pattern where the class has one (or its parent’s plan), otherwise last year’s sales on the same weekdays (the class, else the store), otherwise simply the days elapsed. '
  + 'By plan, By last year and Days elapsed let you pick the source yourself; if your pick has no usable data for a class, the next source is used and the cell’s tooltip says which one (“requested: …”). '
  + 'Every option comes with the report, so switching never reloads; your choice is kept in saved views and share links.';
/** The basis a method asks for first; 'auto' asks for nothing in particular. */
const METHOD_BASIS: Record<TrendMethod, TrendBasis | null> = { auto: null, plan: 'dailyWeights', lastYear: 'lastYear', linear: 'linear' };

/** planNote (flat fields only, never trendAlt): 'planShapeZero' when the plan's weights had no elapsed sales; absent otherwise. */
export interface TrendValues { trendPct: number | null; salesTrend: number | null; trendBasis: TrendBasis | null; trendNote: TrendNote | null; planNote?: PlanNote | null }
/** The trend fields an aggregate cell carries (trendNote only on 'lastYear', planNote only when a child carries it, trendAlt only when the children carry it). */
export type AggregateTrend = Pick<Cell, 'trendPct' | 'salesTrend' | 'trendBasis' | 'trendNote' | 'planNote' | 'trendAlt'>;

const NO_TREND: TrendValues = { trendPct: null, salesTrend: null, trendBasis: null, trendNote: null };
const ALT_METHODS = ['lastYear', 'linear'] as const;

const BASIS_LABELS: Record<TrendBasis, string> = {
  dailyWeights: "Trend from the plan's daily sales pattern",
  lastYear: "Trend from last year's daily sales, same weekdays",
  linear: 'Days elapsed (standard trend)',
  projected: 'ORBIT projection',
  mixed: 'Mixed sources',
};

/** The Sales Trend / Trend % cell title: the basis in words; 'lastYear' adds "(class)" or "(store)" by its note. */
export const trendBasisLabel = (basis: TrendBasis | null | undefined, note?: TrendNote | null): string => {
  if (!basis) return '';
  const label = BASIS_LABELS[basis] ?? '';
  return basis === 'lastYear' && note ? `${label} (${note})` : label;
};

/** What a planShapeZero cell used instead of the plan's weights, by its basis. */
const PLAN_SHAPE_ZERO_USED: Partial<Record<TrendBasis, string>> = { lastYear: "last year's shape was used", linear: 'days elapsed were used' };

/**
 * The Sales Trend / Trend % cell tooltip (Part B): the basis actually used, plus
 * "(requested: <method>)" when a chosen method fell down the chain for this cell (By plan chosen, the
 * class had no V3 weights and trended by last year; a 'mixed' aggregate under an explicit method).
 * Never for 'auto' (it asks for nothing specific) nor for Tier C's projection (never trended again).
 * A 'planShapeZero' note appends why the plan's weights were not used — its daily
 * shape has no sales through `salesThrough` (the payload's last sales day; "this date" when unknown) —
 * so the cell is not read as missing plan data.
 */
export const trendTitle = (t: TrendValues, method: TrendMethod = 'auto', salesThrough?: string | null, lag?: string | null): string => {
  const label = trendBasisLabel(t.trendBasis, t.trendNote);
  if (!label) return label;
  const withLag = (s: string): string => (lag ? `${s} — ${lag}` : s);
  const wanted = METHOD_BASIS[method];
  const base = !wanted || t.trendBasis === wanted || t.trendBasis === 'projected' ? label : `${label} (requested: ${TREND_METHOD_LABELS[method]})`;
  if (t.planNote !== 'planShapeZero') return withLag(base);
  const through = salesThrough ? fmtDate(salesThrough) : 'this date';
  const used = (t.trendBasis && PLAN_SHAPE_ZERO_USED[t.trendBasis]) || 'another basis was used';
  return withLag(`${base} — the plan's daily shape has no sales through ${through}, so ${used}`);
};

/** The cell belongs to a trend period (the backend set a basis). Method-independent: every method trends the same periods. */
export const isTrendCell = (c: Cell | null | undefined): boolean => !!c && c.trendBasis !== null && c.trendBasis !== undefined;

/** The trend values of a cell for a method (see the header); all null outside a trend period. */
export const trendOf = (c: Cell | null | undefined, method: TrendMethod = 'auto'): TrendValues => {
  if (!c || !isTrendCell(c)) return NO_TREND;
  const alt = method === 'lastYear' || method === 'linear' ? c.trendAlt?.[method] : undefined;
  const src = alt ?? c;
  const out: TrendValues = {
    trendPct: isNum(src.trendPct) ? src.trendPct : null,
    salesTrend: isNum(src.salesTrend) ? src.salesTrend : null,
    trendBasis: src.trendBasis ?? null,
    trendNote: ('trendNote' in src ? src.trendNote : null) ?? null,
  };
  if (!alt && c.planNote) out.planNote = c.planNote; // the flat fields only: the other chains never use the plan's weights
  return out;
};

/**
 * Round 2: whether the trend column runs on the plan's daily weights: more than half of the rows that trend use that basis for the
 * method (so "% of period elapsed" reads "% of plan elapsed"; Days elapsed and last-year trends keep the days-elapsed wording).
 */
export const usesPlanWeights = (rows: { periods: Record<string, Cell | undefined> }[], period: number, method: TrendMethod = 'auto'): boolean => {
  let trended = 0;
  let weighted = 0;
  rows.forEach((r) => {
    const c = r.periods[String(period)];
    if (!isTrendCell(c)) return;
    trended++;
    if (trendOf(c, method).trendBasis === 'dailyWeights') weighted++;
  });
  return trended > 0 && weighted * 2 > trended;
};

/** Sales to compare with plan / LY / the lookback average: Sales Trend in a trend period, actual sales otherwise. */
export const comparableSales = (c: Cell | null | undefined, method: TrendMethod = 'auto'): number | null => {
  if (!c) return null;
  if (isTrendCell(c)) return trendOf(c, method).salesTrend;
  return isNum(c.sales) ? c.sales : null;
};

type CoreCell = { sales?: number | null; trendPct?: number | null; salesTrend?: number | null; trendBasis?: TrendBasis | null; trendNote?: TrendNote | null; planNote?: PlanNote | null };

/** TrendService::aggregateCore. */
const aggregateCore = (sales: number | null | undefined, children: CoreCell[]): Pick<Cell, 'trendPct' | 'salesTrend' | 'trendBasis' | 'trendNote' | 'planNote'> => {
  let sum: number | null = null;
  let trendingSales = 0;
  let childSales = false;
  const pcts: (number | null)[] = [];
  const bases = new Set<TrendBasis>();
  const notes = new Map<string, TrendNote | null>();
  const planNotes = new Map<string, PlanNote | null>();
  let anyPlanNote = false;
  children.forEach((c) => {
    if (c.trendBasis === null || c.trendBasis === undefined) return;
    bases.add(c.trendBasis);
    notes.set(c.trendNote ?? '', c.trendNote ?? null);
    planNotes.set(c.planNote ?? '', c.planNote ?? null);
    anyPlanNote = anyPlanNote || (c.planNote !== null && c.planNote !== undefined);
    pcts.push(isNum(c.trendPct) ? c.trendPct : null);
    if (isNum(c.salesTrend)) {
      sum = (sum ?? 0) + c.salesTrend;
      if ('sales' in c) {
        childSales = true;
        trendingSales += isNum(c.sales) ? c.sales : 0;
      }
    }
  });
  if (bases.size === 0) return { trendPct: null, salesTrend: null, trendBasis: null };
  const basis: TrendBasis = bases.size === 1 ? Array.from(bases)[0] : 'mixed';
  const common = pcts.every((p) => p === pcts[0]) ? pcts[0] : null;
  const numerator = childSales ? trendingSales : isNum(sales) ? sales : null;
  const total = sum as number | null;
  const trendPct = basis !== 'projected' && total !== null && total > EPSILON && numerator !== null ? Math.max(0, Math.min(1, numerator / total)) : common;
  const out: Pick<Cell, 'trendPct' | 'salesTrend' | 'trendBasis' | 'trendNote' | 'planNote'> = { trendPct, salesTrend: total, trendBasis: basis };
  if (basis === 'lastYear') out.trendNote = notes.size === 1 ? Array.from(notes.values())[0] : null;
  if (anyPlanNote) out.planNote = planNotes.size === 1 ? Array.from(planNotes.values())[0] : null; // common to every trending child, else null
  return out;
};

/** Trend fields of an aggregate cell from its children's cells, trendAlt included (see the header). */
export const aggregateTrend = (sales: number | null | undefined, children: Cell[]): AggregateTrend => {
  const out: AggregateTrend = aggregateCore(sales, children);
  const withAlt = children.filter((c) => !!c.trendAlt);
  if (out.trendBasis !== null && withAlt.length > 0) {
    const alt = {} as Record<(typeof ALT_METHODS)[number], CoreCell>;
    ALT_METHODS.forEach((m) => {
      // The child's sales only when the cell carries the key (PHP: array_key_exists('sales', $c)).
      const core = aggregateCore(sales, withAlt.map((c) => ({ ...(c.trendAlt?.[m] ?? {}), ...('sales' in c ? { sales: c.sales } : {}) })));
      const entry: CoreCell = { trendPct: core.trendPct ?? null, salesTrend: core.salesTrend ?? null, trendBasis: core.trendBasis ?? null };
      if (m === 'lastYear') entry.trendNote = core.trendNote ?? null;
      alt[m] = entry;
    });
    out.trendAlt = alt as unknown as Cell['trendAlt'];
  }
  return out;
};

// ---- In-progress periods -------------------------------------------------------------
// A trend period is in progress through `salesThrough` (the last sales day). Inflows and markdowns are
// measured, not trended, so they are compared with the plan PRORATED by days elapsed (spec 4.5): elapsed
// days ÷ the period's days, elapsed counting up to salesThrough. Labelled WTD (week) / MTD (month).
// Proration is impossible (the comparison is left blank / uncoloured) when the cell is a trend cell but
// no sales day is known (Tier C has no daily data) or no day of the period has elapsed yet.
const dayOf = (iso: string): number => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d) / DAY_MS;
};
const periodSpan = (period: number, grain: 'weekly' | 'monthly'): { start: number; days: number } => {
  if (grain === 'weekly') return { start: epochWeekStart(period).getTime() / DAY_MS, days: 7 };
  const year = 1970 + Math.floor(period / 12);
  const month = period % 12;
  const start = Date.UTC(year, month, 1) / DAY_MS;
  return { start, days: Date.UTC(year, month + 1, 1) / DAY_MS - start };
};

/** Days of the period through the last sales day (0..days), or null when `salesThrough` is unknown. */
export const daysElapsedIn = (period: number, grain: 'weekly' | 'monthly', salesThrough: string | null | undefined): { elapsed: number; days: number } | null => {
  if (!salesThrough) return null;
  const { start, days } = periodSpan(period, grain);
  return { elapsed: Math.max(0, Math.min(days, dayOf(salesThrough) - start + 1)), days };
};

/** Whether `salesThrough` (the last sales day) falls inside the period (QA fix 10: "days to come" belongs to that period only). */
export const periodContainsSalesDay = (period: number, grain: 'weekly' | 'monthly', salesThrough: string | null | undefined): boolean => {
  if (!salesThrough) return false;
  const { start, days } = periodSpan(period, grain);
  const day = dayOf(salesThrough);
  return day >= start && day <= start + days - 1;
};

/** Days of the period after the last sales day ("3 days to come"); 0 when the sales cover it, null when unknown. */
export const daysToCome = (period: number, grain: 'weekly' | 'monthly', salesThrough: string | null | undefined): number | null => {
  const d = daysElapsedIn(period, grain, salesThrough);
  return d ? d.days - d.elapsed : null;
};

/**
 * "sales through Oct 14 (3 days to come)" for a period whose dates have ended (before `currentPeriod`) while the sales still lag
 * its end, else null (an in-progress period's remainder is simply the days ahead; a period the sales cover has nothing to come).
 */
export const salesLagText = (period: number, grain: 'weekly' | 'monthly', salesThrough: string | null | undefined, currentPeriod: number | null | undefined, capital = false): string | null => {
  const n = daysToCome(period, grain, salesThrough);
  if (!salesThrough || n === null || n <= 0 || !isNum(currentPeriod) || period >= currentPeriod) return null;
  const text = `sales through ${fmtDate(salesThrough)} (${n} ${n === 1 ? 'day' : 'days'} to come)`;
  return capital ? text.charAt(0).toUpperCase() + text.slice(1) : text;
};

export interface ProratedPlan {
  /** The value to compare with: prorated in a trend period, the full value elsewhere; null when proration is impossible. */
  value: number | null;
  /** 'WTD' / 'MTD' only when the value was actually prorated. */
  label: 'WTD' | 'MTD' | null;
  elapsed: number | null;
  days: number | null;
}

/** A plan / LY / average value to compare inflows or markdowns with (see the block comment above). */
export const prorate = (value: number | null | undefined, cell: Cell | null | undefined, period: number, grain: 'weekly' | 'monthly', salesThrough: string | null | undefined): ProratedPlan => {
  const v = isNum(value) ? value : null;
  if (!cell || !isTrendCell(cell)) return { value: v, label: null, elapsed: null, days: null };
  const d = daysElapsedIn(period, grain, salesThrough);
  if (!d || d.elapsed <= 0) return { value: null, label: null, elapsed: null, days: null }; // impossible
  if (d.elapsed >= d.days) return { value: v, label: null, elapsed: d.elapsed, days: d.days }; // the sales cover the period
  return { value: v === null ? null : (v * d.elapsed) / d.days, label: grain === 'weekly' ? 'WTD' : 'MTD', elapsed: d.elapsed, days: d.days };
};

/** The tooltip of a prorated comparison: "WTD: compared with the plan prorated for 4 of 7 days". */
export const prorationTitle = (p: ProratedPlan, against = 'plan'): string | undefined =>
  p.label ? `${p.label}: compared with the ${against} prorated for ${p.elapsed} of ${p.days} days (through the last sales day)` : undefined;

// Targets, status bands and labels for the weekly planning reports, mirroring
// laravel/code/app/Services/Reports/WeeklyPlanning/TargetsService.php (spec section 4.5).
//
// Constants, band maths and the status precedence are copied from the PHP so a status computed
// client-side (after a user override of the target, say) agrees with the one the payload carries.
// Values are unrounded: round at display time, never before comparing. "WOS" means weeks in Weekly
// mode and months (MOS) in Monthly mode; the caller passes consistent figures.
//
// (M1 §5, §6, §8, §9; Adam's D2), mirroring TargetsService::status / seasonExiting / action and
// ReportAssembler::applyActions:
// - Plan-BOM gate (the target period's plan inventory, > 0): never overstocked at or below plan BOM (a WOS above the band
// reads watch_high); overstocked above plan BOM × the multiplier whatever the WOS; between, the WOS band decides. No plan
// BOM: the WOS band alone (own-average target, labelled).
// - Watch splits into watch_low / watch_high.
// - Action (`action`, `rowAction`): Review stays; PNOTB / NOTB / History / Distress / Warehouse get none ("—"); a reorder is
// "Out of season" (seasonExiting), "On order covers it" (cover incl. on order ≥ target) or "Reorder · no OTB" (OTB + Dev ≤ 0);
// overstocked is "Sell down · delay or cancel orders", or "Delay or cancel orders" when |actual − plan BOM| < 10% of plan BOM
// and on order > 50% of it; anything else "No action" (code `hold`). Only a reorder is urgent (`actionAllowsUrgent`).
//: the labels changed, the codes did not (saved views and the API carry the codes).
// hold reads "No action", sell_through "No action · plan steps down", push_orders "Delay or cancel orders", sell_down
// "Sell down · delay or cancel orders". "Push" (in the sense of pushing a delivery date) reads "delay (push out) or cancel".
// Round-2: strong sales beat the season rule (`salesBeatPlanOrLy`: sales above plan or last year keep a reorder a
// Reorder, M1 §9 "corroborated by sales"), and a stock-out with no target (nothing to size a buy by) is Review, never Reorder.
// The browser always derives the action itself (`rowAction`), so a WOS override, a chain row and a vendor / SKU row (whose
// payload carries no action) all follow the one rule; the payload's class-row `summary.action` is the backend's copy.

export const MULTIPLIERS: readonly Multiplier[] = [1.25, 1.5, 2];
/**
 * the multiplier the payload's status / urgent / action are computed at. The browser no longer sends the multiplier
 * (the only thing it changes in the payload is those three fields), so the backend's default applies
 * (WeeklyPlanningReportsController::reportParams, 1.5). Any other chosen multiplier re-bands every row in the browser (`rowStatus`).
 */
export const PAYLOAD_MULTIPLIER: Multiplier = 1.5;
/** Share of each side of the band that counts as "watch" (the outer part nearest the edge). */
export const WATCH_FRACTION = 1 / 3;

export const STATUS_LABELS: Record<Status, string> = {
  on_track: 'On track',
  watch_low: 'Watch – low',
  watch_high: 'Watch – high',
  overstocked: 'Overstocked',
  understocked: 'Understocked',
  out_of_stock: 'Out of stock',
  review: 'Review',
  no_recent_sales: 'No recent sales',
  no_plan: 'No plan',
};

/** the No recent sales pill's title (a status when stock sits with no sales in the lookback; never urgent). */
export const NO_RECENT_SALES_TOOLTIP = 'Stock on hand and no sales in the lookback';
/** M1 §6 Deadstock flag: the tag, the Status filter chip and CSV column share this label. */
export const DEADSTOCK_LABEL = 'Deadstock';
export const DEADSTOCK_TOOLTIP = 'Deadstock: more than a year of supply at the current sell rate, and selling less than last year';
/**
 * Round-2 D9 (backend SuspectData; summary.dataFlags): suspect data is flagged, never excluded. The row label carries a small
 * "Check data" tag whose tooltip says why; the payload's own notice (page notices) names the worst few.
 */
export const CHECK_DATA_LABEL = 'Check data';
export const DATA_FLAG_TEXT: Record<DataFlag, string> = {
  sku_outlier: 'one SKU holds far more inventory than the rest of its class (over 10x the median, or over half the client total)',
  mirrored_inventory: 'a SKU has equal and opposite inventory across locations (over $10K each way)',
  tiny_plan: 'a plan so small that actuals are more than 1,000% away from it',
  unallocated_share: 'Unallocated / adjustments is over 40% of the BOM',
};
export const checkDataTooltip = (flags: readonly DataFlag[], grain?: Grain | null): string => unitize(`Suspect data, kept in the numbers: ${flags.map((f) => DATA_FLAG_TEXT[f] ?? f).join('; ')}`, grain);
/** The Status filter's chips: every status, then the Deadstock flag (a filterable flag, not a status). */
export const DEADSTOCK_FILTER = 'deadstock';

/** the Review pill's title (TargetsService::stockOutStatus — no sales in the lookback nor the last 52 weeks / 12 months). */
export const REVIEW_TOOLTIP = 'Out of stock · no sales in the last 52 weeks';
/** the Review title in the grain's unit (Monthly: the last 12 months, the rule's own window). */
export const reviewTooltip = (grain: Grain | null | undefined): string => unitize(REVIEW_TOOLTIP, grain);
/** the Suggested reorder cell's title by basis; lookback suggestions carry none (the column description covers them). */
export const REORDER_BASIS_TOOLTIPS: Record<Exclude<ReorderBasis, 'lookback'>, string> = {
  last52: 'from the last 52 weeks — no sales in the lookback',
  mixed: 'some variants from the last 52 weeks (no sales in the lookback), the rest from the lookback average',
};

export const STATUS_COLORS: Record<Status, { fg: string; bg: string }> = {
  on_track: { fg: '#027A48', bg: '#ECFDF3' },
  watch_low: { fg: '#B54708', bg: '#FFFAEB' }, // amber, leaning to understocked
  watch_high: { fg: '#93370D', bg: '#FEF0C7' }, // deeper amber, leaning to overstocked (or held back by the plan-BOM gate)
  understocked: { fg: '#B42318', bg: '#FEF3F2' },
  out_of_stock: { fg: '#B42318', bg: '#FEF3F2' },
  review: { fg: '#B54708', bg: '#FFFAEB' }, // amber, as watch: a look, not a red reorder
  no_recent_sales: { fg: '#344054', bg: '#EAECF0' }, // slate: dead weight, not an alarm
  overstocked: { fg: '#6941C6', bg: '#F4EBFF' },
  no_plan: { fg: '#475467', bg: '#F2F4F7' },
};

/** summary.action / TargetsService::ACTION_*. */
export type ActionCode = NonNullable<Summary['action']>;
export const ACTION_LABELS: Record<ActionCode, string> = {
  reorder: 'Reorder',
  reorder_no_otb: 'Reorder · no OTB',
  on_order_covers: 'On order covers it',
  out_of_season: 'Out of season',
  class_type: '—',
  //: labels only; codes unchanged.
  sell_down: 'Sell down · delay or cancel orders',
  push_orders: 'Delay or cancel orders',
  sell_through: 'No action · plan steps down',
  hold: 'No action',
  review: 'Review',
  review_clear: 'Review · clearance', // "clearance" names the outcome (it read "Review / clear")
};
export const ACTION_TOOLTIPS: Partial<Record<ActionCode, string>> = {
  reorder_no_otb: 'Understocked, but OTB + Dev is zero or negative: no open-to-buy left for it',
  on_order_covers: 'Understocked today, but cover including on order reaches the target',
  out_of_season: 'Exiting or out of season: next period plans less sales and less inventory (last year\'s sales without a plan). No buy',
  sell_down: 'Over plan inventory: sell down, and delay (push out) or cancel orders',
  push_orders: 'Inventory is near plan, but on order is large (over half the plan inventory): delay (push out) or cancel orders',
  sell_through: "At or below plan BOM, but next period's plan steps down: sell through current stock rather than reorder.",
  hold: 'On plan: nothing to buy, cancel or sell down this period.', //
  review_clear: 'Stock on hand with no recent sales, above plan inventory (or with no plan inventory): review it and clear it. At or below plan inventory it is Hold: never a clear',
};
/** (M1 §8): service types that get no buy or markdown action (TargetsService::NO_ACTION_SERVICE_TYPES). */
export const NO_ACTION_SERVICE_TYPES: Record<number, string> = { 2: 'PNOTB', 3: 'NOTB', 7: 'History', 8: 'Distress', 9: 'Warehouse' };
/** Round-2 D7 (summary.nonSelling, ReportAssembler::markNonSelling): the note on a non-selling location's "—" action. */
export const NON_SELLING_NOTE = 'Non-selling location';
export const classTypeTooltip = (serviceTypeId: number | null | undefined, nonSelling?: boolean | null): string =>
  nonSelling ? `${NON_SELLING_NOTE}: owned but not selling (warehouse, receiving, HQ, storage, to be split, event). Its stock, inflows and on order count in the chain and class totals and the OTB; it is never judged as a store`
    : `${NO_ACTION_SERVICE_TYPES[serviceTypeId ?? -1] ?? 'This'} class: no buy or markdown action`;
/** review: the class-type action's text (CSV, sort); the table shows a muted "—" titled classTypeTooltip. */
export const classTypeText = (serviceTypeId: number | null | undefined, nonSelling?: boolean | null): string =>
  nonSelling ? `No action (${NON_SELLING_NOTE})` : `No action (${NO_ACTION_SERVICE_TYPES[serviceTypeId ?? -1] ?? 'class type'})`;
/** Delay or cancel orders (push_orders): |actual − plan BOM| below this share of plan BOM is "near zero", and on order above PUSH_ORDERS_ON_ORDER_SHARE of it "large". */
export const PUSH_ORDERS_DEVIATION_SHARE = 0.1;
export const PUSH_ORDERS_ON_ORDER_SHARE = 0.5;

/**
 * (M1 §8 families, ReportAssembler::deferChildrenToPlannedParent): a child class with no plan of its own under
 * a planned parent carries `planParentKey`. It has no target (even under a WOS override), no status, no action and is never
 * urgent: the parent is the analysis level. Health / Action show SEE_PARENT_LABEL with `seeParentTooltip`.
 */
export const SEE_PARENT_LABEL = 'see parent';
export const defersToParent = (summary: Pick<Summary, 'planParentKey'> | null | undefined): boolean => !!summary?.planParentKey;
/** The row details' note under "WOS / target": where the target in force comes from. */
export const TARGET_BASIS_NOTES: Record<TargetBasis, string> = {
  plan: 'plan target',
  own_avg: 'own trailing average (no plan)',
  override: 'your target',
  none: 'no target',
  mixed: 'sales-weighted plan targets (classes differ)',
  parent: 'judged at the parent',
};
/** TARGET_BASIS_NOTES, naming the parent for a child judged at it. */
export const targetBasisNote = (basis: TargetBasis, summary: Pick<Summary, 'planParentLabel'> | null | undefined): string =>
  basis === 'parent' && summary?.planParentLabel ? `judged at ${summary.planParentLabel}` : TARGET_BASIS_NOTES[basis];
export const seeParentTooltip = (summary: Pick<Summary, 'planParentKey' | 'planParentLabel'> | null | undefined): string =>
  `Plan is set on ${summary?.planParentLabel || summary?.planParentKey || 'the parent class'}`;

/** Band edges: `under` is the PHP `lower`, `over` the PHP `upper`. */
export interface Bands {
  under: number;
  over: number;
  watchLow: number;
  watchHigh: number;
}

/** TargetsService::assertMultiplier. */
const assertMultiplier = (multiplier: number): void => {
  if (!(multiplier >= 1)) throw new Error(`Overstock multiplier must be >= 1, got ${multiplier}`);
};

/**
 * Band edges for a target and multiplier: understocked below `under`, overstocked above `over`,
 * watch in the outer WATCH_FRACTION of each side.
 */
export const bands = (target: number, multiplier: number): Bands => {
  assertMultiplier(multiplier);
  const over = target * multiplier;
  const under = target / multiplier;
  return {
    under,
    over,
    watchLow: target - (target - under) * (1 - WATCH_FRACTION),
    watchHigh: target + (over - target) * (1 - WATCH_FRACTION),
  };
};

/**
 * Status precedence (TargetsService::status): out of stock wins over everything, then stock with no sales in the lookback
 * (wos null: no_recent_sales, outside the gate), then no target gives no_plan, then the plan-BOM depth rule and the multiplier band, with the
 * M1 §6 hard gate (at or below plan BOM is never overstocked: watch_high). `planBom` is the target period's plan
 * inventory; null or ≤ 0 = WOS only.
 * Never 'review': that needs the 52-week sales only the payload has read (`stockOutStatus` in PHP), so
 * a client-side re-derivation keeps the payload's Review (`rowStatus`).
 */
export const status = (wos: number | null, target: number | null, multiplier: number, onHand: number, planBom: number | null = null, bomInventory: number | null = null): Status => {
  assertMultiplier(multiplier);
  if (onHand <= 0) return 'out_of_stock';
  if (wos === null) return 'no_recent_sales'; // stock on hand, no sales in the lookback: before the target and the plan-BOM gate
  if (target === null) return 'no_plan';
  const hasBom = planBom !== null && planBom > 0;
  // The inventory compared with plan BOM: the row's own, or (an aggregate) its planned members' only (TargetsService::status).
  const bomInv = bomInventory ?? onHand;
  if (hasBom && bomInv > (planBom as number) * multiplier) return 'overstocked';
  const high: Status = !hasBom || bomInv > (planBom as number) ? 'overstocked' : 'watch_high';
  const b = bands(target, multiplier);
  if (wos > b.over) return high;
  if (wos < b.under) return 'understocked';
  if (wos >= b.watchHigh) return 'watch_high';
  if (wos <= b.watchLow) return 'watch_low';
  return 'on_track';
};

/**
 * TargetsService::deadstock (M1 §6): more than a year of supply at the current sell rate (WOS > 52 weeks, MOS > 12 months; no sales
 * is unbounded supply) AND declining against last year (sales over the lookback below the same periods last year; no LY figure is
 * not declining). Needs stock on hand. A flag beside the status.
 */
export const deadstock = (wos: number | null, onHand: number, lookbackSales: number, lookbackSalesLy: number | null, weekly: boolean): boolean => {
  if (!(onHand > 0)) return false;
  if (wos !== null && wos <= (weekly ? 52 : 12)) return false;
  return lookbackSalesLy !== null && lookbackSales < lookbackSalesLy - SEASON_EPSILON;
};

/** Understocked or out of stock: the statuses a reorder (and urgency) comes from. */
export const isReorderStatus = (s: Status | null | undefined): boolean => s === 'understocked' || s === 'out_of_stock';

type SeasonCell = Pick<Cell, 'salesPlan' | 'inventoryPlan' | 'salesLy'>;
const num = (v: unknown): number | null => (typeof v === 'number' && !Number.isNaN(v) ? v : null);

/** TargetsService::SEASON_WEEKS / SEASON_WEEKLY_DROP: Weekly compares the next 4 weeks' average with the last 4, needing a 10% drop. */
export const SEASON_WEEKS = 4;
export const SEASON_WEEKLY_DROP = 0.1;
type SeasonPeriods = Record<string, Partial<SeasonCell> | undefined>;
/** TargetsService::pair: [this, next] — Monthly the two months, Weekly the 4-week averages over the weeks present. */
const pair = (periods: SeasonPeriods, target: number, key: keyof SeasonCell, weekly: boolean): [number | null, number | null] => {
  const avg = (ps: number[]): number | null => {
    const vals = ps.map((p) => num(periods[String(p)]?.[key])).filter((v): v is number => v !== null);
    return vals.length === 0 ? null : vals.reduce((a, b) => a + b, 0) / vals.length;
  };
  if (!weekly) return [avg([target]), avg([target + 1])];
  const n = SEASON_WEEKS;
  return [avg(Array.from({ length: n }, (_, i) => target - n + 1 + i)), avg(Array.from({ length: n }, (_, i) => target + 1 + i))];
};
/** TargetsService::SEASON_EPSILON: half a cent, since the wire rounds money to 2 dp. */
export const SEASON_EPSILON = 0.005;
const drops = (cur: number, next: number, weekly: boolean): boolean =>
  (weekly ? next <= cur * (1 - SEASON_WEEKLY_DROP) + SEASON_EPSILON : next < cur - SEASON_EPSILON);

/**
 * TargetsService::seasonExiting (M1 §9): exiting or off season at `target`. Plan curve (plan sales and plan inventory on both
 * sides): next plan sales below this AND next plan BOM below this; or no plan sales on either side with plan BOM not building
 * (a building plan BOM is a pre-season build). Else LY sales: next below this, or none on either side. Null when neither is
 * known. Weekly (`weekly`) compares the next 4 weeks' average with the last 4 and needs a 10% drop.
 */
export const seasonExiting = (periods: SeasonPeriods, target: number, weekly = false): boolean | null => {
  const [s0, s1] = pair(periods, target, 'salesPlan', weekly);
  const [i0, i1] = pair(periods, target, 'inventoryPlan', weekly);
  if (s0 !== null && s1 !== null && i0 !== null && i1 !== null) {
    if (s0 <= 0 && s1 <= 0) return i1 <= i0 + SEASON_EPSILON;
    return drops(s0, s1, weekly) && drops(i0, i1, weekly);
  }
  const [l0, l1] = pair(periods, target, 'salesLy', weekly);
  if (l0 !== null && l1 !== null) return (l0 <= 0 && l1 <= 0) || drops(l0, l1, weekly);
  return null;
};

/**
 * TargetsService::salesBeatPlanOrLy (Round-2): do the row's sales exceed its plan or last year, so the plan curve's "exiting /
 * off season" is not believed? Any of: lookback sales over the periods that carry a plan exceed that plan; lookback sales over
 * the periods that carry an LY figure exceed that LY; the target period's sales to date, or its trended sales (`salesTrend`),
 * exceed the target period's plan. Strictly above (beyond SEASON_EPSILON); a zero plan / LY proves nothing.
 */
export const salesBeatPlanOrLy = (periods: Record<string, Partial<Cell> | undefined>, target: number, lookback: number[]): boolean => {
  let salesPlanSide = 0, plan = 0, salesLySide = 0, ly = 0;
  lookback.forEach((p) => {
    const c = periods[String(p)];
    const sales = num(c?.sales) ?? 0;
    const sp = num(c?.salesPlan);
    if (sp !== null) { plan += sp; salesPlanSide += sales; }
    const sl = num(c?.salesLy);
    if (sl !== null) { ly += sl; salesLySide += sales; }
  });
  if (plan > 0 && salesPlanSide > plan + SEASON_EPSILON) return true;
  if (ly > 0 && salesLySide > ly + SEASON_EPSILON) return true;
  const t = periods[String(target)];
  const targetPlan = num(t?.salesPlan) ?? 0;
  const targetSales = Math.max(num(t?.sales) ?? 0, num(t?.salesTrend) ?? 0);
  return targetPlan > 0 && targetSales > targetPlan + SEASON_EPSILON;
};

/** TargetsService::planBomStepsDown: next plan BOM below this (Weekly: 4-week averages, a 10% drop). */
export const planBomStepsDown = (periods: SeasonPeriods, target: number, weekly = false): boolean | null => {
  const [i0, i1] = pair(periods, target, 'inventoryPlan', weekly);
  return i0 === null || i1 === null ? null : drops(i0, i1, weekly);
};

/**
 * TargetsService::onOrderWithin: the on order due in a period starting before `fromPeriod + target` (the target-cover horizon),
 * from summary.onOrderDue; without due dates or a target, the whole span's `onOrder`.
 */
export const onOrderWithin = (due: Record<string, number> | null | undefined, fromPeriod: number | undefined, target: number | null, onOrder: number | null): number | null => {
  if (!due || fromPeriod === undefined || target === null) return onOrder;
  return Object.entries(due).reduce((t, [p, v]) => (Number(p) < fromPeriod + target ? t + (num(v) ?? 0) : t), 0);
};

export interface ActionInputs {
  serviceTypeId?: number | null; seasonExiting?: boolean | null; strongSales?: boolean | null; wos?: number | null; target?: number | null; avgPeriodSales?: number | null;
  onOrder?: number | null; otbDev?: number | null; planBom?: number | null; inventory?: number | null;
  /** planBomStepsDown at the target period (sell_through, "No action · plan steps down"). */
  bomStepsDown?: boolean | null;
  /** The on order due within the target-cover horizon (onOrderWithin); default `onOrder`. */
  coverOnOrder?: number | null;
}

/** TargetsService::action: the row's action from its status (see the header). */
export const action = (s: Status, inp: ActionInputs): ActionCode => {
  if (s === 'review') return 'review';
  if (inp.serviceTypeId !== null && inp.serviceTypeId !== undefined && NO_ACTION_SERVICE_TYPES[inp.serviceTypeId]) return 'class_type';
  const onOrder = Math.max(0, num(inp.onOrder) ?? 0);
  const planBom = num(inp.planBom);
  if (s === 'no_recent_sales') {
    // Above plan BOM (or with none: the gate has nothing to protect) it is dead stock to clear; at or below it, Hold.
    const inv = num(inp.inventory);
    const hasBom = planBom !== null && planBom > 0;
    return !hasBom || (inv !== null && inv > (planBom as number)) ? 'review_clear' : 'hold';
  }
  if (isReorderStatus(s)) {
    const avg = num(inp.avgPeriodSales) ?? 0;
    const target = num(inp.target);
    // Round-2 / (TargetsService::action): no target to size a buy by is noise, not a reorder. A reorder status with no target
    // is always out of stock (status: no target with stock on hand is no_plan): a stock-out with no plan and no stock anywhere in the
    // lookback (no own-average target), whatever it sold (4583 DREAMERS at 341 Sweaters: a few units 10-13 weeks ago, nothing since).
    if (target === null) return 'review';
    if (inp.seasonExiting === true && inp.strongSales !== true) return 'out_of_season'; // Round-2: strong sales outrank the plan curve
    const cover = s === 'out_of_stock' ? 0 : Math.max(0, num(inp.wos) ?? 0); // nothing on hand covers nothing
    const coverOnOrder = num(inp.coverOnOrder) === null ? onOrder : Math.max(0, num(inp.coverOnOrder) as number);
    if (avg > 0 && target !== null && cover + coverOnOrder / avg >= target) return 'on_order_covers';
    const otbDev = num(inp.otbDev);
    if (otbDev !== null && otbDev <= 0) return 'reorder_no_otb';
    return 'reorder';
  }
  if (s === 'overstocked') {
    const inv = num(inp.inventory);
    if (planBom !== null && planBom > 0 && inv !== null && Math.abs(inv - planBom) < PUSH_ORDERS_DEVIATION_SHARE * planBom && onOrder > PUSH_ORDERS_ON_ORDER_SHARE * planBom) return 'push_orders';
    return 'sell_down';
  }
  const inv = num(inp.inventory);
  if ((s === 'on_track' || s === 'watch_low' || s === 'watch_high') && planBom !== null && planBom > 0 && inv !== null && inv <= planBom && inp.bomStepsDown === true) return 'sell_through';
  return 'hold';
};

/** Only a reorder can be urgent (TargetsService::actionAllowsUrgent). */
export const actionAllowsUrgent = (a: ActionCode | null | undefined): boolean => a === 'reorder' || a === 'reorder_no_otb';

/**
 * Understocked, and the on-order within the span will not bring the class back to target
 * (TargetsService::isUrgent). `onOrderWeeks` is on-order within the OTB span ÷ average period
 * sales, on the same basis and period as `wos` and `target`.
 */
export const isUrgent = (wos: number | null, target: number | null, multiplier: number, onOrderWeeks: number): boolean => {
  if (wos === null || target === null) return false;
  if (wos >= bands(target, multiplier).under) return false;
  return wos + onOrderWeeks < target;
};


/**
 * The target a row is judged against: none for a child that defers to its planned parent (basis 'parent',
 * ); else a positive user override wins (basis 'override'), else the row's own target and basis from the
 * payload (TargetsService::targetWos precedence).
 */
export const resolveTarget = (
  summary: Pick<Summary, 'targetWos' | 'targetBasis' | 'planParentKey'> | null | undefined,
  override: number | null | undefined,
): { target: number | null; basis: TargetBasis } => {
  if (defersToParent(summary)) return { target: null, basis: 'parent' }; // judged at the parent, whatever the override
  if (typeof override === 'number' && override > 0) return { target: override, basis: 'override' };
  if (summary && summary.targetWos !== null && summary.targetWos !== undefined) return { target: summary.targetWos, basis: summary.targetBasis };
  return { target: null, basis: 'none' };
};

/**
 * The context `rowStatus` / `rowAction` / `isRowUrgent` need (a subset of `CellContext`, kept here to avoid an import cycle).
 * `targetPeriod` locates the plan BOM and the season cells; without it both read as unknown.
 */
export interface UrgencyContext { wosOverride: number | null; multiplier: number; targetPeriod?: number; currentPeriod?: number; grain?: string; lookback: number[] }
type RowLike = { summary?: Summary | null; periods?: Record<string, Cell>; serviceTypeId?: number | null };
const overridden = (ctx: UrgencyContext): boolean => typeof ctx.wosOverride === 'number' && ctx.wosOverride > 0;
/** The payload's status / urgent hold only for the default multiplier and the plan targets: a WOS override or another multiplier re-judges the row here. */
const rejudged = (ctx: UrgencyContext): boolean => overridden(ctx) || ctx.multiplier !== PAYLOAD_MULTIPLIER;
/** The target period's plan inventory (plan BOM), the M1 §6 gate's reference. */
export const planBomOf = (row: RowLike, targetPeriod: number | undefined): number | null =>
  targetPeriod === undefined ? null : num(row.periods?.[String(targetPeriod)]?.inventoryPlan);
/**
 * The gate pair: plan BOM and the inventory compared with it. An aggregate (subtotal, chain row) carries its planned members'
 * (summary.planBom / planBomInventory, rowTree); a payload row its target cell's plan inventory and its own inventory.
 */
export const gateOf = (row: RowLike, targetPeriod: number | undefined): { planBom: number | null; bomInventory: number | null } => {
  const s = row.summary;
  if (s && s.planBom !== undefined) return { planBom: s.planBom ?? null, bomInventory: s.planBomInventory ?? null };
  return { planBom: planBomOf(row, targetPeriod), bomInventory: s ? s.inventory : null };
};
/** The span the cover horizon starts from: the current period (ReportAssembler::applyActions), else the target period. */
const horizonStart = (ctx: UrgencyContext): number | undefined => ctx.currentPeriod ?? ctx.targetPeriod;
/** The on order counting toward cover for a target (onOrderWithin over summary.onOrderDue). */
export const coverOnOrderOf = (s: Summary, target: number | null, ctx: UrgencyContext): number | null => onOrderWithin(s.onOrderDue, horizonStart(ctx), target, s.onOrder);

/**
 * Status after a user override of the target or another overstock multiplier; the payload's status otherwise. Review is kept under an
 * override: it states the row sold nothing in the lookback or the last 52 weeks, which no target changes. A child that
 * defers to its planned parent has none: null, so status counts and Needs Attention skip it.
 */
export const rowStatus = (row: RowLike, ctx: UrgencyContext): Status | null => {
  const s = row.summary;
  if (!s || defersToParent(s)) return null; // judged at the parent, never re-derived under an override
  if (s.nonSelling) return null; // Round-2 D7: a non-selling location is never judged as a store
  if (s.status === 'review') return 'review';
  if (!rejudged(ctx) || !s.inventoryKnown || typeof s.inventory !== 'number') return s.status;
  const g = gateOf(row, ctx.targetPeriod);
  return status(s.wos, resolveTarget(s, ctx.wosOverride).target, ctx.multiplier, s.inventory, g.planBom, g.bomInventory);
};

/** ReportAssembler::applyActions in the browser: the action of `rowStatus` (null without one). */
export const rowAction = (row: RowLike, ctx: UrgencyContext): ActionCode | null => {
  if (row.summary?.nonSelling) return 'class_type'; // Round-2 D7: "—" with the note "Non-selling location", no status behind it
  const st = rowStatus(row, ctx);
  const s = row.summary;
  if (!st || !s) return null;
  const weekly = ctx.grain === 'weekly';
  const t = ctx.targetPeriod;
  const target = resolveTarget(s, ctx.wosOverride).target;
  const g = gateOf(row, t);
  return action(st, {
    serviceTypeId: s.serviceTypeId ?? row.serviceTypeId ?? null,
    seasonExiting: t === undefined || !row.periods ? null : seasonExiting(row.periods, t, weekly),
    strongSales: t === undefined || !row.periods ? null : salesBeatPlanOrLy(row.periods, t, ctx.lookback),
    bomStepsDown: t === undefined || !row.periods ? null : planBomStepsDown(row.periods, t, weekly),
    wos: s.wos, target, avgPeriodSales: s.avgPeriodSales,
    onOrder: s.onOrder, coverOnOrder: coverOnOrderOf(s, target, ctx), otbDev: s.otbDev ?? null, planBom: g.planBom, inventory: g.bomInventory,
  });
};

/**
 * The one urgency rule for a row (the cell renderer's urgent dot and `needsAttention` both use it):
 * under a positive WOS override or a multiplier other than the payload's, a reorder status and `isUrgent(wos, override target, multiplier, cover on order ÷ avgPeriodSales)`
 * (the on order due within the override's horizon, `coverOnOrderOf`) with 0 weeks on order when either figure is missing or
 * sales are not positive; otherwise the payload's `summary.urgent`. Either way only a reorder action is urgent (
 * not one out of season, of a no-action class type, or covered by on order). No summary, or a deferring child: never urgent.
 */
export const isRowUrgent = (row: RowLike, ctx: UrgencyContext): boolean => {
  const s = row.summary;
  if (!s || defersToParent(s)) return false;
  let base = s.urgent;
  if (rejudged(ctx)) {
    const avg = s.avgPeriodSales;
    const target = resolveTarget(s, ctx.wosOverride).target;
    const cover = coverOnOrderOf(s, target, ctx);
    const onOrder = typeof cover === 'number' && cover > 0 ? cover : 0;
    const onOrderWeeks = typeof avg === 'number' && avg > 0 ? onOrder / avg : 0;
    base = isReorderStatus(rowStatus(row, ctx)) && isUrgent(s.wos, target, ctx.multiplier, onOrderWeeks);
  }
  return base && actionAllowsUrgent(rowAction(row, ctx));
};

/** the sort id of "Most over target" (WOS ÷ target, descending): not a table column, a sort option. */
export const OVER_TARGET_SORT = 'overTarget';
/**
 * WOS ÷ target, the value "Most over target" sorts by: the row's cover against the target in force (a positive WOS override
 * replaces the plan's). Null (so the row sorts last) with no sales (no WOS), no positive target, or a child judged at its parent.
 */
export const overTargetRatio = (row: { summary?: Summary | null }, ctx: { wosOverride: number | null }): number | null => {
  const s = row.summary;
  if (!s || defersToParent(s) || typeof s.wos !== 'number' || Number.isNaN(s.wos)) return null;
  const { target } = resolveTarget(s, ctx.wosOverride);
  return typeof target === 'number' && target > 0 ? s.wos / target : null;
};

// pet-store columns (Adam, 2026-10-21, from a pet retailer's weekly recaps). Pure functions on a row's own cells, so a subtotal,
// chain or location row (whose cells the row tree sums) reads the same way. Retail on every basis.
//
// - Average weekly sales: lookback sales ÷ (the lookback's days ÷ 7). Exact in Monthly (each month's own days), not a fixed 4.33 or 4.43.
// - Weeks of supply: BOM inventory ÷ average weekly sales, in weeks in BOTH grains (the Monthly report's cover column reads months).
// - Inflow weeks of supply: lookback inflows ÷ average weekly sales: how many weeks of selling were brought in (buying pace vs selling).
// - Markdown %: lookback markdowns ÷ lookback sales.
// - Trended OTB (the period in progress only): where the period would end at the trend, against the plan's end of period.
// projected EOM = BOM + received to date − trended sales − trended markdowns (markdowns so far ÷ % of period elapsed)
// trended OTB = plan EOM (next period's plan BOM) − projected EOM − on order due this period
// Positive: open to buy this period to land on the plan; negative: the period ends over plan at the current trend.

const isFiniteNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** The days in a period: 7 for a week; the month's own days for an epoch month. */
export const daysInPeriod = (period: number, grain: Grain): number => {
  if (grain === 'weekly') return 7;
  const year = 1970 + Math.floor(period / 12);
  const month = ((period % 12) + 12) % 12;
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
};

const lookbackCells = (row: AnyRow, ctx: CellContext): { period: number; cell: Cell }[] =>
  ctx.lookback.map((p) => ({ period: p, cell: row.periods[String(p)] })).filter((x): x is { period: number; cell: Cell } => !!x.cell);

/** Σ sales and the days they cover over the lookback (periods with no sales figure are left out of both). */
const lookbackSales = (row: AnyRow, ctx: CellContext): { sales: number; days: number } | null => {
  let sales = 0;
  let days = 0;
  lookbackCells(row, ctx).forEach(({ period, cell }) => {
    if (!isFiniteNum(cell.sales)) return;
    sales += cell.sales;
    days += daysInPeriod(period, ctx.grain);
  });
  return days > 0 ? { sales, days } : null;
};

export const avgWeeklySales = (row: AnyRow, ctx: CellContext): number | null => {
  const s = lookbackSales(row, ctx);
  return s ? s.sales / (s.days / 7) : null;
};

const perWeekOfSales = (amount: number | null, row: AnyRow, ctx: CellContext): number | null => {
  const avg = avgWeeklySales(row, ctx);
  return amount === null || avg === null || avg <= 0 ? null : amount / avg;
};

export const weeksOfSupply = (row: AnyRow, ctx: CellContext): number | null => {
  const bom = row.periods[String(ctx.targetPeriod)]?.inventoryStart;
  return perWeekOfSales(isFiniteNum(bom) ? Math.max(0, bom) : null, row, ctx);
};

export const inflowWeeksOfSupply = (row: AnyRow, ctx: CellContext): number | null => {
  const cells = lookbackCells(row, ctx).filter(({ cell }) => isFiniteNum(cell.inflow));
  return perWeekOfSales(cells.length ? cells.reduce((t, { cell }) => t + (cell.inflow as number), 0) : null, row, ctx);
};

export const markdownPct = (row: AnyRow, ctx: CellContext): number | null => {
  let md = 0;
  let sales = 0;
  let any = false;
  lookbackCells(row, ctx).forEach(({ cell }) => {
    if (!isFiniteNum(cell.sales)) return;
    sales += cell.sales;
    if (isFiniteNum(cell.markdowns)) { md += cell.markdowns; any = true; }
  });
  return any && sales > 0 ? md / sales : null;
};

/** The pieces of the trended OTB (for the tooltip), or null outside the period in progress or without a plan EOM. */
export interface TrendedOtb { value: number; projectedEom: number; planEom: number; dueThisPeriod: number; trendedSales: number; trendedMarkdowns: number }

export const trendedOtb = (row: AnyRow, ctx: CellContext): TrendedOtb | null => {
  const p = ctx.targetPeriod;
  const c = row.periods[String(p)];
  if (!c || !isTrendCell(c)) return null;
  const t = trendOf(c, ctx.trendMethod);
  const planEom = row.periods[String(p + 1)]?.inventoryPlan;
  if (!isFiniteNum(t.salesTrend) || !isFiniteNum(c.inventoryStart) || !isFiniteNum(planEom)) return null;
  const due = row.summary?.onOrderDue;
  if (due === null || due === undefined) return null; // without due dates the span's on order would be counted here
  const md = isFiniteNum(c.markdowns) ? c.markdowns : 0;
  const trendedMarkdowns = isFiniteNum(t.trendPct) && t.trendPct > 0 ? md / t.trendPct : md;
  const projectedEom = c.inventoryStart + (isFiniteNum(c.inflow) ? c.inflow : 0) - t.salesTrend - trendedMarkdowns;
  const dueThisPeriod = isFiniteNum(due[String(p)]) ? (due[String(p)] as number) : 0;
  return { value: planEom - projectedEom - dueThisPeriod, projectedEom, planEom, dueThisPeriod, trendedSales: t.salesTrend, trendedMarkdowns };
};

/**
 * The class rows with `summary.trendedOtb` stamped (their own figure, null outside the period in progress), so the row tree sums it into
 * chain, location and category rows, each class against its own plan EOM (a subtotal's summed cells would mix classes with and without a
 * plan). Rows without a summary are left as they are.
 */
export const withTrendedOtb = <R extends AnyRow>(rows: R[], ctx: CellContext): R[] =>
  rows.map((r) => (r.summary ? { ...r, summary: { ...r.summary, trendedOtb: trendedOtb(r, ctx)?.value ?? null } } : r));

// W2 (D12): allocation, "open to sell". Which stores need stock of a class this period, how much the non-selling
// locations (warehouse, receiving, HQ, storage: D7) hold to send them, and the shortfall to buy / chase or the surplus to hold.
// Allocation, not buying: it moves stock the chain already owns. Mirrored in PHP (App\Services\Reports\WeeklyPlanning\Allocation);
// both are tested on the same table (laravel/code/tests/Unit/WeeklyPlanning/fixtures/m1_parity.json, "allocation").
//
// Definitions (retail dollars, the target period: a week in Weekly, a month in Monthly):
// - Store need = max(0, target × rate − (max(0, BOM) + on order due in the period)).
// target: the store's target cover (WOS / MOS) — a WOS override when one is typed, else the row's own target (plan, else its own
// average); none (judged at its parent, or no target) → no need. rate: the period's plan sales when the store has a plan figure
// for the period (0 included: a plan of 0 means nothing to sell), else its own average period sales over the lookback. BOM: the
// row's beginning-of-period inventory (summary.inventory; unknown → no need; negative stock counts as none on hand). On order due:
// summary.onOrderDue[target period] (the class payload buckets overdue lines into the current period), or the whole on order when
// the row has no due dates (the convention "On order covers it" uses). Non-selling locations have no need.
// - Available = a non-selling location's BOM on hand (max(0, summary.inventory)). On order due there is NOT added.
// - Class (chain) row: need = Σ store needs (stores with a known need; null when none), available = Σ non-selling on hand (null with
// no non-selling location), balance = need − available: > 0 is the shortfall (to buy / chase), < 0 the surplus (hold in the warehouse).
// - The split (row detail, action text): stores by need, largest first (then by name), each takes what it needs from the sources
// with the most left first until the sources run out; `allocate` is what it gets, `from` the sources in the order it takes from them.
// - Gross need (needBeforeOnOrder) = max(0, target × rate − max(0, BOM)): the need before the period's on order is subtracted.
// - Ship / hold (item 12, a display hint): a vendor × class row's on order due in the period against its class's GROSS need there, less
// what the other vendors of that class have due in the same period: "Hold PO" when due > max(0, gross need − others' due), "Take PO"
// otherwise;
// nothing when nothing is due, there are no due dates, or the gross need is unknown. The gross need, because the need already
// subtracts that due (a PO that exactly fills the gap is a Take). With the others' due subtracted the test is the class's total due
// against its gross need, so every vendor of the class at a location gets the same hint; the tooltip names this vendor's share.
// The class is the row that judges (rowTree withShipHints): the location row in Class › Location › Vendor (in a family, the
// parent's location row, M1: the parent is the analysis level), the chain class row in Class by Vendor.
// - At cost: plan sales have no cost figure, so Need and the balance read "—" (not available at cost); Available reads the cost BOM.

/** One line on how the split works (row detail, How it works). */
export const ALLOCATION_RULE = 'Need = target cover × this period\'s plan sales (the store\'s own average without a plan) − (BOM + on order due this period). Stores are served largest need first from the non-selling location with the most stock; what is still needed is the shortfall, what is left the surplus.';

/** The need before on order: max(0, target × rate − max(0, BOM)); null exactly when storeNeed is. */
export const needBeforeOnOrder = (n: Omit<NeedInputs, 'due'>): number | null => storeNeed({ ...n, due: 0 });

/** Rounded to the cent, half away from zero (PHP round). */
export const roundCents = (v: number): number => (Math.sign(v) * Math.round(Math.abs(v) * 100)) / 100 + 0;

export interface NeedInputs { target: number | null; planSales: number | null; avgSales: number | null; bom: number | null; due: number | null }

/** The store need formula (see the header); null when it cannot be worked out. */
export const storeNeed = ({ target, planSales, avgSales, bom, due }: NeedInputs): number | null => {
  if (!isNum(target) || target <= 0 || !isNum(bom)) return null;
  const rate = isNum(planSales) ? planSales : isNum(avgSales) ? avgSales : null;
  if (rate === null) return null;
  const want = target * Math.max(0, rate);
  const have = Math.max(0, bom) + Math.max(0, isNum(due) ? due : 0);
  return Math.max(0, want - have);
};

export interface NeedMember { key: string; label: string; need: number | null }
export interface SourceMember { key: string; label: string; available: number | null }

/** Code-unit order (PHP strcmp), so ties rank the same on both sides. */
const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
const byNeed = (a: { need: number; label: string }, b: { need: number; label: string }): number => b.need - a.need || cmp(a.label, b.label);

/** The class roll-up and the greedy split (see the header). */
export const classAllocation = (stores: NeedMember[], sources: SourceMember[]): RowAllocation => {
  const known = stores.filter((s): s is NeedMember & { need: number } => isNum(s.need));
  const need = known.length ? roundCents(known.reduce((t, s) => t + s.need, 0)) : null;
  const supplies = sources.filter((s): s is SourceMember & { available: number } => isNum(s.available));
  const available = supplies.length ? roundCents(supplies.reduce((t, s) => t + s.available, 0)) : null;
  const balance = need === null && available === null ? null : roundCents((need ?? 0) - (available ?? 0));
  const left = supplies.map((s) => ({ ...s, left: s.available })).sort((a, b) => b.available - a.available || cmp(a.label, b.label));
  const ranked: StoreAllocation[] = known.map((s) => ({ key: s.key, label: s.label, need: roundCents(s.need) })).sort(byNeed).map((s) => {
    let remaining = s.need;
    const from: string[] = [];
    // The source with the most left first, at every step (a store split across two takes the larger first).
    while (remaining > 0.005) {
      const src = left.filter((x) => x.left > 0.005).sort((a, b) => b.left - a.left || cmp(a.label, b.label))[0];
      if (!src) break;
      const take = Math.min(remaining, src.left);
      src.left -= take;
      remaining -= take;
      from.push(src.label);
    }
    return { ...s, allocate: roundCents(s.need - Math.max(0, remaining)), from };
  });
  const out: SourceAllocation[] = left.map((s) => ({ key: s.key, label: s.label, available: roundCents(s.available), allocated: roundCents(s.available - s.left) }));
  return { need, available, balance, stores: ranked, sources: out };
};

/** Item 12: 'hold' ("Hold PO") when the due on order is above the gross need less the other vendors' due, 'take' ("Take PO") within it; null without a due amount or a gross need. */
export const shipHint = (due: number | null | undefined, grossNeed: number | null | undefined, otherDue = 0): 'hold' | 'take' | null => {
  if (!isNum(due) || due <= 0 || !isNum(grossNeed)) return null;
  return due > Math.max(0, grossNeed - Math.max(0, otherDue)) + 0.005 ? 'hold' : 'take';
};

// ---- rows ---------------------------------------------------------------------------------------

/** What the row adapters need of the report context (a subset of CellContext, kept here to avoid an import cycle). */
export interface AllocationContext { targetPeriod: number; wosOverride: number | null }

/** A payload store-class row (`class:<id>`): the only rows whose own figures make a store need (chains and subtotals are summed). */
const isPayloadClassRow = (row: AnyRow): boolean => /^class:\d+$/.test(row.key) && !row.isUnallocated;

/** On order due in the target period: the due map's entry, or the whole on order without due dates. */
const dueIn = (s: Summary, period: number): number | null => (s.onOrderDue ? s.onOrderDue[String(period)] ?? 0 : isNum(s.onOrder) ? s.onOrder : null);

/** The need inputs of a store row (null for a non-selling location, a child judged at its parent, a row that is not a store class row). */
const needInputsOf = (row: AnyRow, ctx: AllocationContext): NeedInputs | null => {
  const s = row.summary;
  if (!s || !isPayloadClassRow(row) || s.nonSelling || defersToParent(s)) return null;
  const cell = row.periods[String(ctx.targetPeriod)];
  return {
    target: resolveTarget(s, ctx.wosOverride).target,
    planSales: cell && isNum(cell.salesPlan) ? cell.salesPlan : null,
    avgSales: isNum(s.avgPeriodSales) ? s.avgPeriodSales : null,
    bom: s.inventoryKnown && isNum(s.inventory) ? s.inventory : null,
    due: dueIn(s, ctx.targetPeriod),
  };
};
/** A store row's own need (null for a non-selling location, a child judged at its parent, a row that is not a store class row). */
export const storeNeedOf = (row: AnyRow, ctx: AllocationContext): number | null => {
  const n = needInputsOf(row, ctx);
  return n ? storeNeed(n) : null;
};
/** A store row's gross need (before on order), what the ship / hold hint reads; null where storeNeedOf is. */
export const storeGrossNeedOf = (row: AnyRow, ctx: AllocationContext): number | null => {
  const n = needInputsOf(row, ctx);
  return n ? needBeforeOnOrder(n) : null;
};

/** A non-selling location's on hand to allocate (retail BOM, floored at 0); null for a store or an unknown BOM. */
export const availableOf = (row: AnyRow): number | null => {
  const s = row.summary;
  if (!s?.nonSelling || !s.inventoryKnown || !isNum(s.inventory)) return null;
  return Math.max(0, s.inventory);
};

/** The location a member row is (its name, else its code). */
export const locationLabel = (row: AnyRow): string => row.locationName ?? row.locationCode ?? row.key;

/** A class's allocation over its location rows (the same class in each store and non-selling location). */
export const allocationForMembers = (rows: AnyRow[], ctx: AllocationContext): RowAllocation => {
  const members = rows.filter((r) => r.summary && !r.isUnallocated);
  const sources = members.filter((r) => r.summary?.nonSelling);
  const costs = sources.map((r) => availableCostOf(r, ctx));
  const out = classAllocation(
    members.filter((r) => !r.summary?.nonSelling).map((r) => ({ key: r.key, label: locationLabel(r), need: storeNeedOf(r, ctx) })),
    sources.map((r) => ({ key: r.key, label: locationLabel(r), available: availableOf(r) })),
  );
  const gross = members.filter((r) => !r.summary?.nonSelling).map((r) => storeGrossNeedOf(r, ctx)).filter(isNum);
  return {
    ...out,
    availableCost: costs.length > 0 && costs.every(isNum) ? roundCents(costs.reduce((t, v) => t + v, 0)) : null,
    grossNeed: gross.length ? roundCents(gross.reduce((t, v) => t + v, 0)) : null,
  };
};

/** A non-selling location's BOM at cost (inventoryCostStart of the target period, floored at 0); null without a cost figure. */
export const availableCostOf = (row: AnyRow, ctx: AllocationContext): number | null => {
  if (!row.summary?.nonSelling) return null;
  const c = row.periods[String(ctx.targetPeriod)]?.inventoryCostStart;
  return isNum(c) ? Math.max(0, c) : null;
};

/** The need a column shows for a row: a stamped class allocation's Σ, else a store row's own. */
export const needOf = (row: AnyRow, ctx: AllocationContext): number | null => (row.allocation ? row.allocation.need : storeNeedOf(row, ctx));
/** The gross need a ship / hold hint reads for a row: a stamped class allocation's Σ, else a store row's own. */
export const grossNeedOf = (row: AnyRow, ctx: AllocationContext): number | null => (row.allocation ? row.allocation.grossNeed ?? null : storeGrossNeedOf(row, ctx));
/** The available a column shows for a row on the basis: a stamped class allocation's Σ, else a non-selling row's own. */
export const availableShownOf = (row: AnyRow, ctx: AllocationContext & { basis?: string }): number | null => {
  if (ctx.basis === 'cost') return row.allocation ? row.allocation.availableCost ?? null : availableCostOf(row, ctx);
  return row.allocation ? row.allocation.available : availableOf(row);
};

// The benchmark metric catalogue and its per-value helpers (Tasks 6 and 8). A LEAF module: it imports only
// the interface and the formatters, so `metricDefinitions.ts` can build the benchmark catalogue columns from it at
// module load. (`benchmarks.ts` imports `rowTree`, which imports `metricDefinitions`; importing these values from
// `benchmarks.ts` there would make a runtime cycle that leaves `BENCHMARK_METRICS` undefined whenever `benchmarks.ts`
// is the entry module.) `benchmarks.ts` re-exports everything here, so its import paths are unchanged.

export interface BenchmarkMetricDef {
  id: BenchmarkMetricId;
  label: string;
  /** The Peer column's own label when "Peer <label>" would read badly (Peer WOS (month-end)). */
  peerLabel?: string;
  short: string;
  description: string;
  /** How the value prints: a fraction as %, weeks, or a plain ratio. */
  format: 'pct' | 'weeks' | 'ratio';
  /** true: higher is better (index > 1 good); false: lower is better; used for tone only. */
  higherIsBetter: boolean;
  /** Needs inflow_metrics_publishable on the peer side (BenchmarkMetrics::INFLOW_METRICS). */
  inflow: boolean;
}

/** The eight benchmark metrics in BenchmarkMetrics::METRICS order (metricsFromSums returns its keys in this order). */
export const BENCHMARK_METRICS: readonly BenchmarkMetricDef[] = [
  { id: 'sellThrough', label: 'Sell-through (monthly)', short: 'ST', description: "The month's sales ÷ (beginning inventory + inflows), a fraction of one month's available stock.", format: 'pct', higherIsBetter: true, inflow: true },
  { id: 'weeksOfStock', label: 'Weeks of stock', peerLabel: 'Peer WOS (month-end)', short: 'WOS', description: "Month-end inventory ÷ that month's sales per week (sales ÷ 4.33).", format: 'weeks', higherIsBetter: false, inflow: false },
  { id: 'turnover', label: 'Turnover (monthly)', short: 'Turn', description: "The month's sales ÷ month-end inventory, at retail (× 12 ≈ annual).", format: 'ratio', higherIsBetter: true, inflow: false },
  { id: 'stockToSales', label: 'Stock-to-sales', short: 'S/S', description: "Beginning inventory ÷ the month's sales.", format: 'ratio', higherIsBetter: false, inflow: false },
  { id: 'markdownPct', label: 'Markdown %', short: 'MD %', description: 'Markdowns ÷ sales.', format: 'pct', higherIsBetter: false, inflow: false },
  { id: 'grossMarginPct', label: 'Gross margin %', short: 'GM %', description: '(Sales − cost of goods sold) ÷ sales, over the sales that carry a cost.', format: 'pct', higherIsBetter: true, inflow: false },
  { id: 'cashMarginPct', label: 'Cash margin %', short: 'CM %', description: '(Sales − inflows at cost) ÷ sales, over the sales with comparable inflows.', format: 'pct', higherIsBetter: true, inflow: true },
  { id: 'salesChangeLy', label: 'Sales vs LY', short: 'vs LY', description: "The month's sales against the same month last year.", format: 'pct', higherIsBetter: true, inflow: false },
];
export const BENCHMARK_METRIC_BY_ID = Object.fromEntries(BENCHMARK_METRICS.map((d) => [d.id, d])) as Record<BenchmarkMetricId, BenchmarkMetricDef>;

/** you ÷ peers; for sales change, you − peers (points). Null when either side is missing or the peer is 0. */
export const benchmarkIndex = (metric: BenchmarkMetricId, own: number | null | undefined, peerValue: number | null | undefined): number | null => {
  if (!isNum(own) || !isNum(peerValue)) return null;
  if (metric === 'salesChangeLy') return own - peerValue;
  return peerValue === 0 ? null : own / peerValue;
};

/** `exact`: weeks / months print the real figure, never "52+" (a row detail or KPI whose index was computed from it). */
export const formatBenchmark = (metric: BenchmarkMetricId, v: number | null | undefined, exact = false): string => {
  const f = BENCHMARK_METRIC_BY_ID[metric].format;
  if (!isNum(v)) return EMPTY;
  return f === 'pct' ? fmtPct(v) : f === 'weeks' ? (exact ? fmtWeeksExact(v) : fmtWeeks(v)) : v.toFixed(2);
};
export const formatIndex = (metric: BenchmarkMetricId, v: number | null | undefined): string =>
  !isNum(v) ? EMPTY : metric === 'salesChangeLy' ? `${v > 0 ? '+' : ''}${fmtPctPts(v)}` : v.toFixed(2);

export const PEER_TEXT_NO_GROUP = 'no peer group';
export const PEER_TEXT_NOT_ENOUGH = 'not enough peers';
export const PEER_TEXT_NO_INFLOW = 'inflows not comparable';

/** Weeks of stock in the report's cover unit (Monthly = months: weeks ÷ weeks in the month; Weekly unchanged). */
export const toCoverUnit = (weeks: number | null | undefined, grain: Grain, weeksInMonth: number): number | null =>
  !isNum(weeks) ? null : grain === 'monthly' ? weeks / weeksInMonth : weeks;

/** Whether a peer value may be shown for this metric: a publishable grain, and the inflow gate for inflow metrics. */
const peerShows = (peer: BenchmarkPeer | null | undefined, metric: BenchmarkMetricId): peer is BenchmarkPeer =>
  !!peer && peer.publishable && (!BENCHMARK_METRIC_BY_ID[metric].inflow || peer.inflowPublishable);

/**
 * THE one gate (cells, index columns, KPI lines, chart markers, cards, row detail): the peer's value where `peerText`
 * would print a number, else null. An index, marker or comparison exists only over this value.
 */
export const gatedPeerValue = (peer: BenchmarkPeer | null | undefined, metric: BenchmarkMetricId): number | null => {
  if (!peerShows(peer, metric)) return null;
  const v = peer.metrics[metric];
  return isNum(v) ? v : null;
};

/** The peer cell text: a number only for a publishable grain (and the inflow gate for inflow metrics). */
export const peerText = (peer: BenchmarkPeer | null | undefined, metric: BenchmarkMetricId, exact = false): string => {
  if (!peer) return PEER_TEXT_NO_GROUP;
  if (!peer.publishable) return PEER_TEXT_NOT_ENOUGH;
  if (!peerShows(peer, metric)) return PEER_TEXT_NO_INFLOW;
  return formatBenchmark(metric, gatedPeerValue(peer, metric), exact);
};

/** Index / comparison tone colours shared by the KPI line, the cards and the row detail (neutral stays dark). */
export const BENCHMARK_TONE_CSS: Record<'good' | 'bad' | 'neutral', string> = { good: 'text-[#027A48]', bad: 'text-[#B42318]', neutral: 'text-[#101828]' };

/**
 * The benchmark month for labels ("Sep 2026"): the payload's `monthLabel` when the context carries it, else the
 * display `label` with "Benchmark: " and the fallback " (latest complete month)" suffix stripped; '' without either.
 */
export const benchmarkMonth = (benchmarks: { label?: string | null; monthLabel?: string | null } | null | undefined): string =>
  benchmarks?.monthLabel || benchmarks?.label?.replace(/^Benchmark: /, '').replace(/ \(latest complete month\)$/, '') || '';

export const FALLBACK_SUFFIX = ' (latest complete month)';

/** Whether the benchmark month is the latest complete month standing in for the requested one: the flag, else the display label's suffix. */
export const isFallbackMonth = (benchmarks: { label?: string | null; isFallback?: boolean | null } | null | undefined): boolean =>
  typeof benchmarks?.isFallback === 'boolean' ? benchmarks.isFallback : !!benchmarks?.label?.endsWith(FALLBACK_SUFFIX);

type MonthSource = { label?: string | null; monthLabel?: string | null; isFallback?: boolean | null } | null | undefined;
/** "Sep 2026", plus " (latest complete month)" when the month is a fallback (Monthly on the in-progress month). */
export const benchmarkMonthNote = (benchmarks: MonthSource): string => {
  const month = benchmarkMonth(benchmarks);
  return month && isFallbackMonth(benchmarks) ? `${month}${FALLBACK_SUFFIX}` : month;
};
/** "Sep 2026 month-end" (+ the fallback note): peer figures are month-end, for the latest complete month. */
export const benchmarkMonthEnd = (benchmarks: MonthSource): string => {
  const month = benchmarkMonth(benchmarks);
  return month ? `${month} month-end${isFallbackMonth(benchmarks) ? FALLBACK_SUFFIX : ''}` : 'month-end';
};

/** "(retail)" on a benchmark label at cost: benchmark ratios are retail-based, whatever the report's basis. "X (month-end)" becomes "X (month-end, retail)". */
export const retailTag = (label: string, basis: PriceBasis | undefined): string =>
  basis !== 'cost' ? label : label.endsWith(')') ? `${label.slice(0, -1)}, retail)` : `${label} (retail)`;
export const RETAIL_BASIS_NOTE = 'Benchmarks are retail-based ratios, so they stay at retail while the report is at cost.';
export const retailTooltip = (text: string, basis: PriceBasis | undefined): string => (basis === 'cost' ? `${text} ${RETAIL_BASIS_NOTE}` : text);

/** Where the cover figure is shown in months (Monthly): weeks of stock ÷ weeks in the month, labelled MOS. */
export const WOS_AS_MOS_LABEL = 'Months of stock (MOS)';
export const isMonthlyCover = (metric: BenchmarkMetricId, grain: Grain | undefined): boolean => metric === 'weeksOfStock' && grain === 'monthly';

/** good / bad / neutral from the index (±10% band; 2 pts for sales change). */
export const indexTone = (metric: BenchmarkMetricId, index: number | null | undefined): 'good' | 'bad' | 'neutral' => {
  if (!isNum(index)) return 'neutral';
  const better = BENCHMARK_METRIC_BY_ID[metric].higherIsBetter;
  const delta = metric === 'salesChangeLy' ? index : index - 1;
  const band = metric === 'salesChangeLy' ? 0.02 : 0.1;
  if (Math.abs(delta) <= band) return 'neutral';
  return (delta > 0) === better ? 'good' : 'bad';
};

// OTB by period and running OTB: the browser side of OtbByPeriod.php.
//
// `summary.otbByPeriod` has one line per PERIOD of the OTB span in the table's own grain: a week in Weekly, a month in Monthly
// (Weekly no longer groups its weeks by month). otb = inflowPlan − received − onOrderDue; running = cumulative otb; the last
// running is summary.otb. M1: "Running (cumulative) OTB governs spendability. Per-period figures are not independent budgets",
// which every tooltip here repeats. The lines are retail only (the backend has no per-period figure at cost).
//
// - Totals follow the kit's rule: a group, subtotal or chain row sums its members' lines by period, and the page totals sum
// the counted rows (`totalRows`) that have an OTB, so Σ per-period OTB = Total OTB and the last running = Total OTB. A sum
// is null unless EVERY member with an OTB carries lines (a remainder row has an OTB but no lines): never a partial period.
// - Weekly Action's optional columns "OTB wk of Oct 18", … (Weekly) / "OTB Nov", … (Monthly) are slots 1..MAX_OTB_SLOTS of the
// span (ids `otbPeriod1` …), labelled from the payload's `otbPeriods`; a slot beyond the span has no column.
// - Plan & OTB shows a period's line in its own column (the week's in Weekly, the month's in Monthly); periods past the last
// column are added up in the "Later" column.

/** The longest span the optional per-period columns cover: the largest weekly option (26 weeks); Monthly's 12 months fit too. */
export const MAX_OTB_SLOTS = 26;
export const OTB_PERIOD_ID_PREFIX = 'otbPeriod';
/** Appended to a per-period OTB title. */
export const OTB_PERIOD_RULE_NOTE = 'A period\'s OTB is not an independent budget: running OTB governs spendability.';
export const RUNNING_OTB_RULE = 'Running (cumulative) OTB governs spendability. Per-period figures are not independent budgets.';

export const otbPeriodColumnId = (slot: number): string => `${OTB_PERIOD_ID_PREFIX}${slot}`;
/** 1-based slot of an `otbPeriodN` column id (1..MAX_OTB_SLOTS), else null. */
export const otbPeriodSlot = (id: string): number | null => {
  const m = /^otbPeriod([1-9]\d*)$/.exec(id);
  const slot = m ? Number(m[1]) : null;
  return slot !== null && slot <= MAX_OTB_SLOTS ? slot : null;
};

/** "Nov" for an epoch month. */
export const monthName = (epochMonth: number): string => MONTHS[((epochMonth % 12) + 12) % 12];
/** "Nov 2026" for an epoch month. */
export const monthYear = (epochMonth: number): string => `${monthName(epochMonth)} ${1970 + Math.floor(epochMonth / 12)}`;
/** "wk of Oct 18" for an epoch week (its Sunday). */
export const weekOf = (epochWeek: number): string => {
  const s = epochWeekStart(epochWeek);
  return `wk of ${MONTHS[s.getUTCMonth()]} ${s.getUTCDate()}`;
};
/** D6: the note on a Monthly plan taken from the V2 history: "Plan as it stood in Sep 2026 (V2)". */
export const v2HistoryNote = (epochMonth: number): string => `Plan as it stood in ${monthYear(epochMonth)} (V2)`;
/** The short name of a span period: "wk of Oct 18" (Weekly) or "Nov" (Monthly). */
export const periodShortName = (period: number, grain: Grain): string => (grain === 'weekly' ? weekOf(period) : monthName(period));
/** The full name of a span period: "Oct 18–24, 2026" (Weekly) or "Nov 2026" (Monthly). */
export const periodFullName = (period: number, grain: Grain): string => (grain === 'weekly' ? formatWeekLabel(period, { withYear: true }) : formatPeriodLabel(period, 'monthly'));
/** The word for one span period in the grain: "week" / "month". */
export const periodNoun = (grain: Grain): string => (grain === 'weekly' ? 'week' : 'month');
/** "OTB wk of Oct 18" / "OTB Nov" (Weekly Action's column header); the generic "OTB period 2" without a payload. */
export const otbPeriodLabel = (slot: number, periods: number[] | undefined | null, grain: Grain): string =>
  periods && isNum(periods[slot - 1]) ? `OTB ${periodShortName(periods[slot - 1], grain)}` : `OTB ${periodNoun(grain)} ${slot}`;

/** A row's lines, or null (no OTB, a vendor row, an older payload). */
export const otbLines = (s: Summary | null | undefined): OtbPeriodLine[] | null =>
  s && Array.isArray(s.otbByPeriod) && s.otbByPeriod.length > 0 ? s.otbByPeriod : null;

/** The line of a 1-based slot (the span's nth period), or null. */
export const otbLineOfSlot = (s: Summary | null | undefined, slot: number): OtbPeriodLine | null => otbLines(s)?.[slot - 1] ?? null;

/** The line of a span period, or null (no lines, a period outside the span). */
export const otbLineOfPeriod = (s: Summary | null | undefined, period: number): OtbPeriodLine | null => otbLines(s)?.find((l) => l.period === period) ?? null;

const FIELDS: (keyof Omit<OtbPeriodLine, 'period'>)[] = ['inflowPlan', 'received', 'onOrderDue', 'otb', 'running'];

/**
 * Σ of members' lines by period (same periods in every member: they come from one payload). Null when no member has an OTB, or
 * a member with an OTB has no lines, or the members' periods differ.
 */
export const sumOtbLines = (summaries: (Summary | null | undefined)[]): OtbPeriodLine[] | null => {
  const withOtb = summaries.filter((s): s is Summary => !!s && isNum(s.otb));
  if (withOtb.length === 0) return null;
  const all = withOtb.map((s) => otbLines(s));
  if (all.some((l) => l === null)) return null;
  const first = all[0] as OtbPeriodLine[];
  if (all.some((l) => (l as OtbPeriodLine[]).length !== first.length || (l as OtbPeriodLine[]).some((x, i) => x.period !== first[i].period))) return null;
  return first.map((line, i) => {
    const out: OtbPeriodLine = { period: line.period, inflowPlan: 0, received: 0, onOrderDue: 0, otb: 0, running: 0 };
    all.forEach((l) => FIELDS.forEach((f) => { out[f] += (l as OtbPeriodLine[])[i][f]; }));
    return out;
  });
};

/** "Running OTB through wk of Oct 18: $X. <rule>" / "… through Nov: …" title of a per-period cell, `money` formats the running figure. */
export const runningTitle = (line: OtbPeriodLine, money: (v: number | null) => string, grain: Grain): string =>
  `Running OTB through ${periodShortName(line.period, grain)}: ${money(line.running)}. ${RUNNING_OTB_RULE}`;

//: the texts that name a window that starts after the target period. The OTB popover's
// caption names the start ("Jan W1 + 13 wk", "Jan 2027 + 3"); the OTB KPIs name the window itself from the payload's `spanPeriods`
// ("Jan W1–Mar W4", "Jan–Apr 2027"), which is what the server actually counted.

const yearOf = (epochMonth: number): number => 1970 + Math.floor(epochMonth / 12);
const monthOf = (epochMonth: number): string => MONTHS[((epochMonth % 12) + 12) % 12];

/** An epoch week as its month and position in it: "Jan W1" (the first week whose Sunday falls in January), "Mar W4". */
export const weekOfMonthLabel = (week: number): string => {
  const month = epochMonthOfWeek(week);
  return `${monthOf(month)} W${weeksOfMonth(month).indexOf(week) + 1}`;
};

/** A window start as the OTB caption names it: "Jan W1" (Weekly) / "Jan 2027" (Monthly). */
export const otbStartLabel = (period: number, grain: Grain): string => (grain === 'weekly' ? weekOfMonthLabel(period) : `${monthOf(period)} ${yearOf(period)}`);

/**
 * The window the payload counted, first to last of `spanPeriods`: "Jan–Apr 2027" / "Nov 2026–Feb 2027" (Monthly), "Jan W1–Mar W4"
 * (Weekly); one period reads alone ("Jan 2027", "Jan W1"). Null without periods.
 */
export const otbWindowText = (spanPeriods: readonly number[] | null | undefined, grain: Grain): string | null => {
  if (!spanPeriods || spanPeriods.length === 0) return null;
  const first = Math.min(...spanPeriods);
  const last = Math.max(...spanPeriods);
  if (grain === 'weekly') return first === last ? weekOfMonthLabel(first) : `${weekOfMonthLabel(first)}–${weekOfMonthLabel(last)}`;
  if (first === last) return `${monthOf(first)} ${yearOf(first)}`;
  return yearOf(first) === yearOf(last) ? `${monthOf(first)}–${monthOf(last)} ${yearOf(first)}` : `${monthOf(first)} ${yearOf(first)}–${monthOf(last)} ${yearOf(last)}`;
};

//: "Distribute OTB" (Weekly Action and Inventory Health). A row with no OTB of its own under a row that
// has one (a child class judged at its family parent, a product or SKU under its class) gets the parent's OTB split the way vendors are:
// distributed OTB = parent's planned inflows over the span × the row's share − the row's own received to date − its own on order
// The share is the row's part of the parent on the allocation basis over the allocation window (the same two controls the vendor views use):
// Sales % of total (net sales), Cash margin % of total (sales − inflow cost), Inventory % of total (BOM inventory at the target period).
// Gross margin needs COGS, which the class / product rows do not carry, so below the vendor level it shares by net sales. Negative values
// count as 0; if every value is 0 the split falls back to net sales, then to an even split. The shares sum to 1 over the children shown; when
// a filter removed some of them, the parent's own figure is the denominator instead (so the rest never inflate). Vendor rows keep the backend's
// split (they have an OTB of their own). Retail only.

const isFinite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export interface DistributedOtb { value: number; share: number; basis: AllocBasis; sharedBy: AllocBasis; parentLabel: string; windowPeriods: number }

/** The allocation window's periods ending at the target period (as VendorAllocationService.windowPeriods). */
export const allocationWindow = (ctx: CellContext): number[] => {
  const n = Math.max(1, ctx.allocation?.window ?? ctx.lookback.length ?? 1);
  return Array.from({ length: n }, (_, i) => ctx.targetPeriod - n + 1 + i);
};

/** A row's figure on the basis over the window, floored at 0; null when it has no figure at all. */
export const allocBasisValue = (row: AnyRow, basis: AllocBasis, ctx: CellContext): number | null => {
  if (basis === 'inventory') {
    const bom = row.periods[String(ctx.targetPeriod)]?.inventoryStart;
    return isFinite(bom) ? Math.max(0, bom) : null;
  }
  let total = 0;
  let any = false;
  allocationWindow(ctx).forEach((p) => {
    const c = row.periods[String(p)];
    if (!c || !isFinite(c.sales)) return;
    any = true;
    total += basis === 'cash_margin' ? c.sales - (isFinite(c.inflowCost) ? c.inflowCost : 0) : c.sales;
  });
  return any ? Math.max(0, total) : null;
};

/**
 * The child's on order that the parent's OTB counts: what is due in the parent's span up to where its plan runs (otbCoveredThrough; overdue
 * lands in the first period), from the child's due-by-period map; without one (products, SKUs), its on order scaled by the part of its own on
 * order the parent's OTB counts.
 */
const onOrderInParentSpan = (child: AnyRow, ps: NonNullable<AnyRow['summary']>): number => {
  const s = child.summary;
  const due = s?.onOrderDue;
  const span = (ps.otbByPeriod ?? []).map((l) => l.period);
  if (due && span.length > 0) {
    const through = isFinite(ps.otbCoveredThrough) ? ps.otbCoveredThrough : span[span.length - 1];
    return Object.entries(due).reduce((t, [p, v]) => (Number(p) <= through && isFinite(v) ? t + v : t), 0);
  }
  // No due dates (product / SKU rows): count the same part of its on order as the parent's OTB counts of its own (a product's own
  // otbOnOrder is its whole on order, so it is not used).
  const all = isFinite(s?.onOrder) ? (s!.onOrder as number) : 0;
  const parentAll = isFinite(ps.onOrder) ? ps.onOrder : null;
  const parentCounted = isFinite(ps.otbOnOrder) ? ps.otbOnOrder : parentAll;
  return parentAll && parentAll > 0 && isFinite(parentCounted) ? all * Math.min(1, Math.max(0, parentCounted / parentAll)) : all;
};

/** The basis actually used below the vendor level (gross margin has no COGS here: net sales). */
export const basisBelowVendor = (basis: AllocBasis): AllocBasis => (basis === 'gross_margin' ? 'net_sales' : basis);

/** Whether a row takes a distributed OTB: it has no OTB of its own, or a child class that defers to its parent's plan. */
export const takesDistributedOtb = (row: AnyRow): boolean => {
  const s = row.summary;
  if (!s || s.otbExcluded) return false;
  return !isFinite(s.otb) || !!s.planParentKey;
};

/**
 * The distributed OTB of each child of `parent` (by row key), or an empty map when the parent has no OTB to share. Only children that take
 * one get an entry; every child counts towards the shares.
 */
export const distributeOtb = (parent: AnyRow, children: AnyRow[], ctx: CellContext, parentLabel: string, filtered = false): Map<string, DistributedOtb> => {
  const out = new Map<string, DistributedOtb>();
  const ps = parent.summary;
  if (!ps || !isFinite(ps.otb) || !isFinite(ps.inflowPlanSpan) || children.length === 0) return out;
  const basis = ctx.allocation?.basis ?? 'net_sales';
  const shareBy = (b: AllocBasis): { values: number[]; den: number } => {
    const values = children.map((c) => allocBasisValue(c, b, ctx) ?? 0);
    const sum = values.reduce((a, v) => a + v, 0);
    // Every child shown: the shares sum to 1 over them (as vendors' do). Some filtered out: the parent's own figure, so the rest never inflate.
    const own = allocBasisValue(parent, b, ctx) ?? 0;
    return { values, den: filtered && own >= sum ? own : sum };
  };
  let sharedBy = basisBelowVendor(basis);
  let { values, den } = shareBy(sharedBy);
  if (den <= 0 && sharedBy !== 'net_sales') {
    sharedBy = 'net_sales';
    ({ values, den } = shareBy(sharedBy));
  }
  children.forEach((c, i) => {
    if (!takesDistributedOtb(c)) return;
    const share = den > 0 ? values[i] / den : 1 / children.length;
    const s = c.summary;
    const received = isFinite(s?.receivedToDate) ? (s!.receivedToDate as number) : 0;
    const onOrder = onOrderInParentSpan(c, ps);
    out.set(c.key, { value: (ps.inflowPlanSpan as number) * share - received - onOrder, share, basis, sharedBy, parentLabel, windowPeriods: allocationWindow(ctx).length });
  });
  return out;
};

// The column catalogue for the three weekly planning reports (Plan 3 ): one list of
// MetricDefinition objects, filtered per report and tier by `columnsFor`. Each definition renders a
// cell through the /3 helpers so the table, the CSV export and the sort all read one value.
//
// Decisions recorded here (the plan leaves them open):
// - `inventory` / `inventoryPlan` / `inventoryVsPlan` / `pctOfInventory` read the TARGET PERIOD'S
// START inventory (`inventoryStart`, falling back to `summary.inventory`, which the backend sets
// from the same cell): the V3 `inventoryPlan` is a begin-of-period value and `summary.wos` is
// computed on start-of-period inventory, so Actual, Plan, vs Plan and WOS describe one instant.
// On the cost basis the cell's `inventoryCostStart` is used (class rows carry it too; Tier C
// none, so "—" titled "not available at cost"). `inventoryCost` always shows the cost cell.
// - (P1-11): every dollar cell reads its value through `basis.ts` (`valueOnBasis` / `summaryOnBasis`): the
// cost figure on the cost basis, or "—" titled NOT_AT_COST_TOOLTIP where the data has none (sales, Sales Trend, plan
// sales / inventory / markdowns, markdowns, LY, OTB + Dev; plan inflow and OTB in Weekly). Never retail in a cost view.
// - Plans are retail, so every comparison with a plan (`inventoryVsPlan`, the Inventory Health KPI
// and row detail) reads the RETAIL actual (`inventoryRetailOf`) whatever basis is displayed; the
// displayed amount still follows the basis (product owner, 2026-10-18; Plan & OTB's `actRetail`).
// - `% of inventory` divides on ONE basis per sibling set (the row tree decides it, for the % and its
// sort alike): cost only when the parent and every sibling carry cost cells, retail otherwise.
// - `cover` renders "52+ / target" when `summary.wos` is null with known inventory > 0 (stock, no
// sales), with `value` Infinity so it sorts above every finite cover; unknown inventory or
// on-hand ≤ 0 renders "—" for the WOS part.
// - A WOS override (`ctx.wosOverride`) re-derives `health` / `action` client-side through
// `targets.status`; without one the payload's `summary.status` is shown as-is.
// - `% of Total` columns read `ctx.pctOfParent` (the row tree computes it per FlatRow); "—" without it.
// - `ctx.currentPeriod` (for class Fresh %) defaults to `targetPeriod` when absent.
// - `plan_otb` is a period matrix in Plan 4; its column set here is the summary / Total column set
// (sales, inflow, inventory, markdowns, on order, OTB, OTB + Dev) the matrix and its CSV share.
// - `otbDev` reads `summary.otbDev` = OTB + (plan BOM − actual BOM) for the span's first period.
// A saved view without an explicit `otbDev` visibility shows it where it is a default (Weekly Action, Plan & OTB):
// `columnStateFor` inserts it after OTB; a view that set it keeps its choice. Inventory Health lists it hidden.
// - `label` text: class rows "code · name", SKU rows "title · sku", vendor rows the vendor name,
// Unallocated / Unclassified / subtotal rows their className.
// - Sales Trend: `salesTrend` / `trendPct` read the target cell through
// `trendOf(cell, ctx.trendMethod)` and carry the basis in words as the cell's `title` (`trendTitle`:
// with "(requested: …)" when the chosen method fell down the chain for that cell); they are
// default columns whose visibility is dynamic: `columnStateFor(…, {trendPeriod})` shows them when
// the target period is a trend period, unless the view chose explicitly. In a trend period
// `salesVsCompare` compares `comparableSales` (Sales Trend) with the full-period plan, the full LY
// period or the lookback average; every other column keeps the actuals.
// - Benchmark columns: `bmPeers`
// ("13 · Sep 2026"), then per benchmark metric a peer value ("Peer <label>") and an index ("<short> index";
// sales change: "vs LY vs peers" in points). They read `ctx.benchmarks.byRowKey[row.key]`: vendor / product /
// SKU rows and rows without an entry show "—"; a non-ok entry shows its status text ("not enough peers",
// "mixed master classes", "no master class", "no peer group") in the peer and Peers cells and "—" in the index;
// an index's tone is its `value` sign only (the renderer stays generic). Always optional and hidden by default;
// `columnsFor` leaves them out altogether when the benchmark level is 'none' (not entitled), so saved views
// that name them are inert there and show them again once the level allows.
// Type-only: benchmarks.ts imports rowTree, which imports this module; a type import keeps the cycle out of the runtime.
// Runtime values come from the LEAF module (benchmarks.ts imports rowTree, which imports this module).

export type MetricKind = 'currency' | 'units' | 'pct' | 'weeks' | 'text' | 'status' | 'action' | 'date';
export type MetricGroup = 'identity' | 'health' | 'sales' | 'inventory' | 'inflow' | 'otb' | 'freshness' | 'lastYear' | 'pctOfTotal' | 'benchmark';
export type PctMeasure = 'sales' | 'inventory' | 'inflow' | 'onOrder';
export type PctOfParent = Record<PctMeasure, number | null>;

/** `v2History` (D6): set (true) only on a Monthly plan value taken from the V2 plan as it stood in the month; absent otherwise. */
export interface CellFlags { estimated: boolean; allocated: boolean; projected: boolean; lyPartial: boolean; v2History?: boolean }
export interface CellValue {
  value: number | string | null; text: string; flags: CellFlags;
  /** Shown as the cell's title (the trend columns: the basis in words). */
  title?: string;
  /** A small tag beside the value ("WTD" / "MTD": compared with the prorated plan). */
  tag?: string;
}

export interface CellContext {
  grain: Grain;
  targetPeriod: number;
  currentPeriod?: number;
  lookback: number[];
  compare: Compare;
  basis: PriceBasis;
  multiplier: Multiplier | number;
  wosOverride: number | null;
  pctOfParent?: PctOfParent | null;
  /** Distribute OTB: the row's share of its parent's OTB (the row tree computes it per FlatRow). */
  distributedOtb?: DistributedOtb | null;
  /** The vendor allocation settings in force ("allocated by {basis} over {window}" tooltip). */
  allocation?: AllocationSettings | null;
  /** Which Sales Trend values every trend consumer reads (`trendOf`); 'auto' when absent (sets it). */
  trendMethod?: TrendMethod;
  /** which open orders the OTB nets ('window' when absent); 'all' changes the OTB texts only (the server did the netting). */
  otbOnOrder?: OtbOnOrder;
  /** The payload's last sales day (Sales Trend's "through"); the planShapeZero tooltip names it. */
  salesThrough?: string | null;
  /** per-row-key benchmark lookup (class:<id>, location:<code>), the label and the level; null = none. */
  benchmarks?: BenchmarkContext | null;
}

/** what the hook resolves from a benchmark payload once per payload + scope filter (the cells, KPIs, charts and cards read it). */
export interface BenchmarkContext {
  byRowKey: Record<string, BenchmarkRowValue>;
  client: BenchmarkRowValue | null;
  label: string | null;
  /** The payload's month alone ("Sep 2026"), for column labels and texts; `label` is the display line (may carry the fallback note). */
  monthLabel?: string | null;
  /** The payload's `isFallback`: the month is the latest complete month standing in for the requested one (display only; `label` already says it). */
  isFallback?: boolean;
  level: BenchmarkLevel;
  weeksInMonth: number;
}

export interface AllocationSettings { basis: AllocBasis; window: number; grain: Grain }

export interface MetricDefinition {
  id: string;
  label: string;
  shortLabel: string;
  description: string;
  kind: MetricKind;
  align: 'left' | 'right' | 'center';
  defaultWidth: number;
  tiers: Tier[];
  reports: ReportKey[];
  defaultVisible: boolean;
  group: MetricGroup;
  cell: (row: AnyRow, ctx: CellContext) => CellValue;
}

export interface ReportColumn { id: string; visible: boolean }

const ALL_TIERS: Tier[] = ['A', 'B', 'C'];
const SKU_TIERS: Tier[] = ['A', 'B'];
const ALL_REPORTS: ReportKey[] = ['inventory_health', 'plan_otb', 'weekly_action'];
const NO_FLAGS: CellFlags = { estimated: false, allocated: false, projected: false, lyPartial: false };

const targetCell = (row: AnyRow, ctx: CellContext): Cell | undefined => row.periods[String(ctx.targetPeriod)];

/**
 * D4: Weekly uses weekly plans only. A class without one (summary.planBasis 'none_weekly') shows a muted "No weekly
 * plan" in its plan and OTB cells (the CSV and sort read the same text, value null).
 */
export const NO_WEEKLY_PLAN_LABEL = 'No weekly plan';
/**
 * Last year's inventory (`inventoryLy`) is last year's START-of-month inventory (LY BOM): LY month M−1's month-end (Tier C:
 * ReportDataSource's inventoryBomLY). It compares with this period's start inventory, never with an end.
 * Weekly has no LY week-start inventory, so it stays empty there.
 */
export const LY_BOM_TOOLTIP = "Last year's start-of-month inventory (LY BOM): last year's prior month-end, compared with this period's start-of-period inventory. Monthly only: Weekly has no LY week-start inventory.";
export const LY_BOM_MISSING_TOOLTIP = "No LY BOM: the client's history starts in last year's month, so there is no prior month-end to start it from.";
export const LY_BOM_MISSING_NOTE = 'No LY prior-month history';
export const LY_BOM_MISSING_KPI = 'No LY BOM: LY history starts later';
export const LY_BOM_WEEKLY_TOOLTIP = 'Weekly has no LY week-start inventory, and the LY month-start inventory is not compared with a week. Switch to Monthly to compare with last year\'s start-of-month inventory.';
export const NO_WEEKLY_PLAN_TOOLTIP = 'This class has no weekly plan: Weekly shows its actuals only (no plan, no OTB). Its target is its own average.';
export const hasNoWeeklyPlan = (row: AnyRow): boolean => row.summary?.planBasis === 'none_weekly';
/**
 * (P0-4): no plan is loaded for the period (summary.planBasis 'none_plan', sources.plans 'none_plan'): the plan
 * and OTB cells read "—" with this title, never an OTB of −on order. The page notice explains it once.
 */
export const NO_PLAN_LOADED_TOOLTIP = 'No plan is loaded for this period, so there is no plan and no open to buy (never minus on order).';
export const hasNoPlanLoaded = (row: AnyRow): boolean => row.summary?.planBasis === 'none_plan';
export const noPlanLoadedCell = (): CellValue => ({ value: null, text: EMPTY, flags: { estimated: false, allocated: false, projected: false, lyPartial: false }, title: NO_PLAN_LOADED_TOOLTIP });
export const noWeeklyPlanCell = (): CellValue => ({ value: null, text: NO_WEEKLY_PLAN_LABEL, flags: { estimated: false, allocated: false, projected: false, lyPartial: false }, title: NO_WEEKLY_PLAN_TOOLTIP });
/**
 * D4 / D5: an OTB that covers part of the span only (the weekly plan ends first): "OTB through <week>", and it nets
 * only the on order due by that week (the On Order column keeps every PO in the span).
 */
export const otbThroughTitle = (row: AnyRow, ctx: CellContext): string | undefined => {
  if (row.summary?.otbBasis !== 'weekly_plan_partial' || !isNum(row.summary.otbCoveredThrough)) return undefined;
  const week = formatPeriodLabel(row.summary.otbCoveredThrough, ctx.grain);
  // counting all open orders, no PO is left out for its due date.
  if (ctx.otbOnOrder === 'all') return `OTB through ${week} (the weekly plan ends there), counting all open orders whatever their due date.`;
  // D5 review: a parent whose plan is its children's lists each planned child's own end (each nets its on order due by then).
  const others = (row.summary.otbCoverage ?? []).filter((c) => c.key !== row.key);
  if (others.length === 0) return `OTB through ${week}. On order due after ${week} isn't counted.`;
  const ends = others.map((c) => `${c.label} through ${formatPeriodLabel(c.through, ctx.grain)}`).join('; ');
  return `OTB through ${week} (the earliest weekly plan end). Weekly plans end: ${ends}. Each class's on order due after its last planned week isn't counted.`;
};
/** D4: wraps a plan / OTB column's cell: "No weekly plan" for a class without one; "—" when no plan is loaded. */
const planCell = (cell: MetricDefinition['cell']): MetricDefinition['cell'] => (row, ctx) =>
  (hasNoWeeklyPlan(row) ? noWeeklyPlanCell() : hasNoPlanLoaded(row) ? noPlanLoadedCell() : cell(row, ctx));
const ALLOC_SHARE_LABELS: Record<string, string> = { net_sales: 'sales', cash_margin: 'cash margin', inventory: 'BOM inventory', gross_margin: 'gross margin' }; // unitized at use
/** Distribute OTB: a row's share of its parent's OTB (retail only), titled with how it was split. */
const distributedOtbCell = (d: DistributedOtb, ctx: CellContext): CellValue => {
  if (ctx.basis === 'cost') return notAtCostCell();
  const how = d.sharedBy === 'inventory' ? 'its share of the BOM inventory' : `its share of ${ALLOC_SHARE_LABELS[d.sharedBy]} over ${d.windowPeriods} ${ctx.grain === 'monthly' ? 'months' : 'weeks'}`;
  const note = d.basis === 'gross_margin' && d.sharedBy === 'net_sales' ? ' (gross margin needs COGS, which is not available below vendors: shared by sales)' : '';
  return { ...out(d.value, fmtCurrency(d.value), { ...NO_FLAGS, allocated: true }), title: unitize(`Distributed from ${d.parentLabel}: ${fmtPct(d.share)} of its planned inflows (${how}${note}) − this row's received − its on order.`, ctx.grain) };
};
/**
 * the OTB cell says how much of the on order it nets when that differs from the On Order column (summary.otbOnOrder
 * vs summary.onOrder: the rest is due after the last planned week). Retail only (the figures are retail); null when they agree.
 */
export const otbOnOrderNote = (row: AnyRow, ctx: CellContext): string | null => {
  const s = row.summary;
  if (ctx.basis === 'cost' || !s || !isNum(s.otbOnOrder) || !isNum(s.onOrder) || Math.abs(s.otbOnOrder - s.onOrder) < 0.005) return null;
  // with every open PO counted the difference is the orders due outside the window (overdue ones included).
  if (ctx.otbOnOrder === 'all') return `On order counted in OTB: ${fmtCurrency(s.otbOnOrder)} (all open orders, whatever their due date) against ${fmtCurrency(s.onOrder)} due in the window.`;
  return `On order counted in OTB: ${fmtCurrency(s.otbOnOrder)} of ${fmtCurrency(s.onOrder)} (the rest is due after the last planned week).`;
};
/** the OTB cell's title when every open order is counted (the column description names the rule). */
export const OTB_ALL_ORDERS_TITLE = 'Counting all open orders, whatever their due date (overdue included).';
const otbCell = (field: 'otb' | 'otbDev'): MetricDefinition['cell'] => planCell((row, ctx) => {
  const b = summaryOnBasis(row, field, ctx.basis);
  if (!b.available) return notAtCostCell();
  const v = b.value;
  const title = isNum(v) ? [ctx.otbOnOrder === 'all' ? OTB_ALL_ORDERS_TITLE : null, otbThroughTitle(row, ctx), field === 'otb' ? otbOnOrderNote(row, ctx) : null].filter(Boolean).join(' ') : '';
  return title ? { ...out(v, fmtCurrency(v)), title } : out(v, fmtCurrency(v));
});

/**
 * / OTB span follows the grain: Weekly Action's optional per-period OTB columns ("OTB wk of Oct 18", … in
 * Weekly; "OTB Nov", "OTB Dec", … in Monthly: the nth period of the OTB span, hidden by default; the page labels them from the
 * payload's otbPeriods and Weekly Action drops the slots beyond the span). Each reads summary.otbByPeriod[n − 1].otb (retail only:
 * "not available at cost") and carries the running OTB through that period as its title. The description is written "week or
 * month" so the grain's unit helper (unitize) reads it in the table's own unit.
 */
/**
 * weekly timing of on order is only as good as the POS's expected date. Shown in How it works and in the "OTB by week"
 * tooltips (this sentence verbatim, so every surface says the same thing).
 */
export const ON_ORDER_TIMING_CAVEAT = 'Weekly on order uses each PO\'s expected date from your POS. In Shopify this is often the cancel date, so weekly timing is approximate. Use Monthly for how much to buy and Weekly for when.';
/** the short form of the timing caveat for a Weekly OTB-by-week / Running OTB cell's title (the grid has one per cell). */
export const ON_ORDER_WEEKLY_CELL_CAVEAT = 'Weekly on order uses each PO\'s expected date from your POS (in Shopify often the cancel date), so weekly timing is approximate.';
export const OTB_PERIOD_TOOLTIP = `Open to buy for one week or month of the span: its planned inflows − what the current period has received (first week or month only) − on order due in it. ${RUNNING_OTB_RULE} Hover a cell for the running OTB through the week or month. ${ON_ORDER_TIMING_CAVEAT}`;
const otbPeriodCell = (slot: number): MetricDefinition['cell'] => planCell((row, ctx) => {
  if (ctx.basis === 'cost') return notAtCostCell();
  const line = otbLineOfSlot(row.summary, slot);
  return line ? { ...out(line.otb, fmtCurrency(line.otb)), title: runningTitle(line, fmtCurrency, ctx.grain) } : out(null, EMPTY);
});

/** (Adam's D1): the OTB + Dev column, KPI and Plan & OTB line tooltip. */
export const OTB_DEV_TOOLTIP = 'Open to buy plus the gap between planned and actual BOM inventory (plan BOM − actual BOM): lower when you start the period over plan, higher when under.';
/** M1 §8, noted once on the OTB KPI: PNOTB / NOTB classes carry no OTB (summary.otbExcluded) and every OTB total leaves them out. */
export const otbExclusionNote = (excluded: number): string =>
  `PNOTB / NOTB classes have no OTB and are left out of the OTB and OTB + Dev totals${excluded > 0 ? ` (${excluded} ${excluded === 1 ? 'class' : 'classes'} here)` : ''}.`;
/**
 * The OTB KPI's note when Weekly OTB covers less than the span (partial weekly plans: `KpiTotals.otbPartial`): "Weekly plans for N
 * classes end before <span end>" (item 7: the count, not just the earliest end). Each class's OTB nets the on order due
 * by its last planned week only, while Monthly OTB uses the monthly plan over the whole span. Null on Monthly or with full coverage.
 */
export const otbCoverageNote = (partial: readonly { through: number }[] | null | undefined, spanEnd: number | null | undefined, grain: Grain): string | null => {
  if (grain !== 'weekly' || !partial || partial.length === 0) return null;
  const n = partial.length;
  const end = isNum(spanEnd) ? formatPeriodLabel(spanEnd, 'weekly') : 'the span ends';
  return `Weekly plans for ${n} ${n === 1 ? 'class' : 'classes'} end before ${end}`;
};
/** Item 7: the OTB tooltip's list of where each partial weekly plan ends (the first 8, then how many more). */
export const otbCoverageEnds = (partial: readonly { label: string; through: number }[] | null | undefined, grain: Grain): string | null => {
  if (grain !== 'weekly' || !partial || partial.length === 0) return null;
  const shown = partial.slice(0, 8).map((p) => `${p.label} through ${formatPeriodLabel(p.through, 'weekly')}`);
  const more = partial.length > 8 ? `; and ${partial.length - 8} more` : '';
  return `Weekly plans end: ${shown.join('; ')}${more}. Each class's OTB counts its on order due by its last planned week; Monthly OTB uses the monthly plan.`;
};
/** The OTB KPI's `note` (the coverage line, then where each partial plan ends; the KPI row shows it in the label's tooltip), or nothing. */
export const otbCoverageNoteProp = (partial: readonly { label: string; through: number }[] | null | undefined, spanPeriods: readonly number[] | null | undefined, grain: Grain): { note?: string } => {
  const line = otbCoverageNote(partial, spanPeriods && spanPeriods.length ? spanPeriods[spanPeriods.length - 1] : null, grain);
  return line ? { note: `${line}. ${otbCoverageEnds(partial, grain)}` } : {};
};
/**
 * A cell's flags for a column. `estimated` (the italic "estimated from sales and inflows" marker)
 * is produced here, not in the renderer, so the table, the CSV and Plan & OTB agree on which
 * values carry it. The cell's `isEstimated` means the period's INVENTORY was back-calculated from
 * sales and inflows (no inventory snapshot for the period: a Sunday, or before the first
 * snapshot); sales, inflow, markdowns, last year and every plan are measured, so only columns
 * whose value is computed from that inventory pass `inventoryDerived`:
 * - yes: `cover` (WOS = inventory ÷ average sales), `inventory`, `inventoryVsPlan` (actual vs plan),
 * `inventoryCost` (the cost start inventory is back-calculated the same way), `pctOfInventory`
 * (the row's share of its parent's inventory).
 * - no: `health` / `action` (a pill, not a value: the WOS cell beside it carries the marker),
 * `thisPeriodSales`, `salesVsCompare`, `salesTrend`, `trendPct`, `inflow*`, `markdowns`, the
 * plan columns, `salesLy` / `inventoryLy` (history), `onOrder` / `otb` (span figures), `freshPct`
 * (reads the CURRENT period's inventory through `classFreshPct`, which the target cell's flag
 * does not describe), the other `% of Total` columns.
 * Plan & OTB's matrix applies the same rule: Inventory Act only (`planOtbCells.ts`).
 */
/** a dollar cell the data has no cost figure for, on the cost basis: "—" titled NOT_AT_COST_TOOLTIP (value null). */
export const notAtCostCell = (flags: CellFlags = NO_FLAGS): CellValue => ({ value: null, text: EMPTY, flags, title: NOT_AT_COST_TOOLTIP });
/** A dollar value on the basis as a cell (see `basis.ts`). */
const basisOut = (b: BasisValue, flags: CellFlags = NO_FLAGS): CellValue => {
  const t = basisText(b, fmtCurrency);
  return t.title ? { value: null, text: t.text, flags, title: t.title } : out(b.value, t.text, flags);
};
// `projected` (the PROJECTED tag) marks only a SALES value that is actually trended — a trend-period cell (Tier C's
// are ORBIT's projection of the sales so far). Inventory, cover, inflow, plans and the rest are measured or planned, never projected.
// `allocated` (the ALLOCATED tag) marks only an allocated PLAN value (`planValue`): the vendor's share of the class plan. Actuals are measured.
const flagsOf = (cell: Cell | undefined, withLy = false, inventoryDerived = false, salesTrended = false, planValue = false): CellFlags =>
  cell ? { estimated: inventoryDerived && cell.isEstimated, allocated: planValue && cell.isAllocated, projected: salesTrended && cell.isProjected && isTrendCell(cell), lyPartial: withLy && cell.lyPartial, ...(planValue && cell.planV2History ? { v2History: true } : {}) } : NO_FLAGS;
const inventoryFlagsOf = (cell: Cell | undefined): CellFlags => flagsOf(cell, false, true);
const out = (value: number | string | null, text: string, flags: CellFlags = NO_FLAGS): CellValue => ({ value, text, flags });
/** Last year's BOM for the target period, Monthly only: Weekly has no LY week-start inventory, so it is never paired with a week. */
export const lyBomOf = (row: AnyRow, ctx: CellContext): number | null =>
  ctx.grain === 'monthly' ? targetCell(row, ctx)?.inventoryLy ?? null : null;
/** The target period's LY BOM is missing because LY month M−1 is before the client's history (Monthly only). */
export const lyBomMissing = (row: AnyRow, ctx: CellContext): boolean =>
  ctx.grain === 'monthly' && !!targetCell(row, ctx)?.inventoryLyMissing;
const lyBomCell = (row: AnyRow, ctx: CellContext): CellValue => {
  if (ctx.grain !== 'monthly') return out(null, EMPTY);
  const c = targetCell(row, ctx);
  const v = lyBomOf(row, ctx);
  if (!isNum(v) && lyBomMissing(row, ctx)) return { ...out(null, EMPTY, flagsOf(c, true)), title: LY_BOM_MISSING_TOOLTIP };
  return basisOut(pickBasis(v, null, ctx.basis), flagsOf(c, true)); // LY inventory is retail only
};

/**
 * True when start inventory shows at cost: the cost basis. every row carries its cost inventory (class rows
 * too) or reads "—" (not available at cost); retail never stands in for cost.
 */
export const inventoryAtCost = (_row: AnyRow, ctx: CellContext): boolean => ctx.basis === 'cost';
/** Start-of-target-period RETAIL inventory on every basis: what comparisons with the (retail) plan and LY use. */
export const inventoryRetailOf = (row: AnyRow, ctx: CellContext): number | null => {
  const c = targetCell(row, ctx);
  if (c && isNum(c.inventoryStart)) return c.inventoryStart;
  return row.summary?.inventory ?? null;
};
/** Start-of-target-period inventory on the chosen basis, with its availability (see `basis.ts`). */
export const inventoryOnBasis = (row: AnyRow, ctx: CellContext): BasisValue =>
  pickBasis(inventoryRetailOf(row, ctx), targetCell(row, ctx)?.inventoryCostStart, ctx.basis);
/** Start-of-target-period inventory on the chosen basis (see the header); null when not available at cost. */
export const inventoryOf = (row: AnyRow, ctx: CellContext): number | null => inventoryOnBasis(row, ctx).value;
/** End-of-target-period inventory on the chosen basis, with its availability. */
export const inventoryEndOnBasis = (row: AnyRow, ctx: CellContext): BasisValue => valueOnBasis(targetCell(row, ctx), 'inventoryEnd', ctx.basis);
/** End-of-target-period inventory on the chosen basis; null when not available at cost. */
export const inventoryEndOf = (row: AnyRow, ctx: CellContext): number | null => inventoryEndOnBasis(row, ctx).value;
/** A summary dollar field on the chosen basis. */
export const rowOnBasis = (row: AnyRow, field: SummaryBasisField, ctx: CellContext): BasisValue => summaryOnBasis(row, field, ctx.basis);
const onOrderOf = (row: AnyRow, ctx: CellContext): number | null => summaryOnBasis(row, 'onOrder', ctx.basis).value;

export interface PctMeasureOptions {
  /** Read inventory on this basis instead of `ctx.basis` (the row tree's one basis per sibling set). */
  inventoryBasis?: PriceBasis;
}
/** The four measures the % of Total columns and the row tree share. */
export const pctMeasureOf = (row: AnyRow, measure: PctMeasure, ctx: CellContext, opts: PctMeasureOptions = {}): number | null => {
  if (measure === 'inventory') return inventoryOf(row, opts.inventoryBasis ? { ...ctx, basis: opts.inventoryBasis } : ctx);
  if (measure === 'onOrder') return onOrderOf(row, ctx);
  return targetCell(row, ctx)?.[measure] ?? null;
};

/** targets.ts `rowStatus` (the payload's status, re-derived under a WOS override; Review kept; none for a deferring child). */
const effectiveStatus = (row: AnyRow, ctx: CellContext): Status | null => rowStatus(row, ctx);

/**
 * the Health pill's title, the figures behind the status: "WOS 23.8 vs target 14.6 (band 9.7–21.9) · BOM $39,500 vs
 * plan $25,000" (the cover unit follows the grain; the target in force is the override's when typed; the band is targets.ts `bands` at the
 * multiplier; BOM vs plan is the plan-BOM gate pair, `gateOf`). A part whose figures are missing is left out; a Watch – high that the gate
 * held back from Overstocked (cover above the band, BOM at or below plan) says so. Null when nothing can be said.
 */
export const healthTooltip = (row: AnyRow, s: Status, ctx: CellContext): string | null => {
  const sum = row.summary;
  if (!sum) return null;
  const unit = COVER_UNIT[ctx.grain === 'monthly' ? 'monthly' : 'weekly'];
  const cap = coverCap(ctx.grain);
  const target = resolveTarget(sum, ctx.wosOverride).target;
  const multiplier = ctx.multiplier >= 1 ? ctx.multiplier : 1;
  const band = isNum(target) && target > 0 ? bands(target, multiplier) : null;
  const parts: string[] = [];
  if (isNum(sum.wos)) {
    parts.push(`${unit} ${fmtWeeks(sum.wos, cap)}${isNum(target) ? ` vs target ${fmtWeeks(target, cap)}` : ''}${band ? ` (band ${fmtWeeks(band.under, cap)}–${fmtWeeks(band.over, cap)})` : ''}`);
  }
  const g = gateOf(row, ctx.targetPeriod);
  const bom = g.bomInventory ?? (sum.inventoryKnown && isNum(sum.inventory) ? sum.inventory : null);
  if (isNum(bom) && isNum(g.planBom)) parts.push(`BOM ${fmtCurrency(bom)} vs plan ${fmtCurrency(g.planBom)}`);
  if (parts.length === 0) return null;
  const gated = s === 'watch_high' && band && isNum(sum.wos) && sum.wos > band.over && isNum(g.planBom) && g.planBom > 0 && isNum(bom) && bom <= g.planBom;
  return unitize(`${parts.join(' · ')}${gated ? ' · at or below plan inventory, so not Overstocked' : ''}`, ctx.grain);
};

/** Health / Action of a child judged at its parent: no value (sorts and counts as none), "see parent" text. */
const seeParentCell = (row: AnyRow, ctx: CellContext): CellValue => ({ ...out(null, SEE_PARENT_LABEL, flagsOf(targetCell(row, ctx))), title: seeParentTooltip(row.summary) });

/** the reorder basis in words for the CSV's "Reorder basis" column; '' without one. */
const REORDER_BASIS_TEXT: Record<ReorderBasis, string> = { lookback: 'lookback', last52: 'last 52 weeks', mixed: 'mixed' };
export const reorderBasisText = (basis: ReorderBasis | null | undefined, grain?: Grain | null): string => (basis ? unitize(REORDER_BASIS_TEXT[basis] ?? '', grain) : '');

const labelText = (row: AnyRow): string => {
  if (row.isUnallocated || row.key === 'unclassified') return row.className ?? EMPTY;
  if (isVendorRow(row)) {
    if (row.level === 'sku') return [fixMojibake(row.productTitle), row.skuId ?? row.variantId].filter(Boolean).join(' · ') || row.key;
    if (row.level === 'vendor' || (row.level === 'vendorClass' && row.labelAs === 'vendor')) return fixMojibake(row.vendor) || EMPTY;
    if (row.level === 'product') return fixMojibake(row.productTitle) || '(No product)';
  }
  // W2: a location row under its class (Class › Location) is labelled by its location.
  if (!isVendorRow(row) && row.labelAs === 'location') return row.locationName ?? row.locationCode ?? EMPTY;
  if (row.classCode && row.className && row.classCode !== row.className) return `${row.classCode} · ${row.className}`;
  return row.className ?? row.classCode ?? (isVendorRow(row) ? fixMojibake(row.vendor) : EMPTY);
};

const coverCell = (row: AnyRow, ctx: CellContext): CellValue => {
  const s = row.summary;
  const flags = inventoryFlagsOf(targetCell(row, ctx));
  if (!s) return out(null, EMPTY, flags);
  const target = resolveTarget(s, ctx.wosOverride).target;
  const noSales = s.wos === null && s.inventoryKnown && isNum(s.inventory) && s.inventory > 0;
  // the cap reads in the grain's unit ("52+" weeks, "12+" months); the value is never capped.
  const cap = coverCap(ctx.grain);
  // a child judged at its parent shows its cover alone, with no target of its own.
  if (defersToParent(s)) return noSales ? out(Infinity, `${cap}+`, flags) : out(s.wos, fmtWeeks(s.wos, cap), flags);
  if (noSales) return out(Infinity, `${cap}+ / ${fmtWeeks(target, cap)}`, flags);
  return out(s.wos, fmtCover(s.wos, target, cap), flags);
};

/**
 * Item 13 (Wendi, 2026-10-22): at vendor level the need shown is the WOS-target need, the store-need formula on the row itself: target
 * cover × this period's plan sales (its own average without a plan) − (BOW/BOM on hand + on order due this period), never below 0. Retail.
 */
export const wosTargetNeed = (row: AnyRow, ctx: CellContext): number | null => {
  const s = row.summary;
  if (!s) return null;
  const cell = row.periods[String(ctx.targetPeriod)];
  return storeNeed({
    target: resolveTarget(s, ctx.wosOverride).target,
    planSales: cell && isNum(cell.salesPlan) ? cell.salesPlan : null,
    avgSales: isNum(s.avgPeriodSales) ? s.avgPeriodSales : null,
    bom: s.inventoryKnown && isNum(s.inventory) ? s.inventory : null,
    due: s.onOrderDue ? s.onOrderDue[String(ctx.targetPeriod)] ?? 0 : isNum(s.onOrder) ? s.onOrder : null,
  });
};

/** a target-period dollar cell on the chosen basis: its cost twin at cost, else "—" (not available at cost). */
const basisCell = (field: CellBasisField, withLy = false) => (row: AnyRow, ctx: CellContext): CellValue => {
  const c = targetCell(row, ctx);
  return basisOut(valueOnBasis(c, field, ctx.basis), flagsOf(c, withLy, false, field === 'sales', field.endsWith('Plan')));
};

/** a vs-compare cell with activity against no baseline (e.g. inflows with none planned) reads this instead of an absurd percentage. */
export const NEW_LABEL = 'new';

/** A negative inflow (returns over inflows) reads −100% at most, titled; a base that is not positive reads "—" (Tier C's −105%). */
export const INFLOW_CLAMPED_TOOLTIP = 'Inflows are negative in this period (returns exceed inflows); the change is shown as −100%.';

/** measured-not-trended measures: in a trend period their plan / LY / average is prorated by the days elapsed, like inflows. */
const PRORATED_MEASURES: readonly ComparableMeasure[] = ['inflow', 'markdowns'];
export const vsCompareCell = (measure: ComparableMeasure) => (row: AnyRow, ctx: CellContext): CellValue => {
  const c = targetCell(row, ctx);
  // Sales in a trend period: Sales Trend (retail) against the full-period plan / LY / lookback average.
  const own = measure === 'sales' ? comparableSales(c, ctx.trendMethod) : c ? (c[measure] as number | null | undefined) ?? null : null;
  // A comparison against an allocated plan keeps the ALLOCATED tag (the base is the vendor's share of the class plan).
  const flags = flagsOf(c, ctx.compare === 'ly', false, measure === 'sales', ctx.compare === 'plan');
  if (!c || !isNum(own)) return out(null, EMPTY, flagsOf(c, false, false, measure === 'sales', ctx.compare === 'plan'));
  let base = compareValue(c, measure, ctx.compare, { periods: row.periods, lookback: ctx.lookback });
  // inflows are measured, not trended, so in a trend period they are compared with the plan (LY, average)
  // PRORATED by the days elapsed, labelled WTD / MTD; "—" where proration is impossible. Markdowns are measured too.
  let tag: string | undefined;
  let title: string | undefined;
  if (PRORATED_MEASURES.includes(measure)) {
    const p = prorate(base, c, ctx.targetPeriod, ctx.grain, ctx.salesThrough);
    base = p.value;
    tag = p.label ?? undefined;
    const proration = prorationTitle(p, ctx.compare === 'plan' ? 'plan' : ctx.compare === 'ly' ? 'last-year value' : 'average');
    // Round 2: Plan Inflows shows the full period; name the prorated figure the % is against, so the basis is visible.
    title = proration && isNum(p.value) ? `${proration} (prorated ${fmtCurrency(p.value)})` : proration;
  }
  // no baseline to speak of (a plan of 0, or crumbs): never a huge percentage. "new" where the period has something
  // (inflows against no planned inflows), else "—"; the title says which baseline is missing.
  if (isNum(base) && !isUsablePctBase(base)) {
    const noun = measure === 'sales' ? 'sales' : measure === 'inflow' ? 'inflows' : 'markdowns';
    const none = ctx.compare === 'plan' ? `no planned ${noun}` : ctx.compare === 'ly' ? `no last-year ${noun}` : `no average ${noun}`;
    const created = isUsablePctBase(own);
    return { ...out(null, created ? NEW_LABEL : EMPTY, flags), ...(tag ? { tag } : {}), title: [title, none].filter(Boolean).join(' · ') };
  }
  let v = isNum(base) ? pctChange(own, base) : null;
  if (measure === 'inflow' && v !== null && v < -1) { v = -1; title = [title, INFLOW_CLAMPED_TOOLTIP].filter(Boolean).join(' · '); }
  return { ...out(v, signedPct(v), flags), ...(tag ? { tag } : {}), ...(title ? { title } : {}) };
};

/** A roll-up where some members have no aging data: the share is weighted over the members that do. */
export const AGING_PARTIAL_TOOLTIP = 'Partial: some rows under this one have no aging data, so this share is weighted over the stock of the rows that do.';
const freshnessCell = (field: 'freshPctSku' | 'aging4wPct' | 'aging8wPct' | 'aging12wPct') => (row: AnyRow): CellValue => {
  // Vendor, SKU and product rows carry their own; class rows (and their subtotals) the buckets rolled up from their SKUs (Inventory Health).
  const v = row.freshness ? row.freshness[field] : null;
  const cell = out(v, fmtPct(v));
  return row.freshness?.partial && v !== null ? { ...cell, title: AGING_PARTIAL_TOOLTIP } : cell;
};

const pctOfCell = (measure: PctMeasure) => (row: AnyRow, ctx: CellContext): CellValue => {
  const v = ctx.pctOfParent ? ctx.pctOfParent[measure] : null;
  // Only the estimated flag (see flagsOf): a share of an estimated inventory is itself estimated.
  return out(v, fmtPct(v), measure === 'inventory' ? { ...NO_FLAGS, estimated: inventoryFlagsOf(targetCell(row, ctx)).estimated } : NO_FLAGS);
};

const trendColumnCell = (field: 'salesTrend' | 'trendPct') => (row: AnyRow, ctx: CellContext): CellValue => {
  const c = targetCell(row, ctx);
  if (!isTrendCell(c)) return out(null, EMPTY, flagsOf(c));
  const t = trendOf(c, ctx.trendMethod);
  const lag = salesLagText(ctx.targetPeriod, ctx.grain, ctx.salesThrough, ctx.currentPeriod);
  const v = t[field];
  // Sales Trend is retail sales carried forward; Trend % is a ratio and stays.
  if (field === 'salesTrend' && !pickBasis(v, null, ctx.basis).available) return notAtCostCell(flagsOf(c, false, false, true));
  return { ...out(v, field === 'trendPct' ? fmtPct(v) : fmtCurrency(v), flagsOf(c, false, false, true)), title: trendTitle(t, ctx.trendMethod, ctx.salesThrough, lag) };
};

// ---- W2: allocation ("open to sell") --------------------------------------------------------------------
export const ALLOC_NEED_DESCRIPTION = 'Allocation, not buying. What a store needs of the class this week or month to reach its target cover: target weeks (or months) of stock × the period\'s plan sales (its own average sales when it has no plan) − (BOM inventory + on order due this period), never below 0. A class row adds up its stores. Retail: plans have no sales at cost.';
export const ALLOC_AVAILABLE_DESCRIPTION = 'What the non-selling locations (warehouse, receiving, HQ, storage) hold of the class to send to the stores: their BOM inventory on hand (on order due there is not counted). A class row adds up its non-selling locations.';
export const ALLOC_BALANCE_DESCRIPTION = 'Need − available on a class row: a shortfall is what the stores need beyond what the non-selling locations hold (to buy / chase); a surplus is what is left there after every store is sent its need (hold in the warehouse).';
/** "Allocate $300 from Headquarters" (a split across two sources names both). */
export const allocateText = (amount: number, from: string[]): string => `Allocate ${fmtCurrency(amount)} from ${from.join(' + ')}`;
const allocateTooltip = (need: number, amount: number, from: string[], grain: Grain): string =>
  `This store needs ${fmtCurrency(need)} of the class this ${grain === 'monthly' ? 'month' : 'week'} to reach its target cover; send ${fmtCurrency(amount)} from ${from.join(' and ')}${amount < need - 0.005 ? ` (all that is left there; the rest, ${fmtCurrency(need - amount)}, is a shortfall to buy or chase)` : ''}. Stores are served largest need first.`;
/** A retail-only figure (the need: plan sales have no cost): "—" titled "not available at cost" on the cost basis. */
const retailOnlyCell = (v: number | null, ctx: CellContext): CellValue => {
  if (ctx.basis === 'cost') return v === null ? out(null, EMPTY) : notAtCostCell();
  return out(v, fmtCurrency(v));
};
/** The Need column's label in the grain's unit: "Need (wk)" / "Need (mo)"; the start-of-period inventory reads BOW / BOM (Wendi, 2026-10-22). */
const GRAIN_LABELS: Record<string, Record<Grain, string>> = {
  allocNeed: { weekly: 'Need (wk)', monthly: 'Need (mo)' },
  inventory: { weekly: 'BOW inventory', monthly: 'BOM inventory' },
  pctOfInventory: { weekly: '% of BOW inventory', monthly: '% of BOM inventory' },
  inventoryLy: { weekly: 'LY month-start inventory', monthly: 'LY BOM inventory' },
};
/** As GRAIN_LABELS for the compact header text; a column listed above but not here keeps its GRAIN_LABELS text. */
const GRAIN_SHORT_LABELS: Record<string, Record<Grain, string>> = {
  inventory: { weekly: 'BOW Inv', monthly: 'BOM Inv' },
  pctOfInventory: { weekly: '% BOW Inv', monthly: '% BOM Inv' },
  inventoryLy: { weekly: 'LY month-start', monthly: 'LY BOM' },
};

/**
 * Wendi (2026-10-22): the Suggested reorder column's header tooltip lists the inputs behind a row's suggestion; the row detail
 * shows the row's own figures (`reorderExplanation`).
 */
export const REORDER_DESCRIPTION =
  'Units to order for a product or SKU: need − on hand − on order, floored at 0, where need = average period units × the target weeks (or months) of stock. ' +
  'The target is the class plan\'s (plan inventory ÷ plan sales) or, with no plan, the class\'s own trailing average. ' +
  'The average units are over the lookback; a stock-out with no sales in the lookback uses its average over the last 52 weeks instead ("52 wk"), and with none there either it is marked Review and gets no units. ' +
  'On hand is the units at the start of the period; on order the open units within the span. A class row reads —. Shown by default in product views and with Show SKUs on; open a row for the figures behind its suggestion.';

/** Which rule sized a row's suggested reorder (`reorderExplanation.rule`). */
export type ReorderRule = 'plan' | 'own_avg' | 'last52' | 'mixed' | 'none';
export interface ReorderExplanation {
  /** The average period units the suggestion used (the lookback average, or the last-52-weeks average for a stock-out). */
  avgUnits: number | null;
  /** The target weeks (months) of stock in force. */
  target: number | null;
  /** avgUnits × target. */
  need: number | null;
  onHand: number | null;
  onOrder: number | null;
  /** The non-selling locations' stock the class could draw on (the row's allocation, where it has one). */
  available: number | null;
  suggested: number | null;
  rule: ReorderRule;
  ruleText: string;
}
const REORDER_RULE_TEXT: Record<ReorderRule, string> = {
  plan: 'plan-based: the class plan\'s target (plan inventory ÷ plan sales) × the lookback average units',
  own_avg: 'trailing average: the class has no plan, so its own trailing-average target × the lookback average units',
  last52: 'last 52 weeks fallback: no sales in the lookback, so the average over the last 52 weeks sizes the buy',
  mixed: 'mixed: some variants from the last 52 weeks (no sales in the lookback), the rest from the lookback average',
  none: 'no target to size a buy by',
};
/**
 * The figures behind a row's Suggested reorder (product and SKU rows; null for a row that carries none). Reads what the payload
 * already has: summary.avgPeriodUnits (or reorderAvgUnits, the last-52-weeks average a stock-out used), the resolved target,
 * the target cell's unitsStart, the row's onOrderUnits and summary.suggestedReorderUnits / reorderBasis.
 */
export const reorderExplanation = (row: AnyRow, ctx: CellContext): ReorderExplanation | null => {
  const s = row.summary;
  if (!s || s.suggestedReorderUnits === undefined) return null;
  const { target, basis } = resolveTarget(s, ctx.wosOverride);
  const reorderBasis = s.reorderBasis ?? null;
  // `reorderAvgUnits` (RowBuilder::applyPriorYear): the last-52-weeks average a stock-out's units were sized by; absent otherwise.
  const used = (s as Summary & { reorderAvgUnits?: number | null }).reorderAvgUnits;
  const avgUnits = isNum(used) ? used : isNum(s.avgPeriodUnits) ? s.avgPeriodUnits : null;
  const onHandRaw = row.periods[String(ctx.targetPeriod)]?.unitsStart;
  const onHand = isNum(onHandRaw) ? onHandRaw : null;
  const onOrder = isNum(row.onOrderUnits) ? row.onOrderUnits : null;
  const rule: ReorderRule = !isNum(target) ? 'none' : reorderBasis === 'last52' ? 'last52' : reorderBasis === 'mixed' ? 'mixed' : basis === 'own_avg' ? 'own_avg' : 'plan';
  const need = isNum(avgUnits) && isNum(target) ? avgUnits * target : null;
  const ruleText = basis === 'override' && rule !== 'none' ? `${REORDER_RULE_TEXT[rule]} (your WOS target in the table; the units are the payload's)` : REORDER_RULE_TEXT[rule];
  return {
    avgUnits, target, need, onHand, onOrder,
    available: row.allocation ? row.allocation.available : null,
    suggested: isNum(s.suggestedReorderUnits) ? s.suggestedReorderUnits : null,
    rule, ruleText: unitize(ruleText, ctx.grain),
  };
};

/** the Action column's description (M1 rules, targets.ts). one short sentence per family.
 *: the labels read "Delay or cancel orders", "Sell down · delay or cancel orders", "No action" and
 * "No action · plan steps down" (codes unchanged). */
export const ACTION_DESCRIPTION =
  'Reorder family: Reorder when understocked or out of stock; Reorder · no OTB when OTB + Dev ≤ 0; held back as On order covers it (on order due within the target\'s weeks (months) reaches the target) or Out of season (next period plans less sales and less inventory); PNOTB / NOTB / History / Distress / Warehouse classes read —. ' +
  'Overstock family: Sell down · delay or cancel orders when overstocked; Delay or cancel orders when inventory is within 10% of plan and on order is over half the plan inventory (delay (push out) the delivery dates or cancel). ' +
  'No action: on plan, nothing to buy, cancel or sell down this period (on track or watch); No action · plan steps down when at or below plan BOM but next period\'s plan steps down, so sell through current stock rather than reorder. ' +
  'Review family: Review when out of stock with no sales in the last 52 weeks, or a stock-out with no target; Review · clearance when stock has no sales in the lookback and sits above plan inventory (or has none); at or below plan inventory it is No action. ' +
  'In Class › Location views a store sent stock reads Allocate $X from <location>. ' +
  'Vendor rows carry Hold PO / Take PO: whether this period\'s on order exceeds the class\'s need.';

const def = (
  id: string, label: string, shortLabel: string, description: string, kind: MetricKind, group: MetricGroup, defaultWidth: number,
  cell: MetricDefinition['cell'], opts: Partial<Pick<MetricDefinition, 'tiers' | 'reports' | 'defaultVisible' | 'align'>> = {},
): MetricDefinition => ({
  id, label, shortLabel, description, kind, group, defaultWidth, cell,
  align: opts.align ?? (kind === 'text' ? 'left' : kind === 'status' || kind === 'action' ? 'center' : 'right'),
  tiers: opts.tiers ?? ALL_TIERS, reports: opts.reports ?? ALL_REPORTS, defaultVisible: opts.defaultVisible ?? false,
});

const BM_REPORTS: ReportKey[] = ['inventory_health', 'weekly_action'];
const VELOCITY_REPORTS: ReportKey[] = ['inventory_health', 'weekly_action'];
const STATUS_TEXT: Record<string, string> = { notEnoughPeers: 'not enough peers', noPeerGroup: 'no peer group', mixedMasterClasses: 'mixed master classes', noMasterClass: 'no master class' };
/** The row's benchmark entry; vendor / product / SKU rows never have one. */
const bmOf = (row: AnyRow, ctx: CellContext): BenchmarkRowValue | undefined => (isVendorRow(row) ? undefined : ctx.benchmarks?.byRowKey[row.key]);
const bmMonth = (ctx: CellContext): string => benchmarkMonth(ctx.benchmarks);

const BENCHMARK_DEFINITIONS: MetricDefinition[] = [
  //: peer counts are not shown; the column keeps its id (saved views) and reads the benchmark month or why there is none.
  def('bmPeers', 'Benchmark month', 'Benchmark', 'The benchmark month for this row\'s peers (class: same vertical and master class; location: same vertical), or why there is no benchmark. Vendor, product and SKU rows have no benchmark.', 'text', 'benchmark', 130, (row, ctx) => {
    const v = bmOf(row, ctx);
    if (!v) return out(null, EMPTY);
    if (v.status !== 'ok') return out(null, STATUS_TEXT[v.status] ?? EMPTY);
    return out(null, bmMonth(ctx));
  }, { reports: BM_REPORTS, align: 'right' }),
  ...BENCHMARK_METRICS.flatMap((m) => [
    def(`bm_${m.id}_peer`, m.peerLabel ?? `Peer ${m.label}`, `Peer ${m.short}`, `Peers' ${m.label.toLowerCase()} in the benchmark month. ${m.description}`, m.format === 'pct' ? 'pct' : m.format === 'weeks' ? 'weeks' : 'text', 'benchmark', 110, (row, ctx) => {
      const v = bmOf(row, ctx);
      if (!v) return out(null, EMPTY);
      if (v.status !== 'ok') return out(null, STATUS_TEXT[v.status] ?? EMPTY);
      // the cover is in the report's unit: Monthly shows the peers' months of stock (weeks ÷ weeks in the month).
      const raw = gatedPeerValue(v.peer, m.id);
      if (m.id === 'weeksOfStock' && ctx.grain === 'monthly' && raw !== null && ctx.benchmarks) {
        const months = toCoverUnit(raw, 'monthly', ctx.benchmarks.weeksInMonth);
        return out(months, formatBenchmark(m.id, months));
      }
      return out(v.peer?.metrics[m.id] ?? null, peerText(v.peer, m.id));
    }, { reports: BM_REPORTS, align: 'right' }),
    def(m.id === 'salesChangeLy' ? 'bm_salesChangeLy_diff' : `bm_${m.id}_index`, m.id === 'salesChangeLy' ? 'vs LY vs peers' : `${m.short} index`, m.id === 'salesChangeLy' ? 'LY vs peers' : `${m.short} idx`,
      m.id === 'salesChangeLy' ? "Your sales change vs last year minus the peers' (points), benchmark month." : `Your ${m.label.toLowerCase()} ÷ the peers' in the benchmark month (1.00 = on the benchmark).`,
      'text', 'benchmark', 100, (row, ctx) => {
        const v = bmOf(row, ctx);
        if (!v || v.status !== 'ok') return out(null, EMPTY);
        const idx = benchmarkIndex(m.id, v.own[m.id], gatedPeerValue(v.peer, m.id));
        return out(idx, formatIndex(m.id, idx));
      }, { reports: BM_REPORTS, align: 'right' }),
  ]),
];

export const METRIC_DEFINITIONS: readonly MetricDefinition[] = [
  def('label', 'Class', 'Class', 'Class, vendor or SKU the row describes.', 'text', 'identity', 280, (row) => out(labelText(row), labelText(row)), { defaultVisible: true }),
  // "weeks of stock (WOS)", never "weeks of supply"; phrased so unitize leaves it as is in Weekly and reads MOS in Monthly.
  def('health', 'Health', 'Health', 'Weeks of stock (WOS), the cover column, against the target band, and inventory against plan: never Overstocked at or below plan inventory (Watch – high instead), always Overstocked above plan inventory × the multiplier. Stock with no sales in the lookback reads No recent sales, at any plan inventory; a Deadstock tag marks more than a year of stock that is selling less than last year. Hover a pill for the figures behind it.', 'status', 'health', 120, (row, ctx) => {
    if (defersToParent(row.summary)) return seeParentCell(row, ctx);
    // a no-action class type (PNOTB, NOTB, History, Distress, Warehouse) is never judged, so Health reads "—" like its
    // Action does, in Inventory Health and Weekly Action alike.
    if (rowAction(row, ctx) === 'class_type') {
      const type = row.summary?.serviceTypeId ?? ('serviceTypeId' in row ? row.serviceTypeId : null);
      return { ...out(null, EMPTY, flagsOf(targetCell(row, ctx))), title: classTypeTooltip(type, row.summary?.nonSelling) };
    }
    const s = effectiveStatus(row, ctx);
    const v = out(s, s ? STATUS_LABELS[s] : EMPTY, flagsOf(targetCell(row, ctx)));
    if (s === 'no_recent_sales') return { ...v, title: NO_RECENT_SALES_TOOLTIP };
    const title = s ? healthTooltip(row, s, ctx) : null;
    return title ? { ...v, title } : v;
  }, { reports: ['inventory_health', 'weekly_action'] }),
  def('action', 'Action', 'Action', ACTION_DESCRIPTION, 'action', 'health', 150, (row, ctx) => {
    if (defersToParent(row.summary)) return seeParentCell(row, ctx);
    const a = rowAction(row, ctx);
    if (a === 'class_type') {
      // The cell renders a muted "—" for a null value; its text names the type for the CSV ("No action (PNOTB)").
      const type = row.summary?.serviceTypeId ?? ('serviceTypeId' in row ? row.serviceTypeId : null);
      return { ...out(null, classTypeText(type, row.summary?.nonSelling), flagsOf(targetCell(row, ctx))), title: classTypeTooltip(type, row.summary?.nonSelling) };
    }
    // W2: a store sent stock from a non-selling location in Class › Location (rowTree stamps `allocate`) is told so.
    // the verdict stays in front of it ("Reorder · no OTB · Allocate $300 from HQ"), so the buy decision is not lost.
    const sent = row.allocate;
    if (sent && sent.allocate > 0) {
      return { ...out(a ?? 'reorder', `${a ? `${ACTION_LABELS[a]} · ` : ''}${allocateText(sent.allocate, sent.from)}`, flagsOf(targetCell(row, ctx))), title: allocateTooltip(sent.need, sent.allocate, sent.from, ctx.grain) };
    }
    const v = out(a, a ? ACTION_LABELS[a] : EMPTY, flagsOf(targetCell(row, ctx)));
    const title = a ? ACTION_TOOLTIPS[a] : undefined;
    return title ? { ...v, title: unitize(title, ctx.grain) } : v;
  }, { reports: ['weekly_action'] }),
  def('thisPeriodSales', 'This period sales', 'Sales', 'Net sales in the selected week or month (retail: not available at cost). In a period still in progress it reads Sales WTD (week to date) or Sales MTD (month to date): the sales so far, with a line comparing them with the plan prorated for the days elapsed. The Trended column shows where the period is heading, against the full plan.', 'currency', 'sales', 120, basisCell('sales')),
  def('salesVsCompare', 'Sales vs compare', 'vs Compare', 'Sales change against the Compare basis (plan, last year or the lookback average).', 'pct', 'sales', 100, vsCompareCell('sales')),
  def('salesPlan', 'Plan sales', 'Plan Sales', 'Planned sales for the period (retail: not available at cost).', 'currency', 'sales', 110, planCell(basisCell('salesPlan'))),
  def('salesTrend', 'Trended sales', 'Trended', "Where the period's sales are heading: sales so far ÷ % of period elapsed. Only for a period still in progress (the one containing the last sales day, and a later one that has started); the vs Plan / LY / Avg columns and the Classes vs plan ▲/▼ KPI use it there. Hover a cell for how it was worked out.", 'currency', 'sales', 120, trendColumnCell('salesTrend')),
  def('trendPct', '% of period elapsed', '% Elapsed', "How much of the period's sales you would expect to have happened through the last sales day: from the plan's daily sales pattern where the class has one, else last year's daily sales on the same weekdays, else simply the days elapsed (clients on monthly-only data see ORBIT's own projection). Hover a cell for how it was worked out.", 'pct', 'sales', 90, trendColumnCell('trendPct')),
  def('cover', 'WOS / target', 'WOS', 'Weeks (or months) of stock (WOS / MOS): BOM inventory ÷ average period sales over the lookback, against the target.', 'weeks', 'inventory', 110, coverCell),
  def('inventory', 'BOM inventory', 'BOM Inv', 'Beginning-of-period (BOM) inventory: what you have on hand at the start of the week or month, on the selected basis.', 'currency', 'inventory', 120, (row, ctx) =>
    basisOut(inventoryOnBasis(row, ctx), inventoryFlagsOf(targetCell(row, ctx)))),
  def('inventoryPlan', 'Plan inventory', 'Plan Inv', 'Planned inventory at the start of the period (retail: plans carry no inventory at cost).', 'currency', 'inventory', 120, planCell(basisCell('inventoryPlan'))),
  def('inventoryVsPlan', 'Inventory vs plan', 'vs Plan', 'BOM inventory against the plan (retail actual: plans are retail).', 'pct', 'inventory', 100, planCell((row, ctx) => {
    const c = targetCell(row, ctx);
    const own = inventoryRetailOf(row, ctx); // plans are retail: compare the retail actual on every basis
    const v = isNum(own) && c && isNum(c.inventoryPlan) ? pctChange(own, c.inventoryPlan) : null;
    return out(v, signedPct(v), flagsOf(c, false, true, false, true));
  })),
  // the deviation in dollars (what OTB + Dev adds or takes away), beside the percentage. Retail: plans carry no BOM at cost.
  def('inventoryVsPlanDollars', 'vs Plan $', 'vs Plan $', 'BOM inventory − plan inventory at the period, in dollars (the deviation).', 'currency', 'inventory', 110, planCell((row, ctx) => {
    const c = targetCell(row, ctx);
    const flags = flagsOf(c, false, true, false, true);
    if (ctx.basis === 'cost') return notAtCostCell(flags);
    const own = inventoryRetailOf(row, ctx);
    const v = isNum(own) && c && isNum(c.inventoryPlan) ? own - c.inventoryPlan : null;
    return out(v, fmtCurrency(v), flags);
  }), { reports: ['inventory_health', 'weekly_action'] }),
  // Wendi (2026-10-22): no "BOM Inv (cost)" column; the cost basis is the report-level basis switch (OTB module).
  def('inflow', 'Inflows', 'Inflows', 'Inflows in the period, on the selected basis.', 'currency', 'inflow', 110, basisCell('inflow')),
  def('inflowPlan', 'Planned inflows', 'Plan Inflows', 'Planned inflows for the period, on the selected basis (at cost: the plan\'s own inflows at cost, Monthly only).', 'currency', 'inflow', 110, planCell(basisCell('inflowPlan'))),
  def('inflowVsCompare', 'Inflows vs compare', 'Inflows vs Compare', 'Inflows change against the Compare basis (plan, last year or the lookback average).', 'pct', 'inflow', 100, vsCompareCell('inflow')),
  def('onOrder', 'On order', 'On Order', 'Open purchase orders within the OTB span, on the selected basis.', 'currency', 'otb', 110, (row, ctx) => {
    const v = onOrderOf(row, ctx);
    return out(v, fmtCurrency(v));
  }),
  def('otb', 'OTB', 'OTB', 'Open to buy (OTB): planned inflows over the span − received to date − on order. When the weekly plan ends before the span, OTB runs through its last planned week and on order due after it isn\'t counted. PNOTB / NOTB classes have none. At cost: the plan\'s inflows at cost − received at cost − on order at cost (Monthly class rows; weekly plans carry no cost). Before the BOM deviation: buy to OTB + Dev, which the Action uses. A buy window that starts later (the OTB control) runs the span from that week or month; with Count on order set to all open orders, every open PO is netted whatever its due date.', 'currency', 'otb', 110, (row, ctx) => (ctx.distributedOtb ? distributedOtbCell(ctx.distributedOtb, ctx) : otbCell('otb')(row, ctx))),
  // Weekly Action default, Plan & OTB's OTB block, optional (hidden) in Inventory Health.
  def('otbDev', 'OTB + Dev', 'OTB + Dev', OTB_DEV_TOOLTIP, 'currency', 'otb', 110, otbCell('otbDev')),
  // / optional (hidden) per-period OTB columns, Weekly Action only; the page names them from the span's periods.
  ...Array.from({ length: MAX_OTB_SLOTS }, (_, i) => def(otbPeriodColumnId(i + 1), `OTB period ${i + 1}`, `OTB p${i + 1}`, OTB_PERIOD_TOOLTIP, 'currency', 'otb', 110, otbPeriodCell(i + 1), { reports: ['weekly_action'] })),
  // Fresh % reads the SKU rollup where the row has freshness (as the aging columns do), else the class lookback ratio,
  // UNCAPPED (over 100% means more came in than is on hand: it sold through).
  def('freshPct', 'Fresh %', 'Fresh', 'Share of on-hand dollars received in the lookback (from SKU inflows where available; else inflows over the lookback ÷ on hand, uncapped).', 'pct', 'freshness', 90, (row, ctx) => {
    if (row.freshness) return freshnessCell('freshPctSku')(row);
    const inflow = sumCells(row.periods, 'inflow', ctx.lookback);
    const onHand = row.periods[String(ctx.currentPeriod ?? ctx.targetPeriod)]?.inventoryEnd;
    const v = inflow === null || !isNum(onHand) || !isPositive(onHand) ? null : inflow / onHand;
    return out(v, fmtPct(v));
  }),
  def('freshPctSku', 'Fresh % (SKU)', 'Fresh (SKU)', 'Per SKU: inflows in the lookback ÷ on hand, summed up the hierarchy then divided.', 'pct', 'freshness', 100, freshnessCell('freshPctSku'), { tiers: SKU_TIERS }),
  // one set of bucket labels everywhere (the row detail's Aging bar and Inflow timing chart use the same).
  def('aging4w', 'Age 0–4w', '0–4w', 'Share of on-hand dollars received in the last 4 weeks (0–4w).', 'pct', 'freshness', 80, freshnessCell('aging4wPct'), { tiers: SKU_TIERS }),
  def('aging8w', 'Age 5–8w', '5–8w', 'Share of on-hand dollars received 5–8 weeks ago.', 'pct', 'freshness', 80, freshnessCell('aging8wPct'), { tiers: SKU_TIERS }),
  def('aging12w', 'Age 8w+', '8w+', 'Share of on-hand dollars received more than 8 weeks ago (8w+) or with no matching inflow.', 'pct', 'freshness', 80, freshnessCell('aging12wPct'), { tiers: SKU_TIERS }),
  def('suggestedReorder', 'Suggested reorder', 'Reorder', REORDER_DESCRIPTION, 'units', 'otb', 110, (row, ctx) => {
    const v = row.summary?.suggestedReorderUnits ?? null;
    const basis = row.summary?.reorderBasis;
    const title = v !== null && (basis === 'last52' || basis === 'mixed') ? unitize(REORDER_BASIS_TOOLTIPS[basis], ctx.grain) : undefined;
    return title ? { ...out(v, fmtUnits(v)), title } : out(v, fmtUnits(v));
  }, { tiers: SKU_TIERS, reports: ['inventory_health', 'weekly_action'] }),
  // W2 (D12): allocation, "open to sell" (allocation.ts). Optional; shown by default in Class › Location only (`columnStateFor`).
  def('allocNeed', 'Need (wk)', 'Need (wk)', ALLOC_NEED_DESCRIPTION, 'currency', 'inventory', 110, (row, ctx) => retailOnlyCell(needOf(row, ctx), ctx), { reports: ['weekly_action'] }),
  def('allocAvailable', 'Available', 'Available', ALLOC_AVAILABLE_DESCRIPTION, 'currency', 'inventory', 110, (row, ctx) => {
    const v = availableShownOf(row, ctx);
    // At cost: the cost BOM, or "—" titled "not available at cost" when there is stock at retail but no cost figure.
    if (v === null && ctx.basis === 'cost' && availableShownOf(row, { ...ctx, basis: 'retail' }) !== null) return notAtCostCell();
    return out(v, fmtCurrency(v));
  }, { reports: ['weekly_action'] }),
  def('allocBalance', 'Shortfall / surplus', 'Shortfall / surplus', ALLOC_BALANCE_DESCRIPTION, 'currency', 'inventory', 130, (row, ctx) => {
    const b = row.allocation ? row.allocation.balance : null;
    if (b === null) return out(null, EMPTY);
    if (ctx.basis === 'cost') return notAtCostCell();
    if (Math.abs(b) < 0.005) return { ...out(0, 'Balanced'), title: 'What the stores need equals what the non-selling locations hold.' };
    return b > 0
      ? { ...out(b, `${fmtCurrency(b)} short`), title: `Shortfall: the stores need ${fmtCurrency(b)} more than the non-selling locations hold. To buy / chase.` }
      : { ...out(b, `${fmtCurrency(-b)} surplus`), title: `Surplus: the non-selling locations hold ${fmtCurrency(-b)} more than the stores need. Hold in the warehouse.` };
  }, { reports: ['weekly_action'] }),
  def('markdowns', 'Markdowns', 'Markdowns', 'Markdown dollars in the period (retail: not available at cost).', 'currency', 'sales', 100, basisCell('markdowns')),
  // pet-store columns (kit/logic/velocity.ts): weekly velocity and weeks of supply in both grains, buying pace, markdown %,
  // and the period in progress's trended OTB. Retail ratios on every basis; the trended OTB is retail only.
  def('avgWeeklySales', 'Avg weekly sales', 'Avg wk sales', 'Average weekly sales over the lookback: its sales ÷ its days ÷ 7 (each month counts its own days in Monthly). Retail.', 'currency', 'sales', 110, (row, ctx) => (ctx.basis === 'cost' ? notAtCostCell() : out(avgWeeklySales(row, ctx), fmtCurrency(avgWeeklySales(row, ctx)))), { reports: VELOCITY_REPORTS }),
  def('markdownPct', 'Markdown %', 'MD %', 'Markdowns as a share of sales over the lookback (Σ markdowns ÷ Σ sales). Retail.', 'pct', 'sales', 90, (row, ctx) => { const v = markdownPct(row, ctx); return out(v, fmtPct(v)); }, { reports: VELOCITY_REPORTS }),
  // Wendi (2026-10-22): the pet-store "Weeks of supply" column is gone: in Weekly it is the WOS column (BOW ÷ average weekly sales), which the
  // Pet stores template now shows; `weeksOfSupply` stays a pure helper.
  def('inflowWos', 'Inflow cover (weekly rate)', 'Inflow cover', 'Inflows over the lookback ÷ average weekly sales, in weeks at the weekly selling rate whatever the grain: how much selling was brought in over the period. Higher than the lookback\'s own length means buying faster than selling. Retail.', 'weeks', 'inflow', 100, (row, ctx) => { const v = inflowWeeksOfSupply(row, ctx); return out(v, fmtWeeks(v)); }, { reports: VELOCITY_REPORTS }),
  def('trendedOtb', 'Trended OTB (this period)', 'Trended OTB', 'For the week or month in progress: plan EOM − projected EOM − on order due this period, where projected EOM = BOM + received so far − trended sales − trended markdowns. Positive: open to buy this period to land on plan; negative: the period ends over plan at the current trend. Retail; "—" outside the period in progress or without a plan.', 'currency', 'otb', 120, (row, ctx) => {
    if (ctx.basis === 'cost') return notAtCostCell(); // retail only (the plan carries no BOM at cost)
    const t = trendedOtb(row, ctx);
    // A subtotal / chain row reads the row tree's Σ of its classes' own figures (withTrendedOtb), never its summed cells.
    const summed = row.summary && 'trendedOtb' in row.summary ? row.summary.trendedOtb ?? null : undefined;
    if (summed !== undefined && (!t || summed === null || Math.abs(summed - t.value) > 0.5)) return summed === null ? out(null, EMPTY) : { ...out(summed, fmtCurrency(summed)), title: 'The sum of its classes\' trended OTB, each against its own plan' };
    if (!t) return out(null, EMPTY);
    return { ...out(t.value, fmtCurrency(t.value)), title: `Plan EOM ${fmtCurrency(t.planEom)} − projected EOM ${fmtCurrency(t.projectedEom)} (trended sales ${fmtCurrency(t.trendedSales)}, trended markdowns ${fmtCurrency(t.trendedMarkdowns)}) − on order due this period ${fmtCurrency(t.dueThisPeriod)}` };
  }, { reports: ['weekly_action'] }),
  def('salesLy', 'Sales LY', 'Sales LY', 'Sales in the same period last year (retail: not available at cost).', 'currency', 'lastYear', 110, basisCell('salesLy', true)),
  def('inventoryLy', 'LY BOM inventory', 'LY BOM', LY_BOM_TOOLTIP, 'currency', 'lastYear', 110, lyBomCell),
  def('pctOfSales', '% of sales', '% Sales', 'Sales as a share of the parent row (top level: of the report total).', 'pct', 'pctOfTotal', 90, pctOfCell('sales')),
  def('pctOfInventory', '% of BOM inventory', '% BOM Inv', 'BOM inventory as a share of the parent row.', 'pct', 'pctOfTotal', 90, pctOfCell('inventory')),
  def('pctOfInflow', '% of inflows', '% Inflows', 'Inflows as a share of the parent row.', 'pct', 'pctOfTotal', 90, pctOfCell('inflow')),
  def('pctOfOnOrder', '% of on order', '% On order', 'On order as a share of the parent row.', 'pct', 'pctOfTotal', 90, pctOfCell('onOrder')),
  ...BENCHMARK_DEFINITIONS,
];

/**
 * The column's header / CSV label for a grain: the cover column reads "WOS" in Weekly and "MOS" in Monthly (the catalogue
 * label is grain-neutral), and so do the peer / index cover columns ("Peer WOS (month-end)" / "WOS idx" become "Peer MOS (month-end)"
 * / "MOS idx" in Monthly); every other column its `label`.
 */
const benchmarkCover = (d: MetricDefinition): boolean => d.group === 'benchmark' && /\bWOS\b/.test(`${d.label} ${d.shortLabel}`);
// A report's own label override (`columnLabelOverrides`, e.g. Inventory Health's "Actual Inv (start of period)") wins over the grain label.
const ownLabel = (d: MetricDefinition): boolean => BY_ID[d.id] === undefined || BY_ID[d.id].label === d.label;
const ownShortLabel = (d: MetricDefinition): boolean => BY_ID[d.id] === undefined || BY_ID[d.id].shortLabel === d.shortLabel;
export const columnLabel = (d: MetricDefinition, grain: Grain | null | undefined): string =>
  grain && GRAIN_LABELS[d.id] && ownLabel(d) ? GRAIN_LABELS[d.id][grain] : d.id === 'cover' && grain ? COVER_UNIT[grain] : benchmarkCover(d) && grain === 'monthly' ? unitize(d.label, grain) : d.label;

/** As `columnLabel`, for the compact header text (`shortLabel`). */
export const columnShortLabel = (d: MetricDefinition, grain: Grain | null | undefined): string =>
  grain && GRAIN_SHORT_LABELS[d.id] && ownShortLabel(d) ? GRAIN_SHORT_LABELS[d.id][grain] : grain && GRAIN_LABELS[d.id] && ownShortLabel(d) ? GRAIN_LABELS[d.id][grain] : d.id === 'cover' && grain ? COVER_UNIT[grain] : benchmarkCover(d) && grain === 'monthly' ? unitize(d.shortLabel, grain) : d.shortLabel;

/**
 * the column's tooltip in the report's own unit (labels go through `columnLabel` / `columnShortLabel`): "weeks (or months)"
 * becomes weeks in Weekly and months in Monthly, WOS / MOS the one, the 52 weeks rule 12 months ("52 wk" / "12 mo"). Aging buckets
 * (weeks by definition) are untouched.
 */
export const withGrainUnits = (d: MetricDefinition, grain: Grain | null | undefined): MetricDefinition => {
  const description = grain ? unitize(d.description, grain) : d.description;
  return description === d.description ? d : { ...d, description };
};

/**
 * a benchmark peer-value / index column with the benchmark month in its label ("Peer Weeks of stock
 * (Sep 2026)": the Columns menu, the header menu title, How it works and the CSV header) and its header tooltip
 * ("Benchmark: Sep 2026. …"). The compact header text (`shortLabel`) stays as it is. The Peers column already names
 * the month in its cell text, and nothing else changes; without a month (no benchmarks) the column is returned as is.
 */
export const withBenchmarkMonth = (d: MetricDefinition, month: string): MetricDefinition => {
  if (d.group !== 'benchmark' || d.id === 'bmPeers' || !month) return d;
  // A label that already ends in a bracket ("Peer WOS (month-end)") takes the month inside it.
  const label = d.label.endsWith(')') ? `${d.label.slice(0, -1)}, ${month})` : `${d.label} (${month})`;
  return { ...d, label, description: `Benchmark: ${month}. ${d.description}` };
};

/**
 * At the cost basis a benchmark peer-value / index column says "(retail)" in its label and header tooltip: benchmark ratios are
 * retail-based whatever the report's basis ("Peer WOS (month-end, Sep 2026)" becomes "Peer WOS (month-end, Sep 2026, retail)").
 */
export const withBenchmarkBasis = (d: MetricDefinition, basis: PriceBasis | undefined): MetricDefinition => {
  if (d.group !== 'benchmark' || d.id === 'bmPeers' || basis !== 'cost') return d;
  return { ...d, label: retailTag(d.label, basis), description: `${d.description} ${RETAIL_BASIS_NOTE}` };
};

/** The Compare setting in a column label: "vs Plan" / "vs LY" / "vs Avg" (short) and "vs plan" / "vs LY" / "vs average" (full). */
const COMPARE_SHORT: Record<Compare, string> = { plan: 'Plan', ly: 'LY', avg: 'Avg' };
const COMPARE_FULL: Record<Compare, string> = { plan: 'plan', ly: 'LY', avg: 'average' };

export interface ContextLabelOptions {
  compare: Compare;
  grain: Grain | null | undefined;
  /** The target period is still in progress (a trend period): the sales column reads Sales WTD / Sales MTD. */
  trendPeriod: boolean;
  /** Round 2: the trend runs on the plan's daily weights, so each class's elapsed share is its own (see `usesPlanWeights`). */
  planWeights?: boolean;
}

/** Round 2: what "% of period elapsed" means when the trend basis is the plan's daily weights (it differs by class). */
export const PLAN_ELAPSED_TOOLTIP = "The share of this class's planned sales for the period that falls in the days so far, from the plan's daily sales pattern. It differs by class (and from the share of the days elapsed) because each class's plan weights its own days. Hover a cell for how it was worked out.";

/**
 * Buyer language: the columns whose name depends on the view. "Sales" becomes "Sales WTD" (Weekly) / "Sales MTD" (Monthly) while
 * the period is in progress, and the two vs-compare columns name what they compare with (the Compare setting). Only the label
 * and short label change; the column id, the cell and the CSV column order are untouched.
 */
export const withContextLabels = (d: MetricDefinition, o: ContextLabelOptions): MetricDefinition => {
  if (d.id === 'thisPeriodSales' && o.trendPeriod) {
    const label = `Sales ${o.grain === 'monthly' ? 'MTD' : 'WTD'}`;
    return { ...d, label, shortLabel: label };
  }
  if (d.id === 'trendPct' && o.trendPeriod && o.planWeights) return { ...d, label: '% of plan elapsed', shortLabel: '% Plan Elapsed', description: PLAN_ELAPSED_TOOLTIP };
  if (d.id === 'salesVsCompare') return { ...d, label: `Sales vs ${COMPARE_FULL[o.compare]}`, shortLabel: `vs ${COMPARE_SHORT[o.compare]}` };
  if (d.id === 'inflowVsCompare') return { ...d, label: `Inflows vs ${COMPARE_FULL[o.compare]}`, shortLabel: `Inflows vs ${COMPARE_SHORT[o.compare]}` };
  return d;
};

export const COLUMN_IDS: readonly string[] = METRIC_DEFINITIONS.map((d) => d.id);

const BY_ID: Record<string, MetricDefinition> = Object.fromEntries(METRIC_DEFINITIONS.map((d) => [d.id, d]));
export const metricById = (id: string): MetricDefinition => {
  const d = BY_ID[id];
  if (!d) throw new Error(`Unknown column id: ${id}`);
  return d;
};

/** Default visible columns per report in display order (spec 7.1–7.3), before tier gating. */
const REPORT_DEFAULTS: Record<ReportKey, string[]> = {
  // the deviation in dollars after vs Plan, and On order after the cover (what is coming against what is held). It sits
  // after the trend pair, which `columnStateFor` keeps right behind the cover column (the pair shows in a trend period only).
  //: Inventory Health is the stock-position report; the trend columns stay in the catalogue, off by default.
  inventory_health: ['label', 'health', 'inventory', 'inventoryPlan', 'inventoryVsPlan', 'inventoryVsPlanDollars', 'cover', 'onOrder', 'freshPct', 'aging4w', 'aging8w', 'aging12w'],
  plan_otb: ['label', 'thisPeriodSales', 'salesTrend', 'trendPct', 'salesPlan', 'inflow', 'inflowPlan', 'inventory', 'inventoryPlan', 'markdowns', 'onOrder', 'otb', 'otbDev'],
  // QA: OTB and OTB + Dev follow Action (they were 13th and 14th, off-screen at 1440 px); a saved view keeps the layout it stored.
  // column review: trimmed to the buy decision. % Plan Elapsed and the three Inflows columns left the
  // defaults (OTB already nets what was received); they stay in the Columns menu. Trended still shows in a trend period only.
  weekly_action: ['label', 'action', 'otb', 'otbDev', 'thisPeriodSales', 'salesTrend', 'salesVsCompare', 'cover', 'inventory', 'onOrder', 'suggestedReorder'],
};

/** Default columns whose visibility is dynamic: shown on a trend period only (`columnStateFor`'s `trendPeriod`). */
export const TREND_COLUMN_IDS: readonly string[] = ['salesTrend', 'trendPct'];
/** W2: optional columns whose visibility is dynamic: shown in the Class › Location views only, unless the view chose. */
export const ALLOCATION_COLUMN_IDS: readonly string[] = ['allocNeed', 'allocAvailable', 'allocBalance'];

/** An unknown tier (capabilities degraded) is treated as the lowest, so SKU-level columns stay hidden. */
const allowedForTier = (d: MetricDefinition, tier: Tier | null | undefined): boolean => d.tiers.includes(tier ?? 'C');

export const defaultColumnIds = (report: ReportKey, tier: Tier | null | undefined): string[] =>
  REPORT_DEFAULTS[report].filter((id) => allowedForTier(metricById(id), tier));

/**
 * Every column available to a report on a tier: the report's defaults first, in spec order and
 * visible (the trend columns hidden: `columnStateFor` decides them per period), then the optional
 * ones in catalogue order, hidden. `% of Total` columns are always hidden by default. The benchmark
 * group is listed only when `benchmarkLevel` (capabilities) is not 'none' — the default, so callers
 * that do not know the level (saved-view defaults, the Tier C fallback) never see it.
 */
export const columnsFor = (report: ReportKey, tier: Tier | null | undefined, benchmarkLevel: BenchmarkLevel = 'none'): ReportColumn[] => {
  const defaults = defaultColumnIds(report, tier);
  const optional = METRIC_DEFINITIONS.filter((d) => d.reports.includes(report) && allowedForTier(d, tier) && !defaults.includes(d.id) && (d.group !== 'benchmark' || benchmarkLevel !== 'none'));
  return [...defaults.map((id) => ({ id, visible: !TREND_COLUMN_IDS.includes(id) })), ...optional.map((d) => ({ id: d.id, visible: false }))];
};

// ---- Wendi (2026-10-22), item 10: expert columns ---------------------------------------------------------------------------
// Admin users (the dev-tools permission, the one the templates use) see the full column catalogue; everyone else sees the client
// list: each report's defaults plus the most common extras. Everything else (trend alternatives, deviation internals, the benchmark /
// peer columns, the pet-store velocity columns, cost variants, the % of Total variants, the per-period OTB slots) is expert-only. A
// saved or shared view that shows an expert column still shows it to the client (`clientColumnIds` keeps an explicit true, and a pin).

/** The non-default columns every client may pick (on top of the report's defaults, which are always offered). */
export const CLIENT_COLUMN_IDS: readonly string[] = [
  'label', 'health', 'action', 'thisPeriodSales', 'salesVsCompare', 'salesPlan', 'salesTrend', 'salesLy', 'cover', 'inventory', 'inventoryPlan', 'inventoryVsPlan', 'inventoryVsPlanDollars', 'inventoryLy',
  'inflow', 'inflowPlan', 'onOrder', 'otb', 'otbDev', 'suggestedReorder', 'freshPct', 'aging4w', 'aging8w', 'aging12w', 'markdowns', 'markdownPct', 'allocNeed', 'allocAvailable', 'allocBalance',
];
/** True for a column only an expert (admin) is offered in this report. */
export const isExpertColumn = (report: ReportKey, id: string): boolean => !CLIENT_COLUMN_IDS.includes(id) && !REPORT_DEFAULTS[report].includes(id);
/** The ids a non-admin's table and Columns menu offer: the client list, plus any expert column the view (a saved / shared one) shows or pins. */
export const clientColumnIds = (report: ReportKey, ids: readonly string[], state: Pick<ReportViewState, 'columns'>): string[] =>
  ids.filter((id) => !isExpertColumn(report, id) || state.columns.visible[id] === true || state.columns.pinned.includes(id));

// ---- Wendi (2026-10-22), item 3: the allocation basis called out on allocated OTB rows --------------------------------------
export const ALLOC_BADGE_LABELS: Record<AllocBasis, string> = { net_sales: 'sales %', cash_margin: 'cash margin %', inventory: 'inventory %', gross_margin: 'gross margin %' };
const ALLOC_BASIS_WORDS: Record<AllocBasis, string> = { net_sales: 'sales', cash_margin: 'cash margin', inventory: 'inventory', gross_margin: 'gross margin' };
export interface AllocatedOtb { basis: AllocBasis; who: 'vendor' | 'product' | 'class'; windowPeriods: number }
/**
 * Whether a row's OTB is an allocated share of its class plan: a vendor (× class) row whose target cell is allocated (the backend's
 * vendor split), or a row given a distributed OTB (Distribute OTB). Null for a class row, a row with no OTB, or no allocation.
 */
export const allocatedOtbOf = (row: AnyRow, ctx: CellContext): AllocatedOtb | null => {
  const windowPeriods = allocationWindow(ctx).length;
  if (ctx.distributedOtb) {
    const who = isVendorRow(row) ? (row.level === 'product' || row.level === 'sku' ? 'product' : 'vendor') : 'class';
    return { basis: ctx.distributedOtb.sharedBy, who, windowPeriods: ctx.distributedOtb.windowPeriods };
  }
  if (!isVendorRow(row) || (row.level !== 'vendorClass' && row.level !== 'vendor') || !isNum(row.summary?.otb)) return null;
  const cell = row.periods[String(ctx.targetPeriod)];
  if (!cell?.isAllocated) return null;
  return { basis: ctx.allocation?.basis ?? 'net_sales', who: 'vendor', windowPeriods };
};
/** "allocated · sales %": the small muted badge beside an allocated OTB. */
export const allocatedOtbBadge = (a: AllocatedOtb): string => `allocated · ${ALLOC_BADGE_LABELS[a.basis]}`;
/** The badge's tooltip (and the row detail's sentence). */
export const allocatedOtbTooltip = (a: AllocatedOtb, grain: Grain): string =>
  `OTB is the class plan split by this ${a.who}'s share of class ${ALLOC_BASIS_WORDS[a.basis]} over the allocation window${a.basis === 'inventory' ? ` (the ${bomAbbr(grain)} inventory at the period)` : ` (${a.windowPeriods} ${grain === 'monthly' ? (a.windowPeriods === 1 ? 'month' : 'months') : a.windowPeriods === 1 ? 'week' : 'weeks'})`}.`;

// ===================================================================================================================
// ==== rowTree.ts ====================================================================================================
// ===================================================================================================================
const actionOf = action;
const deadstockOf = deadstock;
const pctOf = pctOfParent;
const statusOf = status;

// The twelve row groupings (decision A; W2 adds Class › Location and Class › Location › Vendor), named and grouped as Vendor Performance and Top Sellers
// do. "With open-to-buy" views put class rows (which carry the class plan's OTB) at the top; "Without
// open-to-buy" views put vendors or products at the top. The grouping is a dropdown label only: no
// column is hidden. Vendor and product views need SKU-level data (Tier A/B) and are left out for
// Tier C (never disabled). Keys stored before ('class' | 'category' | 'vendor' | 'location',
// in saved views and `?v=v1.` links) migrate through `migrateHierarchyView` in the view-state sanitiser.

export type HierarchyView =
  | 'totalByClass' | 'categoryByClass' | 'locationCategoryClass' | 'classByLocation' | 'classByLocationVendor' | 'classByVendor' | 'classByVendorProduct' | 'classByProduct'
  | 'vendorByClass' | 'totalByVendor' | 'totalByProduct' | 'vendorByProduct' | 'categoryByProduct';
export type ProductGrain = 'product' | 'sku';

export interface HierarchyOption {
  key: HierarchyView;
  label: string;
  hasOtb: boolean;
  /** Needs SKU-level data (Tier A/B): omitted for Tier C. */
  skuLevel: boolean;
  /** Header text of the first column in this view. */
  firstColumn: string;
  /** Info text of the first column (VP FIRST_COLUMN_DESCRIPTIONS). */
  description: string;
  /** What % of Total divides by (VP GROUP_PARENT_RELATIONS); null when every row divides by the report total. */
  parentRelation: string | null;
}

export const HIERARCHY_OPTIONS: readonly HierarchyOption[] = [
  { key: 'totalByClass', label: 'Total by Class', hasOtb: true, skuLevel: false, firstColumn: 'Class', description: 'One row per class; a parent class subtotals the child classes listed under it.', parentRelation: 'class ÷ parent class' },
  { key: 'categoryByClass', label: 'Category by Class', hasOtb: true, skuLevel: false, firstColumn: 'Category / Class', description: 'Grouped by category; each category row subtotals the classes listed under it.', parentRelation: 'class ÷ category' },
  { key: 'locationCategoryClass', label: 'Location › Category › Class', hasOtb: true, skuLevel: false, firstColumn: 'Location / Category / Class', description: 'Grouped by location, then category; each group row subtotals the classes under it.', parentRelation: 'class ÷ category, category ÷ location' },
  // W2 (D12): for allocation. One row per class across the chain (the Total by Class row), then one row per location.
  { key: 'classByLocation', label: 'Class › Location', hasOtb: true, skuLevel: false, firstColumn: 'Class / Location', description: 'Grouped by class across the chain; each class row lists its stores, then its non-selling locations (warehouse, receiving, HQ), with what each store needs and what is available to send it.', parentRelation: 'location ÷ class' },
  { key: 'classByLocationVendor', label: 'Class › Location › Vendor', hasOtb: true, skuLevel: true, firstColumn: 'Class / Location / Vendor', description: 'As Class › Location, with the vendors that sell or stock the class at each location under it, and a Hold / Take hint on a vendor whose on order is due this period.', parentRelation: 'location ÷ class, vendor ÷ location' },
  { key: 'classByVendor', label: 'Class by Vendor', hasOtb: true, skuLevel: true, firstColumn: 'Class / Vendor', description: 'Grouped by class; each class row lists the vendors that sell or stock it, with their share of its plan.', parentRelation: 'vendor ÷ class' },
  // Wendi (2026-10-22): Class › Vendor › Product: Class by Vendor with each vendor's products in that class loaded under it on expand.
  { key: 'classByVendorProduct', label: 'Class › Vendor › Product', hasOtb: true, skuLevel: true, firstColumn: 'Class / Vendor / Product', description: 'Grouped by class; each class row lists the vendors that sell or stock it (with their share of its plan), and opening a vendor loads its products in that class, top sellers first.', parentRelation: 'vendor ÷ class, product ÷ vendor' },
  { key: 'classByProduct', label: 'Class by Product', hasOtb: true, skuLevel: true, firstColumn: 'Class / Product', description: 'Grouped by class; open a class to load its products, top sellers first.', parentRelation: 'product ÷ class' },
  { key: 'vendorByClass', label: 'Vendor by Class', hasOtb: false, skuLevel: true, firstColumn: 'Vendor / Class', description: 'Grouped by vendor; each vendor row subtotals the classes listed under it.', parentRelation: 'class ÷ vendor' },
  { key: 'totalByVendor', label: 'Total by Vendor', hasOtb: false, skuLevel: true, firstColumn: 'Vendor', description: 'One row per vendor, totaled across every location and class in the filters.', parentRelation: null },
  { key: 'totalByProduct', label: 'Total by Product', hasOtb: false, skuLevel: true, firstColumn: 'Product', description: 'One row per product across every class, top sellers over the lookback first (the top 500; switch to Top by inventory to rank by stock on hand and reach dead stock).', parentRelation: null },
  { key: 'vendorByProduct', label: 'Vendor by Product', hasOtb: false, skuLevel: true, firstColumn: 'Vendor / Product', description: 'Grouped by vendor; open a vendor to load its products, top sellers first.', parentRelation: 'product ÷ vendor' },
  { key: 'categoryByProduct', label: 'Category by Product', hasOtb: false, skuLevel: true, firstColumn: 'Category / Product', description: 'Grouped by category; open a category to load its products, top sellers first.', parentRelation: 'product ÷ category' },
];

export const HIERARCHY_BY_KEY = Object.fromEntries(HIERARCHY_OPTIONS.map((o) => [o.key, o])) as Record<HierarchyView, HierarchyOption>;
export const HIERARCHY_VIEWS: readonly HierarchyView[] = HIERARCHY_OPTIONS.map((o) => o.key);
export const DEFAULT_HIERARCHY_VIEW: HierarchyView = 'totalByClass';
export const LEGACY_HIERARCHY_VIEWS: Readonly<Record<string, HierarchyView>> = { class: 'totalByClass', category: 'categoryByClass', vendor: 'vendorByClass', location: 'locationCategoryClass' };

/** A stored view key (current or pre-4b) as a current key; null when unknown. */
export const migrateHierarchyView = (v: unknown): HierarchyView | null => {
  if (typeof v !== 'string') return null;
  if ((HIERARCHY_VIEWS as readonly string[]).includes(v)) return v as HierarchyView;
  // Own keys only: 'toString' / 'constructor' must not resolve through the prototype.
  return Object.prototype.hasOwnProperty.call(LEGACY_HIERARCHY_VIEWS, v) ? LEGACY_HIERARCHY_VIEWS[v] : null;
};

export const hierarchyOptionsFor = (skuViewsAvailable: boolean): HierarchyOption[] => HIERARCHY_OPTIONS.filter((o) => skuViewsAvailable || !o.skuLevel);
export const isProductView = (v: HierarchyView): boolean => v === 'classByProduct' || v === 'totalByProduct' || v === 'vendorByProduct' || v === 'categoryByProduct' || v === 'classByVendorProduct';
/** Views built from /vendor-rows (and so carrying allocWindow / allocBasis). */
export const needsVendorRows = (v: HierarchyView): boolean => v === 'vendorByClass' || v === 'totalByVendor' || v === 'classByVendor' || v === 'vendorByProduct' || v === 'classByLocationVendor' || v === 'classByVendorProduct';
/** The views whose class rows list their vendor × class rows (Class by Vendor, and Class › Vendor › Product with the products below). */
export const isClassByVendorView = (v: HierarchyView): boolean => v === 'classByVendor' || v === 'classByLocationVendor' || v === 'classByVendorProduct';
/**
 * Wendi (2026-10-22), item 13: the views with no class level above the row (Total by Vendor, Vendor by Class, Vendor by Product): the row
 * detail's OTB block says the class OTB is not shown at vendor level and shows the need from the WOS target instead (`wosTargetNeed`).
 */
export const VENDOR_LEVEL_VIEWS: readonly HierarchyView[] = ['totalByVendor', 'vendorByClass', 'vendorByProduct'];
export const isVendorLevelView = (v: HierarchyView): boolean => VENDOR_LEVEL_VIEWS.includes(v);
export const VENDOR_LEVEL_OTB_NOTE = 'Class OTB is not shown at vendor level; the need below is from your WOS target';
/** W2: the Class › Location views (a class row, then its locations), where the allocation columns show by default. */
export const isLocationAllocationView = (v: HierarchyView): boolean => v === 'classByLocation' || v === 'classByLocationVendor';

/**
 * The view KPIs, charts, row counts and Needs Attention are computed on: product views count their
 * class (or vendor) rows, never the lazily loaded products, so totals do not move as products load and
 * Total by Product (capped) still totals every class; Class by Vendor counts classes.
 */
export const totalsViewOf = (v: HierarchyView): HierarchyView => {
  if (v === 'classByProduct' || v === 'totalByProduct' || v === 'classByVendor' || v === 'classByVendorProduct' || isLocationAllocationView(v)) return 'totalByClass';
  if (v === 'categoryByProduct') return 'categoryByClass';
  if (v === 'vendorByProduct') return 'totalByVendor';
  return v;
};

/** What a counted row is called in a view's KPIs ("3 classes", "1 vendor", "12 rows"). */
export const totalsNoun = (v: HierarchyView, n: number): string => {
  const t = totalsViewOf(v);
  const [one, many] = t === 'vendorByClass' ? ['row', 'rows'] : t === 'totalByVendor' ? ['vendor', 'vendors'] : ['class', 'classes'];
  return n === 1 ? one : many;
};

// Client-side shaping of payload rows into the display tree (Plan 3 ): hierarchy views,
// subtotals, % of parent, filters, search, sort, the weekly-planned switch, lazy SKU rows and the
// flatten with depth. Pure: nothing here re-queries (spec 5.3).
//
// Pipeline: build the tree for the view → attach SKU rows / placeholders under leaves (Show SKUs)
// → filters → search → group into Category / Location subtotals → sort → flatten → % of parent.
// Filters and search run BEFORE the subtotals are built, so a subtotal sums the rows it shows.
//
// Decisions recorded here (the plan leaves them open):
// - Total by Class: a class row with child rows is a 'group' and is its own subtotal (the payload
// already rolls children into parents); Vendor by Class likewise uses the vendor row. Category and
// Location subtotals are synthetic ClassRows (`category:<name>`, `location:<code>`,
// `location:<code>:category:<name>`): cells summed per period (null + null stays null, flags
// OR-ed), on order summed, and `summary` rebuilt sum-then-derive: inventory Σ, avgPeriodSales
// from the summed cells over the lookback, wos = Σinv ÷ avg, target = Σ plan inventory ÷ Σ plan
// sales at the target period when both are > 0 ('plan', `targetSource 'weighted'`), else the
// subtotal's own trailing average (mean of Σ inventoryStart over the lookback ÷ avgPeriodSales,
// 'own_avg'), else null/'none' — TargetsService::targetWos. `inventoryKnown` only when every
// member is known (a partly unknown total gets no inventory, wos or status); status and urgency
// via `targets`, otbBasis 'mixed' when members differ, otbCoveredThrough = the earliest.
// - Unclassified stays at the root in every view; Unallocated rows sit under their parent.
// - Filters test every row on its own attributes. A group that passes keeps all its children (as a
// matching container does in search); one that fails survives with the children that pass. A leaf
// that passes keeps the SKU rows under it that pass. Unallocated and SKU rows follow their parent
// for `weeklyPlannedOnly` (they never carry a plan: an Unallocated row under a parent that fails is
// dropped with it) but are tested by the other filters. The
// vendor filter tests only rows that carry a `vendorKey` (class rows pass). The status filter
// drops rows with no status (Unallocated, Unclassified, future targets). `weeklyPlannedOnly` keeps
// a row only if a period cell has `salesPlan !== null` (Tier A
// plans sit at the parent, so a planned parent keeps its unplanned children).
// - Search scope 'class' matches classCode, className and the vendor name (case-insensitive
// substring) on class / vendor rows; a container that matches keeps all its children. An Unallocated
// row never matches on its own label: it follows a parent that matches. Scopes
// 'sku' (skuId / variantId) and 'product' (productTitle) match loaded SKU rows only and keep
// their ancestors; a leaf whose SKUs are not loaded cannot match.
// - Sort orders siblings by the column's `cell.value` (numbers numerically, strings by locale,
// nulls last); Unallocated, Unclassified and placeholder rows stay last among their siblings.
// `% of Total` columns sort on the underlying measure (the denominator is constant within a
// sibling set, and % of parent is computed after sorting); `health` sorts by severity
// (out_of_stock > review > understocked > overstocked > watch > no_plan > on_track).
// - % of parent uses the target period's sales / inflow, start-of-period inventory and on order
// (`metricDefinitions.pctMeasureOf`); top-level rows divide by the report total (Σ roots).
// Inventory divides on ONE basis per sibling set (`siblingInventoryBasis`), which the % of
// inventory sort uses too: cost only when the parent (unless filtered) and every sibling carry cost
// cells, else retail for all (SKU rows at cost under a retail-only class row read retail).
// - Subtotals carry a cost field only when every summed cell has it (no partial cost totals). a cell whose
// retail twin is empty does not count against it, the plan at cost (inflowCostPlan) is summed the same way, and the
// summary's cost OTB fields (otbCost, inflowCostPlanSpan, receivedCostToDate, otbOnOrderCost) are null as soon as one
// member has the retail figure but not its cost (`costSum`).
// - Subtotals (category / location groups, vendors re-summed under a filter) carry Sales Trend with
// the backend's aggregate rule (`aggregateTrend`): Σ salesTrend, Trend % = Σ sales of the trending
// children ÷ that Σ (clamped), basis 'mixed' when the children differ, trendNote / trendAlt per method.
// - The SKU placeholder is an empty ClassRow shell keyed `<parent>:skus`; `groupKey` is the depth-0
// ancestor's key (a depth-0 row's own key).
// - `FlatRow.id` is the path of row keys from the root (`<parent id>/<row.key>`; a root row's id is
// its key). Row keys are NOT unique (the backend keys SKU rows `sku:<variantId>` under every
// location class that stocks them), so React keys, attention items and anything else that needs a
// row's identity use `id`; `row.key` stays the API key (`onExpandSkus`, `skuRowsByParent`,
// `expanded`).
// - A real row (class / vendor group, or a leaf whose SKU rows were filtered) that lost children to
// the filters or the search keeps the payload's own totals (it IS that class) but is flagged
// `filteredChildren`, and its children's % of parent divides by the sum of the visible siblings so
// it still adds up to 100% within what is shown. Synthetic subtotals sum what they show.
// - a row kept only as the ancestor of rows that passed the filters or the search (it
// failed them itself) is flagged `ancestorOnly`, even when none of its children was hidden; totals
// (`pageModel.totalRows`) then count the rows that passed, not it.
// - A sort on an unknown column id (a stale share link or saved view) is ignored.
// - (P0-5, chain roll-up): a class row is a LOCATION class (one per store), so a multi-store client listed each
// class once per store. Order: the Location filter first; then, when the rows it kept span more than one store, every view
// but Location › Category › Class rolls up (`rollUpChain`); then the other filters and the search run on the merged rows, so a
// Status filter sees the chain's status. The roll-up is group (`chainGroupOf`: class code, vendor key, variant id, product
// key, Unallocated, placeholder) → merge (`mergeChainGroup`) → recurse (`recurseChain`) level by level: chain class rows
// `chain:<code>`, vendor × class rows `vendor:<key>:chain:<code>`, products and variants by their own keys, one Unallocated
// row per chain parent. Class rows use chainSummary (the one subtotal rule, rebuildSummary, plus what every store shares);
// vendor × class rows the same on their allocated plan; products and variants ProductRowsAssembler's weighted target
// (variantSummary), freshness re-derived from Σ dollars, units summed. While any store's SKU or product rows are still
// loading, the level is one placeholder whose load string names those stores (`chain-load:a|b`, split by the hook), so a
// partial sum never shows. A class in one store keeps that store's row. Category by Product rolls up only its products'
// variants (its classes are not rows). Location › Category › Class keeps the per-store rows.
// views:
// - Class by Vendor attaches each class's vendor × class rows under its leaf class (which becomes a
// group) and the rows stamped at a parent class under that parent's Unallocated row. Total by
// Vendor is the vendor rows as leaves (Show SKUs loads `vendor:<key>`). Counts and KPIs are taken
// on `totalsViewOf(view)`'s tree by the hook, so they compare rows of one level.
// - Product views attach /product-rows payloads under leaf classes (Class by Product), vendor rows
// (Vendor by Product), `category:<id|none>` subtotals (Category by Product) or at the root (Total by
// Product, under `productParent`: 'all' or the already scoped filtered parent; null = nothing),
// with a `<parent>:products` placeholder (kind `skuPlaceholder`) until loaded and none in the
// expandAll tree. The Unallocated row of a payload is found by `level === 'unallocated'`, never by
// its key. Own-stamped variants at a PARENT class are not shown in Class by Product (as SKUs,
// which attach only under leaves). Show SKUs never applies there; the Product / SKU grain nests
// each product's variants under it.
// - Product keys repeat across parents, so every row of a product payload gets the expansion key
// `<parent>/<row.key>` (`FlatRow.expandKey`); `id` stays the path of row keys and `parentKey` the
// parent's API key.
// - Category by Product groups the bottom rows of the class tree (leaf classes and Unallocated rows)
// by each row's OWN categoryId, as /product-rows' `category:<id>` does, so a child class in another
// category than its parent counts under its own and nothing is summed twice.
// - Under a Location / Category / Class filter every product parent loads a FILTERED scope (
// review): Total by Product `classes:<ids>` (the hook), Category by Product `classes:<passing ids
// whose own category is that one>` (`category:<id>` unfiltered), Vendor by Product
// `vendor:<key>:classes:<that vendor's passing ids>` (`vendor:<key>` unfiltered), Class by Product the
// (passing) class itself. A placeholder's parentKey is that string; 0 ids or more than 1,000 put a
// `<parent>:note` message row there instead (no request). An unfiltered payload is never attached
// under a filter. Because every attached payload is already the filtered scope, its rows (products,
// variants, Unallocated) are not re-tested against those three filters (a product spanning classes
// carries one class's attributes and would be dropped); the Vendor and Status filters still apply.
// - Total by Vendor / Vendor by Product under a Location / Category / Class filter keep a vendor when
// one of its vendor × class rows passes and re-sum its totals from those rows (`makeSubtotal`).
// - Total by Product before its rows arrive shows a root "Loading products…" placeholder; a parent
// whose load failed (retryable) keeps its placeholder with PRODUCT_LOAD_FAILED as text.
// - Product rows follow their parent for "Weekly-planned only" (no plan, as SKUs); the class-scope
// search matches product titles and keeps a container with a matching product; the product scope
// matches product rows too; Category by Product searches only the products.
// - % of parent for vendor and product siblings uses the same one-basis-per-sibling-set helpers.
// Plan 4 must pass `payload.currentPeriod` in `ctx` (class Fresh %), offer the sku / product search
// scopes only while Show SKUs is on (nothing can match otherwise), and build the Plan & OTB period
// matrix itself rather than from `columnsFor` (whose plan_otb set is the Total / CSV column set).

export type SearchScope = 'class' | 'sku' | 'product';
export type FlatRowKind = 'group' | 'leaf' | 'subtotal' | 'unallocated' | 'unclassified' | 'sku' | 'skuPlaceholder';
export type SortDirection = 'asc' | 'desc';

/** Round 2: how many of a chain row's member stores pass the Status filter. */
export interface StoreMatch { matched: number; total: number }

/** a Status filter chip is a status, or the Deadstock flag ('deadstock', a filterable flag that ORs with the statuses). */
export type StatusFilter = Status | typeof DEADSTOCK_FILTER;
export interface RowTreeFilters { locations: FilterSelection; categories: FilterSelection; classes: FilterSelection; vendors: FilterSelection; statuses: StatusFilter[] }
/**
 * `productIds` (W3, the server-side product search): the productIds /product-search matched. When present the search keeps exactly
 * the product rows (and, under Show SKUs or the SKU grain, the variant rows) of those products, shown under their parents, and `text`
 * only says a search is on. Absent = the old text match on the rows' own fields (what the tests of the row tree use).
 */
export interface RowTreeSearch { text: string; scope: SearchScope; productIds?: ReadonlySet<string> }
export interface RowTreeSort { column: string; direction: SortDirection }

export interface RowTreeInput {
  /** Distribute OTB (view state `distributeOtb`): split a parent's OTB to its children without one. */
  distributeOtb?: boolean;
  /**
   * Wendi (2026-10-22), "Show child classes" (view state `showChildClasses`, default true). False folds every class family: the child
   * classes (and the parent's Unallocated row) are not shown and the parent's row is as it is (its figures already include them); the
   * vendor rows of a folded family are merged per vendor, and its products / SKUs load for every class of the family (`familyIds`).
   */
  showChildClasses?: boolean;
  classRows: ClassRow[];
  vendorRows?: VendorRow[];
  view: HierarchyView;
  filters: RowTreeFilters;
  search: RowTreeSearch;
  sort: RowTreeSort | null;
  showSkus: boolean;
  weeklyPlannedOnly: boolean;
  skuRowsByParent: Record<string, VendorRow[] | undefined>;
  /** Product rows per product parent (class:<id> | vendor:<key> | category:<id|none> | all | classes:<ids>), as /product-rows returns them. */
  productRowsByParent?: Record<string, VendorRow[] | undefined>;
  /**
   * Total by Product: the parent string the products were fetched under ('all', or a filtered scope
   * such as 'classes:<ids>' / 'vendor:<key>', whose rows are already scoped). Undefined = 'all';
   * null = no request was sent (empty selection / too many classes) → no rows.
   */
  productParent?: string | null;
  /** Product views: 'sku' nests each product's variant rows under it. Default 'product'. */
  productGrain?: ProductGrain;
  /** Product parents (load strings) whose last load failed and may be retried: their placeholder says so. */
  productFailed?: Record<string, boolean | undefined>;
  /** Keyed by `FlatRow.expandKey`. */
  expanded: Record<string, boolean>;
  /**
   * Treat every group / subtotal / vendor row as expanded and add no SKU placeholders (loaded SKU
   * rows under Show SKUs still attach). KPI and attention totals are computed from
   * `buildRowTree({...input, expandAll: true})`, so they never change when the user expands or
   * collapses rows: `pageModel.totalRows` over that tree is each class the filters kept, once, at its
   * family's level (a parent class, never its children too).
   */
  expandAll?: boolean;
  ctx: CellContext;
}

export interface FlatRow {
  /** Unique within one buildRowTree result: the parent path (see the header). */
  id: string;
  row: AnyRow;
  depth: number;
  kind: FlatRowKind;
  /** The parent row's API key (`row.key`): what a placeholder loads children for. */
  parentKey: string | null;
  groupKey: string | null;
  /**
   * The key `expanded` / `toggleGroup` use for this row: `row.key`, except for rows from a
   * /product-rows payload, which are scoped `<product parent>/<row.key>` (a product key repeats under
   * several parents). Optional only for hand-built FlatRows (consumers fall back to `row.key`).
   */
  expandKey?: string;
  /** A product parent row (category subtotal, leaf class, vendor): the parent string its products load under. */
  productParent?: string;
  expanded: boolean;
  childCount: number;
  pctOfParent: PctOfParent | null;
  /** Distribute OTB: this row's share of its parent's OTB (otbDistribution.ts); absent when the option is off or it has an OTB of its own. */
  distributedOtb?: DistributedOtb;
  /** True when the filters or the search removed some of this row's children (its totals do not). */
  filteredChildren: boolean;
  /**
   * True when the row did not pass the filters or the search itself and is shown only as the ancestor of
   * rows that did. Absent = it passed.
   */
  ancestorOnly?: boolean;
  /**
   * an `ancestorOnly` row that passed every filter and is shown only because the SEARCH matched something
   * below it. It displays its full family figures, so totals count it (not just the matching children), keeping KPI = visible rows.
   */
  searchShown?: boolean;
  /**
   * Round 2: a chain row kept by the Status filter because some of its member stores match (not all): how many. The row's totals
   * are the whole chain's; its children come from the matching stores only. Absent = every store matched or no status filter.
   */
  storeMatch?: StoreMatch;
}

/** `scoped`: a row of a /product-rows payload, already scoped by its parent on the server (see leafPasses). */
/** `loadParent`: a placeholder's load string (its FlatRow.parentKey); `productParent`: see FlatRow. */
/** `ancestorOnly`: see FlatRow. */
/** `familyIds`: a class folded with Show child classes off (its own id and its descendants'), carried to its vendor rows: what its products / SKUs load for. */
interface Node { key: string; row: AnyRow; kind: FlatRowKind; children: Node[]; filtered?: boolean; ancestorOnly?: boolean; searchShown?: boolean; scoped?: boolean; loadParent?: string; productParent?: string; memberPass?: boolean; storeMatch?: StoreMatch; familyIds?: number[] }

const PINNED: FlatRowKind[] = ['unallocated', 'unclassified', 'skuPlaceholder'];
const PCT_MEASURES: PctMeasure[] = ['sales', 'inventory', 'inflow', 'onOrder'];
const PCT_COLUMN_MEASURE: Record<string, PctMeasure> = { pctOfSales: 'sales', pctOfInventory: 'inventory', pctOfInflow: 'inflow', pctOfOnOrder: 'onOrder' };
const STATUS_SEVERITY: Record<Status, number> = { out_of_stock: 7, review: 6, understocked: 5, overstocked: 4, no_recent_sales: 4, watch_low: 3, watch_high: 2, no_plan: 1, on_track: 0 };
const SKU_PARENT_RE = /^(class:\d+|vendor:[^:]+(:class:\d+)?)$/;
const SUM_FIELDS: (keyof Cell)[] = ['sales', 'unitsSold', 'markdowns', 'inflow', 'inflowCost', 'inventoryStart', 'inventoryEnd', 'salesPlan', 'inflowPlan', 'inflowCostPlan', 'inventoryPlan', 'markdownsPlan', 'unitsStart', 'unitsEnd', 'inventoryCostStart', 'inventoryCostEnd', 'salesLy', 'markdownsLy', 'inflowLy', 'inventoryLy'];
/** Each cost field and its retail twin: a cost total needs the cost wherever the retail is known. */
const COST_TWIN: Partial<Record<keyof Cell, keyof Cell>> = { inflowCost: 'inflow', inflowCostPlan: 'inflowPlan', inventoryCostStart: 'inventoryStart', inventoryCostEnd: 'inventoryEnd' };
const COST_FIELDS = Object.keys(COST_TWIN) as (keyof Cell)[];
/** Summary cost fields and their retail twins. */
const SUMMARY_COST_TWIN: [keyof Summary, keyof Summary][] = [['otbCost', 'otb'], ['inflowCostPlanSpan', 'inflowPlanSpan'], ['receivedCostToDate', 'receivedToDate'], ['otbOnOrderCost', 'otbOnOrder']];
const FLAG_FIELDS: (keyof Cell)[] = ['isEstimated', 'isCurrent', 'isAllocated', 'planRolledUp', 'isProjected', 'isFloored', 'lyPartial', 'inventoryLyMissing', 'planV2History'];

const isNumRowTree = (v: unknown): v is number => typeof v === 'number' && !Number.isNaN(v);
const node = (row: AnyRow, kind: FlatRowKind, children: Node[] = []): Node => ({ key: row.key, row, kind, children });
const emptyClassRow = (key: string, attrs: Partial<ClassRow> = {}): ClassRow => ({
  key, locationClassId: null, parentLocationClassId: null, isParent: false, isUnallocated: false, classCode: null, className: null, category: null, categoryId: null,
  locationId: null, locationCode: null, locationName: null, onOrderRetail: null, onOrderCost: null, onOrderUnits: null, periods: {}, summary: null, ...attrs,
});

// ---- trees per view -----------------------------------------------------------------------------

const classKind = (r: ClassRow): FlatRowKind => (r.key === 'unclassified' ? 'unclassified' : r.isUnallocated ? 'unallocated' : 'leaf');
const classParentId = (r: ClassRow): number | null => {
  if (isNumRowTree(r.parentLocationClassId) && r.parentLocationClassId !== r.locationClassId) return r.parentLocationClassId;
  const m = /^class:(\d+):unallocated$/.exec(r.key);
  return m ? Number(m[1]) : null;
};

/** Class rows → roots; children attach to their parent class row when it is in the payload. */
const classTree = (rows: ClassRow[]): Node[] => {
  const nodes = rows.map((r) => node(r, classKind(r)));
  const byId = new Map<number, Node>();
  nodes.forEach((n) => {
    const r = n.row as ClassRow;
    if (isNumRowTree(r.locationClassId) && !r.isUnallocated) byId.set(r.locationClassId, n);
  });
  const roots: Node[] = [];
  nodes.forEach((n) => {
    const pid = n.kind === 'unclassified' ? null : classParentId(n.row as ClassRow);
    const parent = pid === null ? undefined : byId.get(pid);
    if (parent && parent !== n) parent.children.push(n);
    else roots.push(n);
  });
  nodes.forEach((n) => { if (n.kind === 'leaf' && n.children.length > 0) n.kind = 'group'; });
  return roots;
};

/**
 * Show child classes off: every class family folds to its parent row. The class children and the parent's Unallocated row go (the
 * parent's figures already include them); the parent becomes a leaf carrying `familyIds` (its own id and every descendant's), which
 * the vendor / product / SKU attachments read. Non-class children (vendor rows, placeholders) are kept.
 */
const foldFamilies = (nodes: Node[]): Node[] => nodes.map((n) => {
  if (isVendorRow(n.row) || n.children.length === 0) return n;
  const classKids = n.children.filter((c) => !isVendorRow(c.row) && (c.kind === 'leaf' || c.kind === 'group' || c.kind === 'unallocated'));
  if (classKids.length === 0) return { ...n, children: foldFamilies(n.children) };
  const ids = new Set<number>();
  const collect = (m: Node): void => {
    const r = m.row as ClassRow;
    if (!isVendorRow(r) && !r.isUnallocated && isNumRowTree(r.locationClassId)) ids.add(r.locationClassId);
    m.children.forEach(collect);
  };
  collect(n);
  const kept = n.children.filter((c) => !classKids.includes(c));
  return { ...n, kind: n.kind === 'group' && kept.length === 0 ? 'leaf' : n.kind, children: kept, familyIds: Array.from(ids).sort((a, b) => a - b) };
});
/** The class ids a node's products / SKUs load for: its folded family's, else its own. */
const classIdsOf = (n: Node): number[] => (n.familyIds ?? (isNumRowTree(n.row.locationClassId) ? [n.row.locationClassId] : []));

const vendorKind = (r: VendorRow): FlatRowKind => (r.level === 'sku' ? 'sku' : r.level === 'unallocated' ? 'unallocated' : 'leaf');
const isProductRow = (r: AnyRow): boolean => isVendorRow(r) && r.level === 'product';

/** Vendor rows → vendor groups with their vendorClass children (payload order). */
const vendorTree = (rows: VendorRow[]): Node[] => {
  const nodes = rows.map((r) => node(r, vendorKind(r)));
  const byKey = new Map(nodes.map((n) => [n.key, n]));
  const roots: Node[] = [];
  nodes.forEach((n) => {
    const parent = (n.row as VendorRow).parentKey ? byKey.get((n.row as VendorRow).parentKey as string) : undefined;
    if (parent && parent !== n) parent.children.push(n);
    else roots.push(n);
  });
  nodes.forEach((n) => { if (n.kind === 'leaf' && n.children.length > 0) n.kind = 'group'; });
  return roots;
};

/** Show SKUs: loaded SKU rows (then their Unallocated row) under each leaf, else one placeholder. */
const attachSkus = (nodes: Node[], skuRowsByParent: RowTreeInput['skuRowsByParent'], placeholders: boolean): void => {
  nodes.forEach((n) => {
    if (n.kind === 'leaf' && n.children.length === 0 && SKU_PARENT_RE.test(n.key)) {
      // A folded family (Show child classes off) loads every class of the family: its own `class:<id>` key and its descendants' (one
      // placeholder with a chain load string until every one is in, as a chain row does).
      const keys = n.familyIds && n.familyIds.length > 1 ? n.familyIds.map((id) => (n.key.startsWith('vendor:') ? `${n.key.replace(/:class:\d+$/, '')}:class:${id}` : `class:${id}`)) : [n.key];
      const loaded = keys.map((k) => skuRowsByParent[k]);
      if (loaded.every((l): l is VendorRow[] => !!l)) n.children = loaded.flat().map((r) => node(r, vendorKind(r)));
      else if (placeholders) n.children = [{ ...node(emptyClassRow(`${n.key}:skus`), 'skuPlaceholder'), ...(keys.length > 1 ? { loadParent: chainLoadKey(keys) } : {}) }];
    } else attachSkus(n.children, skuRowsByParent, placeholders);
  });
};

/**
 * (product search): the Show SKUs parents of the view's own leaf rows (`class:<id>`, `vendor:<key>[:class:<id>]`), before any
 * chain roll-up. A chain row (`chain:<code>`) is not itself a SKU parent, so the merged tree cannot name the stores' parents once the
 * rows are expanded; this reads the unmerged tree (Location / Category / Class / Vendor filters applied, Status and search not).
 * Empty in a product view (it has no SKU parents).
 */
export const skuParentKeys = (rawInput: RowTreeInput): string[] => {
  if (isProductView(rawInput.view)) return [];
  const input: RowTreeInput = { ...rawInput, classRows: netNonSellingAcrossStores(rawInput.classRows), search: { text: '', scope: 'class' }, filters: { ...rawInput.filters, statuses: [] }, showSkus: false };
  const out = new Set<string>();
  const walk = (nodes: Node[]): void => nodes.forEach((n) => {
    if (n.kind === 'skuPlaceholder' || n.kind === 'sku') return;
    if (n.kind === 'leaf' && n.children.length === 0 && SKU_PARENT_RE.test(n.key)) {
      // A folded family (Show child classes off) loads every class of the family (as attachSkus does).
      if (n.familyIds && n.familyIds.length > 1) n.familyIds.forEach((id) => out.add(n.key.startsWith('vendor:') ? `${n.key.replace(/:class:\d+$/, '')}:class:${id}` : `class:${id}`));
      else out.add(n.key);
    } else walk(n.children);
  });
  walk(filterNodes(baseTree(input), input));
  return Array.from(out).sort();
};

/** Vendor rows only (Total by Vendor, Vendor by Product): one leaf per vendor. */
const leafVendorTree = (rows: VendorRow[]): Node[] => {
  // A root-level Unallocated row (if the payload has one) stays, so % of Total and Sales Trend close.
  const vendorKeys = new Set(rows.filter((r) => r.level === 'vendor').map((r) => r.key));
  return rows
    .filter((r) => r.level === 'vendor' || (r.level === 'unallocated' && !(r.parentKey && vendorKeys.has(r.parentKey))))
    .map((r) => node(r, r.level === 'unallocated' ? 'unallocated' : 'leaf'));
};

/**
 * Class by Vendor: the class tree with each class's vendor × class rows under it. Rows stamped at a
 * parent class go under that parent's Unallocated row (its own-stamped values). Each attached row is a
 * copy hinted `labelAs: 'vendor'` (its parent already names the class), which the label column and
 * everything built on it (table, CSV, attention items, row detail headers, the label sort) read.
 */
const classByVendorTree = (classRows: ClassRow[], vendorRows: VendorRow[], fold = false, ctx?: CellContext): Node[] => {
  const roots = fold ? foldFamilies(classTree(classRows)) : classTree(classRows);
  const byClass = new Map<number, VendorRow[]>();
  vendorRows.forEach((r) => {
    if (r.level !== 'vendorClass' || !isNumRowTree(r.locationClassId)) return;
    byClass.set(r.locationClassId, [...(byClass.get(r.locationClassId) ?? []), r]);
  });
  const attach = (nodes: Node[]): void => nodes.forEach((n) => {
    if (isVendorRow(n.row)) return;
    attach(n.children);
    const r = n.row as ClassRow;
    const own = n.kind === 'leaf' ? r.locationClassId : n.kind === 'unallocated' ? classParentId(r) : null;
    if (!isNumRowTree(own) || n.children.length !== 0) return;
    // A folded family (Show child classes off): the family's vendor × class rows, one row per vendor (merged across the family's classes).
    const familyRows = n.familyIds && n.familyIds.length > 1 && ctx ? n.familyIds.flatMap((id) => byClass.get(id) ?? []) : byClass.get(own) ?? [];
    if (familyRows.length === 0) return;
    const byVendor = new Map<string, VendorRow[]>();
    familyRows.forEach((v) => byVendor.set(v.vendorKey, [...(byVendor.get(v.vendorKey) ?? []), v]));
    n.children = Array.from(byVendor.values()).map((vs) => {
      const row: VendorRow = vs.length === 1 || !ctx ? vs[0] : { ...mergeVendorRows(vs, `vendor:${vs[0].vendorKey}:class:${own}`, ctx, false), locationClassId: own, parentKey: vs[0].parentKey };
      return { ...node({ ...row, labelAs: 'vendor' }, 'leaf'), ...(n.familyIds ? { familyIds: n.familyIds } : {}) };
    });
    if (n.kind === 'leaf') n.kind = 'group';
  });
  attach(roots);
  return roots;
};

/**
 * A /product-rows payload fetched under `parent` as nodes: product rows and the Unallocated row
 * (detected by level); at SKU grain each product's variants under it. Node keys are scoped
 * `<parent>/<row.key>` so expansion never leaks to the same product under another parent.
 */
const productNodes = (parent: string, rows: VendorRow[], grain: ProductGrain): Node[] => {
  const scoped = (r: VendorRow, kind: FlatRowKind, children: Node[] = []): Node => ({ key: `${parent}/${r.key}`, row: r, kind, children, scoped: true });
  const variants = new Map<string, VendorRow[]>();
  if (grain === 'sku') {
    rows.forEach((r) => {
      if (r.level === 'sku' && r.parentKey) variants.set(r.parentKey, [...(variants.get(r.parentKey) ?? []), r]);
    });
  }
  return rows
    .filter((r) => r.level === 'product' || r.level === 'unallocated')
    .map((r) => {
      if (r.level === 'unallocated') return scoped(r, 'unallocated');
      const kids = (variants.get(r.key) ?? []).map((v) => scoped(v, 'sku'));
      return scoped(r, kids.length > 0 ? 'group' : 'leaf', kids);
    });
};

export const PRODUCT_LOAD_FAILED = 'Could not load products. Collapse and expand to retry.';
/** /product-rows' MAX_CLASS_IDS for a `classes:` / `vendor:<key>:classes:` parent. */
export const MAX_PRODUCT_CLASS_IDS = 1000;
/** Where a product parent's products come from: a load string, or a note shown instead (no request). */
type ProductTarget = { key: string } | { note: string };

/** "Loading products…" for `loadParent` (or the retry text after a failed load); none in the expandAll tree. */
const productPlaceholder = (ownerKey: string, loadParent: string, input: RowTreeInput): Node[] =>
  input.expandAll ? [] : [{ ...node(emptyClassRow(`${ownerKey}:products`, { className: splitLoadKey(loadParent).some((k) => input.productFailed?.[k]) ? PRODUCT_LOAD_FAILED : null }), 'skuPlaceholder'), loadParent }];
/** A static message row under a parent (`<parent>:note`): never loads anything. */
const noteNode = (ownerKey: string, text: string): Node => node(emptyClassRow(`${ownerKey}:note`, { className: text }), 'skuPlaceholder');

/**
 * Product parents get the products loaded under their parent string (`parentOf`: the row key, or a
 * filtered scope such as `classes:<ids>`), or one "Loading products…" placeholder whose parentKey is
 * that string; a note target puts a message row there instead (no request).
 */
const attachProducts = (nodes: Node[], input: RowTreeInput, isParent: (n: Node) => boolean, parentOf: (n: Node) => ProductTarget = (n) => ({ key: n.key })): void => {
  const loadedByParent = input.productRowsByParent ?? {};
  nodes.forEach((n) => {
    if (!isParent(n)) {
      attachProducts(n.children, input, isParent, parentOf);
      return;
    }
    const target = parentOf(n);
    if ('note' in target) {
      n.children = input.expandAll ? [] : [noteNode(n.key, target.note)];
      return;
    }
    n.productParent = target.key;
    const loaded = loadedByParent[target.key];
    n.children = loaded ? productNodes(target.key, loaded, input.productGrain ?? 'product') : productPlaceholder(n.key, target.key, input);
  });
};

/** True for a placeholder that loads rows when mounted (not a `:note` message row). */
export const placeholderLoads = (fr: FlatRow): boolean => fr.kind === 'skuPlaceholder' && !!fr.parentKey && !fr.row.key.endsWith(':note');

/** Any Location / Category / Class filter (the product scopes and vendor totals depend on them). */
export const hasScopeFilter = (f: Pick<RowTreeFilters, 'locations' | 'categories' | 'classes'>): boolean =>
  f.locations.codes.length > 0 || f.categories.codes.length > 0 || f.classes.codes.length > 0;

/**
 * The class rows (not Unallocated / Unclassified) that pass the Location / Category / Class filters
 * themselves or through an ancestor class (a passing parent keeps its subtree, as the class tree does).
 */
export const passingClassRows = (classRows: ClassRow[], f: Pick<RowTreeFilters, 'locations' | 'categories' | 'classes'>): ClassRow[] => {
  const classes = classRows.filter((r) => isNumRowTree(r.locationClassId) && !r.isUnallocated && r.key !== 'unclassified');
  const byId = new Map(classes.map((r) => [r.locationClassId as number, r]));
  const passes = (r: ClassRow): boolean => locationClassPass(r, f as RowTreeFilters) && matchesSelection(f.categories, r.category);
  const memo = new Map<number, boolean>();
  const included = (r: ClassRow, seen: Set<number>): boolean => {
    const id = r.locationClassId as number;
    const known = memo.get(id);
    if (known !== undefined) return known;
    seen.add(id);
    const pid = r.parentLocationClassId;
    const parent = isNumRowTree(pid) && pid !== id && !seen.has(pid) ? byId.get(pid) : undefined;
    const out = passes(r) || (!!parent && included(parent, seen));
    memo.set(id, out);
    return out;
  };
  return classes.filter((r) => included(r, new Set()));
};
const sortedIds = (ids: number[]): number[] => Array.from(new Set(ids)).sort((a, b) => a - b);
export const tooManyClassesText = (n: number, subject: string, alternative: string): string =>
  `${subject} matches ${n.toLocaleString('en-US')} ${n === 1 ? 'class' : 'classes'}; ${alternative}`;

/** Category by Product: `category:<id>` unfiltered; under a filter `classes:<passing ids whose own category is this one>`. */
const categoryProductTarget = (input: RowTreeInput): ((n: Node) => ProductTarget) => {
  if (!hasScopeFilter(input.filters)) return (n) => ({ key: n.key });
  const passing = passingClassRows(input.classRows, input.filters);
  return (n) => {
    const ids = sortedIds(passing.filter((r) => `category:${isNumRowTree(r.categoryId) ? r.categoryId : 'none'}` === n.key).map((r) => r.locationClassId as number));
    if (ids.length === 0) return { note: 'No products in this selection.' };
    if (ids.length > MAX_PRODUCT_CLASS_IDS) return { note: tooManyClassesText(ids.length, 'This category', 'narrow the filter, or use Class by Product.') };
    return { key: `classes:${ids.join(',')}` };
  };
};

/**
 * The vendor × class rows of each vendor that pass the Location / Category / Class filters by the same
 * rule as the product scopes: a row passes when its class is in `passingClassRows` (the class or an
 * ancestor passes, so a filter on a parent keeps the vendor rows of its children); a row whose class is
 * not in the class payload is tested on its own fields.
 */
const passingVendorClasses = (input: RowTreeInput): Map<string, VendorRow[]> => {
  const f = input.filters;
  const known = new Set(input.classRows.filter((r) => isNumRowTree(r.locationClassId) && !r.isUnallocated).map((r) => r.locationClassId as number));
  const passing = new Set(passingClassRows(input.classRows, f).map((r) => r.locationClassId as number));
  const out = new Map<string, VendorRow[]>();
  (input.vendorRows ?? []).forEach((r) => {
    if (r.level !== 'vendorClass' || !r.parentKey) return;
    const pass = isNumRowTree(r.locationClassId) && known.has(r.locationClassId)
      ? passing.has(r.locationClassId)
      : locationClassPass(r, f) && matchesSelection(f.categories, r.category);
    if (!pass) return;
    out.set(r.parentKey, [...(out.get(r.parentKey) ?? []), r]);
  });
  return out;
};

/**
 * Total by Vendor / Vendor by Product. No Location / Category / Class filter: the server's vendor rows.
 * Otherwise a vendor is kept when at least one of its vendor × class rows passes, and its totals are
 * re-summed from those rows with the subtotal aggregator (`makeSubtotal`: cells, on order, summary);
 * such a node is `scoped` (the filters are applied), so filterNodes does not re-test its empty class
 * attributes. A root Unallocated row cannot be placed in a location / class and is dropped.
 */
const vendorLeaves = (input: RowTreeInput): Node[] => {
  const rows = input.vendorRows ?? [];
  if (!hasScopeFilter(input.filters)) return leafVendorTree(rows);
  const kids = passingVendorClasses(input);
  return rows.filter((r) => r.level === 'vendor' && kids.has(r.key)).map((v) => {
    const sub = makeSubtotal(kids.get(v.key) as VendorRow[], v.key, '', {}, input.ctx);
    const row: VendorRow = { ...v, periods: sub.periods, onOrderRetail: sub.onOrderRetail, onOrderCost: sub.onOrderCost, onOrderUnits: sub.onOrderUnits, summary: sub.summary, freshness: null };
    return { ...node(row, 'leaf'), scoped: true };
  });
};

/** Vendor by Product: `vendor:<key>` unfiltered; under a filter `vendor:<key>:classes:<that vendor's passing class ids>`. */
const vendorProductTarget = (input: RowTreeInput): ((n: Node) => ProductTarget) => {
  if (!hasScopeFilter(input.filters)) return (n) => ({ key: n.key });
  const kids = passingVendorClasses(input);
  return (n) => {
    const ids = sortedIds((kids.get(n.row.key) ?? []).map((r) => r.locationClassId).filter(isNumRowTree));
    if (ids.length === 0) return { note: 'No products in this selection.' };
    if (ids.length > MAX_PRODUCT_CLASS_IDS) return { note: tooManyClassesText(ids.length, 'This vendor', 'narrow the filter, or use Class by Product.') };
    return { key: `${n.row.key}:classes:${ids.join(',')}` };
  };
};

const isLeafClass = (n: Node): boolean => n.kind === 'leaf' && n.children.length === 0 && !isVendorRow(n.row) && /^class:\d+$/.test(n.key);
/** A leaf class's product parent: its own key, or `classes:<family ids>` for a family folded with Show child classes off. */
const classProductTarget = (n: Node): ProductTarget => (n.familyIds && n.familyIds.length > 1 ? { key: `classes:${n.familyIds.join(',')}` } : { key: n.key });
/** Class › Vendor › Product: a vendor × class row (no products yet) loads `vendor:<key>:classes:<class ids>` (the family's ids when folded). */
const isVendorClassLeaf = (n: Node): boolean => isVendorRow(n.row) && n.row.level === 'vendorClass' && n.children.length === 0;
const vendorClassProductTarget = (n: Node): ProductTarget => {
  const r = n.row as VendorRow;
  const ids = n.familyIds && n.familyIds.length > 0 ? n.familyIds : isNumRowTree(r.locationClassId) ? [r.locationClassId] : [];
  if (ids.length === 0 || !r.vendorKey) return { note: 'No products in this selection.' };
  return { key: `vendor:${r.vendorKey}:classes:${ids.join(',')}` };
};

const PLAN_FIELDS: (keyof Cell)[] = ['salesPlan', 'inflowPlan', 'inventoryPlan', 'markdownsPlan'];
const REMAINDER_SUMMARY_FIELDS: (keyof Summary)[] = ['inventory', 'onOrder', 'onOrderCost', 'receivedToDate', 'inflowPlanSpan', 'otb', 'deviation', 'otbDev', 'otbCost', 'inflowCostPlanSpan', 'receivedCostToDate', 'otbOnOrderCost'];
/**
 * The wire rounds every amount to 2 dp independently (CellCompactor), so an exactly-closing parent can differ from
 * Σ children by up to half a cent per term: a remainder within that band is rounding, not a parent-only value, and
 * a kept remainder is itself rounded to the cent.
 */
const closes = (rem: number, terms: number): boolean => Math.abs(rem) < 0.005 * (terms + 1);
const toCents = (v: number): number => Math.round(v * 100) / 100;
const EMPTY_CELL: Cell = {
  sales: null, unitsSold: null, markdowns: null, inflow: null, inflowCost: null, inventoryStart: null, inventoryEnd: null,
  salesPlan: null, inflowPlan: null, inventoryPlan: null, markdownsPlan: null,
  isEstimated: false, isCurrent: false, isAllocated: false, planRolledUp: false, isProjected: false,
  salesLy: null, markdownsLy: null, inflowLy: null, inventoryLy: null, lyPartial: false,
};
/** a − Σ bs when a and every b are numbers (null b's count as 0 when `nullAsZero`), else null. */
const remainder = (a: unknown, bs: unknown[], nullAsZero: boolean): number | null => {
  if (!isNumRowTree(a)) return null;
  if (!nullAsZero && !bs.every(isNumRowTree)) return null;
  return a - bs.reduce<number>((t, b) => t + (isNumRowTree(b) ? b : 0), 0);
};

/**
 * Category by Product splits a parent class across its children's own categories, so its Unallocated
 * row must carry everything the parent has beyond its direct children: its own-stamped actuals (already
 * in the row), a plan that exists only at the parent (parent plan − Σ children plans, added to the
 * row's plan cells), and the summary's inventory / on order / received / span plan / OTB as parent −
 * Σ children (equal to the own-stamped values when the payload rolls up). A parent with such a
 * remainder and no Unallocated row gets a synthetic one. Computed on the unfiltered tree.
 */
const withParentRemainders = (nodes: Node[]): Node[] => nodes.map((n) => {
  if (n.kind !== 'group' || isVendorRow(n.row)) return n;
  const parent = n.row as ClassRow;
  const children = withParentRemainders(n.children);
  const kids = children.filter((c) => c.kind !== 'unallocated').map((c) => c.row);
  const ownNode = children.find((c) => c.kind === 'unallocated');
  const own = ownNode?.row as ClassRow | undefined;
  let changed = false;
  const periods: Record<string, Cell> = { ...(own?.periods ?? {}) };
  Object.entries(parent.periods).forEach(([k, pc]) => {
    // A period the own row has no cell for starts from an empty cell (every field present, null / false).
    const cell = { ...(periods[k] ?? EMPTY_CELL) } as Record<string, unknown>;
    PLAN_FIELDS.forEach((f) => {
      const rem = remainder(pc?.[f], kids.map((r) => r.periods[k]?.[f]), true);
      if (rem === null || closes(rem, kids.length)) return;
      cell[f] = toCents((isNumRowTree(cell[f]) ? (cell[f] as number) : 0) + rem);
      changed = true;
      if (f === 'inflowPlan') {
        // the plan at cost's remainder, only when every child with a retail plan has its cost.
        const costKnown = kids.every((r) => isNumRowTree(r.periods[k]?.inflowCostPlan) || !isNumRowTree(r.periods[k]?.inflowPlan));
        const costRem = costKnown ? remainder(pc?.inflowCostPlan, kids.map((r) => r.periods[k]?.inflowCostPlan), true) : null;
        cell.inflowCostPlan = costRem === null ? null : toCents((isNumRowTree(cell.inflowCostPlan) ? (cell.inflowCostPlan as number) : 0) + costRem);
      }
    });
    if (periods[k] || PLAN_FIELDS.some((f) => isNumRowTree(cell[f]))) periods[k] = cell as unknown as Cell;
  });
  const onOrder = { onOrderRetail: remainder(parent.onOrderRetail, kids.map((r) => r.onOrderRetail), true), onOrderCost: remainder(parent.onOrderCost, kids.map((r) => r.onOrderCost), true), onOrderUnits: remainder(parent.onOrderUnits, kids.map((r) => r.onOrderUnits), true) };
  let summary: Summary | null = own?.summary ?? null;
  const kidSummaries = kids.map((r) => r.summary);
  if (parent.summary && kidSummaries.every((x): x is Summary => !!x)) {
    const rem = {} as Record<string, number | null>;
    REMAINDER_SUMMARY_FIELDS.forEach((f) => {
      const r = remainder(parent.summary![f], kidSummaries.map((x) => x[f]), false);
      rem[f] = r === null ? null : closes(r, kids.length) ? 0 : toCents(r);
    });
    if (REMAINDER_SUMMARY_FIELDS.some((f) => isNumRowTree(rem[f]) && (rem[f] as number) !== 0)) changed = true;
    summary = {
      inventory: rem.inventory, inventoryKnown: isNumRowTree(rem.inventory) && parent.summary.inventoryKnown && kidSummaries.every((x) => x.inventoryKnown),
      avgPeriodSales: null, wos: null, targetWos: null, targetBasis: 'none', status: null, urgent: false,
      onOrder: rem.onOrder, onOrderCost: rem.onOrderCost, receivedToDate: rem.receivedToDate, inflowPlanSpan: rem.inflowPlanSpan, otb: rem.otb,
      otbBasis: parent.summary.otbBasis, otbCoveredThrough: parent.summary.otbCoveredThrough, deviation: rem.deviation, otbDev: rem.otbDev, otbExcluded: parent.summary.otbExcluded ?? null,
      otbCost: rem.otbCost, inflowCostPlanSpan: rem.inflowCostPlanSpan, receivedCostToDate: rem.receivedCostToDate, otbOnOrderCost: rem.otbOnOrderCost,
    };
  }
  if (!own && !changed) return { ...n, children };
  const row: ClassRow = {
    ...(own ?? emptyClassRow(`${parent.key}:unallocated`, {
      parentLocationClassId: parent.locationClassId, isUnallocated: true, className: 'Unallocated / adjustments', category: parent.category, categoryId: parent.categoryId,
      locationId: parent.locationId, locationCode: parent.locationCode, locationName: parent.locationName,
    })),
    ...(own ? {} : onOrder), periods, summary,
  };
  const remNode = node(row, 'unallocated');
  return { ...n, children: own ? children.map((c) => (c === ownNode ? remNode : c)) : [...children, remNode] };
});

const locationClassPass = (r: AnyRow, f: RowTreeFilters): boolean => matchesSelection(f.locations, r.locationCode) && matchesSelection(f.classes, r.classCode);
const sameCategory = (n: Node, categoryId: number | null): boolean =>
  n.children.every((c) => c.kind === 'skuPlaceholder' || ((c.row.categoryId ?? null) === categoryId && sameCategory(c, categoryId)));

/**
 * Category by Product's members: a parent whose whole (unfiltered) subtree is in its own category stays
 * whole (its own totals, plan and summary); any other parent is replaced by its children (and its
 * Unallocated remainder), each counted under its OWN category. A lifted row is always re-tested against
 * the Category filter (it is grouped by its own category, so a Shoes child of an Apparel parent must
 * not leak into an Apparel filter); against the Location / Class filters it inherits the pass of the
 * nearest ancestor that passed them itself (a Class filter on the parent keeps all of the parent), and
 * is re-tested only below a parent that failed them.
 */
const liftByOwnCategory = (nodes: Node[], f: RowTreeFilters, lifted = false, inherited = false): Node[] => nodes.flatMap((n) => {
  const container = n.kind === 'group' && !isVendorRow(n.row);
  if (container && (n.filtered || !sameCategory(n, n.row.categoryId ?? null))) return liftByOwnCategory(n.children, f, true, inherited || locationClassPass(n.row, f));
  // Only the rows that become members are tested; a lifted container is not (its rows are).
  return !lifted || (matchesSelection(f.categories, n.row.category) && (inherited || locationClassPass(n.row, f))) ? [n] : [];
});

/** The view's rows before filters (Category / Location subtotals are added later by applyView). */
const baseTree = (input: RowTreeInput): Node[] => {
  const fold = input.showChildClasses === false;
  const classes = (): Node[] => (fold ? foldFamilies(classTree(input.classRows)) : classTree(input.classRows));
  switch (input.view) {
    case 'vendorByClass':
      return vendorTree(input.vendorRows ?? []);
    case 'totalByVendor':
      return vendorLeaves(input);
    case 'classByVendor':
    case 'classByLocationVendor':
      return classByVendorTree(input.classRows, input.vendorRows ?? [], fold, input.ctx);
    case 'classByVendorProduct': {
      const roots = classByVendorTree(input.classRows, input.vendorRows ?? [], fold, input.ctx);
      attachProducts(roots, input, isVendorClassLeaf, vendorClassProductTarget);
      return roots;
    }
    case 'vendorByProduct': {
      const roots = vendorLeaves(input);
      attachProducts(roots, input, (n) => n.kind === 'leaf', vendorProductTarget(input));
      return roots;
    }
    case 'totalByProduct': {
      if (input.productParent === null) return [];
      const parent = input.productParent ?? 'all';
      const loaded = input.productRowsByParent?.[parent];
      // Not loaded yet: a root "Loading products…" placeholder (the page keeps its KPIs while it loads).
      return loaded ? productNodes(parent, loaded, input.productGrain ?? 'product') : productPlaceholder(parent, parent, input);
    }
    case 'classByProduct': {
      const roots = classes();
      attachProducts(roots, input, isLeafClass, classProductTarget);
      return roots;
    }
    case 'categoryByProduct':
      return withParentRemainders(classes());
    default:
      return classes();
  }
};

// ---- filters and search -------------------------------------------------------------------------

export const matchesSelection = (sel: FilterSelection, code: string | null | undefined): boolean => {
  if (sel.codes.length === 0) return true;
  const hit = code !== null && code !== undefined && sel.codes.includes(code);
  return sel.exclude ? !hit : hit;
};
const hasWeeklyPlan = (row: AnyRow): boolean => Object.values(row.periods).some((c) => c.salesPlan !== null);

/** The Status filter on one row: its status (re-derived under a WOS override) or the Deadstock flag is among the chips. */
const statusPasses = (r: AnyRow, input: RowTreeInput): boolean => {
  const st = rowStatus(r, input.ctx);
  const flagged = !!r.summary?.deadstock && !defersToParent(r.summary);
  return !!((st && input.filters.statuses.includes(st)) || (flagged && input.filters.statuses.includes(DEADSTOCK_FILTER)));
};

/** Round 2: before the roll-up, note on every store node whether it passes the Status filter on its own. */
const markMemberStatus = (nodes: Node[], input: RowTreeInput): Node[] =>
  nodes.map((n) => (n.kind === 'skuPlaceholder' ? n : { ...n, memberPass: statusPasses(n.row, input), children: markMemberStatus(n.children, input) }));

/** `parentPasses`: whether the parent passed the filters itself (an Unallocated row follows it for Weekly-planned only). */
const leafPasses = (n: Node, input: RowTreeInput, parentPasses = true): boolean => {
  const r = n.row;
  const f = input.filters;
  // A product payload row was fetched for its parent's scope (a class, category, vendor, `classes:<ids>`
  // or `all`); a product spanning classes carries one class's attributes, so re-testing it here would
  // drop it. Only the vendor and status filters (and Weekly-planned only, by its parent) apply to it.
  if (!n.scoped && (!matchesSelection(f.locations, r.locationCode) || !matchesSelection(f.categories, r.category) || !matchesSelection(f.classes, r.classCode))) return false;
  if (isVendorRow(r) && !matchesSelection(f.vendors, r.vendorKey)) return false;
  // review: the status the Health pill shows (rowStatus: re-derived under a WOS override), not the payload's.
  // Round 2: a chain row also passes when any member store does (`storeMatch`, set by the roll-up).
  if (f.statuses.length > 0 && !statusPasses(r, input) && !(n.storeMatch && n.storeMatch.matched > 0)) return false;
  // SKU, product and Unallocated rows carry no plan: they follow their parent. SKU and product rows sit under
  // leaves, which drop them when they fail; an Unallocated row under a failing parent fails with it (
  // review: otherwise the parent's own values leak into the totals beside its passing children).
  if (input.weeklyPlannedOnly && r.isUnallocated && !parentPasses) return false;
  if (input.weeklyPlannedOnly && n.kind !== 'sku' && !r.isUnallocated && !isProductRow(r) && !hasWeeklyPlan(r)) return false;
  return true;
};

/**
 * Every row is tested on its own attributes. A passing group keeps all its children; a failing
 * group survives with the children that pass; a passing leaf keeps the SKU rows that pass; a
 * placeholder follows its parent.
 */
const filterNodes = (nodes: Node[], input: RowTreeInput, parentPasses = true): Node[] =>
  nodes.flatMap((n) => {
    if (n.kind === 'skuPlaceholder') return [n];
    const passes = leafPasses(n, input, parentPasses);
    // Class by Vendor with a Vendor filter: a class row keeps only the vendor rows that pass under it
    // (at any depth) and is dropped when none is left.
    if (isClassByVendorView(input.view) && input.filters.vendors.codes.length > 0 && !isVendorRow(n.row)) {
      const kept = filterNodes(n.children, input, passes);
      if (kept.length === 0 || (!passes && n.kind !== 'group')) return [];
      return [{ ...n, children: kept, filtered: n.filtered || kept.length < n.children.length, ancestorOnly: n.ancestorOnly || !passes }];
    }
    if (n.kind === 'group' && passes) return [n];
    const children = filterNodes(n.children, input, passes);
    const pruned = { ...n, children, filtered: n.filtered || children.length < n.children.length, ancestorOnly: n.ancestorOnly || !passes };
    if (n.kind === 'group') return children.length > 0 ? [pruned] : [];
    return passes ? [pruned] : [];
  });

const labelMatches = (row: AnyRow, text: string): boolean => {
  const fields = [row.classCode, row.className, isVendorRow(row) ? row.vendor : null, isVendorRow(row) && row.level === 'product' ? row.productTitle : null];
  return fields.some((v) => typeof v === 'string' && v.toLowerCase().includes(text));
};
const skuMatches = (row: AnyRow, scope: SearchScope, text: string, productIds?: ReadonlySet<string>): boolean => {
  if (productIds) return isVendorRow(row) && (row.level === 'sku' || row.level === 'product') && typeof row.productId === 'string' && productIds.has(row.productId);
  if (!isVendorRow(row) || (row.level !== 'sku' && !(row.level === 'product' && scope === 'product'))) return false;
  const fields = scope === 'sku' ? [row.skuId, row.variantId] : [row.productTitle];
  return fields.some((v) => typeof v === 'string' && v.toLowerCase().includes(text));
};

const searchNodes = (nodes: Node[], search: RowTreeSearch, text: string): Node[] =>
  nodes.flatMap((n) => {
    if (search.scope === 'class' && !search.productIds) {
      if (n.kind === 'sku' || n.kind === 'skuPlaceholder') return [n];
      // An Unallocated row never matches on its own label ("adj", "unalloc"): it follows a parent that matches.
      if (n.kind === 'unallocated') return [];
      if (labelMatches(n.row, text)) return [n];
      const container = n.kind === 'group' || n.kind === 'subtotal' || n.children.some((c) => isProductRow(c.row));
      if (!container) return [];
      const children = searchNodes(n.children, search, text);
      return children.length > 0 ? [{ ...n, children, filtered: n.filtered || children.length < n.children.length, ancestorOnly: true, searchShown: !n.ancestorOnly }] : [];
    }
    if (n.kind === 'sku') return skuMatches(n.row, search.scope, text, search.productIds) ? [n] : [];
    if (isProductRow(n.row) && skuMatches(n.row, search.scope, text, search.productIds)) return [n];
    const children = searchNodes(n.children, search, text);
    return children.length > 0 ? [{ ...n, children, filtered: n.filtered || children.length < n.children.length, ancestorOnly: true, searchShown: !n.ancestorOnly }] : [];
  });

// ---- subtotals ----------------------------------------------------------------------------------

const sumNullable = (values: (number | null | undefined)[]): number | null => {
  const nums = values.filter(isNumRowTree);
  return nums.length === 0 ? null : nums.reduce((a, b) => a + b, 0);
};

/**
 * Σ a summary cost field, null as soon as one member has its retail twin but not the cost (no partial cost
 * totals); members without the retail figure add nothing, as sumNullable skips them at retail.
 */
const costSum = (summaries: Summary[], cost: keyof Summary, retail: keyof Summary): number | null =>
  summaries.every((m) => isNumRowTree(m[cost]) || !isNumRowTree(retail === 'otbOnOrder' && m.otbOnOrder === undefined ? m.onOrder : m[retail]))
    ? sumNullable(summaries.map((m) => m[cost] as number | null | undefined))
    : null;
const costSummaryFields = (summaries: Summary[]): Pick<Summary, 'otbCost' | 'inflowCostPlanSpan' | 'receivedCostToDate' | 'otbOnOrderCost'> =>
  Object.fromEntries(SUMMARY_COST_TWIN.map(([c, r]) => [c, costSum(summaries, c, r)]));

/** `trendCells`: the LEAF cells the trend aggregates over (TrendService aggregates parents over descendant leaves). */
const sumCellsRowTree = (cells: Cell[], trendCells: Cell[] = cells): Cell => {
  const out: Record<string, unknown> = {};
  SUM_FIELDS.forEach((f) => {
    if (COST_FIELDS.includes(f)) {
      // A cost total only when every summed cell whose retail twin is known has the cost figure: a partial sum would read
      // as the whole at cost. Otherwise null ("not available at cost"); absent when no cell carries the field at all.
      if (!cells.some((c) => c[f] !== undefined)) return;
      const twin = COST_TWIN[f] as keyof Cell;
      const all = cells.every((c) => isNumRowTree(c[f]) || !isNumRowTree(c[twin]));
      out[f] = all ? sumNullable(cells.map((c) => c[f] as number | null | undefined)) : null;
      return;
    }
    if (cells.some((c) => c[f] !== undefined)) out[f] = sumNullable(cells.map((c) => c[f] as number | null | undefined));
  });
  FLAG_FIELDS.forEach((f) => {
    if (cells.some((c) => c[f] !== undefined)) out[f] = cells.some((c) => c[f] === true);
  });
  // Sales Trend: Σ salesTrend, Trend % from the trending children's sales (TrendService::aggregateFields),
  // per method in trendAlt. Never re-derived from sales ÷ Trend % (an Unallocated row's is a remainder).
  const trend = aggregateTrend(out.sales as number | null, trendCells);
  if (trend.trendBasis !== null) Object.assign(out, trend);
  return out as unknown as Cell;
};

const sumPeriods = (rows: AnyRow[], trendRows: AnyRow[] = rows): Record<string, Cell> => {
  const keys = new Set<string>();
  rows.forEach((r) => Object.keys(r.periods).forEach((k) => keys.add(k)));
  const out: Record<string, Cell> = {};
  const cellsOf = (rs: AnyRow[], k: string) => rs.map((r) => r.periods[k]).filter((c): c is Cell => !!c);
  keys.forEach((k) => { out[k] = sumCellsRowTree(cellsOf(rows, k), cellsOf(trendRows, k)); });
  return out;
};

/**
 * The rows a subtotal's Sales Trend aggregates over: the class tree's bottom rows (leaf classes,
 * Unallocated, Unclassified) under each member, as PHP aggregates a parent over its descendant leaves,
 * so a leaf with sales but no Sales Trend stays out of the numerator. A group the filters pruned is
 * taken as its own row (the subtotal sums that row's actuals too); vendor / SKU / product rows are leaves.
 */
const trendLeafRows = (n: Node): AnyRow[] => {
  if (isVendorRow(n.row) || n.filtered) return [n.row];
  const kids = n.children.filter((c) => !isVendorRow(c.row) && c.kind !== 'skuPlaceholder' && c.kind !== 'sku');
  return kids.length === 0 ? [n.row] : kids.flatMap(trendLeafRows);
};

const otbBasisOf = (summaries: Summary[]): OtbBasis => {
  const bases = Array.from(new Set(summaries.map((s) => s.otbBasis).filter((b): b is Exclude<OtbBasis, null> => b !== null)));
  return bases.length === 0 ? null : bases.length === 1 ? bases[0] : 'mixed';
};

/** TargetsService::targetWos for a subtotal: weighted plan target, else the own trailing average. */
const subtotalTarget = (periods: Record<string, Cell>, avgSales: number, ctx: CellContext): Pick<Summary, 'targetWos' | 'targetBasis' | 'targetSource'> => {
  const t = periods[String(ctx.targetPeriod)];
  if (t && isNumRowTree(t.inventoryPlan) && isNumRowTree(t.salesPlan) && t.inventoryPlan > 0 && t.salesPlan > 0) {
    return { targetWos: t.inventoryPlan / t.salesPlan, targetBasis: 'plan', targetSource: 'weighted' };
  }
  const starts = ctx.lookback.map((p) => periods[String(p)]?.inventoryStart).filter(isNumRowTree);
  const avgInventory = starts.length ? starts.reduce((a, b) => a + b, 0) / starts.length : 0;
  const own = avgSales > 0 && avgInventory > 0 ? avgInventory / avgSales : null;
  return own !== null ? { targetWos: own, targetBasis: 'own_avg', targetSource: 'own_avg' } : { targetWos: null, targetBasis: 'none', targetSource: 'none' };
};

/** Sum-then-derive summary for a subtotal (never averaged ratios). */
/**
 * An Unallocated row carries no summary (the backend sends null): its inventory is its target-period
 * start inventory, its on order the row's, its received the current period's inflow; no plan, so no
 * span plan or OTB of its own.
 */
const unallocatedSummary = (r: AnyRow, ctx: CellContext): Summary => {
  const inventory = r.periods[String(ctx.targetPeriod)]?.inventoryStart;
  const current = ctx.currentPeriod === undefined || ctx.currentPeriod === null ? undefined : r.periods[String(ctx.currentPeriod)];
  const received = current?.inflow;
  return {
    inventory: isNumRowTree(inventory) ? inventory : null, inventoryKnown: isNumRowTree(inventory), avgPeriodSales: null, wos: null, targetWos: null, targetBasis: 'none', status: null, urgent: false,
    onOrder: r.onOrderRetail, onOrderCost: r.onOrderCost, receivedToDate: isNumRowTree(received) ? received : null, inflowPlanSpan: null, otb: null, otbBasis: null, otbCoveredThrough: null,
    deviation: null, otbDev: null,
    receivedCostToDate: isNumRowTree(current?.inflowCost) ? current?.inflowCost ?? null : null, otbOnOrderCost: r.onOrderCost, otbCost: null, inflowCostPlanSpan: null,
  };
};
const summaryForTotals = (r: AnyRow, ctx: CellContext): Summary | null => r.summary ?? (r.isUnallocated ? unallocatedSummary(r, ctx) : null);

/**
 * review: a member's plan BOM and the inventory compared with it — a nested aggregate's own pair, else its target
 * cell's plan inventory (> 0) with its inventory. Null for a member without a plan BOM (unplanned, PNOTB, Unallocated).
 */
const memberGate = (r: AnyRow, s: Summary | null, ctx: CellContext): { bom: number; inv: number } | null => {
  if (!s) return null;
  if (s.planBom !== undefined) return isNumRowTree(s.planBom) && s.planBom > 0 && isNumRowTree(s.planBomInventory) ? { bom: s.planBom, inv: s.planBomInventory } : null;
  const bom = r.periods[String(ctx.targetPeriod)]?.inventoryPlan;
  return isNumRowTree(bom) && bom > 0 && isNumRowTree(s.inventory) ? { bom, inv: s.inventory } : null;
};

/** Σ members' on order by due period (summary.onOrderDue); null when a member with on order has no due dates. */
const sumDue = (summaries: Summary[]): Record<string, number> | null => {
  const out: Record<string, number> = {};
  for (const m of summaries) {
    if (!m.onOrderDue) {
      if (isNumRowTree(m.onOrder) && m.onOrder > 0) return null;
      continue;
    }
    Object.entries(m.onOrderDue).forEach(([p, v]) => { out[p] = (out[p] ?? 0) + (isNumRowTree(v) ? v : 0); });
  }
  return out;
};

/** A positive WOS override, else the subtotal's own target: what its status and urgency are judged against. */
const judgedTarget = (targetWos: number | null, ctx: CellContext): number | null => (isNumRowTree(ctx.wosOverride) && ctx.wosOverride > 0 ? ctx.wosOverride : targetWos);

/** Σ of a cell field over the lookback periods; null when no period carries a figure (an unknown LY is not zero). */
const lookbackSum = (periods: Record<string, Cell>, field: 'sales' | 'salesLy', lookback: number[]): number | null => {
  let sum: number | null = null;
  lookback.forEach((p) => { const v = periods[String(p)]?.[field]; if (isNumRowTree(v)) sum = (sum ?? 0) + v; });
  return sum;
};

/** Σ sales over only the lookback periods that carry a last-year figure: the like-for-like side of the Deadstock comparison (a period with no LY is not "declining" against nothing). */
const lookbackSalesWithLy = (periods: Record<string, Cell>, lookback: number[]): number =>
  lookback.reduce((t, p) => { const c = periods[String(p)]; return isNumRowTree(c?.salesLy) && isNumRowTree(c?.sales) ? t + c.sales : t; }, 0);

/**
 * The OTB's own inputs over the members that HAVE an OTB (a member without one, e.g. the HQ location, holds inflows and on
 * order but no plan, and is not in the OTB identity), and the on order of those that do not. Cost twins are null as soon as a
 * counted member has the retail figure but not its cost.
 */
const otbInputFields = (summaries: Summary[]): Pick<Summary, 'otbReceived' | 'otbReceivedCost' | 'otbOnOrder' | 'otbOnOrderCost' | 'onOrderNoOtb' | 'onOrderNoOtbCost'> => {
  const withOtb = summaries.filter((s) => isNumRowTree(s.otb));
  const noOtb = summaries.filter((s) => !isNumRowTree(s.otb));
  type Pair = [number | null | undefined, number | null | undefined];
  const sumPairs = (pairs: Pair[]): [number | null, number | null] => [
    sumNullable(pairs.map((p) => p[0])),
    pairs.every((p) => isNumRowTree(p[1]) || !isNumRowTree(p[0])) ? sumNullable(pairs.map((p) => p[1])) : null,
  ];
  const [otbReceived, otbReceivedCost] = sumPairs(withOtb.map((s): Pair => [s.otbReceived !== undefined ? s.otbReceived : s.receivedToDate, s.otbReceivedCost !== undefined ? s.otbReceivedCost : s.receivedCostToDate]));
  const [otbOnOrder, otbOnOrderCost] = sumPairs(withOtb.map((s): Pair => (s.otbOnOrder !== undefined ? [s.otbOnOrder, s.otbOnOrderCost] : [s.onOrder, s.onOrderCost])));
  const excluded: Pair[] = [
    ...noOtb.map((s): Pair => [s.onOrder, s.onOrderCost]),
    ...withOtb.filter((s) => isNumRowTree(s.onOrderNoOtb)).map((s): Pair => [s.onOrderNoOtb, s.onOrderNoOtbCost]),
  ];
  const [onOrderNoOtb, onOrderNoOtbCost] = sumPairs(excluded);
  return { otbReceived, otbReceivedCost, otbOnOrder, otbOnOrderCost, onOrderNoOtb, onOrderNoOtbCost };
};

/**
 * Round-2 D7: a member with OTB-relevant on order but no plan of its own: a non-selling location (Warehouse, Receiving,
 * HQ, Storage, "To Be Split": summary.nonSelling) with no OTB. Its inflows and on order are the chain's, so when the chain HAS an
 * OTB (some other member does) it counts as an OTB member with no plan inflow: OTB = −received − on order, in the OTB inputs, the
 * deviation-free OTB + Dev and the by-month lines (the first month, like a store's own inflows and overdue on order). Without any
 * other OTB there is no chain OTB to net against, and the member stays in `onOrderNoOtb` (the old rule). Cost twins only when
 * both its inflows and on order are known at cost.
 */
const isOtbNetter = (s: Summary): boolean => s.nonSelling === true && !isNumRowTree(s.otb) && isNumRowTree(s.onOrder);
/** The netter's summary once a member with an OTB (`ref`) gives it something to net against: OTB = −received − on order, no plan inflow. */
const netAgainst = (s: Summary, ref: Summary): Summary => {
  const refLines = otbLines(ref);
  const received = isNumRowTree(s.receivedToDate) ? s.receivedToDate : 0;
  const onOrder = s.onOrder as number;
  const net = -(received + onOrder);
  const costKnown = isNumRowTree(s.onOrderCost) && (isNumRowTree(s.receivedCostToDate) || !isNumRowTree(s.receivedToDate));
  const netCost = costKnown ? -((isNumRowTree(s.receivedCostToDate) ? s.receivedCostToDate : 0) + (s.onOrderCost as number)) : null;
  return {
    ...s,
    inflowPlanSpan: 0, otb: net, otbDev: net, otbBasis: ref.otbBasis, otbReceived: received, otbOnOrder: onOrder,
    ...(costKnown ? { inflowCostPlanSpan: 0, otbCost: netCost, otbOnOrderCost: s.onOrderCost, otbReceivedCost: isNumRowTree(s.receivedCostToDate) ? s.receivedCostToDate : 0 } : { otbCost: null }),
    otbByPeriod: refLines ? refLines.map((l, i) => ({ period: l.period, inflowPlan: 0, received: i === 0 ? received : 0, onOrderDue: i === 0 ? onOrder : 0, otb: i === 0 ? net : 0, running: net })) : null,
  };
};
const withOtbNetters = (summaries: Summary[]): Summary[] => {
  const ref = summaries.find((s) => isNumRowTree(s.otb));
  if (!ref || !summaries.some(isOtbNetter)) return summaries;
  return summaries.map((s) => (isOtbNetter(s) ? netAgainst(s, ref) : s));
};

/**
 * (D7): the netting above, applied ONCE to the payload's class rows, per class across the stores (the chain, by class
 * code), before any view is built. So a non-selling location's class row carries its netted OTB (−received − on order) in every view:
 * the chain row sums it (its member is no longer a netter), the Location view's store rows and group rows show it as their own line,
 * and the OTB total is the same whatever the hierarchy (it used to be the chain's netted total in Total by Class and the un-netted
 * sum of the stores' own OTBs in Location). A class with no OTB in any store is left as it is (its on order stays "without a plan").
 */
export const netNonSellingAcrossStores = (classRows: ClassRow[]): ClassRow[] => {
  const groups = new Map<string, ClassRow[]>();
  classRows.forEach((r) => {
    if (r.isUnallocated || r.key === 'unclassified' || !r.summary) return;
    const code = chainCodeOf(r);
    if (code === null) return;
    const g = groups.get(code);
    if (g) g.push(r);
    else groups.set(code, [r]);
  });
  const netted = new Map<string, Summary>();
  groups.forEach((rows) => {
    const ref = rows.map((r) => r.summary as Summary).find((s) => isNumRowTree(s.otb));
    if (!ref) return;
    rows.forEach((r) => {
      if (isOtbNetter(r.summary as Summary)) netted.set(r.key, netAgainst(r.summary as Summary, ref));
    });
  });
  return netted.size === 0 ? classRows : classRows.map((r) => (netted.has(r.key) ? { ...r, summary: netted.get(r.key) as Summary } : r));
};

/** The union of the members' data flags in their fixed order, or null with none (summary.dataFlags). */
const dataFlagsOf = (summaries: Summary[]): DataFlag[] | null => {
  const order: DataFlag[] = ['sku_outlier', 'mirrored_inventory', 'tiny_plan', 'unallocated_share'];
  const have = new Set(summaries.flatMap((s) => s.dataFlags ?? []));
  const out = order.filter((f) => have.has(f));
  return out.length ? out : null;
};

const rebuildSummary = (rows: AnyRow[], periods: Record<string, Cell>, ctx: CellContext): Summary => {
  const memberSummaries = rows.map((r) => summaryForTotals(r, ctx));
  const summaries = withOtbNetters(memberSummaries.filter((s): s is Summary => !!s));
  const known = summaries.length === rows.length && summaries.every((s) => s.inventoryKnown && isNumRowTree(s.inventory));
  const inventory = known ? sumNullable(summaries.map((s) => s.inventory)) : null;
  const avg = avgOverPeriods(periods, 'sales', ctx.lookback) ?? 0;
  // Metrics::coverage: negative inventory has no cover (never a negative WOS).
  const wos = inventory !== null && inventory >= 0 && avg > 0 ? inventory / avg : null;
  const target = subtotalTarget(periods, avg, ctx);
  const judged = judgedTarget(target.targetWos, ctx); // review: a WOS override re-judges subtotals and chain rows too
  const onOrder = sumNullable(summaries.map((s) => s.onOrder));
  const onOrderDue = sumDue(summaries);
  const covered = summaries.map((s) => s.otbCoveredThrough).filter(isNumRowTree);
  // review: the M1 §6 gate compares the planned members' inventory with their plan BOM only; unplanned,
  // PNOTB and Unallocated stock never makes a subtotal "Overstocked". No member with a plan BOM: no plan BOM.
  const gates = rows.map((r, i) => memberGate(r, memberSummaries[i], ctx)).filter((g): g is { bom: number; inv: number } => g !== null);
  const planBom = gates.length ? gates.reduce((t, g) => t + g.bom, 0) : null;
  const planBomInventory = gates.length ? gates.reduce((t, g) => t + g.inv, 0) : null;
  const status = inventory !== null ? statusOf(wos, judged, ctx.multiplier, inventory, planBom, planBomInventory) : null;
  const out: Summary = {
    inventory, inventoryKnown: inventory !== null, avgPeriodSales: avg, wos, ...target,
    status, urgent: false,
    // M1 §6 (TargetsService::deadstock) on the summed cells: Σ sales over the lookback periods that have an LY figure against that LY.
    deadstock: inventory !== null && deadstockOf(wos, inventory, lookbackSalesWithLy(periods, ctx.lookback), lookbackSum(periods, 'salesLy', ctx.lookback), ctx.grain === 'weekly'),
    onOrder, onOrderCost: sumNullable(summaries.map((s) => s.onOrderCost)),
    receivedToDate: sumNullable(summaries.map((s) => s.receivedToDate)), inflowPlanSpan: sumNullable(summaries.map((s) => s.inflowPlanSpan)),
    otb: sumNullable(summaries.map((s) => s.otb)), otbBasis: otbBasisOf(summaries), otbCoveredThrough: covered.length ? Math.min(...covered) : null,
    // Σ members' deviation and OTB + Dev (a PNOTB / NOTB member's otbDev is null, so it drops out like its OTB).
    deviation: sumNullable(summaries.map((s) => s.deviation)), otbDev: sumNullable(summaries.map((s) => s.otbDev)),
    // pet-store column: Σ members' trended OTB (each class against its own plan EOM; a member without a plan drops out).
    trendedOtb: sumNullable(summaries.map((s) => s.trendedOtb)),
    // Σ members' OTB by month (null unless every member with an OTB has its lines), so Σ per month = this row's OTB.
    otbByPeriod: sumOtbLines(summaries),
    // the OTB at cost and its inputs, Σ members only when every member with the retail figure has its cost.
    ...costSummaryFields(summaries),
    ...otbInputFields(summaries),
    planBom, planBomInventory, ...(onOrderDue ? { onOrderDue } : {}),
    // Round-2 D9: a merged row is suspect when any member is (the flags are kept, never excluded).
    ...(dataFlagsOf(summaries) ? { dataFlags: dataFlagsOf(summaries) as DataFlag[] } : {}),
  };
  const cover = coverOnOrderOf(out, judged, ctx);
  out.urgent = inventory !== null && isReorderStatus(status) && isUrgent(wos, judged, ctx.multiplier, avg > 0 && isNumRowTree(cover) ? cover / avg : 0);
  // Round-2 D7 / an aggregate made only of non-selling locations' rows (a Location subtotal for "To Be Split" or "Headquarters",
  // a chain of such stores) is itself not judged: no status, target, urgency or Deadstock, the marker and its note; its totals stay.
  // Unallocated rows (a synthetic summary) are neither selling nor non-selling and do not decide it.
  const judgedMembers = rows.filter((r) => r.summary);
  if (judgedMembers.length > 0 && judgedMembers.every((r) => r.summary?.nonSelling)) {
    Object.assign(out, { status: null, urgent: false, deadstock: false, targetWos: null, targetBasis: 'none', nonSelling: true, nonSellingNote: judgedMembers[0].summary?.nonSellingNote ?? null });
  }
  return withAction(out, periods, ctx);
};

/**
 * ReportAssembler::applyActions on a summary built here (subtotals, chain rows, products): summary.action from
 * its status on the summed cells (season and plan-BOM step at the target period, Weekly smoothed; the planned members' gate
 * pair; Σ on order and the part due within the horizon; Σ OTB + Dev; its serviceTypeId), judged against a WOS override when
 * one is set, and urgency kept to a reorder. Called again once a chain row's status (Review), deferral or type is settled.
 */
const withAction = (out: Summary, periods: Record<string, Cell>, ctx: CellContext): Summary => {
  if (!out.status) return Object.assign(out, { action: null, urgent: false });
  const weekly = ctx.grain === 'weekly';
  const target = judgedTarget(out.targetWos, ctx);
  out.action = actionOf(out.status, {
    serviceTypeId: out.serviceTypeId ?? null, seasonExiting: seasonExiting(periods, ctx.targetPeriod, weekly), strongSales: salesBeatPlanOrLy(periods, ctx.targetPeriod, ctx.lookback), bomStepsDown: planBomStepsDown(periods, ctx.targetPeriod, weekly),
    wos: out.wos, target, avgPeriodSales: out.avgPeriodSales, onOrder: out.onOrder, coverOnOrder: coverOnOrderOf(out, target, ctx), otbDev: out.otbDev ?? null,
    planBom: out.planBom !== undefined ? out.planBom : planBomOf({ periods }, ctx.targetPeriod), inventory: out.planBom !== undefined ? out.planBomInventory ?? null : out.inventory,
  });
  out.urgent = out.urgent && actionAllowsUrgent(out.action);
  return out;
};

const allSame = <T,>(values: T[]): T | null => (values.length > 0 && values.every((v) => v === values[0]) ? values[0] : null);
const sumField = (summaries: Summary[], f: keyof Summary): number | null => sumNullable(summaries.map((m) => m[f] as number | null | undefined));
/** The out-of-stock status Review when every member reads Review: none of them sold in the last 52 weeks either. */
const keepReview = (out: Summary, members: (Summary | null)[]): void => {
  if (out.status === 'out_of_stock' && members.every((m) => m?.status === 'review')) out.status = 'review';
};

/**
 * The ONE subtotal rule (Category / Location subtotals, vendors re-summed under a filter, and 's chain rows) is
 * rebuildSummary: Σ inventory, WOS = Σ inventory ÷ Σ average sales (null for negative inventory, Metrics::coverage), target =
 * Σ plan inventory ÷ Σ plan sales at the target period else the own average (TargetsService::targetWos), status and urgency
 * from targets.ts, on order / received / span inflow / OTB / deviation / OTB + Dev Σ. chainSummary adds, for one class across
 * stores: OTB on order and plan / actual BOM Σ; each store's partial-coverage entry labelled with its store; what only every
 * store shares (planBasis, otbExcluded, Review, and the deferral, then judged at the chain parent `parentKey`). A
 * mixed deferral (some stores judged at their parent, some on their own plan) is judged on the chain's own figures.
 */
const chainSummary = (rows: AnyRow[], periods: Record<string, Cell>, ctx: CellContext, parentKey: string | null): Summary | null => {
  const members = rows.map((r) => r.summary);
  if (members.every((m) => m === null)) return null;
  const known = members.filter((m): m is Summary => !!m);
  const out: Summary = {
    ...rebuildSummary(rows, periods, ctx),
    spanBomPlan: sumField(known, 'spanBomPlan'), spanBomActual: sumField(known, 'spanBomActual'),
    otbCoverage: rows.flatMap((r) => (r.summary?.otbCoverage ?? []).map((c) => ({ ...c, label: `${c.label} · ${r.locationName ?? r.locationCode ?? ''}` }))),
    otbExcluded: allSame(members.map((m) => m?.otbExcluded ?? null)),
    planBasis: allSame(members.map((m) => m?.planBasis ?? null)),
    // the class type when every store shares it (a PNOTB class in every store gets no action).
    serviceTypeId: allSame(rows.map((r) => r.summary?.serviceTypeId ?? ('serviceTypeId' in r ? r.serviceTypeId ?? null : null))),
  };
  keepReview(out, members);
  if (known.length > 0 && known.every((m) => m.nonSelling)) {
    // Round-2 D7: a chain made only of non-selling locations is itself not judged (its totals stay).
    Object.assign(out, { status: null, urgent: false, deadstock: false, targetWos: null, targetBasis: 'none', nonSelling: true, nonSellingNote: known[0].nonSellingNote ?? null });
  }
  if (members.every((m) => defersToParent(m))) {
    // a child judged at its parent in every store is judged at the chain parent.
    Object.assign(out, { targetWos: null, targetBasis: 'parent', status: null, urgent: false, planParentKey: parentKey ?? known[0].planParentKey ?? null, planParentLabel: known[0].planParentLabel ?? null });
  }
  return withAction(out, periods, ctx);
};

/** Units fields of vendor / SKU / product rows: Σ average units and Σ suggested reorder (ProductRowsAssembler's sums), basis common or mixed. */
const unitsSummary = (known: Summary[]): Partial<Summary> => {
  if (!known.some((m) => m.avgPeriodUnits !== undefined || m.suggestedReorderUnits !== undefined)) return {};
  const bases = Array.from(new Set(known.map((m) => m.reorderBasis).filter((b): b is NonNullable<typeof b> => !!b)));
  return {
    avgPeriodUnits: sumField(known, 'avgPeriodUnits'), suggestedReorderUnits: sumField(known, 'suggestedReorderUnits'),
    reorderBasis: bases.length === 0 ? null : bases.length === 1 ? bases[0] : 'mixed',
  };
};

/** A vendor × class (or vendor / Unallocated) row across stores: the subtotal rule on its allocated plan cells. */
const vendorChainSummary = (rows: AnyRow[], periods: Record<string, Cell>, ctx: CellContext): Summary => {
  const known = rows.map((r) => r.summary).filter((m): m is Summary => !!m);
  const out: Summary = { ...chainSummary(rows, periods, ctx, null) as Summary, ...unitsSummary(known) };
  if (known.some((m) => m.otbExcluded)) out.otbExcluded = allSame(rows.map((r) => r.summary?.otbExcluded ?? null));
  // W2 item 12: Σ on order due this period (null when one store's read failed; absent when none carries it).
  if (known.some((m) => m.onOrderDueThisPeriod !== undefined)) {
    out.onOrderDueThisPeriod = known.some((m) => m.onOrderDueThisPeriod === null) ? null : sumField(known, 'onOrderDueThisPeriod') ?? 0;
  }
  return out;
};

/**
 * A product or variant across stores (no plan, no OTB): the target is ProductRowsAssembler::productTarget's weighted rule over
 * the members (Σ targetWos × average sales ÷ Σ average sales, a plain mean when none sold), basis their common one or
 * 'mixed', targetSource 'weighted'; status and urgency from it on the summed cover; units summed.
 */
const variantSummary = (rows: AnyRow[], periods: Record<string, Cell>, ctx: CellContext): Summary => {
  const members = rows.map((r) => r.summary);
  const known = members.filter((m): m is Summary => !!m);
  const base = rebuildSummary(rows, periods, ctx);
  const withTarget = known.filter((m) => isNumRowTree(m.targetWos));
  const weight = withTarget.reduce((t, m) => t + (isNumRowTree(m.avgPeriodSales) ? m.avgPeriodSales : 0), 0);
  const targetWos = withTarget.length === 0 ? null : weight > 0
    ? withTarget.reduce((t, m) => t + (m.targetWos as number) * (isNumRowTree(m.avgPeriodSales) ? m.avgPeriodSales : 0), 0) / weight
    : withTarget.reduce((t, m) => t + (m.targetWos as number), 0) / withTarget.length;
  const targetBasis = withTarget.length === 0 ? 'none' : allSame(withTarget.map((m) => m.targetBasis)) ?? 'mixed';
  const avg = base.avgPeriodSales ?? 0;
  const onOrderWeeks = avg > 0 && base.onOrder !== null ? base.onOrder / avg : 0;
  const judged = judgedTarget(targetWos, ctx);
  const status = base.inventory !== null ? statusOf(base.wos, judged, ctx.multiplier, base.inventory, base.planBom ?? null, base.planBomInventory ?? null) : null;
  const out: Summary = {
    ...base, targetWos, targetBasis, targetSource: withTarget.length === 0 ? 'none' : 'weighted',
    status,
    urgent: base.inventory !== null && isReorderStatus(status) && isUrgent(base.wos, judged, ctx.multiplier, onOrderWeeks),
    inflowPlanSpan: null, otb: null, otbCoveredThrough: null, otbBasis: allSame(known.map((m) => m.otbBasis)), deviation: null, otbDev: null,
    otbCost: null, inflowCostPlanSpan: null, otbByPeriod: null,
    ...unitsSummary(known),
    serviceTypeId: allSame(known.map((m) => m.serviceTypeId ?? null)),
  };
  keepReview(out, members);
  return withAction(out, periods, ctx);
};

export const makeSubtotal = (rows: AnyRow[], key: string, label: string, attrs: Partial<ClassRow>, ctx: CellContext, trendRows: AnyRow[] = rows): ClassRow => {
  const periods = sumPeriods(rows, trendRows);
  return emptyClassRow(key, {
    ...attrs, className: label, isParent: true,
    onOrderRetail: sumNullable(rows.map((r) => r.onOrderRetail)), onOrderCost: sumNullable(rows.map((r) => r.onOrderCost)), onOrderUnits: sumNullable(rows.map((r) => r.onOrderUnits)),
    periods, summary: rebuildSummary(rows, periods, ctx),
    ...classAging(rows),
  });
};

/** Group root nodes into subtotal nodes by `by`, keeping Unclassified at the root (after the groups). */
const groupRoots = (roots: Node[], by: (r: AnyRow) => { key: string; label: string; attrs: Partial<ClassRow> }, ctx: CellContext): Node[] => {
  const groups = new Map<string, { label: string; attrs: Partial<ClassRow>; members: Node[] }>();
  const passthrough: Node[] = [];
  roots.forEach((n) => {
    if (n.kind === 'unclassified') {
      passthrough.push(n);
      return;
    }
    const g = by(n.row);
    const entry = groups.get(g.key) ?? { label: g.label, attrs: g.attrs, members: [] };
    entry.members.push(n);
    groups.set(g.key, entry);
  });
  const out = Array.from(groups.entries()).map(([key, g]) => ({ key, kind: 'subtotal' as FlatRowKind, children: g.members, row: makeSubtotal(g.members.map((m) => m.row), key, g.label, g.attrs, ctx, g.members.flatMap(trendLeafRows)) }));
  return [...out, ...passthrough];
};

const byCategory = (prefix: string, extra: Partial<ClassRow>) => (r: AnyRow) => {
  const category = r.category ?? '';
  return { key: `${prefix}category:${category}`, label: category || '(No category)', attrs: { ...extra, category: r.category, categoryId: r.categoryId } };
};
/** Product parents are keyed by category id (`category:<id>`, `category:none`), as /product-rows takes them. */
const byCategoryId = (r: AnyRow) => ({
  key: `category:${isNumRowTree(r.categoryId) ? r.categoryId : 'none'}`, label: r.category || '(No category)', attrs: { category: r.category, categoryId: r.categoryId },
});
const byLocation = (r: AnyRow) => ({
  key: `location:${r.locationCode ?? ''}`, label: r.locationName ?? r.locationCode ?? '(No location)',
  attrs: { locationId: r.locationId, locationCode: r.locationCode, locationName: r.locationName },
});

// ---- chain roll-up ---------------------------------------------------------------

/** Views that keep one row per store: Location › Category › Class (the stores are its grouping). */
export const PER_STORE_VIEWS: readonly HierarchyView[] = ['locationCategoryClass'];
export const CHAIN_KEY_PREFIX = 'chain:';
/** A placeholder's load string for several stores' rows (`chain-load:class:1|class:2`); the hook splits it (splitLoadKey). */
export const CHAIN_LOAD_PREFIX = 'chain-load:';
/** A class's identity across stores: its class code (trimmed, case-insensitive), else its name; null when it has neither. */
export const chainCodeOf = (r: AnyRow): string | null => {
  const code = (r.classCode ?? '').trim().toUpperCase() || (r.className ?? '').trim().toUpperCase();
  return code === '' ? null : code;
};
/** The chain row key of a class (`chain:<code>`): the tree's merged row and its benchmark entry share it. */
export const chainKeyOf = (r: AnyRow): string | null => {
  const code = chainCodeOf(r);
  // Encoded: a code or name with "/" (or "|") must not break FlatRow id paths or chain load strings.
  return code === null ? null : `${CHAIN_KEY_PREFIX}${encodeURIComponent(code)}`;
};
/** One load string for several parents (a single parent stays itself). */
export const chainLoadKey = (keys: string[]): string => {
  const unique = Array.from(new Set(keys));
  return unique.length === 1 ? unique[0] : `${CHAIN_LOAD_PREFIX}${unique.join('|')}`;
};
/** The parents a load string names: the members of a chain load, else the key itself. */
export const splitLoadKey = (key: string): string[] => (key.startsWith(CHAIN_LOAD_PREFIX) ? key.slice(CHAIN_LOAD_PREFIX.length).split('|') : [key]);

const storesIn = (nodes: Node[], out: Set<string> = new Set()): Set<string> => {
  nodes.forEach((n) => {
    if (n.row.locationCode !== null && n.row.locationCode !== undefined && n.row.locationCode !== '') out.add(n.row.locationCode);
    storesIn(n.children, out);
  });
  return out;
};
/** Whether a view of these class rows rolls up across stores (more than one store, and not a per-store view). */
export const chainApplies = (view: HierarchyView, classRows: AnyRow[]): boolean =>
  !PER_STORE_VIEWS.includes(view) && new Set(classRows.filter((r) => !isVendorRow(r) && r.key !== 'unclassified' && r.locationCode).map((r) => r.locationCode)).size > 1;
/** Classes a chain view counts (the caption): one per chain code, plus each class row with no code. */
export const chainClassCount = (classRows: ClassRow[]): number => {
  const codes = new Set<string>();
  let uncoded = 0;
  classRows.forEach((r) => {
    if (r.isUnallocated || r.key === 'unclassified') return;
    const c = chainCodeOf(r);
    if (c === null) uncoded++;
    else codes.add(c);
  });
  return codes.size + uncoded;
};
/** Vendor × class rows a chain Vendor by Class counts: one per vendor and chain code. */
export const chainVendorClassCount = (vendorRows: VendorRow[]): number =>
  new Set(vendorRows.filter((r) => r.level === 'vendorClass').map((r) => `${r.vendorKey}|${chainCodeOf(r) ?? r.key}`)).size;

/** Step 1, group: a node's merge identity among its siblings; null = never merged (passes through, its children still merge). */
const chainGroupOf = (n: Node): string | null => {
  if (n.kind === 'skuPlaceholder') return n.row.key.endsWith(':note') ? null : 'PLACEHOLDER';
  if (n.kind === 'unclassified' || n.kind === 'subtotal') return null;
  if (n.kind === 'unallocated') return 'UNALLOCATED';
  const r = n.row;
  const byCode = (): string | null => { const c = chainCodeOf(r); return c === null ? null : `C:${c}`; };
  if (!isVendorRow(r)) return byCode();
  if (r.level === 'vendor') return `V:${r.vendorKey}`;
  if (r.level === 'vendorClass') return r.labelAs === 'vendor' ? `V:${r.vendorKey}` : byCode();
  if (r.level === 'sku') return r.variantId ? `S:${r.variantId}` : null;
  if (r.level === 'product') return `P:${r.key}`;
  return null;
};

/** A node with the key of the parent row it came from (a placeholder loads under it). */
interface ChainItem { n: Node; from: string | null }

/** Step 2, merge: one node from the same identity's nodes in several stores (see the header). */
const mergeChainGroup = (items: ChainItem[], ctx: CellContext, parent: AnyRow | null, parentNodeKey: string | null, byLocation = false): Node => {
  const members = items.map((i) => i.n);
  const first = members[0];
  if (byLocation && (isLocationLeafGroup(members) || isFamilyGroup(members))) return locationGroup(members, ctx, parent);
  if (members.length === 1) return { ...first, children: recurseChain(first.children.map((c) => ({ n: c, from: first.row.key })), ctx, first.row, first.key, byLocation) };
  const r = first.row;
  const unallocated = first.kind === 'unallocated';
  const parentKey = parent ? parent.key : null;
  let key: string;
  if (unallocated) key = `${parentKey ?? CHAIN_KEY_PREFIX}:unallocated`;
  else if (!isVendorRow(r)) key = chainKeyOf(r) as string;
  else if (r.level === 'vendorClass') key = `vendor:${r.vendorKey}:${chainKeyOf(r) ?? r.key}`;
  else key = r.key;
  const row: AnyRow = isVendorRow(r)
    ? mergeVendorRows(members.map((m) => m.row as VendorRow), key, ctx, r.level === 'sku' || r.level === 'product')
    : chainRow(members, key, ctx, parentKey, unallocated);
  const nodeKey = first.scoped ? `${parentNodeKey ?? ''}/${r.key}` : key;
  // Round 2: under a Status filter (members carry `memberPass`), a chain row that only some stores satisfy shows those stores' rows.
  const marked = members.some((m) => m.memberPass !== undefined);
  const matching = marked ? members.filter((m) => m.memberPass) : members;
  const partial = marked && matching.length > 0 && matching.length < members.length;
  const children = recurseChain((partial ? matching : members).flatMap((m) => m.children.map((c) => ({ n: c, from: m.row.key }))), ctx, row, nodeKey, byLocation);
  const ancestorOnly = members.every((m) => m.ancestorOnly);
  const searchShown = ancestorOnly && members.every((m) => m.searchShown);
  const filtered = (partial || members.some((m) => m.filtered || m.ancestorOnly)) && !ancestorOnly;
  const productParents = members.map((m) => m.productParent).filter((k): k is string => !!k);
  return {
    key: nodeKey, row, kind: members.some((m) => m.kind === 'group') ? 'group' : first.kind, children,
    ...(filtered ? { filtered: true } : {}), ...(ancestorOnly ? { ancestorOnly: true } : {}), ...(searchShown ? { searchShown: true } : {}), ...(first.scoped ? { scoped: true } : {}),
    ...(marked ? { storeMatch: { matched: matching.length, total: members.length } } : {}),
    ...(productParents.length > 0 ? { productParent: chainLoadKey(productParents) } : {}),
  };
};

/**
 * Step 3, recurse: merge one level (the children of one row, from every store). While any store's rows are still
 * loading, the level is one placeholder that loads every store's (`chainLoadKey`), so a partial sum never shows.
 */
const recurseChain = (items: ChainItem[], ctx: CellContext, parent: AnyRow | null, parentNodeKey: string | null, byLocation = false): Node[] => {
  const groups = new Map<string, ChainItem[]>();
  const order: (string | ChainItem)[] = [];
  // A placeholder always loads under the store row it came from (its own parent), never the chain row it now sits under:
  // with one store loaded (even empty) and one pending, the pending store's placeholder is alone at this level.
  items.map((i) => (i.n.kind === 'skuPlaceholder' && !i.n.loadParent && i.from && !i.n.row.key.endsWith(':note') ? { ...i, n: { ...i.n, loadParent: i.from } } : i)).forEach((item) => {
    const g = chainGroupOf(item.n);
    if (g === null) {
      order.push(item);
      return;
    }
    if (!groups.has(g)) {
      groups.set(g, []);
      order.push(g);
    }
    groups.get(g)!.push(item);
  });
  const placeholders = groups.get('PLACEHOLDER');
  if (placeholders && new Set(items.map((i) => i.from)).size > 1) {
    const ph = placeholders[0].n;
    const suffix = ph.row.key.slice(ph.row.key.lastIndexOf(':'));
    const failed = placeholders.find((p) => p.n.row.className)?.n.row.className ?? null;
    const loadParent = chainLoadKey(placeholders.map((p) => p.n.loadParent ?? p.from ?? ''));
    return [{ ...ph, key: `${parentNodeKey ?? ''}${suffix}`, row: { ...ph.row, key: `${parent?.key ?? ''}${suffix}`, className: failed }, loadParent }];
  }
  return order.map((entry) => (typeof entry === 'string' ? mergeChainGroup(groups.get(entry)!, ctx, parent, parentNodeKey, byLocation) : mergeChainGroup([entry], ctx, parent, parentNodeKey, byLocation)));
};

/**
 * A multi-store client: one row per class (and per vendor × class, product and variant under it) across the stores
 * the Location filter kept (no-op for one store and for the per-store views).
 */
const rollUpChain = (roots: Node[], view: HierarchyView, ctx: CellContext): Node[] => {
  // W2: the Class › Location views always build their class › location level, for one store too.
  if (isLocationAllocationView(view)) return withShipHints(recurseChain(roots.map((n) => ({ n, from: null })), ctx, null, null, true), ctx);
  const merged = !PER_STORE_VIEWS.includes(view) && storesIn(roots).size > 1 ? recurseChain(roots.map((n) => ({ n, from: null })), ctx, null, null) : roots;
  return view === 'classByVendor' || view === 'classByVendorProduct' ? withShipHints(merged, ctx) : merged;
};

// ---- Class › Location (W2, D12) ---------------------------------------------------------------

/**
 * A class level whose members are leaf classes (no class or Unallocated rows below them, only vendor rows, SKU rows or a placeholder):
 * in the Class › Location views it becomes the class row and one row per location under it.
 */
const isLocationLeafGroup = (members: Node[]): boolean => {
  const first = members[0];
  if (isVendorRow(first.row) || (first.kind !== 'leaf' && first.kind !== 'group')) return false;
  return members.every((m) => m.children.every((c) => isVendorRow(c.row) || c.kind === 'skuPlaceholder'));
};

/** A class child of a class row (not a vendor, SKU, placeholder, Unallocated or Unclassified row). */
const isClassChild = (c: Node): boolean => !isVendorRow(c.row) && (c.kind === 'leaf' || c.kind === 'group');
/**
 * (M1: in a class family the parent is the analysis level): a class level whose members are family parents, every
 * class below them judged at the parent (`planParentKey`). In the Class › Location views the location level goes here, under the
 * parent: each location row is that store's parent row (the family's sum there, judged at the parent's plan), with its sub-classes
 * below it (collapsed unless expanded), so the store rows carry status, need, Allocate and Ship / hold.
 */
const isFamilyGroup = (members: Node[]): boolean => {
  if (isVendorRow(members[0].row) || members[0].kind !== 'group') return false;
  // A sub-class judged on its own anywhere keeps the family nested; one with no target of its own (a non-selling, closed or NOTB
  // store's: 3027 Headquarters, Castle Rock, Union Station) does not count against it.
  const kids = members.flatMap((m) => m.children.filter(isClassChild));
  const unjudged = (c: Node): boolean => !c.row.summary || !!c.row.summary.nonSelling || c.row.summary.targetBasis === 'none';
  return kids.some((c) => defersToParent(c.row.summary)) && kids.every((c) => defersToParent(c.row.summary) || unjudged(c));
};

/** item 6: a location with no sales, stock, plan or on order in the window (a closed or NOTB store): left out of Class › Location. */
const isInactiveLocation = (row: AnyRow): boolean => {
  const s = row.summary;
  const zero = (v: unknown): boolean => !isNumRowTree(v) || v === 0;
  if (s && (!zero(s.inventory) || !zero(s.onOrder))) return false;
  return Object.values(row.periods).every((c) => !c || (zero(c.sales) && zero(c.salesPlan) && zero(c.inventoryStart) && zero(c.inventoryEnd) && zero(c.inflow)));
};

/**
 * Class › Location: the class row (the chain row of Total by Class, or the one store's own row) carrying the class's allocation, then
 * the location rows: each member's own row as it is (its figures, status, OTB — a non-selling location's is already netted once by
 * netNonSellingAcrossStores — and its own vendor / SKU children, not merged), labelled by its location, stores first then non-selling
 * locations, each by name. A store that is sent stock carries `allocate`. Under a Status filter only the matching stores are listed.
 */
const locationGroup = (members: Node[], ctx: CellContext, parent: AnyRow | null): Node => {
  const first = members[0];
  const single = members.length === 1;
  const key = single ? first.key : (chainKeyOf(first.row) as string);
  const allocation = allocationForMembers(members.map((m) => m.row), ctx);
  const row: AnyRow = { ...(single ? first.row : chainRow(members, key, ctx, parent ? parent.key : null, false)), allocation };
  const marked = members.some((m) => m.memberPass !== undefined);
  const matching = marked ? members.filter((m) => m.memberPass) : members;
  const partial = marked && matching.length > 0 && matching.length < members.length;
  const sent = new Map(allocation.stores.filter((s) => s.allocate > 0).map((s) => [s.key, s]));
  const rank = (m: Node): string => `${m.row.summary?.nonSelling ? 1 : 0}|${(m.row.locationName ?? m.row.locationCode ?? '').toLowerCase()}`;
  const listed = (partial ? matching : members).filter((m) => !isInactiveLocation(m.row));
  const children = listed.slice().sort((a, b) => (rank(a) < rank(b) ? -1 : rank(a) > rank(b) ? 1 : 0)).map((m): Node => {
    const own = sent.get(m.row.key);
    const locRow = { ...m.row, labelAs: 'location' as const, ...(own ? { allocate: own } : {}) } as AnyRow;
    return { ...m, key: `${key}/${m.row.key}`, row: locRow, children: m.children };
  });
  const ancestorOnly = members.every((m) => m.ancestorOnly);
  const searchShown = ancestorOnly && members.every((m) => m.searchShown);
  const filtered = (partial || members.some((m) => m.filtered || m.ancestorOnly)) && !ancestorOnly;
  return {
    key, row, kind: children.length > 0 ? 'group' : 'leaf', children,
    ...(filtered ? { filtered: true } : {}), ...(ancestorOnly ? { ancestorOnly: true } : {}), ...(searchShown ? { searchShown: true } : {}),
    ...(marked && !single ? { storeMatch: { matched: matching.length, total: members.length } } : {}),
  };
};

/**
 * Item 12, Ship / hold (a display hint, no status). Each vendor × class row with on order due in the period
 * (`summary.onOrderDueThisPeriod`) is judged by the nearest row above it with a known gross need (allocation.ts `grossNeedOf`: the
 * location row in Class › Location › Vendor; in a family the store's parent row, since a sub-class defers to it; the chain class row in
 * Class by Vendor): Hold when its due is above that gross need less what the other vendors judged by the same row have due, Take
 * within it (allocation.ts `shipHint`). the gross need (before on order), because the need already nets that due.
 */
const isVendorClassNode = (n: Node): boolean => isVendorRow(n.row) && n.row.level === 'vendorClass';
const dueOf = (n: Node): number => { const d = n.row.summary?.onOrderDueThisPeriod; return isNumRowTree(d) && d > 0 ? d : 0; };
/** Σ the due of the vendor rows a judge row judges: below it, not below a deeper row with its own gross need. */
const judgedDue = (n: Node, ctx: CellContext): number => n.children.reduce((t, c) => {
  if (isVendorClassNode(c)) return t + dueOf(c);
  if (isVendorRow(c.row) || c.row.summary?.nonSelling || grossNeedOf(c.row, ctx) !== null) return t;
  return t + judgedDue(c, ctx);
}, 0);
const withShipHints = (nodes: Node[], ctx: CellContext, judge: { gross: number; due: number } | null = null): Node[] => nodes.map((n) => {
  if (isVendorClassNode(n)) {
    if (!judge) return n;
    const due = n.row.summary?.onOrderDueThisPeriod;
    const other = Math.max(0, judge.due - dueOf(n));
    const hint = shipHint(due, judge.gross, other);
    return hint ? { ...n, row: { ...n.row, shipHint: { hint, due: due as number, need: judge.gross, otherDue: Math.round(other * 100) / 100 } } } : n;
  }
  if (isVendorRow(n.row) || n.children.length === 0) return n;
  const gross = grossNeedOf(n.row, ctx);
  // A non-selling location has no need of its own, and its vendors are not the stores' (no hint there).
  const next = n.row.summary?.nonSelling ? null : gross !== null ? { gross, due: judgedDue(n, ctx) } : judge;
  const kids = withShipHints(n.children, ctx, next);
  return kids.every((k, i) => k === n.children[i]) ? n : { ...n, children: kids };
});

/** One chain class row from the same class's rows in several stores (an Unallocated chain row carries no summary). */
const chainRow = (members: Node[], key: string, ctx: CellContext, parentKey: string | null, unallocated: boolean): ClassRow => {
  const rows = members.map((m) => m.row as ClassRow);
  const first = rows[0];
  const sub = makeSubtotal(rows, key, first.className ?? '', {}, ctx, members.flatMap(trendLeafRows));
  const serviceType = allSame(rows.map((r) => r.serviceTypeId ?? null));
  const summary = unallocated ? null : chainSummary(rows, sub.periods, ctx, parentKey);
  if (summary) delete summary.targetSource; // a class row (targetSource is a vendor / SKU row field)
  return {
    ...sub,
    classCode: first.classCode, className: first.className, isParent: rows.some((r) => r.isParent), isUnallocated: unallocated,
    category: allSame(rows.map((r) => r.category)) ?? first.category, categoryId: allSame(rows.map((r) => r.categoryId)) ?? first.categoryId,
    chainStores: new Set(rows.map((r) => r.locationCode)).size,
    ...(serviceType !== null ? { serviceTypeId: serviceType } : {}),
    summary,
    // W2: Σ its stores' need and its non-selling locations' stock (the allocation columns, in every chain view).
    ...(unallocated ? {} : { allocation: allocationForMembers(rows, ctx) }),
  };
};

/** a subtotal's / chain row's class-level aging: its members' blocks summed, then divided (none when no member has one). */
const classAging = (rows: AnyRow[]): { freshness?: Freshness } => {
  const f = rollUpMembersFreshness(rows);
  return f ? { freshness: f } : {};
};

/**
 * ONE aging rule for every browser roll-up (category / class subtotals, chain class rows, merged vendor / product rows): members
 * with no aging block are skipped and the shares are weighted over the members that have one; null when none has one. `partial`
 * is set when any member was skipped, so the cell's tooltip can say the share covers only part of the row's stock.
 */
const rollUpMembersFreshness = (rows: AnyRow[]): Freshness | null => {
  const blocks = rows.map((r) => r.freshness).filter((f): f is Freshness => !!f);
  if (blocks.length === 0) return null;
  const rolled = rollUpFreshness(blocks);
  return blocks.length < rows.length ? { ...rolled, partial: true } : rolled;
};

const FRESHNESS_MEASURES = ['onHandUnits', 'onHandRetail', 'freshUnits', 'freshRetail', 'aging4wUnits', 'aging4wRetail', 'aging8wUnits', 'aging8wRetail', 'aging12wUnits', 'aging12wRetail'] as const;
/** FreshnessService::rollUp: Σ the measures, the ratios re-derived on Σ retail on hand (never averaged). */
const rollUpFreshness = (items: Freshness[]): Freshness => {
  const sum = {} as Record<(typeof FRESHNESS_MEASURES)[number], number>;
  FRESHNESS_MEASURES.forEach((m) => { sum[m] = items.reduce((t, f) => t + (isNumRowTree(f[m]) ? f[m] : 0), 0); });
  const pct = (v: number): number | null => (sum.onHandRetail > 0 ? v / sum.onHandRetail : null);
  return { ...sum, freshPctSku: pct(sum.freshRetail), aging4wPct: pct(sum.aging4wRetail), aging8wPct: pct(sum.aging8wRetail), aging12wPct: pct(sum.aging12wRetail) };
};

/**
 * One vendor × class, vendor, Unallocated, product or variant row from the same row in several stores: cells, on order and
 * freshness summed and re-derived; the summary by chainSummary (vendor × class rows: plan-weighted target, as a vendor
 * re-summed under a filter) or variantSummary (products and variants: ProductRowsAssembler::productTarget's weighted rule).
 */
const mergeVendorRows = (rows: VendorRow[], key: string, ctx: CellContext, variant: boolean): VendorRow => {
  const first = rows[0];
  const sub = makeSubtotal(rows, key, '', {}, ctx);
  const summary = rows.every((r) => r.summary === null) ? null : variant ? variantSummary(rows, sub.periods, ctx) : vendorChainSummary(rows, sub.periods, ctx);
  return {
    ...first, key, periods: sub.periods, onOrderRetail: sub.onOrderRetail, onOrderCost: sub.onOrderCost, onOrderUnits: sub.onOrderUnits, summary,
    freshness: rollUpMembersFreshness(rows),
    locationClassId: null, locationId: null, locationCode: null, locationName: null,
    planClassId: allSame(rows.map((r) => r.planClassId)),
    category: allSame(rows.map((r) => r.category)) ?? first.category, categoryId: allSame(rows.map((r) => r.categoryId)) ?? first.categoryId,
  };
};

const applyView = (roots: Node[], view: HierarchyView, ctx: CellContext, filters: RowTreeFilters): Node[] => {
  if (view === 'categoryByClass') return groupRoots(roots, byCategory('', {}), ctx);
  // Each class by its OWN categoryId (the /product-rows category:<id> rule): group the bottom rows, so a
  // child class in another category than its parent is counted there and nothing is counted twice.
  if (view === 'categoryByProduct') return groupRoots(liftByOwnCategory(roots, filters), byCategoryId, ctx);
  if (view !== 'locationCategoryClass') return roots;
  return groupRoots(roots, byLocation, ctx).map((loc) =>
    loc.kind === 'subtotal' ? { ...loc, children: groupRoots(loc.children, byCategory(`${loc.key}:`, byLocation(loc.row).attrs), ctx) } : loc,
  );
};

// ---- sort and flatten ---------------------------------------------------------------------------

const compareValues = (a: unknown, b: unknown, direction: SortDirection): number => {
  const an = a === null || a === undefined;
  const bn = b === null || b === undefined;
  if (an || bn) return an && bn ? 0 : an ? 1 : -1;
  // A composite key (Action priority: [rank, ratio]) compares element by element; a null element sorts last in either direction.
  if (Array.isArray(a) && Array.isArray(b)) {
    for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
      const d = compareValues(a[i], b[i], direction);
      if (d !== 0) return d;
    }
    return 0;
  }
  const d = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  return direction === 'asc' ? d : -d;
};

/**
 *: the sort id of "Action priority", Weekly Action's default sort: not a table column, a sort option
 * (like OVER_TARGET_SORT). Rows rank by their action, most pressing first: an urgent reorder, then reorders, reorders with no OTB,
 * sell-downs, push orders, review, review / clear, sell through, on order covers it, out of season, hold, and last a no-action class
 * type (or no action). Ties break by WOS ÷ target, highest first, as "Most overstocked first" does. Descending is that order; ascending
 * reverses it. A row with no summary, or a child judged at its parent, has no rank and sorts last either way.
 */
export const ACTION_PRIORITY_SORT = 'actionPriority';
/** The action order of ACTION_PRIORITY_SORT, most pressing first (an urgent reorder outranks them all: `actionPriorityRank`). */
export const ACTION_PRIORITY_ORDER: readonly ActionCode[] = ['reorder', 'reorder_no_otb', 'sell_down', 'push_orders', 'review', 'review_clear', 'sell_through', 'on_order_covers', 'out_of_season', 'hold', 'class_type'];
/** The rank a row sorts by under ACTION_PRIORITY_SORT: higher is more pressing (urgent reorder highest, class_type / no action 0); null without a summary or when judged at the parent. */
export const actionPriorityRank = (row: AnyRow, ctx: CellContext): number | null => {
  const s = row.summary;
  if (!s || defersToParent(s)) return null;
  const a = rowAction(row, ctx);
  if (a === null || a === 'class_type') return 0;
  const i = ACTION_PRIORITY_ORDER.indexOf(a);
  const rank = i < 0 ? 0 : ACTION_PRIORITY_ORDER.length - i; // reorder = 11 … hold = 1
  return a === 'reorder' && isRowUrgent(row, ctx) ? rank + 1 : rank;
};

/** The value a row sorts by: % of Total on the measure itself, health by severity, else the cell value. */
const sortValue = (row: AnyRow, column: string, ctx: CellContext, inventoryBasis: PriceBasis): unknown => {
  if (column === OVER_TARGET_SORT) return overTargetRatio(row, ctx); // "Most over target": WOS ÷ target, none last
  if (column === ACTION_PRIORITY_SORT) { // "Action priority": the action's rank, then WOS ÷ target; no rank last
    const rank = actionPriorityRank(row, ctx);
    return rank === null ? null : [rank, overTargetRatio(row, ctx)];
  }
  const measure = PCT_COLUMN_MEASURE[column];
  if (measure) return pctMeasureOf(row, measure, ctx, { inventoryBasis });
  const v = metricById(column).cell(row, ctx).value;
  return column === 'health' && typeof v === 'string' ? STATUS_SEVERITY[v as Status] ?? null : v;
};

const KNOWN_SORT_COLUMNS = new Set<string>([...COLUMN_IDS, OVER_TARGET_SORT, ACTION_PRIORITY_SORT]);

const sortNodes = (nodes: Node[], sort: RowTreeSort, ctx: CellContext, parent: Node | null = null): Node[] => {
  // The same inventory basis the sibling set's % is computed on (`siblingInventoryBasis`).
  const inventoryBasis = siblingInventoryBasis(parent, nodes, ctx);
  const movable = nodes.filter((n) => !PINNED.includes(n.kind)).map((n) => ({ n, v: sortValue(n.row, sort.column, ctx, inventoryBasis) }));
  movable.sort((a, b) => compareValues(a.v, b.v, sort.direction));
  const pinned = nodes.filter((n) => PINNED.includes(n.kind));
  return [...movable.map((m) => m.n), ...pinned].map((n) => ({ ...n, children: sortNodes(n.children, sort, ctx, n) }));
};

/** The row a sibling set's % divides by: none for roots and under a filtered row (Σ of the visible siblings). */
const denominatorRow = (parent: Node | null): AnyRow | null => (parent && !parent.filtered ? parent.row : null);

/**
 * The ONE inventory basis of a sibling set, for its % of inventory and its sort alike: cost only when
 * the denominator row (if any) and every sibling carry cost cells, else retail, so the siblings'
 * shares sum to 100% and never divide cost by retail.
 */
const siblingInventoryBasis = (parent: Node | null, nodes: Node[], ctx: CellContext): PriceBasis => {
  if (ctx.basis !== 'cost') return 'retail';
  const den = denominatorRow(parent);
  const rows = nodes.filter((n) => n.kind !== 'skuPlaceholder').map((n) => n.row);
  return (den ? [den, ...rows] : rows).every((r) => inventoryAtCost(r, ctx)) ? 'cost' : 'retail';
};

/** A sibling set's denominators (each measure; inventory on the set's basis) and that basis. */
interface PctBase { totals: PctOfParent; inventoryBasis: PriceBasis }

const pctBaseFor = (parent: Node | null, nodes: Node[], ctx: CellContext): PctBase => {
  const inventoryBasis = siblingInventoryBasis(parent, nodes, ctx);
  const den = denominatorRow(parent);
  const totals = {} as PctOfParent;
  PCT_MEASURES.forEach((m) => {
    totals[m] = den ? pctMeasureOf(den, m, ctx, { inventoryBasis }) : sumNullable(nodes.map((n) => pctMeasureOf(n.row, m, ctx, { inventoryBasis })));
  });
  return { totals, inventoryBasis };
};

const pctOfParentFor = (row: AnyRow, kind: FlatRowKind, base: PctBase, ctx: CellContext): PctOfParent | null => {
  if (kind === 'skuPlaceholder') return null;
  const out = {} as PctOfParent;
  PCT_MEASURES.forEach((m) => {
    const own = pctMeasureOf(row, m, ctx, { inventoryBasis: base.inventoryBasis });
    const den = base.totals[m];
    out[m] = isNumRowTree(own) && isNumRowTree(den) ? pctOf(own, den) : null;
  });
  return out;
};

const distributionLabel = (r: AnyRow): string => {
  const x = r as { title?: string | null; vendor?: string | null; classCode?: string | null; className?: string | null };
  return x.title ?? (x.classCode ? `${x.classCode} · ${x.className ?? ''}`.trim() : x.className ?? x.vendor ?? 'its parent');
};

const flatten = (nodes: Node[], depth: number, parent: Node | null, parentId: string | null, groupKey: string | null, input: RowTreeInput, out: FlatRow[]): void => {
  const base = pctBaseFor(parent, nodes, input.ctx);
  const distributed = input.distributeOtb && parent
    ? distributeOtb(parent.row, nodes.filter((n) => n.kind !== 'skuPlaceholder').map((n) => n.row), input.ctx, distributionLabel(parent.row), !!parent.filtered)
    : null;
  nodes.forEach((n) => {
    const expanded = n.children.length > 0 && (!!input.expandAll || !!input.expanded[n.key]);
    const gk = depth === 0 ? n.row.key : groupKey;
    const id = parentId === null ? n.row.key : `${parentId}/${n.row.key}`;
    out.push({
      id, row: n.row, depth, kind: n.kind, parentKey: n.loadParent ?? (parent ? parent.row.key : null), groupKey: gk, expandKey: n.key, expanded, childCount: n.children.length,
      ...(n.productParent ? { productParent: n.productParent } : {}),
      pctOfParent: pctOfParentFor(n.row, n.kind, base, input.ctx), filteredChildren: !!n.filtered,
      ...(distributed?.has(n.row.key) ? { distributedOtb: distributed.get(n.row.key) } : {}),
      ...(n.ancestorOnly ? { ancestorOnly: true } : {}),
      ...(n.searchShown ? { searchShown: true } : {}),
      ...(n.storeMatch && n.storeMatch.matched > 0 && n.storeMatch.matched < n.storeMatch.total ? { storeMatch: n.storeMatch } : {}),
    });
    if (expanded) flatten(n.children, depth + 1, n, id, gk, input, out);
  });
};

const NO_SELECTION: FilterSelection = { codes: [], exclude: false };
/** phase 1: the Location filter alone (it decides which stores roll up). */
const locationOnly = (input: RowTreeInput): RowTreeInput => ({
  ...input, weeklyPlannedOnly: false, filters: { locations: input.filters.locations, categories: NO_SELECTION, classes: NO_SELECTION, vendors: NO_SELECTION, statuses: [] },
});
/** Phase 2: every other filter (the Location filter already ran; chain rows carry no store). */
const withoutLocation = (input: RowTreeInput): RowTreeInput => ({ ...input, filters: { ...input.filters, locations: NO_SELECTION } });

/** The direct child rows of the flat row `id` in a depth-first tree (an expanded tree lists every child). */
export const childRowsOf = (flatRows: readonly FlatRow[], id: string): AnyRow[] => {
  const prefix = `${id}/`;
  return flatRows.filter((f) => f.id.startsWith(prefix) && !f.id.slice(prefix.length).includes('/')).map((f) => f.row);
};

/**
 * A chain row's child classes for its row detail (Child drivers), unfiltered like a store row's (which reads the payload):
 * the Total by Class tree with the Location filter only (it decides which stores the chain spans), expanded.
 */
export const chainChildRows = (classRows: ClassRow[], ctx: CellContext, locations: FilterSelection, parentKey: string): AnyRow[] => {
  const flat = buildRowTree({
    classRows, view: 'totalByClass', filters: { locations, categories: NO_SELECTION, classes: NO_SELECTION, vendors: NO_SELECTION, statuses: [] },
    search: { text: '', scope: 'class' }, sort: null, showSkus: false, weeklyPlannedOnly: false, skuRowsByParent: {}, expanded: {}, expandAll: true, ctx,
  });
  const parent = flat.find((f) => f.row.key === parentKey);
  return parent ? childRowsOf(flat, parent.id) : [];
};

/** The display tree as a flat list (see the header for the rules). */
export const buildRowTree = (rawInput: RowTreeInput): FlatRow[] => {
  // D7: the non-selling locations' OTB netting is a property of the payload rows, the same in every view.
  const input: RowTreeInput = { ...rawInput, classRows: netNonSellingAcrossStores(rawInput.classRows) };
  const productView = isProductView(input.view);
  const text = input.search.text.trim().toLowerCase();
  let roots = baseTree(input);
  // Show SKUs never applies to a product view (the Product / SKU grain does).
  if (input.showSkus && !productView) attachSkus(roots, input.skuRowsByParent, !input.expandAll);
  // the Location filter first, then the roll-up across the stores it kept, then every other filter and the
  // search on the chain rows (a chain row's status is the chain's, not one store's). Category by Product rolls up its
  // products' variants after they attach (its class rows are not shown).
  roots = filterNodes(roots, locationOnly(input));
  if (input.view !== 'categoryByProduct') roots = rollUpChain(input.filters.statuses.length > 0 ? markMemberStatus(roots, input) : roots, input.view, input.ctx);
  roots = filterNodes(roots, withoutLocation(input));
  // Category by Product: the search applies to the products, after they replace the classes below.
  if (text && input.view !== 'categoryByProduct') roots = searchNodes(roots, input.search, text);
  roots = applyView(roots, input.view, input.ctx, input.filters);
  if (input.view === 'categoryByProduct') {
    attachProducts(roots, input, (n) => n.kind === 'subtotal', categoryProductTarget(input));
    roots = rollUpChain(roots, input.view, input.ctx);
    roots = roots.map((r) => (r.kind === 'subtotal' ? { ...r, children: filterNodes(r.children, input) } : r));
    if (text) roots = searchNodes(roots, input.search, text);
  }
  if (input.sort && KNOWN_SORT_COLUMNS.has(input.sort.column)) roots = sortNodes(roots, input.sort, input.ctx);
  const out: FlatRow[] = [];
  flatten(roots, 0, null, null, null, input, out);
  return out;
};

// ===================================================================================================================
// ==== planEdits.ts ==================================================================================================
// ===================================================================================================================
//: Plan & OTB what-if edits of PLAN SALES. A buyer types a new plan sales figure into a Plan Sales
// cell; the edit is a scenario kept in the view (saved views and ?v= links carry it), never written back to the plan. The other plan
// values follow by holding the plan's ratios (Adam's choice):
//
// - Markdowns plan keeps its % of sales: markdownsPlan × k, k = new sales ÷ plan sales.
// - BOM plan keeps its stock-to-sales: inventoryPlan × k, EXCEPT the target period's BOM (the period you are in has started; its BOM is
// fixed, so the deviation and OTB + Dev's own term do not move).
// - Inflows plan is the plan's own inflow corrected by the changes, so a plan whose inflows do not tie out exactly keeps its shape:
// inflow'(p) = inflow(p) + Δsales(p) + Δmarkdowns(p) + ΔBOM(p + 1) − ΔBOM(p) (EOM(p) is BOM(p + 1)).
// - No negative inflows, as Plan Builder's adjust_inflow Rule A: from the target period on, a negative plan inflow is
// carried BACK into the period before (that period buys less), back to the target period; what it cannot absorb carries forward. The
// BOMs are then rebuilt forward, so a BOM below the stock-to-sales value (lowered) or above it (raised) is reported (`raised`, signed)
// and the matrix marks it with an explanation.
// - OTB, OTB + Dev and the OTB by period / running lines move by the inflow change inside the span (received and on order are actuals).
//
// The edits apply to the CLASS ROWS of the payload before the row tree is built, so every subtotal, chain row, total, KPI and the CSV
// follow without any other code knowing. An edit is keyed by the row it was typed on: `class:<id>` (a class at one location) or
// `chain:<code>` (the chain row of a multi-store class), whose new figure is spread over the stores by their plan sales (each store
// takes the same factor k). A row whose plan sales is zero or missing takes the new figure as is, with no ratio to hold (markdowns and
// BOM unchanged). Retail only: the cost figures (inflowCostPlan, otbCost) are not recalculated.

/** Row key → period (as a string, the payload's cell key) → the new plan sales. */
export type PlanEdits = Record<string, Record<string, number>>;
/** Each grain keeps its own edits (a week and a month are different periods). */
export interface PlanEditsByGrain { weekly: PlanEdits; monthly: PlanEdits }

export const EMPTY_PLAN_EDITS: PlanEditsByGrain = { weekly: {}, monthly: {} };
/** At most this many edited cells per grain are kept (a saved view or link stays small). */
export const MAX_PLAN_EDITS = 400;
const EDIT_KEY_RE = /^(class|chain):/;

const isNumPlanEdits = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Sanitises stored edits (saved views, links): known key shapes, finite non-negative figures, capped. */
export const normalizePlanEdits = (v: unknown): PlanEdits => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: PlanEdits = {};
  let n = 0;
  Object.entries(v as Record<string, unknown>).forEach(([key, periods]) => {
    if (!EDIT_KEY_RE.test(key) || !periods || typeof periods !== 'object' || Array.isArray(periods)) return;
    Object.entries(periods as Record<string, unknown>).forEach(([p, value]) => {
      if (n >= MAX_PLAN_EDITS || !/^\d+$/.test(p) || !isNumPlanEdits(value) || value < 0) return;
      (out[key] ??= {})[p] = Math.round(value * 100) / 100;
      n += 1;
    });
  });
  return out;
};
export const normalizePlanEditsByGrain = (v: unknown): PlanEditsByGrain => {
  const r = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  return { weekly: normalizePlanEdits(r.weekly), monthly: normalizePlanEdits(r.monthly) };
};

/** Sets (or, with null, clears) one edit; returns new edits. */
export const withPlanEdit = (edits: PlanEdits, rowKey: string, period: number, value: number | null): PlanEdits => {
  const { [rowKey]: row = {}, ...rest } = edits;
  const { [String(period)]: _old, ...others } = row; // eslint-disable-line @typescript-eslint/no-unused-vars
  const next = value === null ? others : { ...others, [String(period)]: Math.max(0, Math.round(value * 100) / 100) };
  return Object.keys(next).length > 0 ? { ...rest, [rowKey]: next } : rest;
};
export const planEditCount = (edits: PlanEdits): number => Object.values(edits).reduce((n, p) => n + Object.keys(p).length, 0);

/**
 * What applyPlanEdits returns: the edited payload; each edited cell's original plan sales (row key → period → figure); and, per row key
 * (the class row's and its chain row's) and period, how far the plan BOM moved (signed: − lowered, + raised) because an inflow would have gone negative.
 */
export interface AppliedPlanEdits { payload: ClassReportPayload; originals: PlanEdits; raised: PlanEdits; changed: PlanEditChanges }
/**
 * Wendi (2026-10-22), 8a: every cell an edit moved, by row key (the class row's and its chain row's) → period (or 'span' for the
 * span figures) → field → the figure before the edit: salesPlan (periods other than the edited cell, e.g. the stores of a chain edit),
 * markdownsPlan, inventoryPlan, inflowPlan; 'span' → otb, otbDev; a span period → otbPeriod, otbRunning. The matrix marks those cells.
 */
export type PlanEditChanges = Record<string, Record<string, Record<string, number>>>;
export const PLAN_EDIT_SPAN_KEY = 'span';

/** Per class row and period: the factor k (new ÷ plan) or, where plan sales is 0 / missing, the absolute new figure. */
type Change = { kind: 'factor'; k: number } | { kind: 'absolute'; value: number };

const changesFor = (rows: ClassRow[], edits: PlanEdits): { changes: Map<string, Map<number, Change>>; originals: PlanEdits } => {
  const changes = new Map<string, Map<number, Change>>();
  const originals: PlanEdits = {};
  const set = (row: ClassRow, period: number, c: Change) => {
    if (!changes.has(row.key)) changes.set(row.key, new Map());
    changes.get(row.key)!.set(period, c);
  };
  const planOf = (r: ClassRow, p: string): number | null => (isNumPlanEdits(r.periods[p]?.salesPlan) ? (r.periods[p]!.salesPlan as number) : null);
  // Chain edits first, so a store's own edit (a location row in Class › Location) wins over its chain's.
  Object.entries(edits).filter(([key]) => key.startsWith('chain:')).forEach(([key, periods]) => {
    const members = rows.filter((r) => chainKeyOf(r) === key);
    Object.entries(periods).forEach(([p, value]) => {
      const planned = members.filter((r) => planOf(r, p) !== null);
      if (planned.length === 0) return;
      const sum = planned.reduce((t, r) => t + (planOf(r, p) as number), 0);
      (originals[key] ??= {})[p] = sum;
      planned.forEach((r) => set(r, Number(p), sum > 0 ? { kind: 'factor', k: value / sum } : { kind: 'absolute', value: value / planned.length }));
    });
  });
  Object.entries(edits).filter(([key]) => key.startsWith('class:')).forEach(([key, periods]) => {
    const row = rows.find((r) => r.key === key);
    if (!row) return;
    Object.entries(periods).forEach(([p, value]) => {
      const plan = planOf(row, p);
      if (plan === null) return;
      (originals[key] ??= {})[p] = plan;
      set(row, Number(p), plan > 0 ? { kind: 'factor', k: value / plan } : { kind: 'absolute', value });
    });
  });
  return { changes, originals };
};

const PLAN_EDIT_FIELDS: readonly ('salesPlan' | 'markdownsPlan' | 'inventoryPlan' | 'inflowPlan')[] = ['salesPlan', 'markdownsPlan', 'inventoryPlan', 'inflowPlan'];
/** Every cell of `after` whose plan figure differs from `before` (the edited sales cells themselves left out), with the figure before. */
const changedCells = (before: ClassRow, after: ClassRow, edited: Set<number>): Record<string, Record<string, number>> => {
  const out: Record<string, Record<string, number>> = {};
  const note = (key: string, field: string, was: number | null | undefined, now: number | null | undefined): void => {
    if (!isNumPlanEdits(was) || !isNumPlanEdits(now) || Math.abs(was - now) < 0.005) return;
    (out[key] ??= {})[field] = was;
  };
  Object.entries(after.periods).forEach(([key, cell]) => {
    const was = before.periods[key];
    PLAN_EDIT_FIELDS.forEach((f) => { if (!(f === 'salesPlan' && edited.has(Number(key)))) note(key, f, was?.[f], cell[f]); });
  });
  const bs = before.summary;
  const as = after.summary;
  if (bs && as) {
    note(PLAN_EDIT_SPAN_KEY, 'otb', bs.otb, as.otb);
    note(PLAN_EDIT_SPAN_KEY, 'otbDev', bs.otbDev, as.otbDev);
    (as.otbByPeriod ?? []).forEach((l, i) => {
      const w = bs.otbByPeriod?.[i];
      if (!w || w.period !== l.period) return;
      note(String(l.period), 'otbPeriod', w.otb, l.otb);
      note(String(l.period), 'otbRunning', w.running, l.running);
    });
  }
  return out;
};

const applyToRow = (row: ClassRow, rowChanges: Map<number, Change>, targetPeriod: number, spanPeriods: readonly number[], raisedByRow: Map<string, Map<number, number>>): ClassRow => {
  const dSales = new Map<number, number>();
  const dMarkdowns = new Map<number, number>();
  const dBom = new Map<number, number>();
  rowChanges.forEach((c, p) => {
    const cell = row.periods[String(p)];
    const sales = cell?.salesPlan as number;
    if (c.kind === 'absolute') {
      dSales.set(p, c.value - sales);
      return;
    }
    dSales.set(p, sales * c.k - sales);
    if (isNumPlanEdits(cell?.markdownsPlan)) dMarkdowns.set(p, (cell!.markdownsPlan as number) * (c.k - 1));
    if (p > targetPeriod && isNumPlanEdits(cell?.inventoryPlan)) dBom.set(p, (cell!.inventoryPlan as number) * (c.k - 1));
  });
  // 1. The ratio changes, cell by cell.
  const periods: Record<string, Cell> = {};
  Object.entries(row.periods).forEach(([key, cell]) => {
    const p = Number(key);
    const ds = dSales.get(p) ?? 0;
    const dm = dMarkdowns.get(p) ?? 0;
    const db = dBom.get(p) ?? 0;
    const di = isNumPlanEdits(cell.inflowPlan) ? ds + dm + (dBom.get(p + 1) ?? 0) - db : 0;
    periods[key] = ds === 0 && dm === 0 && db === 0 && di === 0 ? cell : {
      ...cell,
      salesPlan: isNumPlanEdits(cell.salesPlan) ? cell.salesPlan + ds : cell.salesPlan,
      markdownsPlan: isNumPlanEdits(cell.markdownsPlan) ? cell.markdownsPlan + dm : cell.markdownsPlan,
      inventoryPlan: isNumPlanEdits(cell.inventoryPlan) ? cell.inventoryPlan + db : cell.inventoryPlan,
      inflowPlan: isNumPlanEdits(cell.inflowPlan) ? cell.inflowPlan + di : cell.inflowPlan,
    };
  });
  // 2. No negative inflows, as Plan Builder's utils.adjust_inflow Rule A: a negative inflow is taken off the period before's purchase,
  // repeatedly, back to the target period (which has started, so nothing goes before it); what the target period cannot absorb is
  // carried forward. Then the BOMs are rebuilt forward (BOM(p + 1) = BOM(p) + inflow(p) − sales(p) − markdowns(p)) as an exact change on
  // top of the stock-to-sales values: each BOM moves by the sum of the inflow changes before it, so the chain rejoins the plan.
  const forward = Object.keys(periods).map(Number).filter((p) => p >= targetPeriod).sort((x, y) => x - y);
  const run: number[] = [];
  for (const p of forward) { // the consecutive periods with a plan inflow, from the target on
    if (!isNumPlanEdits(periods[String(p)].inflowPlan) || (run.length > 0 && p !== run[run.length - 1] + 1)) break;
    run.push(p);
  }
  const before = run.map((p) => periods[String(p)].inflowPlan as number);
  const vals = before.slice();
  for (let i = vals.length - 1; i > 0; i -= 1) {
    if (vals[i] < 0) { vals[i - 1] += vals[i]; vals[i] = 0; }
  }
  for (let i = 0; i < vals.length - 1; i += 1) {
    if (vals[i] < 0) { vals[i + 1] += vals[i]; vals[i] = 0; }
  }
  if (vals.length > 0 && vals[vals.length - 1] < 0) vals[vals.length - 1] = 0; // nothing left to absorb it (past the last period loaded)
  const raised = new Map<number, number>();
  let shift = 0;
  run.forEach((p, i) => {
    const cell = periods[String(p)];
    const bom = isNumPlanEdits(cell.inventoryPlan) && Math.abs(shift) >= 0.5 ? cell.inventoryPlan + shift : cell.inventoryPlan;
    if (bom !== cell.inventoryPlan) raised.set(p, shift);
    if (bom !== cell.inventoryPlan || vals[i] !== before[i]) periods[String(p)] = { ...cell, inflowPlan: vals[i], inventoryPlan: bom };
    shift += vals[i] - before[i];
  });
  const dInflow = new Map<number, number>();
  Object.entries(periods).forEach(([key, cell]) => {
    const before = row.periods[key]?.inflowPlan;
    if (isNumPlanEdits(cell.inflowPlan) && isNumPlanEdits(before) && cell.inflowPlan !== before) dInflow.set(Number(key), cell.inflowPlan - before);
  });
  raisedByRow.set(row.key, raised);
  const summary = row.summary;
  if (!summary || !isNumPlanEdits(summary.otb)) return { ...row, periods };
  const dSpan = spanPeriods.reduce((t, p) => t + (dInflow.get(p) ?? 0), 0);
  let cumulative = 0;
  const otbByPeriod: OtbPeriodLine[] | null | undefined = Array.isArray(summary.otbByPeriod)
    ? summary.otbByPeriod.map((l) => {
        const d = dInflow.get(l.period) ?? 0;
        cumulative += d;
        return d === 0 && cumulative === 0 ? l : { ...l, inflowPlan: l.inflowPlan + d, otb: l.otb + d, running: l.running + cumulative };
      })
    : summary.otbByPeriod;
  return {
    ...row,
    periods,
    summary: {
      ...summary,
      inflowPlanSpan: isNumPlanEdits(summary.inflowPlanSpan) ? summary.inflowPlanSpan + dSpan : summary.inflowPlanSpan,
      otb: summary.otb + dSpan,
      otbDev: isNumPlanEdits(summary.otbDev) ? summary.otbDev + dSpan : summary.otbDev,
      otbByPeriod,
    },
  };
};

/** The payload with the edits applied (see the header); the same object when there are none. */
export const applyPlanEdits = (payload: ClassReportPayload, edits: PlanEdits): AppliedPlanEdits => {
  if (planEditCount(edits) === 0) return { payload, originals: {}, raised: {}, changed: {} };
  const { changes, originals } = changesFor(payload.rows, edits);
  if (changes.size === 0) return { payload, originals, raised: {}, changed: {} };
  const raisedByRow = new Map<string, Map<number, number>>();
  const rows = payload.rows.map((r) => (changes.has(r.key) ? applyToRow(r, changes.get(r.key)!, payload.targetPeriod, payload.spanPeriods ?? [], raisedByRow) : r));
  // The raised BOMs by the class row's key and summed by its chain row's key (the row a multi-store table shows).
  const raised: PlanEdits = {};
  raisedByRow.forEach((byPeriod, key) => {
    const r = rows.find((x) => x.key === key);
    const chain = r ? chainKeyOf(r) : null;
    byPeriod.forEach((amount, p) => {
      [key, chain].forEach((k) => {
        if (!k) return;
        const at = (raised[k] ??= {});
        at[String(p)] = (at[String(p)] ?? 0) + amount;
      });
    });
  });
  // 8a: every other cell the edit moved, by the class row's key and summed (the figures before) by its chain row's key. A store's own
  // edited sales cell is left out; the chain row's sales cell is marked when a store (not the chain) was edited.
  const changed: PlanEditChanges = {};
  const chainCells = new Map<string, Set<string>>(); // chain key → "period|field" moved in some member store
  payload.rows.forEach((before, i) => {
    const after = rows[i];
    if (before === after) return;
    const chain = chainKeyOf(before);
    const chainEdited = new Set(chain ? Object.keys(edits[chain] ?? {}).map(Number) : []);
    const own = new Set(Object.keys(edits[before.key] ?? {}).map(Number));
    const cells = changedCells(before, after, new Set([...Array.from(own), ...Array.from(chainEdited)]));
    if (Object.keys(cells).length > 0) changed[before.key] = cells;
    if (!chain) return;
    // The chain row's cell moved when a member's did; a store's own edited sales cell moves the chain's sales cell too (the chain was not edited there).
    const forChain = changedCells(before, after, chainEdited);
    Object.entries(forChain).forEach(([period, fields]) => Object.keys(fields).forEach((field) => {
      if (!chainCells.has(chain)) chainCells.set(chain, new Set());
      chainCells.get(chain)!.add(`${period}|${field}`);
    }));
  });
  // The chain's figure before an edit is the sum over ALL its member stores (changed or not), never only the moved ones.
  chainCells.forEach((keys, chain) => {
    const members = payload.rows.filter((r) => chainKeyOf(r) === chain);
    keys.forEach((pf) => {
      const [period, field] = pf.split('|');
      let sum = 0;
      let any = false;
      members.forEach((r) => {
        const s = r.summary;
        const v = period === PLAN_EDIT_SPAN_KEY ? (field === 'otb' ? s?.otb : s?.otbDev)
          : field === 'otbPeriod' || field === 'otbRunning' ? (() => { const l = (s?.otbByPeriod ?? []).find((x) => x.period === Number(period)); return l ? (field === 'otbPeriod' ? l.otb : l.running) : null; })()
            : r.periods[period]?.[field as 'salesPlan' | 'markdownsPlan' | 'inventoryPlan' | 'inflowPlan'];
        if (isNumPlanEdits(v)) { sum += v; any = true; }
      });
      if (any) ((changed[chain] ??= {})[period] ??= {})[field] = sum;
    });
  });
  return { payload: { ...payload, rows }, originals, raised, changed };
};

/** The row key a Plan Sales edit is stored under for a table row: its own key for a class or chain row; null for anything else. */
export const planEditKeyOf = (rowKey: string): string | null => (EDIT_KEY_RE.test(rowKey) ? rowKey : null);

/** The grain's edits. */
export const editsForGrain = (all: PlanEditsByGrain | undefined, grain: Grain): PlanEdits => (all ? all[grain] : {});

// ===================================================================================================================
// ==== viewState.ts ==================================================================================================
// ===================================================================================================================
// The one serialisable view object (Plan 3 spec 6 "Views"): what a report page holds,
// what a saved view stores and what a share link encodes. Pure functions only; the hook in
// kit/hooks/useReportViewState.ts owns the React side.
//
// Decisions recorded here:
// - A saved view never fixes the period (`toSavedViewPayload` clears it, as Vendor Performance
// drops custom dates): a view saved in October opens on the current week in December. Share
// links keep the period, so a link shows the same numbers to everyone.
// - Share envelopes carry the report and the client (`encodeShareState(state, {report, clientId})`)
// and decode to null for another report or client: the written `?v=` survives remounts (a
// dashboard keyed by client, tabs sharing the `v` param) and must not leak into them. Older
// unscoped envelopes still decode.
// - Share links are `v1.` + URL-safe base64 (RFC 4648 §5, no padding) of the UTF-8 JSON, so the
// token survives copy-paste without percent-encoding. The decoder also accepts standard base64.
// - `decodeShareState` / `fromSavedViewPayload` go through one sanitiser: unknown keys dropped,
// wrong types replaced by the defaults for the report and the payload's grain, `schema` must be 1 or 2
// for a share link (a saved view is trusted to be ours and only needs to be an object).
// - `columns.visible` is the only source of column visibility, the `% of Total` group included.
// `showPctOfTotal` is a convenience flag: `setShowPctOfTotal` writes the four `pctOf*` entries,
// and the sanitiser keeps the flag in step ("any % of Total column visible").
// - Share links carry the period (the hook pins it to what is shown, so a recipient sees the same
// week); saved views do not (`toSavedViewPayload` clears it).
// - `?v=` is written with history.replaceState, so router hooks (`useSearchParams`) never see it:
// Plan 4 must read `v` through this module / the hook, not the router.
// - `applyTierFallback` repairs what the controls only coerce for display (an unavailable grain,
// a lookback outside the grain's typed range, an allocation window missing from the grain's list, a multiplier that is not an
// option) and the Tier C / capability rules of spec 3, one notice per change. A grain change
// clears `period` (a week number is not a month number). `compare: 'ly'` is kept: LY
// availability is a payload fact — `payload.lastYear != null` (Tier C carries the RM last year
// with `historyStart: null`, which still counts as available) — not a capability; partial
// history is flagged per cell by `lyPartial`, not by hiding the option.
// - Hierarchy keys are 's ten views; a stored pre-4b key ('class', 'category', 'vendor',
// 'location') is migrated by `migrateHierarchyView`; an unknown one falls back to Total by Class.
// `productGrain` ('product' | 'sku') is the Product / SKU toggle of the product views.
// - The Sales Trend columns (`TREND_COLUMN_IDS`) have no default entry in `columns.visible`: absent
// means "dynamic" (shown on a trend period, `columnStateFor`'s `trendPeriod`); an explicit
// true / false is the user's choice and wins. Reset drops them again.
// - `trendMethod` (Part B) is the Sales Trend method every trend consumer reads through
// `CellContext.trendMethod`: 'auto' (default) | 'plan' | 'lastYear' | 'linear'. It is view state only
// (saved views, share links, the saved-view equality), NOT a server parameter: every method is in the
// payload, so switching never refetches. Missing or invalid decodes to 'auto'; 'plan' without V3 plans
// (Tier B) falls back to 'auto' in `applyTierFallback`.
// - `sort.column` must be a catalogue column id (`metricById` throws on anything else); an unknown
// one drops the sort. Two sort ids are not columns: ACTION_PRIORITY_SORT ("Action priority", Weekly Action's default) and
// OVER_TARGET_SORT ("Most overstocked first") — the report-wide default sorts, treated alike everywhere here.
// -: the Suggested reorder column (`PRODUCT_COLUMN_IDS`) is dynamic too: without an explicit
// choice it shows in the product views or with Show SKUs on (`columnStateFor`), where the rows it is sized for are.

export type PlanOtbMode = 'detailed' | 'basic' | 'otb';
export type PlanOtbRow = 'inflow' | 'inventory' | 'markdowns' | 'onOrder' | 'otb';
export interface PlanOtbOptions { mode: PlanOtbMode; rows: PlanOtbRow[]; showPrevious: boolean; hideFutureActuals: boolean; hidePeriodDates: boolean; hideRowColours: boolean; showBenchmarks: boolean; /** Plan 4e W4: the LY rows (Sales, Inflows, Markdowns, and BOM inventory in Monthly) and the variance against LY on the Act rows. */ showLy: boolean }
export interface ViewFilters { locations: FilterSelection; categories: FilterSelection; classes: FilterSelection; vendors: FilterSelection; statuses: string[] }
export interface ViewColumns { visible: Record<string, boolean>; order: string[]; widths: Record<string, number>; pinned: string[] }

export interface ReportViewState {
  schema: 2;
  view: HierarchyView;
  productGrain: ProductGrain;
  grain: Grain; period: number | null; lookback: number; compare: Compare;
  /** OTB span follows the grain: Monthly's span in months (0 = this month), Weekly's in weeks (OTB_SPAN_WEEK_OPTIONS). Each grain keeps its own. */
  otbSpanMonths: number; otbSpanWeeks: number; basis: PriceBasis; multiplier: Multiplier;
  /**
   *: the period (in the grain) the OTB span starts at, null = the target period (the rule).
   * At most OTB_START_MAX_AHEAD periods after the target; `withGrain` clears it (a week is not a month). A fetch key (`queryParamsOf`).
   */
  otbStart: number | null;
  /** Which open orders the OTB nets: 'window' (due inside the window, the rule) or 'all' (every open PO whenever due, overdue included). A fetch key. */
  otbOnOrder: OtbOnOrder;
  allocWindow: number; allocBasis: AllocBasis;
  filters: ViewFilters;
  search: { text: string; scope: SearchScope };
  showSkus: boolean; weeklyPlannedOnly: boolean; showPctOfTotal: boolean; freshnessShown: boolean; inventoryDetail: 'detail' | 'compact';
  /** Weekly Action's OTB + Dev KPI: include the deviation (plan BOM − actual BOM, the default) or show the OTB without it. View-only (no refetch). */
  kpiOtbIncludeDev: boolean;
  /** Distribute OTB (Inventory Action, Inventory Health): split a parent's OTB to child classes, products and SKUs (otbDistribution.ts). */
  distributeOtb: boolean;
  /** Wendi (2026-10-22): "Show child classes" (Options), default on. Off folds every class family to its parent row in the class-based hierarchies of all three reports. */
  showChildClasses: boolean;
  /** Plan & OTB what-if edits of plan sales, per grain (planEdits.ts). Kept in saved views and links; never written to the plan. */
  planEdits: PlanEditsByGrain;
  trendMethod: TrendMethod;
  planOtb?: PlanOtbOptions;
  wosOverride: number | null;
  /** `column` is a catalogue column id, or a report-wide sort id (ACTION_PRIORITY_SORT "Action priority", OVER_TARGET_SORT "Most overstocked first": sort options rather than columns). */
  sort: { column: string; direction: 'asc' | 'desc' } | null;
  /** Total by Product: rank (and cap) the products by sales (default) or by inventory ("Top by inventory"). */
  productSort: ProductSort;
  columns: ViewColumns;
}
export type ProductSort = 'sales' | 'inventory';
/**: the report-wide default sorts, sort options rather than columns; `sanitise` and the product-rank rule treat them alike. */
export const REPORT_SORT_IDS: readonly string[] = [ACTION_PRIORITY_SORT, OVER_TARGET_SORT];
export const isReportSortId = (column: string): boolean => REPORT_SORT_IDS.includes(column);
/**
 * Round 2: Total by Product's server ranking (Top by sales / inventory, capped at 500) is the table order unless the buyer sorted a column.
 * A report-wide default sort ("Action priority", "Most overstocked first") would reorder the capped list, so entering the view or picking a
 * rank drops it (a column sort the user clicked is kept; picking either sort afterwards in the Sort select still works).
 */
export const sortForProductRank = (sort: ReportViewState['sort']): ReportViewState['sort'] => (sort && isReportSortId(sort.column) ? null : sort);
/** What a saved view stores: the state with the period cleared. */
export type SavedViewPayload = ReportViewState;

export const SHARE_STATE_PREFIX = 'v1.';
/**
 * Version 2: Weekly Action's default sort became "Most over target" (now "Action priority", ACTION_PRIORITY_SORT, since the
 * of 2026-10-22; a saved "Most overstocked first" (OVER_TARGET_SORT) is kept as saved). Views and links saved at version 1 stored
 * `sort: null` explicitly for "never chose a sort", so they could never pick the new default up: `normalizeState` reads a
 * version-1 (or unversioned) null as "no sort stored". A null in a version-2 payload is the user's own "no sort" and is kept.
 * Version 1 payloads still decode; anything newer than this build does not.
 */
export const VIEW_STATE_SCHEMA = 2;
const LEGACY_VIEW_STATE_SCHEMA = 1;

const GRAINS: Grain[] = ['weekly', 'monthly'];
const VENDOR_VIEW_LABELS = HIERARCHY_OPTIONS.filter((o) => needsVendorRows(o.key)).map((o) => o.label).join(', ');
const ALLOCATION_NOTICE = `Allocation settings apply only to the vendor views (${VENDOR_VIEW_LABELS}), which need SKU-level data; using the defaults.`;
const COMPARES: Compare[] = ['plan', 'ly', 'avg'];
const BASES: PriceBasis[] = ['retail', 'cost'];
const ALLOC_BASES: AllocBasis[] = ['net_sales', 'gross_margin', 'cash_margin', 'inventory'];
const PLAN_OTB_MODES: PlanOtbMode[] = ['detailed', 'basic', 'otb'];
const PLAN_OTB_ROWS: PlanOtbRow[] = ['inflow', 'inventory', 'markdowns', 'onOrder', 'otb'];
/**
 * the OTB span is a typed number. Weekly 1–26 weeks, Monthly 1–6 months (a saved 0 = "this month" still reads; the control
 * clamps a typed value to 1). The options below are only the quick presets. A saved view or link outside the range is clamped to it.
 */
export const OTB_SPAN_RANGE: Record<Grain, { min: number; max: number }> = { weekly: { min: 1, max: 26 }, monthly: { min: 1, max: 6 } };
const OTB_SPAN_MONTHS_MIN = 0;
export const OTB_SPAN_WEEK_OPTIONS: readonly number[] = [4, 8, 13, 26];
export const OTB_SPAN_MONTH_OPTIONS: readonly number[] = [1, 2, 3, 6];
export const DEFAULT_OTB_SPAN_WEEKS = 13;
const clampInt = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, Math.round(n)));
/**
 * The week option nearest an old weekly `otbSpanMonths` (months × 4.33): a saved view or share link from before the weekly span
 * existed. 3 months → 13 weeks, 6 → 26, 0 / 1 → 4.
 */
export const weeksFromMonths = (months: number): number => nearestOtbSpanWeeks(months * 4.33);
/** The OTB_SPAN_WEEK_OPTIONS value nearest a number of weeks (only for migrating a pre-weekly-span `otbSpanMonths`; a typed span is clamped, not snapped). */
export const nearestOtbSpanWeeks = (weeks: number): number =>
  OTB_SPAN_WEEK_OPTIONS.reduce((best, o) => (Math.abs(o - weeks) < Math.abs(best - weeks) ? o : best), OTB_SPAN_WEEK_OPTIONS[0]);
export const OTB_SPAN_UNIT: Record<Grain, string> = { weekly: 'wk', monthly: 'mo' };
/** how far after the target the window may start (the backend's limit): 26 weeks / 12 months. */
export const OTB_START_MAX_AHEAD: Record<Grain, number> = { weekly: 26, monthly: 12 };
const OTB_ON_ORDER_CHOICES: OtbOnOrder[] = ['window', 'all'];
/** The buy-window starts the control offers: the `target` + 1 … `target` + OTB_START_MAX_AHEAD periods (the target itself is "null"). */
export const otbStartOptions = (grain: Grain, target: number): number[] => Array.from({ length: OTB_START_MAX_AHEAD[grain] }, (_, i) => target + i + 1);
/** A stored buy-window start that applies to `target`: a whole period after it, within the grain's limit; anything else is the target (null). */
export const otbStartFor = (otbStart: number | null | undefined, grain: Grain, target: number): number | null =>
  isNumViewState(otbStart) && Number.isInteger(otbStart) && otbStart > target && otbStart <= target + OTB_START_MAX_AHEAD[grain] ? otbStart : null;
/** The span in the grain's own unit, as the OTB control and KPI label it: "13 wk", "3 mo", "this month". */
export const otbSpanText = (s: Pick<ReportViewState, 'grain' | 'otbSpanMonths' | 'otbSpanWeeks'>): string =>
  // a Monthly span of N covers this month AND the N after it (the payload's spanPeriods), so it says so.
  s.grain === 'weekly' ? `${s.otbSpanWeeks} ${OTB_SPAN_UNIT.weekly}` : s.otbSpanMonths === 0 ? 'this month' : `this mo + ${s.otbSpanMonths}`;
const PRODUCT_SORTS: ProductSort[] = ['sales', 'inventory'];
// (P1-9): Inventory Health gains 13 weeks, the shared default.
const INVENTORY_HEALTH_LOOKBACK: Record<Grain, readonly number[]> = { weekly: [1, 2, 4, 13], monthly: [1, 2, 3] };
const GRAIN_LABEL: Record<Grain, string> = { weekly: 'Weekly', monthly: 'Monthly' };
const GRAIN_UNIT: Record<Grain, string> = { weekly: 'week', monthly: 'month' };
/** The buyer-facing line for a window that was reset on a grain switch: "Switched to a 3-month lookback for Monthly". */
const windowNotice = (grain: Grain, n: number, what: string): string => `Switched to a ${n}-${GRAIN_UNIT[grain]} ${what} for ${GRAIN_LABEL[grain]}`;
const SKU_COLUMN_IDS = METRIC_DEFINITIONS.filter((d) => !d.tiers.includes('C')).map((d) => d.id);
export const PCT_COLUMN_IDS: readonly string[] = METRIC_DEFINITIONS.filter((d) => d.group === 'pctOfTotal').map((d) => d.id);
const KNOWN_COLUMN_IDS = new Set<string>(COLUMN_IDS);
const SEARCH_TEXT_MAX = 200;

const emptySelection = (): FilterSelection => ({ codes: [], exclude: false });
const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNumViewState = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const oneOf = <T extends string | number>(v: unknown, list: readonly T[], fallback: T): T => (list.includes(v as T) ? (v as T) : fallback);
const uniqueStrings = (v: unknown): string[] | null => (Array.isArray(v) ? Array.from(new Set(v.filter((x): x is string => typeof x === 'string'))) : null);

/** the typed lookback range per grain (re-exported from the control). */
/** The allocation windows VendorAllocationService accepts and the reports' quick values; the typed lookback's range per grain. */
export const LOOKBACK_OPTIONS: Record<Grain, readonly number[]> = { weekly: [4, 8, 13, 26, 52], monthly: [1, 3, 6, 12] };
export const LOOKBACK_RANGE: Record<Grain, { min: number; max: number }> = { weekly: { min: 1, max: 52 }, monthly: { min: 1, max: 12 } };

export const lookbackInRange =(grain: Grain, n: number): boolean => Number.isInteger(n) && n >= LOOKBACK_RANGE[grain].min && n <= LOOKBACK_RANGE[grain].max;
/** The lookback quick presets a report offers (spec 7.1 gives Inventory Health its short list); any whole number in range is valid. */
export const lookbackOptionsFor = (report: ReportKey, grain: Grain): readonly number[] =>
  report === 'inventory_health' ? INVENTORY_HEALTH_LOOKBACK[grain] : LOOKBACK_OPTIONS[grain];
/**
 * (P1-9, D2): one default lookback for all three reports — 13 weeks / 3 months, the backend's default — so the
 * same class gets the same target, status and action in each. A saved view keeps the lookback it stored (an explicit choice).
 */
export const SHARED_DEFAULT_LOOKBACK: Record<Grain, number> = { weekly: 13, monthly: 3 };
/** The state with another grain: the period cleared (a week number is not a month number) and the lookback back at that grain's default (13 weeks is not 13 months). Run `applyTierFallback` on the result. */
export const withGrain = (s: ReportViewState, grain: Grain): ReportViewState => ({ ...s, grain, period: null, otbStart: null, lookback: SHARED_DEFAULT_LOOKBACK[grain] ?? s.lookback });
const defaultLookback = (_report: ReportKey, grain: Grain): number => SHARED_DEFAULT_LOOKBACK[grain];
const defaultAllocWindow = (grain: Grain): number => (grain === 'weekly' ? 13 : 3);

/** The past periods Plan & OTB's "Show previous 4 weeks / months" shows, asked of the server as `history`. */
export const PLAN_OTB_HISTORY = 4;
/** W4: the LY rows default on in Weekly (spikes by week are the use case), off in Monthly. */
export const defaultPlanOtbOptions = (grain: Grain = 'weekly'): PlanOtbOptions => ({ mode: 'detailed', rows: [...PLAN_OTB_ROWS], showPrevious: false, hideFutureActuals: false, hidePeriodDates: false, hideRowColours: false, showBenchmarks: false, showLy: grain === 'weekly' });

/**: the columns sized for product / SKU rows: shown by default in the product views or with Show SKUs on. */
export const PRODUCT_COLUMN_IDS: readonly string[] = ['suggestedReorder'];
/**
 * Columns with no default entry in `columns.visible` (dynamic until the user chooses): the trend columns (shown on a trend period),
 * W2, the allocation columns (shown in the Class › Location views) and the product columns (product views or Show SKUs).
 */
export const DYNAMIC_COLUMN_IDS: readonly string[] = [...TREND_COLUMN_IDS, ...ALLOCATION_COLUMN_IDS, ...PRODUCT_COLUMN_IDS];

/** Every catalogue column (Tier A) in order with its default visibility (% of Total hidden; the dynamic columns absent). */
const defaultColumns = (report: ReportKey): ViewColumns => {
  const cols = columnsFor(report, 'A');
  return { visible: Object.fromEntries(cols.filter((c) => !DYNAMIC_COLUMN_IDS.includes(c.id)).map((c) => [c.id, c.visible])), order: cols.map((c) => c.id), widths: {}, pinned: [] };
};

/** the base column each % of Total column sits next to. */
const PCT_BASE_COLUMN: Record<string, string> = { pctOfSales: 'thisPeriodSales', pctOfInventory: 'inventory', pctOfInflow: 'inflow', pctOfOnOrder: 'onOrder' };

/**
 * The "% of Total" button: shows or hides the % of Total columns (`ids`: the ones the report's catalogue offers, default all four)
 * and records it on the flag. Showing also moves each one right after its base column (Sales, BOM inventory, Inflows, On order) in
 * the order, unless that base is pinned (pinned columns stay contiguous); hiding keeps the order.
 */
export const setShowPctOfTotal = (s: ReportViewState, on: boolean, ids: readonly string[] = PCT_COLUMN_IDS): ReportViewState => {
  let order = s.columns.order;
  if (on) {
    order = [...order];
    ids.forEach((id) => {
      const base = PCT_BASE_COLUMN[id];
      if (!base || !order.includes(base) || s.columns.pinned.includes(base) || s.columns.pinned.includes(id)) return;
      order = order.filter((x) => x !== id);
      order.splice(order.indexOf(base) + 1, 0, id);
    });
  }
  const visible = { ...s.columns.visible, ...Object.fromEntries(ids.map((id) => [id, on])) };
  return { ...s, showPctOfTotal: anyPctVisible(visible), columns: { ...s.columns, order, visible } };
};
const anyPctVisible = (visible: Record<string, boolean>): boolean => PCT_COLUMN_IDS.some((id) => visible[id] === true);

/** the freshness columns (Fresh %, Fresh % (SKU), the aging buckets). Ordinary columns in the Columns menu; the Freshness switch shows or hides them together. */
export const FRESHNESS_IDS: readonly string[] = METRIC_DEFINITIONS.filter((d) => d.group === 'freshness').map((d) => d.id);

/**
 * The Freshness switch (Inventory Health): on shows the report's default freshness columns (`defaults`, those offered and visible by default),
 * off hides every freshness column. `freshnessShown` follows, so the aging KPI and row detail read the same choice.
 */
export const setFreshnessShown = (s: ReportViewState, on: boolean, defaults: readonly string[]): ReportViewState => ({
  ...s,
  freshnessShown: on,
  columns: { ...s.columns, visible: { ...s.columns.visible, ...Object.fromEntries(FRESHNESS_IDS.map((id) => [id, on && defaults.includes(id)])) } },
});

/**
 * Sets the column visibility map (ColumnsMenu / a header "Hide") and re-derives `showPctOfTotal`,
 * so the flag never drifts from `columns.visible`.
 */
export const withColumnVisibility = (s: ReportViewState, visible: Record<string, boolean>): ReportViewState => ({
  ...s,
  showPctOfTotal: anyPctVisible(visible),
  columns: { ...s.columns, visible },
});

export const defaultViewState = (report: ReportKey, grain: Grain): ReportViewState => ({
  schema: 2,
  view: DEFAULT_HIERARCHY_VIEW, productGrain: 'product',
  grain, period: null, lookback: defaultLookback(report, grain), compare: 'plan',
  otbSpanMonths: 3, otbSpanWeeks: DEFAULT_OTB_SPAN_WEEKS, otbStart: null, otbOnOrder: 'window', basis: 'retail', multiplier: 1.5,
  allocWindow: defaultAllocWindow(grain), allocBasis: 'net_sales',
  filters: { locations: emptySelection(), categories: emptySelection(), classes: emptySelection(), vendors: emptySelection(), statuses: [] },
  search: { text: '', scope: 'product' },
  showSkus: false, weeklyPlannedOnly: false, showPctOfTotal: false, freshnessShown: true, inventoryDetail: 'detail',
  kpiOtbIncludeDev: true,
  distributeOtb: false,
  showChildClasses: true,
  planEdits: EMPTY_PLAN_EDITS,
  trendMethod: 'auto',
  ...(report === 'plan_otb' ? { planOtb: defaultPlanOtbOptions(grain) } : {}),
  wosOverride: null,
  //: Weekly Action opens on Action priority (urgent reorders first, then by action; rowTree.ts); a saved view keeps its own sort.
  sort: report === 'weekly_action' ? { column: ACTION_PRIORITY_SORT, direction: 'desc' } : null,
  productSort: 'sales',
  columns: defaultColumns(report),
});

// ---- sanitising ---------------------------------------------------------------------------------

const selectionOf = (v: unknown, d: FilterSelection): FilterSelection => {
  if (!isPlainObject(v)) return d;
  return { codes: uniqueStrings(v.codes) ?? d.codes, exclude: isBool(v.exclude) ? v.exclude : d.exclude };
};
const boolMap = (v: unknown, d: Record<string, boolean>): Record<string, boolean> =>
  isPlainObject(v) ? Object.fromEntries(Object.entries(v).filter(([k, b]) => KNOWN_COLUMN_IDS.has(k) && isBool(b))) as Record<string, boolean> : d;
// a saved width is clamped to the resizer's own range (clampReportColumnWidth), so a hand-edited or corrupt value cannot break the layout.
const numMap = (v: unknown, d: Record<string, number>): Record<string, number> =>
  isPlainObject(v) ? Object.fromEntries(Object.entries(v).filter(([k, n]) => KNOWN_COLUMN_IDS.has(k) && isNumViewState(n) && (n as number) > 0).map(([k, n]) => [k, clampReportColumnWidth(n as number)])) as Record<string, number> : d;
const columnIds = (v: unknown): string[] | null => {
  const ids = uniqueStrings(v);
  return ids ? ids.filter((id) => KNOWN_COLUMN_IDS.has(id)) : null;
};

/** a saved Status filter on the old 'watch' keeps both halves of the split (watch_low, watch_high). */
const migrateStatuses = (statuses: string[] | null): string[] | null =>
  statuses === null ? null : Array.from(new Set(statuses.flatMap((x) => (x === 'watch' ? ['watch_low', 'watch_high'] : [x]))));

/**
 * W3: the one search is a product search (server side), so `scope` is always 'product'. A view or link saved with
 * the removed class search (scope 'class', or none) opens with no search text; a saved 'sku' / 'product' search keeps its text.
 */
const productSearchOf = (raw: Record<string, unknown>, d: ReportViewState['search']): ReportViewState['search'] => {
  const kept = raw.scope === 'sku' || raw.scope === 'product';
  return { text: kept && typeof raw.text === 'string' ? raw.text.slice(0, SEARCH_TEXT_MAX) : d.text, scope: 'product' };
};

/** Merges an untrusted object onto the defaults for the report and its (valid) grain. */
const normalizeState = (raw: unknown, report: ReportKey): ReportViewState => {
  const r: Record<string, unknown> = isPlainObject(raw) ? raw : {};
  const grain = oneOf(r.grain, GRAINS, 'weekly');
  const d = defaultViewState(report, grain);
  const filters = isPlainObject(r.filters) ? r.filters : {};
  const search = isPlainObject(r.search) ? r.search : {};
  const columns = isPlainObject(r.columns) ? r.columns : {};
  // A payload with no `sort` key at all takes the report's default sort; an explicit null is the user's "no sort" and is kept in a
  // version-2 payload. Only a payload saved before the version bump (a legacy null) is mapped to "absent", i.e. the default.
  const legacy = r.schema !== VIEW_STATE_SCHEMA;
  const rawSort = legacy && r.sort === null ? undefined : r.sort;
  const sort: ReportViewState['sort'] = rawSort === undefined ? d.sort : isPlainObject(rawSort) && typeof rawSort.column === 'string' && (KNOWN_COLUMN_IDS.has(rawSort.column) || isReportSortId(rawSort.column)) && (rawSort.direction === 'asc' || rawSort.direction === 'desc')
    ? { column: rawSort.column, direction: rawSort.direction === 'asc' ? 'asc' : 'desc' }
    : null;
  const planOtb = d.planOtb && isPlainObject(r.planOtb)
    ? {
        mode: oneOf(r.planOtb.mode, PLAN_OTB_MODES, d.planOtb.mode),
        rows: (uniqueStrings(r.planOtb.rows) ?? d.planOtb.rows).filter((x): x is PlanOtbRow => PLAN_OTB_ROWS.includes(x as PlanOtbRow)),
        showPrevious: isBool(r.planOtb.showPrevious) ? r.planOtb.showPrevious : d.planOtb.showPrevious,
        hideFutureActuals: isBool(r.planOtb.hideFutureActuals) ? r.planOtb.hideFutureActuals : d.planOtb.hideFutureActuals,
        hidePeriodDates: isBool(r.planOtb.hidePeriodDates) ? r.planOtb.hidePeriodDates : d.planOtb.hidePeriodDates,
        hideRowColours: isBool(r.planOtb.hideRowColours) ? r.planOtb.hideRowColours : d.planOtb.hideRowColours,
        showBenchmarks: isBool(r.planOtb.showBenchmarks) ? r.planOtb.showBenchmarks : d.planOtb.showBenchmarks,
        showLy: isBool(r.planOtb.showLy) ? r.planOtb.showLy : d.planOtb.showLy,
      }
    : d.planOtb;
  const visible = boolMap(columns.visible, d.columns.visible);
  // a view saved while "% of Total" was a switch carries the flag and no choice for the columns: it opens with them shown.
  const rawVisible = isPlainObject(columns.visible) ? columns.visible : {};
  const legacyPct = r.showPctOfTotal === true && !PCT_COLUMN_IDS.some((id) => id in rawVisible);
  const period = isNumViewState(r.period) && r.period >= 0 ? Math.trunc(r.period) : null;
  // a whole period >= 0; with a stored period it must be after it and within the grain's limit (a saved view
  // has no period, so the check against the current period happens in queryParamsOf); anything else is the target (null).
  const otbStart = isNumViewState(r.otbStart) && Number.isInteger(r.otbStart) && r.otbStart >= 0 && (period === null || otbStartFor(r.otbStart, grain, period) !== null) ? r.otbStart : null;
  const state: ReportViewState = {
    schema: 2,
    view: migrateHierarchyView(r.view) ?? d.view,
    productGrain: oneOf(r.productGrain, ['product', 'sku'] as const, d.productGrain),
    grain,
    period,
    otbStart,
    otbOnOrder: oneOf(r.otbOnOrder, OTB_ON_ORDER_CHOICES, d.otbOnOrder),
    lookback: isNumViewState(r.lookback) ? r.lookback : d.lookback,
    compare: oneOf(r.compare, COMPARES, d.compare),
    otbSpanMonths: isNumViewState(r.otbSpanMonths) && r.otbSpanMonths >= 0 ? clampInt(r.otbSpanMonths, OTB_SPAN_MONTHS_MIN, OTB_SPAN_RANGE.monthly.max) : d.otbSpanMonths,
    otbSpanWeeks: isNumViewState(r.otbSpanWeeks) && r.otbSpanWeeks >= 1 ? clampInt(r.otbSpanWeeks, OTB_SPAN_RANGE.weekly.min, OTB_SPAN_RANGE.weekly.max)
      // A weekly payload from before the weekly span (no otbSpanWeeks key): its months choice maps to the nearest week option.
      : r.otbSpanWeeks === undefined && grain === 'weekly' && isNumViewState(r.otbSpanMonths) ? weeksFromMonths(r.otbSpanMonths) : d.otbSpanWeeks,
    basis: oneOf(r.basis, BASES, d.basis),
    multiplier: isNumViewState(r.multiplier) ? (r.multiplier as Multiplier) : d.multiplier,
    allocWindow: isNumViewState(r.allocWindow) ? r.allocWindow : d.allocWindow,
    allocBasis: oneOf(r.allocBasis, ALLOC_BASES, d.allocBasis),
    filters: {
      locations: selectionOf(filters.locations, d.filters.locations),
      categories: selectionOf(filters.categories, d.filters.categories),
      classes: selectionOf(filters.classes, d.filters.classes),
      vendors: selectionOf(filters.vendors, d.filters.vendors),
      statuses: migrateStatuses(uniqueStrings(filters.statuses)) ?? d.filters.statuses,
    },
    search: productSearchOf(search, d.search),
    showSkus: isBool(r.showSkus) ? r.showSkus : d.showSkus,
    weeklyPlannedOnly: isBool(r.weeklyPlannedOnly) ? r.weeklyPlannedOnly : d.weeklyPlannedOnly,
    showPctOfTotal: anyPctVisible(visible),
    freshnessShown: isBool(r.freshnessShown) ? r.freshnessShown : d.freshnessShown,
    kpiOtbIncludeDev: isBool(r.kpiOtbIncludeDev) ? r.kpiOtbIncludeDev : d.kpiOtbIncludeDev,
    distributeOtb: isBool(r.distributeOtb) ? r.distributeOtb : d.distributeOtb,
    showChildClasses: isBool(r.showChildClasses) ? r.showChildClasses : d.showChildClasses,
    planEdits: normalizePlanEditsByGrain(r.planEdits),
    inventoryDetail: oneOf(r.inventoryDetail, ['detail', 'compact'] as const, d.inventoryDetail),
    trendMethod: oneOf(r.trendMethod, TREND_METHODS, d.trendMethod),
    ...(planOtb ? { planOtb } : {}),
    wosOverride: isNumViewState(r.wosOverride) && r.wosOverride > 0 ? r.wosOverride : null,
    sort,
    productSort: oneOf(r.productSort, PRODUCT_SORTS, d.productSort),
    columns: {
      visible,
      order: columnIds(columns.order) ?? d.columns.order,
      widths: numMap(columns.widths, d.columns.widths),
      pinned: columnIds(columns.pinned) ?? d.columns.pinned,
    },
  };
  // a view saved while the Freshness switch hid the columns (freshnessShown false, no freshness choice of its own) opens
  // with them hidden, now that the switch only sets their visibility.
  if (r.freshnessShown === false && !FRESHNESS_IDS.some((id) => id in rawVisible)) {
    state.columns = { ...state.columns, visible: { ...state.columns.visible, ...Object.fromEntries(FRESHNESS_IDS.map((id) => [id, false])) } };
  }
  if (!legacyPct) return state;
  const offered = new Set(columnsFor(report, 'A').map((c) => c.id));
  return setShowPctOfTotal(state, true, PCT_COLUMN_IDS.filter((id) => offered.has(id)));
};

// ---- share links --------------------------------------------------------------------------------

const utf8ToBase64Url = (s: string): string =>
  btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const base64UrlToUtf8 = (s: string): string => {
  const b = s.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(escape(atob(b + '='.repeat((4 - (b.length % 4)) % 4))));
};

/** Who a share envelope belongs to. Both are optional so older (unscoped) links still decode. */
export interface ShareScope { report?: ReportKey; clientId?: string | number }

export const encodeShareState = (state: ReportViewState, scope: ShareScope = {}): string =>
  `${SHARE_STATE_PREFIX}${utf8ToBase64Url(JSON.stringify({ ...state, ...(scope.report ? { report: scope.report } : {}), ...(scope.clientId !== undefined ? { clientId: String(scope.clientId) } : {}) }))}`;

/**
 * `null` for anything that is not a v1 envelope around an object with `schema: 1` or `2`, and for an
 * envelope scoped to another report or (when `clientId` is given) another client. An unscoped
 * envelope (older links) is accepted.
 */
export const decodeShareState = (encoded: string, report: ReportKey, clientId?: string | number): ReportViewState | null => {
  if (typeof encoded !== 'string' || !encoded.startsWith(SHARE_STATE_PREFIX)) return null;
  try {
    const parsed: unknown = JSON.parse(base64UrlToUtf8(encoded.slice(SHARE_STATE_PREFIX.length)));
    if (!isPlainObject(parsed) || (parsed.schema !== VIEW_STATE_SCHEMA && parsed.schema !== LEGACY_VIEW_STATE_SCHEMA)) return null;
    if (parsed.report !== undefined && parsed.report !== report) return null;
    if (parsed.clientId !== undefined && clientId !== undefined && String(parsed.clientId) !== String(clientId)) return null;
    return normalizeState(parsed, report);
  } catch {
    return null;
  }
};

/**
 * True when a `?v=` envelope stores a pre-4b hierarchy key ('vendor', …): the hook rewrites such a
 * URL once on mount so the address bar (and any re-shared link) carries the new key.
 */
export const shareStateHasLegacyView = (encoded: string): boolean => {
  if (typeof encoded !== 'string' || !encoded.startsWith(SHARE_STATE_PREFIX)) return false;
  try {
    const parsed: unknown = JSON.parse(base64UrlToUtf8(encoded.slice(SHARE_STATE_PREFIX.length)));
    return isPlainObject(parsed) && typeof parsed.view === 'string' && Object.prototype.hasOwnProperty.call(LEGACY_HIERARCHY_VIEWS, parsed.view);
  } catch {
    return false;
  }
};

// ---- saved views --------------------------------------------------------------------------------

export const toSavedViewPayload = (state: ReportViewState): SavedViewPayload => ({ ...state, period: null });
/**
 * A payload saved by a NEWER schema than this build knows is ignored (the report's defaults), not guessed at: the same rule
 * decodeShareState applies to a link (there it returns null). A payload with no schema (older saves) is still read.
 */
export const fromSavedViewPayload = (payload: unknown, report: ReportKey): ReportViewState =>
  isPlainObject(payload) && typeof payload.schema === 'number' && payload.schema > VIEW_STATE_SCHEMA ? normalizeState(null, report) : normalizeState(payload, report);

// ---- tier fallback ------------------------------------------------------------------------------

export interface TierFallbackResult { state: ReportViewState; notices: string[] }

const sameSelection = (a: FilterSelection, b: FilterSelection): boolean => a.exclude === b.exclude && a.codes.length === b.codes.length && a.codes.every((c) => b.codes.includes(c));

/**
 * Repairs a state against what the client can do (spec 3) and what the controls offer. Returns a
 * new state only when something changed; the notices are one line per change, in the order the
 * controls appear.
 */
export const applyTierFallback = (input: ReportViewState, capabilities: Capabilities, report?: ReportKey): TierFallbackResult => {
  const notices: string[] = [];
  let s = input;
  const patch = (p: Partial<ReportViewState>, notice: string) => {
    s = { ...s, ...p };
    notices.push(notice);
  };
  const tier: Tier = capabilities.tier ?? 'C';

  if (!GRAINS.includes(s.grain)) patch({ grain: capabilities.weeklyMode ? 'weekly' : 'monthly', period: null }, `Unknown time grain "${String(s.grain)}"; showing ${capabilities.weeklyMode ? 'Weekly' : 'Monthly'}.`);
  if (!capabilities.weeklyMode && s.grain === 'weekly') patch({ grain: 'monthly', period: null }, 'Weekly mode needs daily sales data; showing Monthly.');

  // A lookback counts the grain's own periods (13 weeks is not 13 months): when the downgrade above changed the grain it starts from
  // that grain's default (a grain toggle does the same through `withGrain`); within one grain any whole number in range is valid.
  if (s.grain !== input.grain && GRAINS.includes(s.grain) && s.lookback !== SHARED_DEFAULT_LOOKBACK[s.grain]) {
    patch({ lookback: SHARED_DEFAULT_LOOKBACK[s.grain] }, windowNotice(s.grain, SHARED_DEFAULT_LOOKBACK[s.grain], 'lookback'));
  }
  if (!lookbackInRange(s.grain, s.lookback)) {
    const next = report ? defaultLookback(report, s.grain) : defaultLookback('weekly_action', s.grain);
    patch({ lookback: next }, windowNotice(s.grain, next, 'lookback'));
  }

  if (!capabilities.hasV3Plans && s.weeklyPlannedOnly) patch({ weeklyPlannedOnly: false }, 'The weekly-planned switch needs V3 weekly plans; showing every class.');
  if (!capabilities.hasV3Plans && s.trendMethod === 'plan') patch({ trendMethod: 'auto' }, 'Trend by plan needs V3 plans; using Auto (last year, then days elapsed).');

  // Plan & OTB lays out class plans, so it offers the class views only (its config sets supportsVendorView false); a saved
  // view or link from before carrying a vendor or product view opens on Total by Class.
  if (report === 'plan_otb' && HIERARCHY_BY_KEY[s.view].skuLevel) patch({ view: DEFAULT_HIERARCHY_VIEW }, `Plan & OTB shows class views only; showing Total by Class instead of ${HIERARCHY_BY_KEY[s.view].label}.`);
  if (tier === 'C') {
    if (HIERARCHY_BY_KEY[s.view].skuLevel) patch({ view: DEFAULT_HIERARCHY_VIEW }, `The ${HIERARCHY_BY_KEY[s.view].label} view needs SKU-level data; showing Total by Class.`);
    if (s.showSkus) patch({ showSkus: false }, 'SKU rows are not available for this client.');
    if (s.search.text !== '') patch({ search: { text: '', scope: 'product' } }, 'Product search is not available for this client; the search was cleared.');
    if (!sameSelection(s.filters.vendors, emptySelection())) patch({ filters: { ...s.filters, vendors: emptySelection() } }, 'The Vendor filter is not available for this client and was cleared.');
    // Only the report's own SKU-level columns count, judged as the table would show them.
    const catalogue = report ? columnsFor(report, 'A') : METRIC_DEFINITIONS.map((d) => ({ id: d.id, visible: d.defaultVisible }));
    const skuColumns = catalogue.filter((c) => SKU_COLUMN_IDS.includes(c.id));
    const shown = skuColumns.filter((c) => {
      const explicit = s.columns.visible[c.id];
      return explicit === true || (explicit === undefined && c.visible) || s.columns.pinned.includes(c.id);
    });
    if (shown.length > 0) {
      const visible = { ...s.columns.visible };
      skuColumns.forEach((c) => { visible[c.id] = false; });
      patch({ columns: { ...s.columns, visible, pinned: s.columns.pinned.filter((id) => !SKU_COLUMN_IDS.includes(id)) } }, 'SKU-level columns are not available for this client and were hidden.');
    }
    const allocWindow = defaultAllocWindow(s.grain);
    if (s.allocWindow !== allocWindow || s.allocBasis !== 'net_sales') patch({ allocWindow, allocBasis: 'net_sales' }, ALLOCATION_NOTICE);
  }

  // The product search box shows only while product or SKU rows are in the view (product hierarchies, or Show SKUs on); text left over
  // from a view that had them (a saved view, a link) would filter nothing and still show as a chip, so it goes. No notice: nothing the user chose is lost on screen.
  if (s.search.text !== '' && !s.showSkus && !isProductView(s.view)) s = { ...s, search: { ...s.search, text: '' } };

  if (!LOOKBACK_OPTIONS[s.grain].includes(s.allocWindow)) {
    const next = defaultAllocWindow(s.grain);
    patch({ allocWindow: next }, windowNotice(s.grain, next, 'allocation window'));
  }
  if (!MULTIPLIERS.includes(s.multiplier)) patch({ multiplier: 1.5 }, `Overstock multiplier ${String(s.multiplier)} is not an option; using 1.5.`);
  if (!capabilities.hasReceiving && s.freshnessShown) patch({ freshnessShown: false }, 'Freshness needs receiving data and is hidden for this client.');

  return { state: s, notices };
};

/**
 * ColumnsMenu's Reset: the report's default columns for the tier (order, visibility, no widths or
 * pins) on the state's grain, with `showPctOfTotal` re-derived (off: % of Total is hidden by default).
 */
export const resetColumns = (s: ReportViewState, report: ReportKey, tier: Tier | null | undefined): ReportViewState => {
  const computed = columnStateFor(defaultViewState(report, s.grain), report, tier);
  const columns = { ...computed, visible: { ...computed.visible } };
  DYNAMIC_COLUMN_IDS.forEach((id) => { delete columns.visible[id]; }); // back to dynamic
  return { ...s, columns, showPctOfTotal: anyPctVisible(columns.visible) };
};

// ---- derived ------------------------------------------------------------------------------------

/**
 * The server-side params (spec 5.3): the only values whose change re-queries. no `multiplier` — it only moves status /
 * urgent / action, which the browser derives (targets.ts `rowStatus`), so a change of it never refetches.
 */
export const queryParamsOf = (s: ReportViewState, currentPeriod: number): ReportParams | VendorRowsParams => {
  const base: ReportParams = {
    grain: s.grain, target: s.period ?? currentPeriod, lookback: s.lookback, compare: s.compare,
    // OTB span follows the grain: Weekly sends its weeks, Monthly its months; the other is never sent (the backend ignores it).
    ...(s.grain === 'weekly' ? { otbSpanWeeks: s.otbSpanWeeks } : { otbSpanMonths: s.otbSpanMonths }),
    // the start only when it is after the target (and within the limit), the on-order choice only when 'all'.
    ...(otbStartFor(s.otbStart, s.grain, s.period ?? currentPeriod) !== null ? { otbStart: s.otbStart as number } : {}),
    ...(s.otbOnOrder === 'all' ? { otbOnOrder: 'all' as const } : {}),
    basis: s.basis,
    // Plan & OTB's "Show previous 4": the payload must carry four past periods whatever the (shorter) lookback is.
    ...(s.planOtb?.showPrevious ? { history: PLAN_OTB_HISTORY } : {}),
  };
  return needsVendorRows(s.view) ? { ...base, allocWindow: s.allocWindow, allocBasis: s.allocBasis } : base;
};

/**
 * The table's column state for a report and tier: the view's order restricted to the catalogue
 * (a column missing from it inserted after its nearest preceding catalogue neighbour), visibility from the view's explicit choices over the
 * catalogue defaults (which hide the % of Total group). The trend columns without an explicit choice
 * follow `opts.trendPeriod` (shown when the target period is a trend period). `opts.benchmarkLevel`
 * admits the benchmark group to the catalogue — it must match the `columnsFor` call that
 * builds the table's columns, because ReportTable shows any column its state does not mark hidden;
 * a saved view's explicit choice for a benchmark column is kept and applies whenever the level allows.
 */
export const columnStateFor = (s: ReportViewState, report: ReportKey, tier: Tier | null | undefined, opts: { trendPeriod?: boolean; benchmarkLevel?: BenchmarkLevel } = {}): ReportColumnState => {
  const catalogue = columnsFor(report, tier, opts.benchmarkLevel ?? 'none');
  const ids = catalogue.map((c) => c.id);
  const known = new Set(ids);
  const order = s.columns.order.filter((id, i, arr) => known.has(id) && arr.indexOf(id) === i);
  // A catalogue id the stored order lacks (a column added since the view was saved) goes right after
  // its nearest preceding catalogue neighbour that is in the order (first when there is none).
  ids.forEach((id, i) => {
    if (order.includes(id)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j -= 1) {
      const k = order.indexOf(ids[j]);
      if (k >= 0) { at = k + 1; break; }
    }
    order.splice(at, 0, id);
  });
  const visible: Record<string, boolean> = {};
  const defaults = defaultColumnIds(report, tier); // a trend column the report does not show by default stays off in a trend period too
  catalogue.forEach((c) => {
    const explicit = s.columns.visible[c.id];
    visible[c.id] = typeof explicit === 'boolean' ? explicit
      : TREND_COLUMN_IDS.includes(c.id) ? !!opts.trendPeriod && defaults.includes(c.id)
        : ALLOCATION_COLUMN_IDS.includes(c.id) ? isLocationAllocationView(s.view)
          : PRODUCT_COLUMN_IDS.includes(c.id) ? (isProductView(s.view) || s.showSkus) && defaults.includes(c.id) : c.visible;
  });
  return {
    order,
    visible,
    widths: Object.fromEntries(Object.entries(s.columns.widths).filter(([id]) => known.has(id))),
    pinned: s.columns.pinned.filter((id) => known.has(id)),
  };
};

//: view TEMPLATES, built into the code (no database rows), offered in the Saved views menu to dev-tools users
// only. Applying one sets the report up for a kind of client; an admin then uses Save view → Shared to give that client's buyers the view
// (the existing saved-view flow), so a template is never visible to a client by itself.
//
// "Pet stores" follows the weekly recaps a pet retailer (Wendy) keeps by hand: store › department › class, the last 13 weeks (her L90),
// against plan, with average weekly sales, weeks of supply (in weeks in both grains), plan vs actual inventory, markdowns and markdown %,
// inflows against plan and inflow weeks of supply, on order, OTB + Dev, the period in progress's trended OTB and the mix check (each class's
// share of sales, inventory and inflows).

export interface ViewTemplate {
  id: string;
  name: string;
  description: string;
  /** The view-state changes the template makes (on top of the current state: period and filters stay). */
  build: (state: ReportViewState, tier: Tier | null | undefined) => Partial<ReportViewState>;
}

/** Shows exactly `ids` (in that order, after the label), every other column of the report hidden. */
const columnsOnly = (state: ReportViewState, report: ReportKey, tier: Tier | null | undefined, ids: string[]): Partial<ReportViewState> => {
  const catalogue = columnsFor(report, tier ?? 'A').map((c) => c.id);
  const wanted = ['label', ...ids].filter((id) => catalogue.includes(id));
  const visible = Object.fromEntries(catalogue.map((id) => [id, wanted.includes(id)]));
  const next = withColumnVisibility(state, visible);
  return { columns: { ...next.columns, order: [...wanted, ...catalogue.filter((id) => !wanted.includes(id))] }, showPctOfTotal: next.showPctOfTotal };
};

// Trimmed to Wendy's sheets, in her order: sales (plan, actual, trend, weekly average), markdown %, inflows (actual,
// plan, weeks of supply), inventory (BOM, plan, weeks of supply), the mix (inventory, sales and inflow % of total), OTB + Dev (her "Inflow
// Plan + Deviation"), trended OTB and on order.
// Wendi (2026-10-22): the separate "Weeks of supply" column is gone; the template shows the WOS column (in Weekly, the same BOW ÷ average weekly sales).
export const PET_STORE_COLUMNS: readonly string[] = [
  'salesPlan', 'thisPeriodSales', 'salesTrend', 'avgWeeklySales', 'markdownPct', 'inflow', 'inflowPlan', 'inflowWos',
  'inventory', 'inventoryPlan', 'cover', 'pctOfInventory', 'pctOfSales', 'pctOfInflow', 'otbDev', 'trendedOtb', 'onOrder',
];

const PET_STORES: ViewTemplate = {
  id: 'pet-stores',
  name: 'Pet stores',
  description: 'Store › department › class over the last 13 weeks: weekly sales, weeks of supply, plan vs actual inventory, markdown %, inflow weeks of supply, OTB + Dev, trended OTB and the sales / inventory / inflow mix.',
  build: (state, tier) => {
    const base: ReportViewState = { ...state, view: tier === 'C' ? 'categoryByClass' : 'locationCategoryClass', lookback: state.grain === 'monthly' ? 3 : 13, compare: 'plan' };
    return { view: base.view, lookback: base.lookback, compare: base.compare, ...columnsOnly(base, 'weekly_action', tier, [...PET_STORE_COLUMNS]) };
  },
};

// "Boutique" (Melanie's demo feedback, 2026-10-20): boutiques buy for the month and use the week to place stock. Two templates of one name:
// Weekly Action in Weekly for allocation (Class › Location: each store's need this week against the warehouse's stock, ship / hold), and
// Plan & OTB in Monthly for open to buy and performance (category by class, the plan against actuals and LY, the months before shown).
// A client without weekly data (Tier C) keeps Monthly and the class views (allocation needs the location rows).
// no weeksOfSupply here (in Weekly it duplicates the cover column); Pet stores keeps it (weeks in both grains).
export const BOUTIQUE_ALLOCATION_COLUMNS: readonly string[] = [
  'action', 'allocNeed', 'allocAvailable', 'allocBalance', 'cover', 'inventory', 'onOrder', 'thisPeriodSales', 'salesTrend', 'salesVsCompare', 'avgWeeklySales',
];

const BOUTIQUE_ALLOCATION: ViewTemplate = {
  id: 'boutique',
  name: 'Boutique',
  description: 'Weekly allocation: each class by store (Class › Location), what each store needs this week, what the warehouse has to send, the shortfall or surplus, and ship / hold on on order. Use Plan & OTB\'s Boutique view in Monthly for open to buy.',
  build: (state, tier) => {
    const weekly = tier !== 'C';
    const base: ReportViewState = { ...(weekly && state.grain !== 'weekly' ? withGrain(state, 'weekly') : state), view: weekly ? 'classByLocation' : 'categoryByClass', compare: 'plan' };
    base.lookback = base.grain === 'weekly' ? 13 : 3;
    return { grain: base.grain, period: base.period, view: base.view, lookback: base.lookback, compare: base.compare, ...columnsOnly(base, 'weekly_action', tier, [...BOUTIQUE_ALLOCATION_COLUMNS]) };
  },
};

const BOUTIQUE_OTB: ViewTemplate = {
  id: 'boutique',
  name: 'Boutique',
  description: 'Monthly open to buy and performance: category by class, the plan against actuals and last year for sales, inventory, inflows and markdowns, on order and OTB, with the 4 months before. Use Inventory Action\'s Boutique view in Weekly to allocate.',
  build: (state) => {
    const base = state.grain !== 'monthly' ? withGrain(state, 'monthly') : state;
    return {
      grain: base.grain, period: base.period, lookback: base.lookback, view: 'categoryByClass', compare: 'plan',
      planOtb: { ...defaultPlanOtbOptions('monthly'), mode: 'detailed', showPrevious: true, showLy: true },
    };
  },
};

// Seven more, each 8–10 columns with one job. Filters and sort are part of a template: the Reorder run is a one-click
// list because it filters to Understocked / Out of stock and sorts by Action priority; Clearance filters to Overstocked / No recent sales.

const NO_STATUSES: string[] = [];
const grainLookback = (grain: ReportViewState['grain'], weeks: number, months: number): number => (grain === 'weekly' ? weeks : months);

export const REORDER_RUN_COLUMNS: readonly string[] = ['action', 'cover', 'inventory', 'onOrder', 'avgWeeklySales', 'salesTrend', 'otbDev', 'suggestedReorder'];
const REORDER_RUN: ViewTemplate = {
  id: 'reorder-run',
  name: 'Reorder run',
  description: 'The buy list: understocked and out-of-stock classes with their SKUs, sorted by Action priority, with cover, on order, weekly velocity, OTB + Dev and suggested reorder units.',
  build: (state, tier) => {
    const base: ReportViewState = { ...state, view: 'totalByClass', lookback: grainLookback(state.grain, 4, 1), compare: 'plan', showSkus: tier !== 'C', filters: { ...state.filters, statuses: ['understocked', 'out_of_stock'] }, sort: { column: ACTION_PRIORITY_SORT, direction: 'desc' } };
    return { view: base.view, lookback: base.lookback, compare: base.compare, showSkus: base.showSkus, filters: base.filters, sort: base.sort, ...columnsOnly(base, 'weekly_action', tier, [...REORDER_RUN_COLUMNS]) };
  },
};

export const CLEARANCE_COLUMNS: readonly string[] = ['action', 'cover', 'inventoryVsPlanDollars', 'inventory', 'aging12w', 'markdownPct', 'onOrder', 'otbDev'];
const CLEARANCE: ViewTemplate = {
  id: 'clearance',
  name: 'Clearance candidates',
  description: 'What to mark down, delay or cancel: overstocked classes and classes with no recent sales, most overstocked first, with dollars over plan, stock aged 8w+, markdown %, on order and OTB + Dev.',
  build: (state, tier) => {
    const base: ReportViewState = { ...state, view: 'totalByClass', compare: 'plan', showSkus: false, filters: { ...state.filters, statuses: ['overstocked', 'no_recent_sales'] }, sort: { column: OVER_TARGET_SORT, direction: 'desc' } };
    return { view: base.view, compare: base.compare, showSkus: base.showSkus, filters: base.filters, sort: base.sort, ...columnsOnly(base, 'weekly_action', tier, [...CLEARANCE_COLUMNS]) };
  },
};

export const VENDOR_CHECK_IN_COLUMNS: readonly string[] = ['thisPeriodSales', 'salesVsCompare', 'inventory', 'cover', 'inflow', 'onOrder', 'otb'];
const VENDOR_CHECK_IN: ViewTemplate = {
  id: 'vendor-check-in',
  name: 'Vendor check-in',
  description: 'Before a rep call or market: each class by vendor with sales against plan, stock, cover, inflows, on order (Hold PO / Take PO) and the vendor\'s share of the OTB.',
  build: (state, tier) => {
    const base: ReportViewState = { ...state, view: tier === 'C' ? 'categoryByClass' : 'classByVendor', compare: 'plan', showSkus: false, filters: { ...state.filters, statuses: NO_STATUSES }, sort: null };
    return { view: base.view, compare: base.compare, showSkus: base.showSkus, filters: base.filters, sort: base.sort, ...columnsOnly(base, 'weekly_action', tier, [...VENDOR_CHECK_IN_COLUMNS]) };
  },
};

export const AGING_COLUMNS: readonly string[] = ['health', 'inventory', 'cover', 'aging12w', 'aging8w', 'freshPct', 'markdownPct', 'inventoryVsPlanDollars'];
const AGING: ViewTemplate = {
  id: 'aging',
  name: 'Aging & freshness',
  description: 'Where stock is going stale: each class\'s share of on-hand dollars aged 8w+ and 5–8w, what came in over the lookback, markdown %, and dollars over plan, oldest first.',
  build: (state, tier) => {
    const base: ReportViewState = { ...state, view: 'totalByClass', compare: 'plan', filters: { ...state.filters, statuses: NO_STATUSES }, sort: { column: 'aging12w', direction: 'desc' } };
    return { view: base.view, compare: base.compare, filters: base.filters, sort: base.sort, ...columnsOnly(base, 'inventory_health', tier, [...AGING_COLUMNS]) };
  },
};

export const RECEIPTS_COLUMNS: readonly string[] = ['aging4w', 'inflow', 'inflowPlan', 'inflowWos', 'inventory', 'cover', 'thisPeriodSales', 'salesVsCompare'];
const RECEIPTS: ViewTemplate = {
  id: 'receipts',
  name: 'Did the buy land?',
  description: 'Last period\'s receipts against plan and whether they are selling: stock received in the last 4 weeks, inflows vs plan, inflow weeks of supply, stock, cover and sales against plan.',
  build: (state, tier) => {
    const base: ReportViewState = { ...state, view: 'totalByClass', lookback: grainLookback(state.grain, 4, 1), compare: 'plan', filters: { ...state.filters, statuses: NO_STATUSES }, sort: null };
    return { view: base.view, lookback: base.lookback, compare: base.compare, filters: base.filters, sort: base.sort, ...columnsOnly(base, 'inventory_health', tier, [...RECEIPTS_COLUMNS]) };
  },
};

const OTB_BY_MONTH: ViewTemplate = {
  id: 'otb-by-month',
  name: 'Open to buy by month',
  description: 'The buy budget and nothing else: Monthly, OTB lines only (OTB + Dev, OTB by month, Running OTB) over this month and the 3 after it.',
  build: (state) => {
    const base = state.grain !== 'monthly' ? withGrain(state, 'monthly') : state;
    return { grain: base.grain, period: base.period, lookback: base.lookback, otbSpanMonths: 3, view: 'totalByClass', compare: 'plan', planOtb: { ...defaultPlanOtbOptions('monthly'), mode: 'otb', showPrevious: false } };
  },
};

const MONTHLY_REVIEW: ViewTemplate = {
  id: 'monthly-review',
  name: 'Monthly review',
  description: 'The month-end meeting: sales, BOM inventory and markdowns, plan against actual and last year, by category and class, with the 4 months before.',
  build: (state) => {
    const base = state.grain !== 'monthly' ? withGrain(state, 'monthly') : state;
    return { grain: base.grain, period: base.period, lookback: base.lookback, view: 'categoryByClass', compare: 'plan', planOtb: { ...defaultPlanOtbOptions('monthly'), mode: 'detailed', rows: ['inventory', 'markdowns'], showPrevious: true, showLy: true } };
  },
};

/** The templates a report offers (dev-tools users only; the page checks the permission). */
export const VIEW_TEMPLATES: Partial<Record<ReportKey, readonly ViewTemplate[]>> = {
  weekly_action: [PET_STORES, BOUTIQUE_ALLOCATION, REORDER_RUN, CLEARANCE, VENDOR_CHECK_IN],
  inventory_health: [AGING, RECEIPTS],
  plan_otb: [BOUTIQUE_OTB, OTB_BY_MONTH, MONTHLY_REVIEW],
};

// The React side of the view state (Plan 3 ): URL → defaults on mount (both through
// applyTierFallback, so the first load and `reset` agree), `?v=` written back with
// history.replaceState (debounced, never a navigation — router hooks such as useSearchParams will
// NOT see it; Plan 4 must read `v` through this hook, not the router), saved views applied the way
// Vendor Performance does (defaults first, pins cleared when the view carries a column order), and
// `queryParams` memoised by value so a page's fetch effect depends on it and re-queries only when
// a server-side param changes (spec 5.3). Saved views exclude the period; share links include it
// (`shareUrl` pins `period` to what is shown, so a recipient sees the same week or month).
// `set` is a plain merge EXCEPT when it changes the grain: then the period is cleared (a week
// number is not a month number) and the tier fallback re-runs so the lookback and allocation window
// are valid for the new grain (its notices replace the held ones). A saved view in another grain
// likewise loads without the current period.
//
// Saved views by URL: `?sv=<id>` next to `v`. Applying a saved view remembers it
// (`savedViewId`) for as long as the state, period aside, still equals what the view applied (so a
// period pick keeps it, any other change drops it, and undoing the change brings it back); the URL
// write and `shareUrl` carry `sv` only then. On mount `sv` is read into `pendingSavedViewId`, the
// page resolves it once the saved views list is in (`resolveSavedView`): without a `v` the record is
// applied; with a `v` the link's state wins and the record only marks the view active when it
// matches; a missing record keeps the state and adds the notice "That saved view isn't available."
// (or "Saved views could not be loaded." when the list request failed). Deleting the active view
// (`clearSavedView`) forgets it so the URL and the share link stop carrying a dead id. `sv` is
// scoped like `v`: ignored next to a `v` written for another report or client.

export const URL_PARAM = 'v';
export const SAVED_VIEW_URL_PARAM = 'sv';
/** Names the report in a share link; ClientAnalytics reads it to open that report on load. */
export const REPORT_URL_PARAM = 'report';
export const URL_WRITE_DEBOUNCE_MS = 300;
export const SAVED_VIEW_UNAVAILABLE_NOTICE = "That saved view isn't available.";
export const SAVED_VIEWS_UNAVAILABLE_NOTICE = 'Saved views could not be loaded.';

export interface UseReportViewStateOptions {
  reportKey: ReportKey;
  /** The client the state belongs to: written into the `?v=` envelope and checked when reading it. */
  clientId?: string | number;
  capabilities: Capabilities;
  /** The period shown when the state has none (the report hook passes its `defaultPeriod`). */
  currentPeriod: number;
  /** Read `?v=` / `?sv=` on mount (default true). */
  initialFromUrl?: boolean;
}

export interface UseReportViewStateResult {
  state: ReportViewState;
  set: (partial: Partial<ReportViewState>) => void;
  reset: () => void;
  applySaved: (record: SavedViewRecord<SavedViewPayload>) => void;
  shareUrl: () => string;
  queryParams: ReportParams | VendorRowsParams;
  /** Tier-fallback notices from the last load / apply / capability change (spec 3), plus the missing-saved-view notice. */
  notices: string[];
  /** The saved view the state still equals (period aside); null once it is modified, or none was applied. */
  savedViewId: number | null;
  /** The `?sv=` id read on mount, until the page resolves it. */
  pendingSavedViewId: number | null;
  /** Resolves `pendingSavedViewId` with the listed record (applied, or marked active next to a `v`), or null (not available; `listFailed` when the list itself could not be loaded). */
  resolveSavedView: (record: SavedViewRecord<SavedViewPayload> | null, opts?: { listFailed?: boolean }) => void;
  /** The saved view with this id was deleted: forget it as the active view (the state itself stays). */
  clearSavedView: (id: number) => void;
}

interface ActiveSavedView { id: number; key: string }
interface Held { state: ReportViewState; notices: string[]; savedView: ActiveSavedView | null }

/** A key-order-independent string of the state without its period: what a saved view compares on. */
const savedViewKey = (state: ReportViewState): string => stableStringify(toSavedViewPayload(state));
const stableStringify = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (typeof v === 'object' && v !== null) {
    return `{${Object.keys(v as Record<string, unknown>).sort().map((k) => `${JSON.stringify(k)}:${stableStringify((v as Record<string, unknown>)[k])}`).join(',')}}`;
  }
  return JSON.stringify(v) ?? 'undefined';
};

/** The `?v=` state, only when it was written for this report and client (else null: no share link). */
export const readUrlState = (reportKey: ReportKey, clientId?: string | number): ReportViewState | null => {
  if (typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get(URL_PARAM);
  return v ? decodeShareState(v, reportKey, clientId) : null;
};

/** The `?sv=` id (a positive integer), unless it sits next to a `?v=` that is not ours (then it is not ours either). */
export const readUrlSavedViewId = (reportKey: ReportKey, clientId?: string | number): number | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const sv = params.get(SAVED_VIEW_URL_PARAM);
  if (!sv || !/^\d+$/.test(sv)) return null;
  const id = Number(sv);
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  const v = params.get(URL_PARAM);
  if (v && decodeShareState(v, reportKey, clientId) === null) return null;
  return id;
};

/** The `?v=` is ours (decodes for this report and client) and stores a pre-4b view key. */
const urlHasLegacyView = (reportKey: ReportKey, clientId?: string | number): boolean => {
  if (typeof window === 'undefined') return false;
  const v = new URLSearchParams(window.location.search).get(URL_PARAM);
  return !!v && readUrlState(reportKey, clientId) !== null && shareStateHasLegacyView(v);
};

const urlWith = (state: ReportViewState, scope: ShareScope, savedViewId: number | null): string => {
  const url = new URL(window.location.href);
  url.searchParams.set(URL_PARAM, encodeShareState(state, scope));
  // The analytics page keeps its report in state, so the link names it (weekly_action -> weekly-action) to open it, not the list.
  if (scope.report) url.searchParams.set(REPORT_URL_PARAM, scope.report.replace(/_/g, '-'));
  if (savedViewId !== null) url.searchParams.set(SAVED_VIEW_URL_PARAM, String(savedViewId));
  else url.searchParams.delete(SAVED_VIEW_URL_PARAM);
  return url.toString();
};

/** What applying a saved view holds: the record's state over the defaults, the current period kept in the same grain, pins cleared when it carries a column order. */
const appliedState = (record: SavedViewRecord<SavedViewPayload>, current: ReportViewState, capabilities: Capabilities, reportKey: ReportKey) => {
  const payload = record.payload as Partial<SavedViewPayload> | null | undefined;
  const loaded = fromSavedViewPayload(payload, reportKey);
  const hasOrder = Array.isArray(payload?.columns?.order);
  return applyTierFallback(
    { ...loaded, period: loaded.grain === current.grain ? current.period : null, columns: hasOrder ? { ...loaded.columns, pinned: [] } : loaded.columns },
    capabilities,
    reportKey
  );
};
/** Browser-only fallback records have negative ids and are never addressable. */
const activeFor = (record: SavedViewRecord<SavedViewPayload>, state: ReportViewState): ActiveSavedView | null => (record.id > 0 ? { id: record.id, key: savedViewKey(state) } : null);

export function useReportViewState({ reportKey, clientId, capabilities, currentPeriod, initialFromUrl = true }: UseReportViewStateOptions): UseReportViewStateResult {
  const defaults = useCallback(() => defaultViewState(reportKey, capabilities.weeklyMode ? 'weekly' : 'monthly'), [reportKey, capabilities.weeklyMode]);

  const [held, setHeld] = useState<Held>(() => ({ ...applyTierFallback((initialFromUrl && readUrlState(reportKey, clientId)) || defaults(), capabilities, reportKey), savedView: null }));
  const { state, notices } = held;
  const [pendingSavedViewId, setPendingSavedViewId] = useState<number | null>(() => (initialFromUrl ? readUrlSavedViewId(reportKey, clientId) : null));
  // A `v` next to the pending `sv` (ours): the link's numbers win, the saved view only marks itself active
  // when it matches. Read ONCE at mount, on purpose: the `v` is consumed into the initial state right here,
  // and by the time the page resolves the `sv` (after the saved views list and the capabilities are in) the
  // URL may already have been rewritten and the capsKey effect may have re-run the tier fallback on that
  // state. Neither changes what the link asked for, so the decision "link present → compare, not apply"
  // must not be re-derived later from the URL or the state.
  const pendingHasUrlState = useRef(initialFromUrl && pendingSavedViewId !== null && readUrlState(reportKey, clientId) !== null);

  // A capability change (the client switched, or the capabilities arrived late) re-checks the state.
  const capsKey = JSON.stringify(capabilities);
  const prevCapsKey = useRef(capsKey);
  useEffect(() => {
    if (prevCapsKey.current === capsKey) return;
    prevCapsKey.current = capsKey;
    setHeld((h) => ({ ...applyTierFallback(h.state, capabilities, reportKey), savedView: h.savedView }));
  }, [capsKey, capabilities, reportKey]);

  const scopeRef = useRef<ShareScope>({ report: reportKey, clientId });
  scopeRef.current = { report: reportKey, clientId };

  // Active while the state (period aside) still equals what the saved view applied; derived, never stored.
  const stateKey = useMemo(() => savedViewKey(state), [state]);
  const savedViewId = held.savedView && held.savedView.key === stateKey ? held.savedView.id : null;

  // A ?v= carrying a pre-4b hierarchy key is rewritten once on mount (to the migrated state).
  // Initialised lazily: the URL is decoded on the first render only, not on every render.
  const migrateUrl = useRef<boolean | null>(null);
  if (migrateUrl.current === null) migrateUrl.current = !!initialFromUrl && urlHasLegacyView(reportKey, clientId);

  // Write ?v= (and ?sv=) after the state settles; skipped on mount so a plain page URL stays plain.
  // While an `sv` is pending it is kept in the URL; once resolved it stays only for an active saved view.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      if (!migrateUrl.current) return undefined;
      migrateUrl.current = false;
    }
    const timer = setTimeout(() => {
      if (typeof window !== 'undefined') window.history.replaceState(window.history.state, '', urlWith(state, scopeRef.current, savedViewId ?? pendingSavedViewId));
    }, URL_WRITE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [state, savedViewId, pendingSavedViewId]);

  const set = useCallback(
    (partial: Partial<ReportViewState>) => {
      setHeld((h) => {
        const merged = { ...h.state, ...partial };
        // W3: the product search exists only while product or SKU rows are in the view; leaving them (Show SKUs off, a non-product hierarchy) clears it.
        const next = merged.search.text !== '' && !merged.showSkus && !isProductView(merged.view) ? { ...merged, search: { ...merged.search, text: '' } } : merged;
        if (partial.grain === undefined || partial.grain === h.state.grain) return { state: next, notices: h.notices, savedView: h.savedView };
        // The new grain starts from its default lookback unless the same call sets one.
        const switched = partial.lookback === undefined ? withGrain(next, next.grain) : { ...next, period: null, otbStart: null };
        return { ...applyTierFallback(switched, capabilities, reportKey), savedView: h.savedView };
      });
    },
    [capabilities, reportKey]
  );

  const reset = useCallback(() => {
    setHeld({ ...applyTierFallback(defaults(), capabilities, reportKey), savedView: null });
  }, [defaults, capabilities, reportKey]);

  const applySaved = useCallback(
    (record: SavedViewRecord<SavedViewPayload>) => {
      setHeld((h) => {
        const applied = appliedState(record, h.state, capabilities, reportKey);
        return { ...applied, savedView: activeFor(record, applied.state) };
      });
    },
    [capabilities, reportKey]
  );

  const resolveSavedView = useCallback(
    (record: SavedViewRecord<SavedViewPayload> | null, opts: { listFailed?: boolean } = {}) => {
      setPendingSavedViewId(null);
      if (!record) {
        const notice = opts.listFailed ? SAVED_VIEWS_UNAVAILABLE_NOTICE : SAVED_VIEW_UNAVAILABLE_NOTICE;
        setHeld((h) => (h.notices.includes(notice) ? h : { ...h, notices: [...h.notices, notice] }));
        return;
      }
      if (!pendingHasUrlState.current) {
        applySaved(record);
        return;
      }
      setHeld((h) => {
        const applied = appliedState(record, h.state, capabilities, reportKey);
        const active = activeFor(record, applied.state);
        return active && active.key === savedViewKey(h.state) ? { ...h, savedView: active } : h;
      });
    },
    [applySaved, capabilities, reportKey]
  );

  const clearSavedView = useCallback((id: number) => {
    setHeld((h) => (h.savedView && h.savedView.id === id ? { ...h, savedView: null } : h));
  }, []);

  const shareUrl = useCallback(
    () => urlWith({ ...state, period: state.period ?? currentPeriod }, { report: reportKey, clientId }, savedViewId),
    [state, currentPeriod, reportKey, clientId, savedViewId]
  );

  const paramsKey = JSON.stringify(queryParamsOf(state, currentPeriod));
  const queryParams = useMemo<ReportParams | VendorRowsParams>(() => JSON.parse(paramsKey), [paramsKey]);

  return { state, set, reset, applySaved, shareUrl, queryParams, notices, savedViewId, pendingSavedViewId, resolveSavedView, clearSavedView };
}

// lower floor, so a buyer can tighten a column further than the 80px the Vendor Performance / Top Sellers tables allow. The widths are
// kept in the view state (ReportTable writes them to `columnState.widths` after a drag), so there is no localStorage here.

export const REPORT_MIN_COLUMN_WIDTH = 48;
export const REPORT_MAX_COLUMN_WIDTH = 600;
export const REPORT_COLUMN_RESIZE_STEP = 10;

export const clampReportColumnWidth = (width: number): number =>
  Math.round(Math.min(REPORT_MAX_COLUMN_WIDTH, Math.max(REPORT_MIN_COLUMN_WIDTH, width)));


// ---- capability gating (from primitives.tsx; pure, so it lives with the logic and ui.tsx re-exports it) --------------------
// Tier gating: `vendor` (the Vendor hierarchy and SKU-level columns) needs SKU-level data on a Tier A/B client; Tier C never
// sees vendor rows even if a flag is mis-set. The kit never reads the client context itself: the pages fetch `capabilities`
// and pass it down.

export type CapabilityRequirement = 'skuLevel' | 'weeklyMode' | 'hasReceiving' | 'vendor';

export const capabilityAllows = (capabilities: Capabilities | null | undefined, requires: CapabilityRequirement): boolean => {
  if (!capabilities) return false;
  if (requires === 'vendor') return capabilities.skuLevel && capabilities.tier !== null && capabilities.tier !== 'C';
  return capabilities[requires];
};

// ===================================================================================================================
// ==== pageModel.ts ==================================================================================================
// ===================================================================================================================
// Peer benchmarks in the weekly planning reports. Pure. The formulas mirror
// laravel/.../WeeklyPlanning/BenchmarkMetrics.php (benchmark.v_client_vs_benchmark): fractions, one complete month,
// SAFE_DIVIDE (null only on a zero divisor, negatives kept). Always sum then derive, never average: the browser
// re-sums only where it must (a class row's subtree, a filtered location / client), and divides once.
// Row rules: class rows = own + descendant sums vs G3 of the master class (mixed master classes
// or none: no peer); location subtotals = the backend's whole-store entity (unfiltered, one store) or the passing
// classes re-summed, vs G4; client = the backend entity or the passing classes re-summed, vs G4. Nothing for
// Unallocated, Unclassified, category subtotals, vendor, product or SKU rows. A benchmark value is the benchmark's
// own definition for one complete month, shown next to the report's numbers, never substituted for them.

// The metric catalogue and per-value helpers (BENCHMARK_METRICS, benchmarkIndex, formatBenchmark, formatIndex,
// peerText, PEER_TEXT_*, indexTone) live in the leaf module benchmarkMetrics.ts, so metricDefinitions.ts can build the
// benchmark columns from them without a runtime cycle (this module imports rowTree, which imports metricDefinitions).

const ZERO: BenchmarkSums = { salesRetail: 0, salesCost: 0, salesCosted: 0, markdowns: 0, inventoryBom: 0, inventoryEom: 0, inflowRetailCovered: 0, inflowCostCovered: 0, salesWithInflow: 0, salesRetailPrior: 0, monthRows: 0, priorRows: 0 };
const SUM_KEYS = Object.keys(ZERO) as (keyof BenchmarkSums)[];
/** BigQuery SAFE_DIVIDE: null on a null operand or a zero divisor. */
const div = (n: number | null, d: number | null): number | null => (n === null || d === null || d === 0 ? null : n / d);
/** NULLIF(v, 0). */
const nz = (v: number): number | null => (v === 0 ? null : v);
const numPageModel = (v: number | null | undefined): number => (isNum(v) ? v : 0);

/** BenchmarkMetrics::fromSums, line for line (missing keys count as 0). */
export const metricsFromSums = (s: BenchmarkSums, weeksInMonth: number): BenchmarkMetrics => {
  const bom = nz(numPageModel(s.inventoryBom));
  const eom = nz(numPageModel(s.inventoryEom));
  const sales = numPageModel(s.salesRetail);
  const prior = numPageModel(s.salesRetailPrior);
  const withInflow = numPageModel(s.salesWithInflow);
  return {
    sellThrough: div(nz(withInflow), bom === null ? null : bom + numPageModel(s.inflowRetailCovered)),
    weeksOfStock: div(eom, div(sales, weeksInMonth)),
    turnover: div(sales, eom),
    stockToSales: div(bom, sales),
    markdownPct: div(numPageModel(s.markdowns), sales),
    grossMarginPct: div(numPageModel(s.salesCosted) - numPageModel(s.salesCost), numPageModel(s.salesCosted)),
    cashMarginPct: div(withInflow - numPageModel(s.inflowCostCovered), withInflow),
    salesChangeLy: numPageModel(s.priorRows) > 0 && prior > 0 ? sales / prior - 1 : null,
  };
};

const NO_METRICS: BenchmarkMetrics = { sellThrough: null, weeksOfStock: null, turnover: null, stockToSales: null, markdownPct: null, grossMarginPct: null, cashMarginPct: null, salesChangeLy: null };
/** A scope that carries stock or rows but no sales (a Receiving location): every metric is sales-based, none has a meaning. */
export const hasNoSales = (s: BenchmarkSums | null | undefined): boolean =>
  !!s && numPageModel(s.salesRetail) === 0 && (numPageModel(s.monthRows) > 0 || numPageModel(s.inventoryBom) !== 0 || numPageModel(s.inventoryEom) !== 0);
/** The metrics, or all null ("—") when the scope has no sales (turnover would otherwise print 0.00 against real peers). */
export const guardNoSales = (metrics: BenchmarkMetrics, s: BenchmarkSums | null | undefined): BenchmarkMetrics => (hasNoSales(s) ? { ...NO_METRICS } : metrics);

/** BenchmarkMetrics::addSums: Σ of two sum sets, a missing key is 0. */
export const addSums = (a: BenchmarkSums, b: BenchmarkSums): BenchmarkSums => {
  const out = { ...ZERO };
  SUM_KEYS.forEach((k) => { out[k] = numPageModel(a[k]) + numPageModel(b[k]); });
  return out;
};

/** Monthly: the target month is the benchmark month; Weekly: null (the service picks the latest complete month). */
export const benchmarkMonthParam = (grain: Grain, targetPeriod: number): number | null => (grain === 'monthly' ? targetPeriod : null);

/** Statuses with nothing to show at all (no month either); every other status names the month. */
const isSilent = (p: BenchmarkPayload): boolean => p.status === 'not_entitled' || p.status === 'unavailable' || p.status === 'month_out_of_window';

export const benchmarkLabel = (p: BenchmarkPayload | null | undefined): string | null => {
  if (!p || !p.monthLabel || isSilent(p)) return null;
  return `Benchmark: ${p.monthLabel}${p.isFallback ? ' (latest complete month)' : ''}`;
};

export type BenchmarkRowStatus = 'ok' | 'notEnoughPeers' | 'noPeerGroup' | 'mixedMasterClasses' | 'noMasterClass';
export interface BenchmarkRowValue {
  level: 'class' | 'location' | 'client';
  own: BenchmarkMetrics;
  peer: BenchmarkPeer | null;
  status: BenchmarkRowStatus;
  /** Re-summed from the classes the filters keep (or from classes spanning stores), not the whole store / business. */
  filtered: boolean;
  /** Class rows: the master class the G3 peers are for; null when none or mixed. */
  masterClass?: string | null;
  /** "<vertical> · [<master class> ·] <month>" for the cell tooltips and cards. */
  peerLabel: string;
  /** The payload sums behind `own` (class, chain and location entries); lets the KPIs re-sum the rows the report shows. Absent on a client entry from the backend. */
  sums?: BenchmarkSums | null;
}

const peerStatusOf = (peer: BenchmarkPeer | null): BenchmarkRowStatus => (!peer ? 'noPeerGroup' : peer.publishable ? 'ok' : 'notEnoughPeers');
/** Σ of the payload sums of these classes; null when none of them is in the payload. */
const sumsOf = (ids: number[], p: BenchmarkPayload): BenchmarkSums | null => {
  let s: BenchmarkSums | null = null;
  ids.forEach((id) => {
    const c = p.classes[String(id)];
    if (c) s = addSums(s ?? ZERO, c.sums);
  });
  return s;
};

/** Every class id with its descendants (the report rolls children into parents), cycle-safe. */
const descendants = (rows: ClassRow[]): Map<number, number[]> => {
  const kids = new Map<number, number[]>();
  rows.forEach((r) => {
    if (isNum(r.locationClassId) && isNum(r.parentLocationClassId) && r.parentLocationClassId !== r.locationClassId) {
      kids.set(r.parentLocationClassId, [...(kids.get(r.parentLocationClassId) ?? []), r.locationClassId]);
    }
  });
  const out = new Map<number, number[]>();
  const walk = (id: number, seen: Set<number>): number[] => {
    if (seen.has(id)) return [];
    seen.add(id);
    return [id, ...(kids.get(id) ?? []).flatMap((k) => walk(k, seen))];
  };
  rows.forEach((r) => { if (isNum(r.locationClassId)) out.set(r.locationClassId, walk(r.locationClassId, new Set())); });
  return out;
};

type ScopeFilters = Pick<RowTreeFilters, 'locations' | 'categories' | 'classes'>;
const canShow = (p: BenchmarkPayload | null | undefined): p is BenchmarkPayload => !!p && !!p.client && !isSilent(p);
const realClassRows = (rows: ClassRow[]): ClassRow[] => rows.filter((r) => isNum(r.locationClassId) && !r.isUnallocated && r.key !== 'unclassified');
const vertical = (p: BenchmarkPayload): string => p.vertical ?? 'No vertical';

/**
 * The per-row-key lookup the cells, KPIs, charts and row details read: `class:<id>` (class level only) and
 * `location:<code>` (the row tree's location subtotal key), and `chain:<code>` for a class across stores. Built from the payload rows, BEFORE the tree, so
 * benchmark columns sort like any column. Class rows are never re-summed under a filter (a parent keeps its whole
 * subtree, as the report's own totals do); location subtotals are.
 */
export const resolveRowBenchmarks = (classRows: ClassRow[], p: BenchmarkPayload | null | undefined, filters: ScopeFilters): Record<string, BenchmarkRowValue> => {
  const out: Record<string, BenchmarkRowValue> = {};
  if (!canShow(p)) return out;
  const w = p.weeksInMonth;
  const label = p.monthLabel ?? '';
  const real = realClassRows(classRows);
  if (p.level === 'class') {
    const tree = descendants(real);
    const subtreeOf = (r: ClassRow): number[] => tree.get(r.locationClassId as number) ?? [r.locationClassId as number];
    const classEntry = (ids: number[], filtered: boolean): BenchmarkRowValue | null => {
      const sums = sumsOf(ids, p);
      if (!sums) return null;
      const mcs = Array.from(new Set(ids.map((id) => p.classes[String(id)]).filter(Boolean).map((c) => c.masterClass)));
      const mc = mcs.length === 1 ? mcs[0] : undefined;
      const peer = mc ? p.peers.masterClass[mc] ?? null : null;
      return {
        level: 'class', own: guardNoSales(metricsFromSums(sums, w), sums), sums, peer, masterClass: mc ?? null, filtered,
        status: mcs.length > 1 ? 'mixedMasterClasses' : mc === null || mc === undefined ? 'noMasterClass' : peerStatusOf(peer),
        peerLabel: `${vertical(p)} · ${mcs.length > 1 ? 'mixed' : mc ?? 'no master class'} · ${label}`,
      };
    };
    real.forEach((r) => {
      const v = classEntry(subtreeOf(r), false);
      if (v) out[r.key] = v;
    });
    // a chain row (`chain:<code>`, one class across the stores the Location filter keeps) sums its member
    // stores' subtrees and derives once, as a class row does; the master-class gating is the same. Re-summed (filtered)
    // when the Location filter narrows the stores.
    const byChain = new Map<string, ClassRow[]>();
    real.filter((r) => matchesSelection(filters.locations, r.locationCode)).forEach((r) => {
      const key = chainKeyOf(r);
      if (key) byChain.set(key, [...(byChain.get(key) ?? []), r]);
    });
    byChain.forEach((rows, key) => {
      if (new Set(rows.map((r) => r.locationCode)).size < 2) return;
      const v = classEntry(Array.from(new Set(rows.flatMap(subtreeOf))), filters.locations.codes.length > 0);
      if (v) out[key] = v;
    });
  }
  const filtered = hasScopeFilter(filters);
  const passing = filtered ? passingClassRows(classRows, filters) : real;
  const byCode = new Map<string, ClassRow[]>();
  passing.forEach((r) => {
    const code = `location:${r.locationCode ?? ''}`;
    byCode.set(code, [...(byCode.get(code) ?? []), r]);
  });
  byCode.forEach((rows, code) => {
    const ids = rows.map((r) => r.locationClassId as number);
    // The benchmark's store id: the payload class's (the location key rule; null = no store grain, counts toward no
    // store) when the payload knows the class, else the row's own client location id (the location level trims the
    // classes). The fallback is only for classes the payload has no entry for.
    const stores = Array.from(new Set(rows.map((r) => { const id = String(r.locationClassId); return id in p.classes ? p.classes[id].locationId : r.locationId; }).filter(isNum)));
    const whole = !filtered && stores.length === 1 ? p.locations[String(stores[0])] : undefined;
    const sums = whole ? null : sumsOf(ids, p);
    // location level: no per-class sums reach the browser; only the whole-store entity can be shown.
    if (!whole && !sums) return;
    out[code] = {
      level: 'location', own: whole ? guardNoSales(whole.metrics, whole.sums) : guardNoSales(metricsFromSums(sums as BenchmarkSums, w), sums), sums: whole ? whole.sums : sums, peer: p.peers.vertical,
      status: peerStatusOf(p.peers.vertical), filtered: !whole, peerLabel: `${vertical(p)} · ${label}`,
    };
  });
  return out;
};

/** The client-level value (KPIs, You vs peers): the backend entity, or the passing classes re-summed under a scope filter. */
export const clientBenchmark = (classRows: ClassRow[], p: BenchmarkPayload | null | undefined, filters: ScopeFilters): BenchmarkRowValue | null => {
  if (!canShow(p)) return null;
  const filtered = hasScopeFilter(filters);
  const sums = filtered ? sumsOf(passingClassRows(classRows, filters).map((r) => r.locationClassId as number), p) : null;
  if (filtered && !sums) return null;
  const entity = p.client as NonNullable<BenchmarkPayload['client']>;
  return {
    level: 'client', own: sums ? guardNoSales(metricsFromSums(sums, p.weeksInMonth), sums) : guardNoSales(entity.metrics, entity.sums), peer: p.peers.vertical,
    status: peerStatusOf(p.peers.vertical), filtered, peerLabel: `${vertical(p)} · ${p.monthLabel ?? ''}`,
  };
};

// `toCoverUnit` (weeks of stock in the report's cover unit) lives in benchmarkMetrics.ts, which this module re-exports.

/** A by-class chart's peer marker: the row's peer value (cover in the grain's unit), gated like peerText; null = no marker. */
export const chartPeerFor = (v: BenchmarkRowValue | undefined, metric: BenchmarkMetricId, grain: Grain, weeksInMonth: number): number | null => {
  if (!v || v.status !== 'ok') return null;
  const raw = gatedPeerValue(v.peer, metric);
  return metric === 'weeksOfStock' ? toCoverUnit(raw, grain, weeksInMonth) : raw;
};

/** How a benchmark figure is shown: the report's grain (Monthly cover is months of supply), the cost basis (labels say "retail"). */
export interface BenchmarkView { grain?: Grain; weeksInMonth?: number; basis?: PriceBasis }

/** The metric's label in the view: "Months of stock (MOS)" for Monthly cover, "(retail)" appended at cost. */
export const metricLabelIn = (metric: BenchmarkMetricId, view: BenchmarkView = {}): string =>
  retailTag(isMonthlyCover(metric, view.grain) ? WOS_AS_MOS_LABEL : BENCHMARK_METRIC_BY_ID[metric].label, view.basis);

const inView = (metric: BenchmarkMetricId, v: number | null | undefined, view: BenchmarkView): number | null | undefined =>
  isMonthlyCover(metric, view.grain) && view.weeksInMonth ? toCoverUnit(v, 'monthly', view.weeksInMonth) : v;

/** Your value in the view (Monthly cover ÷ weeks in the month); `exact` prints the real cover, never "52+". */
export const ownIn = (metric: BenchmarkMetricId, v: number | null | undefined, view: BenchmarkView = {}, exact = true): string =>
  formatBenchmark(metric, inView(metric, v, view), exact);

/** The peers' value in the view, words where it is gated (peerText). */
export const peerIn = (peer: BenchmarkPeer | null | undefined, metric: BenchmarkMetricId, view: BenchmarkView = {}, exact = true): string => {
  const raw = gatedPeerValue(peer, metric);
  return raw === null ? peerText(peer, metric) : formatBenchmark(metric, inView(metric, raw, view), exact);
};

/** A benchmark KPI's label: "Weeks of stock vs peers (month-end)", with ", filtered" and, at cost, ", retail" inside the parentheses. */
export const kpiLabel = (base: string, opts: { filtered?: boolean; basis?: PriceBasis }): string =>
  `${base} (month-end${opts.filtered ? ', filtered' : ''}${opts.basis === 'cost' ? ', retail' : ''})`;

// The Needs Attention engine (Plan 3 spec 6 "one engine for statuses and reasons"): turns
// the flat row list the table shows into a ranked list of rows that need a look, each with ONE reason
// line. The dashboards render it through NeedsAttentionList; Ask Indie can read the same items later.
//
// Decisions recorded here (the plan leaves them open):
// - One item per row, carrying its highest-severity reason (precedence below). A row that is both
// understocked and selling below plan reads as understocked; the table still shows the rest.
// - Severity: 4 urgent understocked / out of stock / Review, 3 understocked / overstocked, 2 sales below
// the compare basis / aging, 1 no plan. Within a severity, the RETAIL dollars at stake desc; Review
// (out of stock, no sales in the lookback or the last 52 weeks) has nothing at stake and ranks
// after every urgent reorder, before the understocked rows.
// - `dollars` (shown) is the inventory at stake, per reason: urgent / understocked / overstocked /
// aging / no plan → start inventory on the chosen basis (`inventoryOf`); out of stock →
// avgPeriodSales × the resolved target, the stock that SHOULD be on hand (0 with no target, retail:
// sales are retail); sales below the compare basis → the target period's sales (retail; its Sales
// Trend in a trend period, the value `salesVsCompare` compared).
// - on the cost basis `dollars` is the inventory at cost, or null ("—") where the row has none; the
// retail-only figures (out of stock's should-be stock, sales below compare) are null, and the overstock reason names no
// dollar amount over plan (plan BOM is retail). Ranking stays retail, so the order never changes with the basis.
// - Ranking reads the same figures with inventory always RETAIL (`inventoryRetailOf`, the basis of
// `summary.inventory` that WOS is measured on), so on the cost basis an urgent row and an out of
// stock row still sort on one basis while the dollars shown follow the basis.
// - "No plan" surfaces only when nothing else is wrong with the row (lowest precedence, severity 1).
// - (M1 §5, §6, §8): a PNOTB / NOTB / History / Distress / Warehouse class is never listed (action "—"). An
// understocked / out-of-stock row whose reorder is held back (on order covers it, out of season) is not listed as a stock
// problem; it can still surface for aging or sales. Only a reorder (Reorder, Reorder · no OTB) is urgent. Within
// severity 3, rows rank by dollars away from plan BOM (the worst first: |retail inventory − plan BOM|), else by
// inventory, planned rows (a plan BOM) before unplanned ones; the overstocked reason names the dollars over plan, and the push_orders label ("Delay or cancel orders",
// ) when that is the action.
// - Needs Attention lists actionable statuses only (`ACTIONABLE`: review, out of stock, understocked, overstocked,
// no recent sales, no plan). An On track or Watch class is never listed, whatever its sales vs compare or aging: it carried an
// "On track" pill under "needs attention". The overstock reason names the rule that decided it: the WOS wording when cover is above the band
// ("Overstocked: 12.0 vs 6.0 (2.0× target)"), and when only the depth rule fired (BOM above plan BOM × the multiplier, M1 §6)
// "Overstocked: BOM $X, 2.1× plan BOM" (the WOS pair "31.3 vs 37.2 (0.8× target)" contradicted the status).
// - Callers wrap `needsAttention` in `useMemo` keyed on flatRows, ctx and the thresholds: it walks
// every row and evaluates two columns per row.
// - Status and target follow a WOS override the same way the Health column does (`metricById('health')`
// → `targets.status`); urgency is `targets.isRowUrgent` (re-derived with `isUrgent` under an
// override, else the payload's `urgent`), the same rule as the table's urgent dot.
// - Callers pass `pageModel.totalRows(totalsRows)`: each class once, at its family's
// level, so a parent class is listed and its child classes are not.
// - Subtotal, Unallocated, Unclassified and SKU-placeholder rows are never listed; a row with no
// summary or no status (future period, not yet loaded) is skipped.
// - `salesBelowThreshold` (default 0.2) and `agingThreshold` (default 0.5) are caller options: Plan 4
// exposes them as user-visible settings, they are not constants.

export type AttentionSeverity = 1 | 2 | 3 | 4;

export interface AttentionItem {
  /** The FlatRow's `id` (unique per table), not `row.key` (SKU keys repeat across parents). */
  key: string;
  label: string;
  severity: AttentionSeverity;
  reason: string;
  status: Status;
  /** The dollars shown, on the basis; null on the cost basis where there is no cost figure. */
  dollars: number | null;
  row: AnyRow;
}

export interface NeedsAttentionOptions {
  /** Sales vs the compare basis at or below −threshold is a reason (fraction; default 0.2 = 20%). */
  salesBelowThreshold?: number;
  /** Share of on-hand dollars older than 12 weeks at or above this is a reason (fraction; default 0.5). */
  agingThreshold?: number;
  /** Keep only the first `limit` items after sorting. */
  limit?: number;
}

export const DEFAULT_SALES_BELOW_THRESHOLD = 0.2;
export const DEFAULT_AGING_THRESHOLD = 0.5;

/** The statuses a row can be listed under (see the header); On track and the Watch statuses never are. */
const ACTIONABLE: Status[] = ['review', 'out_of_stock', 'understocked', 'overstocked', 'no_recent_sales', 'no_plan'];

const EXCLUDED_KINDS: FlatRowKind[] = ['subtotal', 'unallocated', 'unclassified', 'skuPlaceholder'];
const COMPARE_LABEL: Record<Compare, string> = { plan: 'plan', ly: 'LY', avg: 'avg' };

const nonNeg = (v: number | null | undefined): number => (isNum(v) && v > 0 ? v : 0);

/** `rank` is `dollars` with inventory read retail (see the header); it orders items and is not shown. */
interface Found { severity: AttentionSeverity; reason: string; dollars: number | null; rank: number }

const coverUnitOf = (ctx: CellContext): string => coverUnit(ctx.grain);

/** The one reason for a row, by precedence; null when the row is fine. */
const HELD_BACK: ActionCode[] = ['on_order_covers', 'out_of_season'];

const findReason = (row: AnyRow, status: Status, action: ActionCode | null, ctx: CellContext, opts: Required<Omit<NeedsAttentionOptions, 'limit'>>): Found | null => {
  const s = row.summary!;
  const cost = ctx.basis === 'cost';
  const own = inventoryOf(row, ctx); // null at cost without a cost inventory
  const inventory = isNum(own) || !cost ? nonNeg(own) : null;
  const retailOnly = (v: number): number | null => (cost ? null : v);
  const retail = inventoryRetailOf(row, ctx);
  const gate = gateOf(row, ctx.targetPeriod);
  const planBom = gate.planBom;
  // the worst first by dollars away from plan BOM (M1 §6; an aggregate's planned members only), else the inventory at stake.
  const bomInv = gate.bomInventory ?? retail;
  const vsPlan = isNum(planBom) && planBom > 0 && isNum(bomInv) ? bomInv - planBom : null;
  const atStake = { dollars: inventory, rank: vsPlan !== null ? Math.abs(vsPlan) : nonNeg(retail) };
  const target = resolveTarget(s, ctx.wosOverride).target;
  const unit = coverUnitOf(ctx);
  const cap = coverCap(ctx.grain); // "52+" in Weekly, "12+" in Monthly
  const noOtb = action === 'reorder_no_otb' ? ' · no OTB' : '';
  const heldBack = action !== null && HELD_BACK.includes(action);

  if (status === 'review') return { severity: 4, reason: unitize('Review: out of stock, no sales in 52 weeks', ctx.grain), dollars: 0, rank: -1 };
  if (status === 'out_of_stock' && !heldBack) {
    const should = isNum(target) ? nonNeg(s.avgPeriodSales) * nonNeg(target) : 0;
    return { severity: 4, reason: `Out of stock${noOtb}`, dollars: retailOnly(should), rank: should };
  }
  if (status === 'understocked' && !heldBack) {
    const urgent = isRowUrgent(row, ctx);
    const cover = `${fmtWeeks(s.wos, cap)} ${unit} vs ${fmtWeeks(target, cap)} target`;
    return urgent
      ? { severity: 4, reason: `Urgent: ${cover}, on order won't cover${noOtb}`, ...atStake }
      : { severity: 3, reason: `Understocked: ${cover}${noOtb}`, ...atStake };
  }
  if (status === 'overstocked') {
    const noSales = s.wos === null;
    // The depth rule decided (BOM above plan BOM × the multiplier) while the cover is not above the band: say so, never a WOS pair that reads fine.
    const hasBom = isNum(planBom) && planBom > 0 && isNum(bomInv);
    const wosAbove = !noSales && isNum(target) && target > 0 && s.wos! > target * ctx.multiplier;
    if (hasBom && !wosAbove && !noSales && isNum(target) && target > 0 && bomInv > planBom * ctx.multiplier) {
      const times = `${(bomInv / planBom).toFixed(1)}× plan BOM`;
      const bom = isNum(inventory) ? `BOM ${fmtCurrency(inventory)}, ` : '';
      const overDepth = vsPlan !== null && vsPlan > 0 ? (cost ? ' · over plan' : ` · ${fmtCurrency(vsPlan)} over plan`) : '';
      const pushDepth = action === 'push_orders' ? ` · ${ACTION_LABELS.push_orders}` : '';
      return { severity: 3, reason: `Overstocked: ${bom}${times}${overDepth}${pushDepth}`, ...atStake };
    }
    const multiple = noSales ? 'no sales' : `${isNum(target) && target > 0 ? (s.wos! / target).toFixed(1) : EMPTY}× target`;
    const cover = noSales ? `${cap}+` : fmtWeeks(s.wos, cap);
    const over = vsPlan !== null && vsPlan > 0 ? (cost ? ' · over plan' : ` · ${fmtCurrency(vsPlan)} over plan`) : '';
    const what = action === 'push_orders' ? ` · ${ACTION_LABELS.push_orders}` : ''; // Sell down is the overstock default
    return { severity: 3, reason: `Overstocked: ${cover} ${unit} vs ${fmtWeeks(target, cap)} (${multiple})${over}${what}`, ...atStake };
  }

  if (status === 'no_recent_sales') {
    // Never urgent (no reorder). Above plan BOM (action Review / clear) it ranks with overstock; at or below it (Hold) as a lesser look.
    const clear = action === 'review_clear';
    const over = vsPlan !== null && vsPlan > 0 ? (cost ? ' · over plan' : ` · ${fmtCurrency(vsPlan)} over plan`) : '';
    const onHand = isNum(inventory) ? ` · ${fmtCurrency(inventory)} on hand` : '';
    return { severity: clear ? 3 : 2, reason: `No recent sales${onHand}${over}${clear ? ` · ${ACTION_LABELS.review_clear}` : ''}`, ...atStake };
  }

  const aging = isVendorRow(row) && row.freshness ? row.freshness.aging12wPct : null;
  if (isNum(aging) && aging >= opts.agingThreshold) {
    return { severity: 2, reason: `${fmtPct(aging)} of stock older than 12 weeks`, ...atStake };
  }
  const vs = metricById('salesVsCompare').cell(row, ctx).value;
  if (isNum(vs) && vs <= -opts.salesBelowThreshold) {
    const sales = comparableSales(row.periods[String(ctx.targetPeriod)], ctx.trendMethod); // Sales Trend in a trend period, as the vs-compare value
    return { severity: 2, reason: `Sales ${fmtPct(Math.abs(vs))} below ${COMPARE_LABEL[ctx.compare]}`, dollars: retailOnly(nonNeg(sales)), rank: nonNeg(sales) };
  }
  if (status === 'no_plan') return { severity: 1, reason: 'No plan', ...atStake };
  return null;
};

/** Rows that need a look, one item each, severity desc then retail dollars at stake desc (see the header). */
export const needsAttention = (flatRows: FlatRow[], ctx: CellContext, opts: NeedsAttentionOptions = {}): AttentionItem[] => {
  const resolved = {
    salesBelowThreshold: opts.salesBelowThreshold ?? DEFAULT_SALES_BELOW_THRESHOLD,
    agingThreshold: opts.agingThreshold ?? DEFAULT_AGING_THRESHOLD,
  };
  const ranked: { item: AttentionItem; rank: number; planned: boolean }[] = [];
  flatRows.forEach((f) => {
    if (EXCLUDED_KINDS.includes(f.kind) || !f.row.summary) return;
    const status = metricById('health').cell(f.row, ctx).value as Status | null;
    if (!status) return;
    const action = rowAction(f.row, ctx);
    if (action === 'class_type') return; // M1 §8: never a candidate for anything
    if (!ACTIONABLE.includes(status)) return; // QA fix 6: On track / Watch are never listed
    const found = findReason(f.row, status, action, ctx, resolved);
    if (!found) return;
    const { rank, ...shown } = found;
    // review: within a severity, rows judged against a plan BOM rank before unplanned ones.
    const g = gateOf(f.row, ctx.targetPeriod);
    ranked.push({ item: { key: f.id, label: metricById('label').cell(f.row, ctx).text, status, row: f.row, ...shown, reason: unitize(shown.reason, ctx.grain) }, rank, planned: isNum(g.planBom) && g.planBom > 0 });
  });
  ranked.sort((a, b) => b.item.severity - a.item.severity || Number(b.planned) - Number(a.planned) || b.rank - a.rank);
  const items = ranked.map((r) => r.item);
  return typeof opts.limit === 'number' ? items.slice(0, Math.max(0, opts.limit)) : items;
};

// Pure helpers the Plan 4 report configs build their KPI tiles, dashboard charts and filter option
// lists on (Plan 4 ).
//
// Callers (hook) MUST pass the expandAll tree — `buildRowTree({...input, expandAll: true})`
// — to `kpiTotals`, `weightedCover`, `salesTrend`, `chartRowsByClass` and `needsAttention`, never the
// table's own flat rows: KPIs and attention items then follow the filters and the search but never
// change when the user expands or collapses a row, and a filtered group contributes only the
// children that passed (its own payload totals and status are not counted).
//
// Decisions recorded here (the plan leaves them open):
// - `totalRows` counts every class exactly once, at its family's analysis
// level: the highest row on each path that carries a class summary. A class with child classes counts as
// one row (the payload's parent already rolls its children and own-stamped values in:
// ReportAssembler::rollUpHierarchy, PlanService::rollUpParents), whether expanded or not. Note the plan:
// `rollUpParents` gives a parent Σ children only when its own plan is all zeros; when any own plan measure
// is non-zero the parent's own plan stands (children's plans are not added). Its children
// and its Unallocated row never count again. Rows that are not classes are descended through and never
// counted: Category / Location subtotals, vendor groups (Vendor by Class counts its vendor × class rows),
// and a class group the filters or the search kept only as the ancestor of rows that passed
// (`ancestorOnly`) or pruned (`filteredChildren`), whose passing rows count instead. A row with nothing
// listed below it (a collapsed subtotal) counts itself. SKU attachments never count: a class showing SKU
// rows (or their placeholder) counts itself, so loading SKUs never changes a KPI, chart or Needs Attention
// (SKU rows carry no OTB and would make counts per SKU). Unallocated and Unclassified rows reached on
// their own (Unclassified at the root; an Unallocated row under a filtered parent or a vendor) are
// included in the sums but not in `rows`, `byStatus`, `urgent` or the per-class helpers.
// - `salesTrend` (spec 4.5, decided 2026-10-18) compares over the LOOKBACK and ignores Compare: a
// class with a sales plan in every lookback period compares Σ lookback sales with Σ lookback plan;
// any other class compares its target-period sales with its own lookback average.
// - Sales Trend: when a class's target cell is a trend
// cell, the planned branch's window is the lookback PLUS the target period (a plan required in every
// period of it), valued with `comparableSales` (Sales Trend in a trend period, actual elsewhere); the
// unplanned branch compares the target's Sales Trend with the own lookback ACTUAL average. The
// result also carries `actual` — the same counts on actual sales over the lookback alone (the
// pre-trend rule: "Actual so far") — and `trendPeriod` (some counted row's target cell trends).
// `kpiTotals.sales` stays the actual target-period sales; `salesTrend` is Σ Sales Trend of the
// counted trend cells and `trendPct` their aggregate Trend % (Σ sales ÷ Σ Sales Trend). The
// `salesVariance` chart compares `comparableSales` too. Every trend value is read through
// `trendOf` / `comparableSales` with `ctx.trendMethod`.
// - Status and urgency follow a WOS override exactly as the Health column and `needsAttention` do
// (`metricById('health')`, `isRowUrgent`).
// - `weightedCover` reads the same `totalRows` set as `kpiTotals` and divides Σ `summary.inventory`
// (the figure `summary.wos` is measured on) by Σ `avgPeriodSales` over rows with known inventory
// and an average; the target is Σ plan inventory ÷ Σ plan sales at the target period over rows
// with both (the subtotal rule in `makeSubtotal`),
// or the WOS override when one is set.
// - `chartRowsByClass` names are the row label; in Location › Category › Class " — <location>" and in
// Vendor by Class / Class by Vendor " — <vendor>" are appended so one class in several locations / vendors gives
// distinct bars. Measures: `cover` = `summary.wos` capped at WEEKS_CAP (stock with no sales is
// WEEKS_CAP too, so a bar can be drawn and one slow class does not flatten the axis) against the
// resolved target; `otb` = `summary.otb` against `inflowPlanSpan`;
// `salesVariance` = target-period sales − the compare value (dollars) against the compare value;
// `fresh` = `freshness.freshPctSku` on vendor and SKU rows that carry freshness, else `classFreshPct`
// (as the Fresh % column); `aging12` = `freshness.aging12wPct` (class rows carry it too, rolled up from their SKUs
// in Inventory Health: ), null without freshness.
// - (P1-11): every dollar total reads `basis.ts` (the columns' rule): on the cost basis a total is the Σ of the
// cost figures, and null with its key in `notAtCost` as soon as one counted row has the retail figure but not its cost (no
// partial cost totals, never retail in a cost total). Counts and statuses do not change with the basis. `salesRetail` keeps
// the retail sales for the Sales Trend tooltip, which compares retail on every basis. Charts: `otb` and its plan follow the
// basis too.
// - `agingShown` is the one aging rule (Inventory Health's KPI, dashboard card and row detail): an
// A/B client with SKU-level data and receiving, and the Freshness switch on.
// - `optionsFromRows`: one option per distinct code with the number of payload rows carrying it;
// class labels read "code · name"; location labels the location name (the code when unnamed);
// vendor counts are the vendor's vendorClass rows (the classes it supplies), 1 for a vendor with
// none. Unallocated / Unclassified rows and null or empty codes are skipped.

const EPSILON_PAGE_MODEL = 1e-9;
const NOT_A_CLASS: FlatRowKind[] = ['unallocated', 'unclassified', 'skuPlaceholder'];
const STATUSES: Status[] = ['on_track', 'watch_low', 'watch_high', 'overstocked', 'understocked', 'out_of_stock', 'review', 'no_recent_sales', 'no_plan'];
const ACTIONS = Object.keys(ACTION_LABELS) as ActionCode[];

/** Sum skipping nulls; null when every member is null (or there are none). */
const sumOf = (values: (number | null | undefined)[]): number | null => {
  let sum = 0;
  let any = false;
  values.forEach((v) => {
    if (isNum(v)) {
      sum += v;
      any = true;
    }
  });
  return any ? sum : null;
};

/** A SKU row's parent key (rowTree's SKU_PARENT_RE_PAGE_MODEL): what the SKU-level Unallocated row hangs under. */
const SKU_PARENT_RE_PAGE_MODEL = /^(class:\d+|vendor:[^:]+(:class:\d+)?)$/;
/** Rows attached under a leaf by Show SKUs: SKU rows, the placeholder, and the SKU-level Unallocated row. */
const isSkuAttachment = (f: FlatRow): boolean =>
  f.kind === 'sku' || f.kind === 'skuPlaceholder' || (f.kind === 'unallocated' && isVendorRow(f.row) && SKU_PARENT_RE_PAGE_MODEL.test(f.row.parentKey ?? ''));

/**
 * A row totals descend through instead of counting: a synthetic Category / Location subtotal, a vendor group
 * (Vendor by Class), and a class group the filters or the search pruned or kept only as the ancestor of rows
 * that passed (it keeps the payload's family totals, but the rows that passed are what the user asked for).
 */
// `filteredChildren` is defensive: on the totals trees a pruned class group is always `ancestorOnly` too; only Class
// by Vendor's display tree under a Vendor filter prunes a group that passed, and totals never read that tree.
// a class group shown only because the search matched a child (`searchShown`, it passed the filters) is counted
// itself, at its full-family figures, as its row displays them; a filter-only context group still counts what passed.
const descendsInto = (f: FlatRow): boolean =>
  f.kind === 'subtotal' || (f.kind === 'group' && ((!f.searchShown && (f.filteredChildren || !!f.ancestorOnly)) || (isVendorRow(f.row) && f.row.level === 'vendor')));

/**
 * The rows totals count: each class exactly once, at its family's analysis level (M1 §8: the plan lives at
 * the parent and children roll up into it). Depth-first, the first row on a path that carries a class
 * summary counts and everything below it is skipped (its children, its Unallocated row, SKU attachments);
 * subtotals, vendor groups and filtered class groups are descended through (see `descendsInto`). A row with
 * nothing listed below it (a collapsed subtotal) counts itself. SKU attachments never count.
 */
export function totalRows(flatRows: FlatRow[]): FlatRow[] {
  const out: FlatRow[] = [];
  let skipBelow: number | null = null;
  flatRows.forEach((f, i) => {
    if (skipBelow !== null) {
      if (f.depth > skipBelow) return;
      skipBelow = null;
    }
    if (isSkuAttachment(f)) return;
    // Flat rows are depth-first: this row's descendants are the following rows deeper than it.
    const hasDescendant = i + 1 < flatRows.length && flatRows[i + 1].depth > f.depth;
    if (hasDescendant && descendsInto(f)) return;
    out.push(f);
    skipBelow = f.depth;
  });
  return out;
}

const classRowsOf = (flatRows: FlatRow[]): FlatRow[] => totalRows(flatRows).filter((f) => !NOT_A_CLASS.includes(f.kind));

/**
 * Σ over totalRows of: target-period sales, salesPlan, inflow, inflowPlan, start inventory (ctx basis), inventoryPlan, onOrder, otb, otbDev; count by status; urgent count.
 * `inventory` is on the displayed basis while plans are retail: never compare it with `inventoryPlan`
 * (a vs-plan figure sums `inventoryRetailOf` over the planned rows, as Inventory Health's KPI does).
 */
export interface KpiTotals {
  sales: number | null; salesPlan: number | null; inflow: number | null; inflowPlan: number | null;
  inventory: number | null; inventoryPlan: number | null; onOrder: number | null; otb: number | null;
  /** Σ OTB + Dev over the counted rows, and how many counted classes are PNOTB / NOTB (no OTB, left out of both). */
  otbDev: number | null; otbExcluded: number;
  /**
   * review: the OTB's own inputs over the counted rows that HAVE an OTB (summary.inflowPlanSpan, receivedToDate,
   * summary.otbOnOrder, else summary.onOrder; retail): otbInflowPlan − otbReceived − otbOnOrder = otb on either grain.
   */
  otbInflowPlan: number | null; otbReceived: number | null; otbOnOrder: number | null;
  /**
   * the on order (on the basis) of the counted classes that have NO OTB (no plan, PNOTB / NOTB). It is in no
   * OTB input above and shown once beside them ("+ $X on order on classes without a plan"); null / 0 when there is none.
   */
  onOrderNoOtb: number | null;
  /** QA: the earliest week a PARTIAL weekly plan covers among the counted rows with an OTB (summary.otbCoveredThrough), else null: Weekly OTB then covers less than the span. */
  otbCoveredThrough: number | null;
  /** item 7: each such row (its label and its last planned week), earliest first: the OTB tooltip and coverage note list them. */
  otbPartial: { label: string; through: number }[];
  /**
   * Σ OTB by month over the counted rows that have an OTB (totalRows, the Total OTB rule): Σ otb = otb and the last
   * running = otb. Retail; null when a counted row with an OTB has no lines, or at cost (no per-month figure at cost).
   */
  otbByPeriod: OtbPeriodLine[] | null;
  byStatus: Record<Status, number>; urgent: number; rows: number;
  /** count by action (targets.ts rowAction), and the reorders (Reorder + Reorder · no OTB, not the suppressed ones). */
  byAction: Record<ActionCode, number>; reorder: number;
  /** review: byStatus without the no-action class types (PNOTB, NOTB, History, Distress, Warehouse). */
  byStatusActionable: Record<Status, number>;
  /** `rows` without the no-action class types: the "of N" for byStatusActionable. */
  rowsActionable: number;
  /** Σ Sales Trend of the counted trend cells (null outside a trend period) and their aggregate Trend %. */
  salesTrend: number | null; trendPct: number | null;
  /** Σ target-period sales at retail on every basis (the Sales Trend tooltip compares retail). */
  salesRetail: number | null;
  /** the dollar totals that are not available on the cost basis (some counted row has no cost figure); empty at retail. */
  notAtCost: KpiDollarKey[];
}
/** The dollar totals of KpiTotals (each read on the basis). */
export type KpiDollarKey = 'sales' | 'salesPlan' | 'inflow' | 'inflowPlan' | 'inventory' | 'inventoryPlan' | 'onOrder' | 'otb' | 'otbDev' | 'otbInflowPlan' | 'otbReceived' | 'otbOnOrder';

const kpiTrend = (cells: (Cell | undefined)[], ctx: CellContext): Pick<KpiTotals, 'salesTrend' | 'trendPct'> => {
  // The method's values as plain cells, so the aggregate rule (Σ sales ÷ Σ Sales Trend) applies to them.
  const trended = cells.filter((c): c is Cell => isTrendCell(c)).map((c) => ({ ...c, ...trendOf(c, ctx.trendMethod), trendAlt: undefined }));
  if (trended.length === 0) return { salesTrend: null, trendPct: null };
  const agg = aggregateTrend(sumOf(trended.map((c) => c.sales)), trended);
  return { salesTrend: agg.salesTrend ?? null, trendPct: agg.trendPct ?? null };
};

export function kpiTotals(flatRows: FlatRow[], ctx: CellContext): KpiTotals {
  const counted = totalRows(flatRows);
  const cells = counted.map((f) => f.row.periods[String(ctx.targetPeriod)]);
  const byStatus = Object.fromEntries(STATUSES.map((s) => [s, 0])) as Record<Status, number>;
  const byAction = Object.fromEntries(ACTIONS.map((a) => [a, 0])) as Record<ActionCode, number>;
  const byStatusActionable = Object.fromEntries(STATUSES.map((s) => [s, 0])) as Record<Status, number>;
  let urgent = 0;
  let rows = 0;
  let rowsActionable = 0;
  let otbExcluded = 0;
  counted.forEach((f) => {
    if (NOT_A_CLASS.includes(f.kind)) return;
    rows += 1;
    if (!f.row.summary) return;
    if (f.row.summary.otbExcluded) otbExcluded += 1;
    const status = metricById('health').cell(f.row, ctx).value as Status | null;
    if (status) byStatus[status] += 1;
    const a = rowAction(f.row, ctx);
    if (a) byAction[a] += 1;
    if (status && a !== 'class_type') byStatusActionable[status] += 1;
    if (a !== 'class_type') rowsActionable += 1;
    if (isRowUrgent(f.row, ctx)) urgent += 1;
  });
  // The rows behind Total OTB (a retail OTB): their OTB inputs on the basis reconcile with it (− received − on order).
  const withOtb = counted.filter((f) => isNum(f.row.summary?.otb));
  const partialRows = withOtb.filter((f) => f.row.summary?.otbBasis === 'weekly_plan_partial' && isNum(f.row.summary.otbCoveredThrough));
  const partialCover = partialRows.map((f) => f.row.summary!.otbCoveredThrough as number);
  const otbPartial = partialRows
    .map((f) => ({ label: metricById('label').cell(f.row, ctx).text, through: f.row.summary!.otbCoveredThrough as number }))
    .sort((a, b) => a.through - b.through || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));
  const noOtbClasses = counted.filter((f) => !NOT_A_CLASS.includes(f.kind) && f.row.summary && !isNum(f.row.summary.otb));
  // Plus the on order a merged row (chain / subtotal) holds for its members without an OTB: its own OTB nets only the rest.
  const noOtbOnOrder = sumOf([
    ...noOtbClasses.map((f) => summaryOnBasis(f.row, 'onOrder', ctx.basis)),
    ...withOtb.map((f) => summaryOnBasis(f.row, 'onOrderNoOtb', ctx.basis)),
  ].filter((v) => v.available).map((v) => v.value));
  const notAtCost: KpiDollarKey[] = [];
  const total = (key: KpiDollarKey, values: BasisValue[]): number | null => {
    const t = costTotal(values);
    if (!t.available) notAtCost.push(key);
    return t.value;
  };
  const cellTotal = (key: KpiDollarKey, field: CellBasisField) => total(key, cells.map((c) => valueOnBasis(c, field, ctx.basis)));
  const summaryTotal = (key: KpiDollarKey, field: SummaryBasisField, rows: FlatRow[] = counted) => total(key, rows.map((f) => summaryOnBasis(f.row, field, ctx.basis)));
  return {
    sales: cellTotal('sales', 'sales'),
    salesRetail: sumOf(cells.map((c) => c?.sales)),
    ...kpiTrend(cells, ctx),
    salesPlan: cellTotal('salesPlan', 'salesPlan'),
    inflow: cellTotal('inflow', 'inflow'),
    inflowPlan: cellTotal('inflowPlan', 'inflowPlan'),
    inventory: total('inventory', counted.map((f) => inventoryOnBasis(f.row, ctx))),
    inventoryPlan: cellTotal('inventoryPlan', 'inventoryPlan'),
    onOrder: summaryTotal('onOrder', 'onOrder'),
    otb: summaryTotal('otb', 'otb'),
    otbDev: summaryTotal('otbDev', 'otbDev'),
    otbExcluded,
    otbInflowPlan: summaryTotal('otbInflowPlan', 'inflowPlanSpan', withOtb),
    otbReceived: summaryTotal('otbReceived', 'otbReceived', withOtb),
    otbOnOrder: summaryTotal('otbOnOrder', 'otbOnOrder', withOtb),
    onOrderNoOtb: noOtbOnOrder,
    otbCoveredThrough: partialCover.length > 0 ? Math.min(...partialCover) : null,
    otbPartial,
    otbByPeriod: ctx.basis === 'cost' ? null : sumOtbLines(withOtb.map((f) => f.row.summary)),
    notAtCost,
    byStatus,
    byAction,
    reorder: byAction.reorder + byAction.reorder_no_otb,
    byStatusActionable,
    rowsActionable,
    urgent,
    rows,
  };
}

/** Weighted cover: Σinventory ÷ Σ avgPeriodSales; weighted target: Σ plan inventory ÷ Σ plan sales at the target period. */
export function weightedCover(flatRows: FlatRow[], ctx: CellContext): { cover: number | null; target: number | null } {
  let inv = 0;
  let avg = 0;
  let planInv = 0;
  let planSales = 0;
  totalRows(flatRows).forEach((f) => {
    const s = f.row.summary;
    if (s && s.inventoryKnown && isNum(s.inventory) && isNum(s.avgPeriodSales)) {
      inv += s.inventory;
      avg += s.avgPeriodSales;
    }
    const c = f.row.periods[String(ctx.targetPeriod)];
    if (c && isNum(c.inventoryPlan) && isNum(c.salesPlan)) {
      planInv += c.inventoryPlan;
      planSales += c.salesPlan;
    }
  });
  const override = typeof ctx.wosOverride === 'number' && ctx.wosOverride > 0 ? ctx.wosOverride : null;
  return {
    cover: avg > EPSILON_PAGE_MODEL && inv >= 0 ? inv / avg : null,
    target: override ?? (planSales > EPSILON_PAGE_MODEL && planInv > EPSILON_PAGE_MODEL ? planInv / planSales : null),
  };
}

const compareOf = (f: FlatRow, ctx: CellContext): { sales: number | null; base: number | null } => {
  const c = f.row.periods[String(ctx.targetPeriod)];
  const own = comparableSales(c, ctx.trendMethod); // Sales Trend in a trend period
  if (!c || !isNum(own)) return { sales: null, base: null };
  const base = compareValue(c, 'sales', ctx.compare, { periods: f.row.periods, lookback: ctx.lookback });
  return { sales: own, base: isNum(base) ? base : null };
};

export interface SalesTrendCounts { above: number; below: number }
/** The Sales Trend KPI: counts valued with Sales Trend, the same on actual sales so far, and whether a trend period is in play. */
export interface SalesTrendKpi extends SalesTrendCounts { actual: SalesTrendCounts; trendPeriod: boolean }

/** One class's value and base for the Sales Trend KPI (see the header); `useTrend` false = the actual-sales rule. */
const trendComparison = (periods: Record<string, Cell>, ctx: CellContext, useTrend: boolean): { value: number | null; base: number | null } => {
  const target = periods[String(ctx.targetPeriod)];
  const trendTarget = useTrend && isTrendCell(target);
  const win = trendTarget && !ctx.lookback.includes(ctx.targetPeriod) ? [...ctx.lookback, ctx.targetPeriod] : ctx.lookback;
  const salesOf = (c: Cell | undefined): number | null => (useTrend ? comparableSales(c, ctx.trendMethod) : c && isNum(c.sales) ? c.sales : null);
  const planned = win.length > 0 && win.every((p) => isNum(periods[String(p)]?.salesPlan));
  if (planned) return { value: sumOf(win.map((p) => salesOf(periods[String(p)]))), base: sumCells(periods, 'salesPlan', win) };
  return { value: salesOf(target), base: avgOverPeriods(periods, 'sales', ctx.lookback) };
};

/**
 * Classes above vs below (spec 4.5 + ): Σ sales vs Σ plan when every period of the window has a
 * plan, else target-period sales vs the own lookback average; in a trend period the target counts at
 * Sales Trend (see the header). Compare is ignored.
 */
export function salesTrend(flatRows: FlatRow[], ctx: CellContext): SalesTrendKpi {
  const counts = { above: 0, below: 0 };
  const actual = { above: 0, below: 0 };
  let trendPeriod = false;
  const tally = (into: SalesTrendCounts, { value, base }: { value: number | null; base: number | null }) => {
    if (!isNum(value) || !isNum(base)) return;
    if (value > base) into.above += 1;
    else if (value < base) into.below += 1;
  };
  classRowsOf(flatRows).forEach((f) => {
    const { periods } = f.row;
    if (isTrendCell(periods[String(ctx.targetPeriod)])) trendPeriod = true;
    tally(counts, trendComparison(periods, ctx, true));
    tally(actual, trendComparison(periods, ctx, false));
  });
  return { ...counts, actual, trendPeriod };
}

export type ChartMeasure = 'cover' | 'otb' | 'salesVariance' | 'aging12' | 'fresh';
/** A type alias, not an interface, so it is assignable to ChartCard's `ChartDatum` (a Record). */
export type ClassChartDatum = { name: string; value: number | null; plan?: number | null };

const datumFor = (f: FlatRow, ctx: CellContext, measure: ChartMeasure): Omit<ClassChartDatum, 'name'> => {
  const { row } = f;
  const s = row.summary;
  switch (measure) {
    case 'cover': {
      const target = resolveTarget(s, ctx.wosOverride).target;
      if (!s) return { value: null, plan: target };
      const noSales = s.wos === null && s.inventoryKnown && isNum(s.inventory) && s.inventory > 0;
      return { value: noSales ? WEEKS_CAP : isNum(s.wos) ? Math.min(s.wos, WEEKS_CAP) : null, plan: target };
    }
    case 'otb': {
      // on the basis; a row without its cost figure has no bar (never a retail bar in a cost chart).
      const otb = summaryOnBasis(row, 'otb', ctx.basis);
      const plan = summaryOnBasis(row, 'inflowPlanSpan', ctx.basis);
      return { value: otb.value, plan: plan.value };
    }
    case 'salesVariance': {
      const { sales, base } = compareOf(f, ctx);
      return { value: sales !== null && base !== null ? sales - base : null, plan: base };
    }
    case 'fresh':
      // SKU-level freshness where the row carries it, else the class formula the Fresh % column uses.
      return { value: isVendorRow(row) && row.freshness ? row.freshness.freshPctSku : classFreshPct(row.periods, ctx.lookback, ctx.currentPeriod ?? ctx.targetPeriod) };
    case 'aging12':
      return { value: row.freshness?.aging12wPct ?? null }; // class rows carry it too
    default:
      return { value: null };
  }
};

/** One datum per row for charts: {name: label, value: measure, plan: plan measure}. */
const chartName = (f: FlatRow, ctx: CellContext, view: HierarchyView | undefined): string => {
  const label = metricById('label').cell(f.row, ctx).text;
  const { row } = f;
  // A vendor × class row: "class — vendor" in Vendor by Class; in Class by Vendor it is labelled by its vendor
  // (`labelAs`), so the class is the suffix instead.
  const suffix = view === 'locationCategoryClass'
    ? row.locationName ?? row.locationCode
    : (view === 'vendorByClass' || view === 'classByVendor' || view === 'classByVendorProduct') && isVendorRow(row) && row.level !== 'vendor'
      ? (row.labelAs === 'vendor' ? metricById('label').cell({ ...row, labelAs: undefined }, ctx).text : fixMojibake(row.vendor))
      : null;
  return suffix ? `${label} — ${suffix}` : label;
};

/** One datum per row for charts: {name: label (+ location / vendor in those views), value: measure, plan: plan measure}. */
export function chartRowsByClass(flatRows: FlatRow[], ctx: CellContext, measure: ChartMeasure, view?: HierarchyView): ClassChartDatum[] {
  return classRowsOf(flatRows).map((f) => ({ name: chartName(f, ctx, view), ...datumFor(f, ctx, measure) }));
}

/** chartRowsByClass plus `peer` (the class's G3 value, cover in the grain's unit) and `peerNote`; class level only. */
export type ClassChartDatumWithPeer = ClassChartDatum & { peer: number | null; peerNote: string | null };

/**
 * the same rows as chartRowsByClass with the row's G3 peer value for `metric` (a "marker" series) and a
 * tooltip note ("8 peers · Tops · Sep 2026"). `peer` is null (no marker) without benchmarks, below class level,
 * for a row without an entry and for an unpublishable grain or gated inflow metric (`chartPeerFor`).
 */
export function chartRowsByClassWithPeers(flatRows: FlatRow[], ctx: CellContext, measure: ChartMeasure, view: HierarchyView | undefined, metric: BenchmarkMetricId): ClassChartDatumWithPeer[] {
  const b = ctx.benchmarks;
  const month = benchmarkMonthEnd(b);
  const classFirst = peerMarkersShownIn(view);
  return classRowsOf(flatRows).map((f) => {
    const v = b && b.level === 'class' && classFirst ? b.byRowKey[f.row.key] : undefined;
    const peer = b && v ? chartPeerFor(v, metric, ctx.grain, b.weeksInMonth) : null;
    return {
      name: chartName(f, ctx, view), ...datumFor(f, ctx, measure), peer,
      peerNote: peer === null || !v ? null : `${v.masterClass ?? 'no master class'} · ${month}`, // no peer counts on screen
    };
  });
}

/**
 * the by-class charts carry the G3 "Peers" marker in the views whose rows are classes. The vendor and product
 * views (Vendor by Class, Total by Vendor, Vendor by Product, Total / Category by Product) list vendors or products, which have no
 * benchmark, so they get no marker (and no legend entry).
 */
const NO_PEER_MARKER_VIEWS: readonly HierarchyView[] = ['vendorByClass', 'totalByVendor', 'vendorByProduct', 'totalByProduct', 'categoryByProduct'];
export const peerMarkersShownIn = (view: HierarchyView | undefined): boolean => !view || !NO_PEER_MARKER_VIEWS.includes(view);

/** The chart's series plus the red "Peers (month-end, Sep 2026)" marker: class level only, in class-first views (`peerMarkersShownIn`). */
export const withPeers = (series: ChartSeries[], ctx: CellContext, view: HierarchyView | undefined): ChartSeries[] => {
  const b = ctx.benchmarks;
  if (!b || b.level !== 'class' || !peerMarkersShownIn(view)) return series;
  const fallback = isFallbackMonth(b) ? ', latest complete month' : '';
  return [...series, { key: 'peer', label: `Peers (month-end, ${benchmarkMonth(b)}${fallback})`, kind: 'marker', color: '#F04438' }];
};

export const NO_CLASS_PEERS_NOTE = "No class-level peers: classes aren't mapped to master classes";

/**
 * The chart note that explains an empty marker series: the client has class-level benchmarks but none of its classes is
 * mapped to a master class, so no class has a G3 peer group (every entry's status is noMasterClass). Null when any class has a
 * peer group (a missing marker there is its own gate: unpublishable, mixed) or the view draws no markers.
 */
export const peerMarkerNote = (ctx: CellContext, view: HierarchyView | undefined): string | null => {
  const b = ctx.benchmarks;
  if (!b || b.level !== 'class' || !peerMarkersShownIn(view)) return null;
  const entries = Object.entries(b.byRowKey).filter(([, v]) => v.level === 'class');
  return entries.length > 0 && entries.every(([, v]) => v.status === 'noMasterClass') ? NO_CLASS_PEERS_NOTE : null;
};

/**
 * The client-level benchmark KPIs' own value from the rows the report shows (totalRows of the filtered tree), so a benchmark
 * KPI and its neighbours count the same classes: the row entries' payload sums added and divided once. Falls back to the
 * client entity (`scope: 'client'`: the hook's scope-filter value) when a counted row has no entry with sums (a vendor or
 * product row, a category subtotal, a location-level payload). The peer, status and labels are the client's.
 */
export const benchmarkForRows = (flatRows: FlatRow[], ctx: CellContext): { value: BenchmarkRowValue; scope: 'rows' | 'client' } | null => {
  const b = ctx.benchmarks;
  const c = b?.client;
  if (!b || !c) return null;
  const counted = classRowsOf(flatRows);
  // A class or chain row the benchmark payload has no entry for sold and stocked nothing in the month: it adds nothing. Any other row
  // without sums (vendor, product, category subtotal) cannot be mapped to the payload, so the client level stands in.
  const sumsOf = (f: FlatRow) => (isVendorRow(f.row) ? undefined : b.byRowKey[f.row.key]?.sums ?? undefined);
  const classLike = (f: FlatRow): boolean => !isVendorRow(f.row) && (f.row.key.startsWith('class:') || f.row.key.startsWith('chain:'));
  const known = counted.map(sumsOf).filter((x): x is NonNullable<typeof x> => !!x);
  if (known.length === 0 || counted.some((f) => !sumsOf(f) && !classLike(f))) return { value: c, scope: 'client' };
  const sums = known.reduce((a, x) => addSums(a, x), addSums({} as never, {} as never));
  return { value: { ...c, own: guardNoSales(metricsFromSums(sums, b.weeksInMonth), sums), sums }, scope: 'rows' };
};

/** Store names by client location id for the You vs peers card (the payload's `locations` keys are location ids): the row's location name, else its code. */
export const storeNamesOf = (rows: readonly ClassRow[] | undefined): Record<string, string> => {
  const out: Record<string, string> = {};
  (rows ?? []).forEach((r) => {
    if (isNum(r.locationId) && !(String(r.locationId) in out)) {
      const name = r.locationName ?? r.locationCode;
      if (name) out[String(r.locationId)] = name;
    }
  });
  return out;
};

type Tally = Map<string, { label: string; count: number }>;
const tally = (t: Tally, code: string | null | undefined, label: string | null | undefined, by = 1): void => {
  if (code === null || code === undefined || code === '') return;
  const hit = t.get(code);
  if (hit) hit.count += by;
  else t.set(code, { label: label || code, count: by });
};
const toOptions = (t: Tally): FilterOption[] =>
  Array.from(t.entries())
    .map(([code, { label, count }]) => ({ code, label, count }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }) || a.code.localeCompare(b.code));

const classLabel = (r: ClassRow): string | null =>
  r.classCode && r.className && r.classCode !== r.className ? `${r.classCode} · ${r.className}` : r.className ?? r.classCode;

/** Filter option lists from the payload rows (codes = locationCode / category / classCode / vendorKey), with row counts, sorted by label. */
export function optionsFromRows(classRows: ClassRow[], vendorRows?: VendorRow[]): { locations: FilterOption[]; categories: FilterOption[]; classes: FilterOption[]; vendors: FilterOption[] } {
  const locations: Tally = new Map();
  const categories: Tally = new Map();
  const classes: Tally = new Map();
  const vendors: Tally = new Map();
  classRows.forEach((r) => {
    if (r.isUnallocated || r.key === 'unclassified') return;
    tally(locations, r.locationCode, r.locationName);
    tally(categories, r.category, r.category);
    tally(classes, r.classCode, classLabel(r));
  });
  const rows = vendorRows ?? [];
  rows.forEach((r) => {
    if (r.level === 'vendorClass') tally(vendors, r.vendorKey, fixMojibake(r.vendor));
  });
  rows.forEach((r) => {
    if (r.level === 'vendor' && r.vendorKey && !vendors.has(r.vendorKey)) tally(vendors, r.vendorKey, fixMojibake(r.vendor));
  });
  return { locations: toOptions(locations), categories: toOptions(categories), classes: toOptions(classes), vendors: toOptions(vendors) };
}

/** The aging buckets can exist and are wanted: A/B with SKU-level data and receiving, and Freshness shown. */
export function agingShown(capabilities: Capabilities | null | undefined, freshnessShown: boolean): boolean {
  return capabilityAllows(capabilities, 'vendor') && !!capabilities?.hasReceiving && freshnessShown;
}

/** One child class behind a family's figures: its inventory, share of the parent's, and cover. */
export interface FamilyDriver { row: ClassRow; inventory: number | null; share: number | null; wos: number | null }
export const FAMILY_DRIVERS_LIMIT = 3;

/**
 * (M1 §8: the parent is the analysis level): the direct child classes that drive a parent's figures, the
 * `limit` largest by inventory (selected basis), each with its share of the parent's inventory and its cover. Unallocated,
 * Unclassified and vendor-level rows are never drivers; a childless row (or a vendor row) has none. `rows` are the class payload's rows.
 */

export function familyDrivers(parent: AnyRow, rows: readonly AnyRow[], ctx: CellContext, limit = FAMILY_DRIVERS_LIMIT): FamilyDriver[] {
  // a chain parent (`chain:<code>`, no location class id) takes the rows given as its children (childRowsOf).
  const chain = parent.key.startsWith('chain:');
  if (isVendorRow(parent) || (parent.locationClassId === null && !chain)) return [];
  const total = inventoryOf(parent, ctx);
  return rows
    .filter((r): r is ClassRow => !isVendorRow(r) && !r.isUnallocated && r.key !== 'unclassified' && (chain || r.parentLocationClassId === parent.locationClassId))
    .map((r) => {
      const inventory = inventoryOf(r, ctx);
      return { row: r, inventory, share: isNum(inventory) && isNum(total) && total > 0 ? inventory / total : null, wos: r.summary?.wos ?? null };
    })
    .sort((a, b) => (b.inventory ?? -Infinity) - (a.inventory ?? -Infinity))
    .slice(0, Math.max(0, limit));
}

/**
 * Round 2: the location-class rows (one per store, straight from the payload, never the chain rows) whose action is a reorder
 * (Reorder, Reorder · no OTB). A chain row can read "0 to reorder" while a store of it is understocked; the KPI says how many.
 */
export const storeLevelReorders = (payloadRows: AnyRow[], ctx: CellContext): number =>
  payloadRows.filter((r) => {
    if (isVendorRow(r) || r.isUnallocated || r.key === 'unclassified' || !r.summary) return false;
    const a = rowAction(r, ctx);
    return a === 'reorder' || a === 'reorder_no_otb';
  }).length;

//: the compact OTB-by-period table shows the periods up to the plan end and
// one "Later" line summing the rest, instead of a long tail of $0 weeks after the weekly plan has ended.
//
// - The plan end is the partial weekly plan's last planned week (`summary.otbCoveredThrough`, D5) when it has one, else the
// last period of the span with a planned inflow, a receipt or on order due. Null (nothing planned) means no trimming.
// - The Later line sums inflowPlan / received / onOrderDue / otb over the trimmed periods and keeps the running OTB of the last one,
// so the table still ends at the row's total OTB (running OTB governs spendability: RUNNING_OTB_RULE).

export interface OtbLaterLine { count: number; inflowPlan: number; received: number; onOrderDue: number; otb: number; running: number }

const active = (l: OtbPeriodLine): boolean => l.inflowPlan !== 0 || l.received !== 0 || l.onOrderDue !== 0;

/** The last period the plan covers (see the header), or null. */
export const otbPlanEnd = (s: Pick<Summary, 'otbBasis' | 'otbCoveredThrough'> | null | undefined, lines: OtbPeriodLine[]): number | null => {
  if (s && s.otbBasis === 'weekly_plan_partial' && isNum(s.otbCoveredThrough)) return s.otbCoveredThrough;
  const last = [...lines].reverse().find(active);
  return last ? last.period : null;
};

/** The lines up to `planEnd` (at least the first) and the rest summed into one Later line (null when nothing is left over). */
export const trimOtbLines = (lines: OtbPeriodLine[], planEnd: number | null): { shown: OtbPeriodLine[]; later: OtbLaterLine | null } => {
  if (planEnd === null) return { shown: lines, later: null };
  const shown = lines.filter((l, i) => i === 0 || l.period <= planEnd);
  const rest = lines.slice(shown.length);
  if (rest.length === 0) return { shown: lines, later: null };
  const later: OtbLaterLine = { count: rest.length, inflowPlan: 0, received: 0, onOrderDue: 0, otb: 0, running: rest[rest.length - 1].running };
  rest.forEach((l) => { later.inflowPlan += l.inflowPlan; later.received += l.received; later.onOrderDue += l.onOrderDue; later.otb += l.otb; });
  return { shown, later };
};

// "Sales ahead: plan vs LY": plan sales from the target period forward over the OTB span
// (weeks in Weekly, months in Monthly) against last year's sales for the same periods.
//
// - LY is the payload's own alignment: each period cell's `salesLy` (Weekly: the backend's LY week map, LastYearService), so nothing
// is re-aligned here. Sales, plan and LY are retail on every basis (there are no sales at cost).
// - A row's chart reads that row; the dashboard reads the rows the totals count (`totalRows`, pageModel.ts) and sums them per period.
// A per-period sum is null (a gap) when no row has the figure, never a zero.
// - The % difference is plan ÷ LY − 1 where both are known and LY is positive. The totals compare like with like: Σ plan and Σ LY over
// the periods that have both.
// - The note names the periods with no plan, with no LY, or with partial LY (`lyPartial`).

export interface SalesAheadDatum { period: number; name: string; plan: number | null; ly: number | null; diffPct: number | null; planNote: string }
export interface SalesAheadTotals { plan: number | null; ly: number | null; diffPct: number | null }
export interface SalesAhead { data: SalesAheadDatum[]; totals: SalesAheadTotals; note: string | null }

/** The target period and every later period of the OTB span, ascending. */
export const salesAheadPeriods = (targetPeriod: number, spanPeriods: readonly number[] | null | undefined): number[] =>
  Array.from(new Set([targetPeriod, ...(spanPeriods ?? []).filter((p) => p > targetPeriod)])).sort((a, b) => a - b);

const pct = (plan: number | null, ly: number | null): number | null => (isNum(plan) && isNum(ly) && ly > 0 ? plan / ly - 1 : null);
const noun = (grain: Grain): string => (grain === 'weekly' ? 'week' : 'month');

export const salesAhead = (rows: readonly AnyRow[], periods: readonly number[], grain: Grain): SalesAhead => {
  const noPlan: string[] = [];
  const noLy: string[] = [];
  const partial: string[] = [];
  let tPlan = 0, tLy = 0, both = 0;
  const data = periods.map((p): SalesAheadDatum => {
    let plan: number | null = null;
    let ly: number | null = null;
    let lyPartial = false;
    for (const r of rows) {
      const c = r.periods[String(p)];
      if (isNum(c?.salesPlan)) plan = (plan ?? 0) + c.salesPlan;
      if (isNum(c?.salesLy)) ly = (ly ?? 0) + c.salesLy;
      if (c?.lyPartial) lyPartial = true;
    }
    const name = formatPeriodLabel(p, grain);
    if (plan === null) noPlan.push(name);
    if (ly === null) noLy.push(name);
    if (lyPartial) partial.push(name);
    if (plan !== null && ly !== null) { tPlan += plan; tLy += ly; both += 1; }
    const diffPct = pct(plan, ly);
    const planNote = plan === null && ly === null ? `No sales plan or LY sales for this ${noun(grain)}`
      : plan === null ? `No sales plan for this ${noun(grain)}`
        : ly === null ? `No LY sales for this ${noun(grain)}`
          : `Plan vs LY: ${signedPct(diffPct)}${lyPartial ? ' (LY partial)' : ''}`;
    return { period: p, name, plan, ly, diffPct, planNote };
  });
  const totals: SalesAheadTotals = both > 0 ? { plan: tPlan, ly: tLy, diffPct: pct(tPlan, tLy) } : { plan: null, ly: null, diffPct: null };
  let note: string | null;
  if (data.length > 0 && noPlan.length === data.length && noLy.length === data.length) note = `No sales plan or LY sales for these ${noun(grain)}s`;
  else {
    // a long run of periods reads as a count and a range, not a list of every period.
    const span = (names: string[]) => (names.length <= 3 ? names.join(', ') : `${names.length} ${noun(grain)}s (${names[0]} to ${names[names.length - 1]})`);
    const parts = [
      noPlan.length ? `No sales plan for ${span(noPlan)}` : '',
      noLy.length ? `No LY sales for ${span(noLy)}` : '',
      partial.length ? `LY is partial for ${span(partial)}` : '',
    ].filter(Boolean);
    note = parts.length ? parts.join(' · ') : null;
  }
  return { data, totals, note };
};

/** The report-level chart: the rows the totals count (`totalRows`) over the target period forward through the OTB span. */
export const salesAheadForRows = (counted: readonly { row: AnyRow }[], targetPeriod: number, spanPeriods: readonly number[] | null | undefined, grain: Grain): SalesAhead =>
  salesAhead(counted.map((f) => f.row), salesAheadPeriods(targetPeriod, spanPeriods), grain);

// CSV export (Plan 3 spec 6 "Logic"): exactly the visible table. One line per flat row,
// the label indented two spaces per depth, every value the cell text the table shows (so the CSV
// and the screen never disagree), then three flag columns read from the target period's cell.
// Text cells go through VP's csvCell formula guard; numbers only get quoted for commas. SKU
// placeholder rows ("Loading SKUs…") are a loading state, not data, and are skipped. When Suggested
// reorder is exported, a "Reorder basis" column follows the flags; when Health is exported a "Deadstock" Y column (the M1 §6 flag) precedes it (lookback / last 52 weeks /
// mixed, blank without units), since the table shows the basis as a suffix the cell text leaves out.
// a dollar column's header names its price basis ("On order (cost)", "Inflow (retail)"); `Inventory at
// cost` is cost on every basis and says so already.
// numeric cells are plain numbers (no "$", no thousands separators, ASCII minus, percents as
// percent numbers, so 45.7 is 45.7%) so a spreadsheet can sum and sort them; text cells stay the table's text.
// A column that compares with a plan prorated for an in-progress period (Inflows vs plan) carries "WTD" / "MTD" in its header.
// The WOS / MOS cell is split into "WOS" and "Target WOS" (MOS in Monthly) columns. `includeExpanded` exports the
// expanded flat rows with a "Level" column (0 = top level); false exports the top-level rows only.

export const CSV_FLAG_HEADERS = ['Estimated', 'Allocated'] as const;
export const CSV_REORDER_BASIS_HEADER = 'Reorder basis';
/** the M1 §6 flag as a "Y" column, after the flags, when the Health column is exported (a flag beside the status). */
export const CSV_DEADSTOCK_HEADER = DEADSTOCK_LABEL;
const LABEL_ID = 'label';
const REORDER_ID = 'suggestedReorder';
const HEALTH_ID = 'health';
const INDENT = '  ';
const TEXT_KINDS: MetricDefinition['kind'][] = ['text', 'status', 'action'];

const isTextColumn = (d: MetricDefinition): boolean => d.id === LABEL_ID || TEXT_KINDS.includes(d.kind);
const flag = (on: boolean | undefined): string => (on ? 'Y' : '');
/** csvCell's formula guard, applied to the label before the indent (a spreadsheet trims the spaces). */
const labelCell = (text: string, depth: number): string => csvCell(INDENT.repeat(depth) + (CSV_FORMULA_RE.test(text) ? `'${text}` : text), false);

/** The label column first, then the given columns in order (without a repeated label). */
const csvColumns = (columns: MetricDefinition[]): MetricDefinition[] => [metricById(LABEL_ID), ...columns.filter((c) => c.id !== LABEL_ID)];

/** `labels` (optional) overrides header text by column id, e.g. a page's "Actual Inv (start of period)". */
export interface CsvOptions {
  /** true: every flat row shown (expanded rows too) plus a "Level" column; false: top-level rows only. Absent: the rows as given, no Level column. */
  includeExpanded?: boolean;
}

export const CSV_LEVEL_HEADER = 'Level';
const COVER_ID = 'cover';
/** Whole cents are plenty for dollars, units and weeks; fewer digits would lose real information a spreadsheet user sums. */
const NUMBER_DIGITS = 2;
/** The text the table shows for stock with no sales ("52+" in Weekly, "12+" in Monthly: units.ts coverCap); a number would claim a cover that does not exist. */
const noSalesCover = (ctx: CellContext): string => `${coverCap(ctx.grain)}+`;

/** A finite number as a plain decimal string (ASCII minus, no grouping, no "-0"). */
export const csvNumber = (v: number, digits = NUMBER_DIGITS): string => {
  const r = Number(v.toFixed(digits));
  return String(Object.is(r, -0) ? 0 : r);
};

const isFiniteNumPageModel = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const SCALE_TO_PCT_IDS = new Set(['bm_salesChangeLy_diff']);
const isPctColumn = (d: MetricDefinition): boolean => d.kind === 'pct' || SCALE_TO_PCT_IDS.has(d.id);

/** A cell's CSV text: the plain number for a numeric column, else the cell text. A numeric column with no number is blank. */
const numericText = (c: MetricDefinition, v: CellValue): string | null => {
  if (c.id === LABEL_ID || c.id === COVER_ID) return null;
  const numericKind = c.kind === 'currency' || c.kind === 'units' || c.kind === 'weeks' || c.kind === 'pct';
  // Benchmark index / Peers columns are 'text' kind but carry a number; only when the text itself shows one (a gated peer prints words).
  const textShowsNumber = /\d/.test(v.text);
  const numberInTextColumn = c.kind === 'text' && isFiniteNumPageModel(v.value) && textShowsNumber;
  if (!numericKind && !numberInTextColumn) return null;
  if (!isFiniteNumPageModel(v.value) || !textShowsNumber) return '';
  return csvNumber(isPctColumn(c) ? v.value * 100 : v.value);
};

const coverCsv = (fr: FlatRow, v: CellValue, ctx: CellContext): { wos: string; target: string } => {
  const s = fr.row.summary;
  const target = resolveTarget(s, ctx.wosOverride).target;
  const wos = v.value === Infinity ? noSalesCover(ctx) : isFiniteNumPageModel(v.value) ? csvNumber(v.value) : '';
  return { wos, target: isFiniteNumPageModel(target) ? csvNumber(target) : '' };
};

export const buildReportCsv = (flatRows: FlatRow[], columns: MetricDefinition[], ctx: CellContext, labels?: Record<string, string>, options: CsvOptions = {}): string => {
  const cols = csvColumns(columns);
  const withBasis = cols.some((c) => c.id === REORDER_ID);
  const withLevel = options.includeExpanded === true;
  const withDeadstock = cols.some((c) => c.id === HEALTH_ID);
  const extraHeaders = [...CSV_FLAG_HEADERS, ...(withDeadstock ? [CSV_DEADSTOCK_HEADER] : []), ...(withBasis ? [CSV_REORDER_BASIS_HEADER] : [])];
  const shown = flatRows.filter((fr) => fr.kind !== 'skuPlaceholder' && (options.includeExpanded !== false || fr.depth === 0));
  const rowCtxOf = (fr: FlatRow): CellContext => (fr.pctOfParent || fr.distributedOtb ? { ...ctx, pctOfParent: fr.pctOfParent, distributedOtb: fr.distributedOtb ?? null } : ctx);
  const valuesOf = shown.map((fr) => cols.map((c) => c.cell(fr.row, rowCtxOf(fr))));
  // a column whose cells compare with a plan PRORATED for the days elapsed (inflows / markdowns vs compare in a
  // period in progress) says so in its header, "Inflows vs plan WTD (%)": the cells carry the "WTD" / "MTD" tag the table shows.
  const prorated = (i: number): string | null => valuesOf.map((vs) => vs[i].tag).find((t) => t === 'WTD' || t === 'MTD') ?? null;
  const headerOf = (c: MetricDefinition, i: number): string => {
    const label = labels?.[c.id] ?? columnLabel(c, ctx.grain);
    const tag = prorated(i);
    const marked = tag && !label.includes(tag) ? `${label} ${tag}` : label;
    const withBasisText = c.kind === 'currency' ? `${marked}${basisSuffix(ctx.basis)}` : marked;
    return c.kind === 'pct' ? `${withBasisText} (%)` : SCALE_TO_PCT_IDS.has(c.id) ? `${withBasisText} (pts)` : withBasisText;
  };
  const headerCells: string[] = [];
  cols.forEach((c, i) => {
    headerCells.push(csvCell(headerOf(c, i), true));
    if (c.id === LABEL_ID && withLevel) headerCells.push(CSV_LEVEL_HEADER);
    if (c.id === COVER_ID) headerCells.push(csvCell(`Target ${columnLabel(c, ctx.grain)}`, true));
  });
  const header = [...headerCells, ...extraHeaders.map((h) => csvCell(h, true))].join(',');
  const lines = shown.map((fr, r) => {
    const cellValues = valuesOf[r];
    const values: string[] = [];
    cellValues.forEach((v, i) => {
      const c = cols[i];
      if (c.id === LABEL_ID) {
        values.push(labelCell(v.text, fr.depth));
        if (withLevel) values.push(String(fr.depth));
      } else if (c.id === COVER_ID) {
        const split = coverCsv(fr, v, ctx);
        values.push(split.wos, split.target);
      } else {
        const n = numericText(c, v);
        values.push(n ?? csvCell(v.text, isTextColumn(c)));
      }
    });
    // Estimated follows the exported columns' own flags (metricDefinitions' inventory-derived rule), so
    // a row is marked exactly when the table shows an italic value in it; the other two flags are per cell.
    const estimated = cellValues.some((v) => v.flags.estimated);
    const basis = withBasis ? [csvCell(fr.row.summary?.suggestedReorderUnits == null ? '' : reorderBasisText(fr.row.summary?.reorderBasis, ctx.grain), true)] : [];
    const dead = withDeadstock ? [flag(fr.row.summary?.deadstock)] : [];
    // Allocated likewise follows the exported cells' own flags: only a plan value (or a comparison against the plan) is tagged.
    const allocated = cellValues.some((v) => v.flags.allocated);
    return [...values, flag(estimated), flag(allocated), ...dead, ...basis].join(',');
  });
  return [header, ...lines].join('\r\n');
};

const pad = (n: number): string => String(n).padStart(2, '0');

/** The CSV's report name: the report key with dashes, except Weekly Action, which reads "Inventory Action" everywhere user-facing (its key is unchanged). */
export const CSV_REPORT_NAMES: Partial<Record<ReportKey, string>> = { weekly_action: 'inventory-action' };
/** `<report-name>-<yyyy-mm-dd>-<view>.csv`, dated in the browser's local time. */
export const csvFileName = (report: ReportKey, state: ReportViewState, now: Date = new Date()): string =>
  `${CSV_REPORT_NAMES[report] ?? report.replace(/_/g, '-')}-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${state.view}.csv`;

export const exportReportCsv = (report: ReportKey, state: ReportViewState, flatRows: FlatRow[], columns: MetricDefinition[], ctx: CellContext, labels?: Record<string, string>, options?: CsvOptions): void =>
  downloadCsv(buildReportCsv(flatRows, columns, ctx, labels, options), csvFileName(report, state));
